import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FaEdit, FaCheck, FaTimes, FaCalendar, FaUser, FaUserPlus, FaPhone, FaMapMarkerAlt, FaHome, FaBriefcase, FaFileAlt, FaHistory, FaArrowLeft, FaChevronLeft, FaChevronRight, FaChevronDown, FaChevronUp, FaReplyAll, FaSms, FaListAlt, FaInfoCircle, FaFilePdf, FaPlus } from 'react-icons/fa';
import api from '../config/api';
import { generateFicheClientPdf } from '../utils/generateFicheClientPdf';
import { useAuth } from '../contexts/AuthContext';
import { useRouteParams } from '../contexts/RouteParamsContext';
import { useModalScrollLock } from '../hooks/useModalScrollLock';
import { getEtatsGroupedByPhase } from '../utils/etatsByPhase';
import { formatRdvDateTime, formatRdvDateOnly, formatRdvTimeOnly } from '../utils/formatRdvDateTime';
import { splitSlotDisplayName } from '../utils/splitSlotDisplayName';
import { ficheHasR2Placed, normalizeIsR2Field } from '../utils/ficheR2Placed';
import {
  shouldDisplayDecalageRequest,
  isRdvDatePastByAtLeastOneDay,
} from '../utils/ficheTableIndicators';
import { differenceInMinutes, differenceInHours, differenceInDays, differenceInMonths, format, addMonths } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import './FicheDetail.css';
import FicheCompletudeSection from '../components/FicheCompletudeSection';
import {
  resolveSignerProduitKind,
  SignerPacSelect,
  SignerProduitPacPvFields,
  SignerBonusAnnonceSelect,
} from '../components/SignerProduitFormFields';
import { validateSignerCompteRenduForm, alertSignerCompteRenduValidation } from '../utils/validateSignerCompteRendu';
import { validateCreateRdvForm, alertCreateRdvValidation } from '../utils/validateCreateRdvForm';
import { getEtatContrastColor } from '../utils/etatColorContrast';
import {
  COMPTE_RENDU_COMMERCIAL_OPTIONS,
  applyCompteRenduOptionChange,
  getCompteRenduOptionLabel,
  isCommercialAnnulerReproSimpleOption,
  resolveCompteRenduOptionFromCr,
  resolveOptionKey,
} from '../utils/compteRenduCommercialOptions';
import CompteRenduEarlyVerification from '../components/CompteRenduEarlyVerification';
import CodeVerificationModal from '../components/CodeVerificationModal';
import { isBeforeRdvDateTime, isRdvDateBeforeToday, formatLocalYmd } from '../utils/compteRenduEarlyVerification';
import { resolveConfRevenuAfterTypeContratChange } from '../utils/revenuTypeContrat';
import {
  isPolicyClosedSlot,
  canCreateRdvOnPolicyClosedSlot,
  isSlotVisuallyClosed,
  isDbClosedAvailabilityCell,
} from '../utils/planningSlotPolicy';
import { formatCommercialDisplayName } from '../utils/commercialDisplayName';
import {
  normalizeSituationConjugaleForm,
  showConfProfessionMrFields,
  showConfProfessionMmeFields,
  clearHiddenConfProfessionFields,
  pickProfessionOrContratValue,
} from '../utils/confSituationConjugale';
/** Style option / select « Nouvel état » : fond = couleur état, texte contrasté. */
function getEtatSelectOptionStyle(color) {
  const bg = color || '#cccccc';
  return {
    backgroundColor: bg,
    color: getEtatContrastColor(bg),
  };
}
function getRdvSeulRawLabel(fiche) {
  const raw = String(fiche?.conf_presence_couple ?? '').toUpperCase().trim();
  if (!raw) return null;
  if (raw === 'MR SEUL SANS MME' || (raw.includes('MR') && raw.includes('SANS MME'))) {
    return 'MR SEUL SANS MME';
  }
  if (
    raw === 'MME SEULE SANS MR' ||
    raw === 'MME SEUL SANS MR' ||
    (raw.includes('MME') && raw.includes('SANS MR'))
  ) {
    return 'MME SEULE SANS MR';
  }
  return null;
}

const RDV_SEUL_PRESENCE_OPTIONS = [
  { value: 'MR SEUL SANS MME', label: 'MR SEUL SANS MME' },
  { value: 'MME SEULE SANS MR', label: 'MME SEULE SANS MR' },
  { value: 'RAS PRESENCE CLIENT(S)', label: 'RAS PRESENCE CLIENT(S)' },
];

/** Mode de chauffage (VARCHAR en base) : affichage tel quel. */
function modeChauffageAffiche(confProp, modeProp) {
  const v = confProp ?? modeProp;
  if (v == null || v === '') return '';
  return String(v).trim();
}

/** Session commercial (fonction 5) : champs non modifiables dans le détail fiche. */
const COMMERCIAL_SESSION_READONLY_FIELDS = new Set([
  'commentaire_qualite',
  'conf_commentaire_produit',
  'motif_qualif',
  'commentaire',
  'etude',
  'details_etude',
  'conf_deja_etude',
  'conf_deja_fait_etude',
  'conf_details_etude',
  'etude_raison',
  'date_rdv_time',
]);

function hasConfValue(v) {
  return v != null && String(v).trim() !== '';
}

function resolveDetailsEtudeForFiche(fiche) {
  const agent = String(fiche?.details_etude ?? '').trim();
  if (agent) return agent;
  return String(fiche?.conf_details_etude ?? '').trim() || null;
}

function normalizeDetailItemLabel(s) {
  return String(s ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Pseudo confirmateur / commercial : toujours en majuscules à l'affichage. */
function formatAgentPseudoDisplay(value) {
  if (value == null || value === '') return value;
  return String(value).trim().toUpperCase();
}

function normalizePlanningDayName(dayName) {
  return String(dayName || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function isAdminOrBackofficeFonction(fonction) {
  return [1, 2, 7, 11].includes(Number(fonction));
}

function isSameDateDecalageSelection(selectMinutes) {
  return !selectMinutes || selectMinutes === '0' || selectMinutes === 'same';
}

function addMinutesToDateTimeString(dateTimeValue, minutesToAdd) {
  const s = String(dateTimeValue ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const [, y, mo, d, h, mi, se = '00'] = m;
  const base = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(se)
  );
  if (Number.isNaN(base.getTime())) return null;
  base.setMinutes(base.getMinutes() + Number(minutesToAdd || 0));
  const pad = (n) => String(n).padStart(2, '0');
  return `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())} ${pad(base.getHours())}:${pad(base.getMinutes())}:${pad(base.getSeconds())}`;
}

function splitDateTimeForInput(dateTimeValue, fallback = {}) {
  const s = String(dateTimeValue ?? '').trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/);
  if (m) return { date: m[1], time: m[2] };
  return { date: fallback.date || '', time: fallback.time || '' };
}

function getDateTimeLocalValue(dateValue, timeValue) {
  if (!dateValue) return '';
  return `${dateValue}T${timeValue || '00:00'}`;
}

/** Détecte l’affichage « Confirmer » : id 7 ou libellé d’état (API peut envoyer autre chose). */
function isEtatConfirmerLike(etatId, etatTitre) {
  if (Number(etatId) === 7) return true;
  const t = normalizeDetailItemLabel(etatTitre)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return t === 'confirmer' || /^confirmer\b/i.test(t);
}

/** Cadre qualité confirmation : Qualité Confirmation (4), RP Confirmation (13) ; backoffice (11) si login contient « backoffice ». */
function canShowQualiteConfirmationBackofficePanel(user) {
  if (!user) return false;
  const fn = Number(user.fonction);
  if (fn === 4 || fn === 13) return true;
  if (fn === 11) {
    return String(user.login || '').trim().toLowerCase().includes('backoffice');
  }
  return false;
}

/**
 * Mise en évidence détails CONFIRMER : style inline (couleur + gras) pour ne pas dépendre du CSS du modal.
 * Garde aussi les classes pour cohérence avec FicheDetail.css.
 */
function getConfirmerDetailHighlight(etatId, etatTitre, itemLabel) {
  const L = normalizeDetailItemLabel(itemLabel);
  const bold = { fontWeight: 700 };
  const agentBold = { fontWeight: 900 };
  // Commercial : toujours en bleu, quel que soit l'etat (gere aussi « Commercial 1 », « Commercial 2 »)
  if (/^Commercial(\s*\d+)?$/i.test(L)) {
    return {
      className: 'fiche-detail-etat-confirmer-val--commercial',
      style: { ...agentBold, color: '#1d4ed8', fontSize: '15px', textTransform: 'uppercase' },
      dataHl: 'commercial',
    };
  }
  // Confirmateur : toujours en rouge, quel que soit l'etat (gere aussi « Confirmateur 1 », « Confirmateur 2 », « Confirmateur 3 »)
  if (/^Confirmateur(\s*\d+)?$/i.test(L)) {
    return {
      className: 'fiche-detail-etat-confirmer-val--confirmateur',
      style: { ...agentBold, color: '#b91c1c', fontSize: '15px', textTransform: 'uppercase' },
      dataHl: 'confirmateur',
    };
  }
  // Audité par (qualité qualification) : même mise en évidence que Confirmateur
  if (/^Audité par$/i.test(L)) {
    return {
      className: 'fiche-detail-etat-confirmer-val--confirmateur',
      style: { ...agentBold, color: '#b91c1c', fontSize: '15px', textTransform: 'uppercase' },
      dataHl: 'confirmateur',
    };
  }
  // Date RDV : surlignage vert encadre, uniquement quand l'etat est CONFIRMER
  if (L === 'Date RDV' && isEtatConfirmerLike(etatId, etatTitre)) {
    return {
      className: 'fiche-detail-etat-confirmer-val--rdv',
      style: {
        ...bold,
        color: '#15803d',
        border: '4px solid #15803d',
        borderRadius: '6px',
        padding: '2px 8px',
        display: 'inline-block',
        backgroundColor: '#f0fdf4',
      },
      dataHl: 'rdv',
    };
  }
  // Date signature (= date_rdv_time de la ligne fiches_histo SIGNER) : rose = couleur état SIGNER
  if (/^Signé le$/i.test(L) || /^Date signature$/i.test(L) || /^DATE SIGNATURE$/i.test(L)) {
    const rose = '#FF3380';
    return {
      className: 'fiche-detail-etat-signe-le',
      style: {
        fontWeight: 900,
        color: rose,
        border: `4px solid ${rose}`,
        borderRadius: '6px',
        padding: '2px 8px',
        display: 'inline-block',
        backgroundColor: '#fff0f6',
        fontSize: '15px',
      },
      dataHl: 'signe-le',
    };
  }
  return null;
}

function renderHeureRdvAvantBadge(heureRdvAvant) {
  if (!heureRdvAvant) return null;
  const timeOnly = formatRdvTimeOnly(heureRdvAvant);
  if (!timeOnly) return null;
  return (
    <span
      className="fiche-detail-etat-confirmer-val--rdv-avant"
      data-confirmer-hl="rdv-avant"
    >
      Avant: {timeOnly}
    </span>
  );
}

/** Cadre infos demande(s) de décalage sous la Date RDV (état CONFIRMER actuel / historique). */
function normalizeDecalageDateTime(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim().replace('T', ' ');
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[ ](\d{1,2}):(\d{2})/);
  if (!m) return s;
  return `${m[1]} ${String(m[2]).padStart(2, '0')}:${m[3]}`;
}

function isSameDecalageDateTime(a, b) {
  const na = normalizeDecalageDateTime(a);
  const nb = normalizeDecalageDateTime(b);
  return Boolean(na && nb && na === nb);
}

function parseDecalageDatePart(value) {
  const m = String(value || '').match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

function isDecalageSansHeure(decalage) {
  if (!decalage?.date_nouvelle) return true;
  return isSameDecalageDateTime(decalage.date_nouvelle, decalage.date_prevu);
}

function isDecalagePending(decalage) {
  return decalage?.id_etat == null || Number(decalage.id_etat) === 1;
}

function getDecalageDemandeStatus(decalage) {
  if (isDecalagePending(decalage)) return 'pending';
  const id = Number(decalage?.id_etat);
  const titre = String(decalage?.etat_dec || '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (id === 3 || id === 4 || titre.includes('REFUS')) return 'refused';
  if (id === 2 || titre.includes('ACCEPT') || titre.includes('VALID')) return 'accepted';
  if (id === 6 || titre.includes('ANNUL')) return 'refused';
  return 'pending';
}

function getDecalageDemandeBorderColor(status) {
  if (status === 'accepted') return '#16a34a'; // vert
  if (status === 'refused') return '#dc2626'; // rouge
  return '#ea580c'; // orangé — en cours
}

function renderDecalageDemandesUnderRdv(decalages, actions = null) {
  if (!Array.isArray(decalages) || decalages.length === 0) return null;
  const sorted = [...decalages].sort((a, b) => {
    const ta = a?.date_creation ? new Date(a.date_creation).getTime() : 0;
    const tb = b?.date_creation ? new Date(b.date_creation).getTime() : 0;
    return tb - ta;
  });
  const canTreat = !!actions?.canTreat;
  const isLoading = !!actions?.isLoading;
  return (
    <div
      className="fiche-etat-decalage-demandes"
      style={{
        marginTop: '10px',
        width: '100%',
        maxWidth: '720px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      {sorted.map((d, idx) => {
        const etatLabel =
          (d.etat_dec && String(d.etat_dec).trim()) ||
          (isDecalagePending(d) ? 'En attente' : `État #${d.id_etat}`);
        const expediteur =
          (d.expediteur_pseudo && String(d.expediteur_pseudo).trim()) ||
          (d.expediteur != null ? `ID ${d.expediteur}` : '—');
        const message = d.message != null && String(d.message).trim() !== '' ? String(d.message).trim() : '—';
        const dateCreation = d.date_creation
          ? new Date(d.date_creation).toLocaleString('fr-FR')
          : '—';
        const isPending = isDecalagePending(d);
        const status = getDecalageDemandeStatus(d);
        const borderColor = getDecalageDemandeBorderColor(status);
        const showActions = canTreat && isPending && d?.id;
        return (
          <div
            key={d.id || `decalage-${idx}`}
            className={`fiche-etat-decalage-demande-box fiche-etat-decalage-demande-box--${status}`}
            style={{
              background: 'transparent',
              color: '#ffffff',
              border: `2px solid ${borderColor}`,
              borderRadius: '6px',
              padding: '10px 12px',
              fontSize: '13px',
              fontWeight: 600,
              lineHeight: 1.45,
            }}
          >
            <div
              style={{
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                marginBottom: '6px',
                color: borderColor,
              }}
            >
              Décalage{sorted.length > 1 ? ` #${idx + 1}` : ''}
              {isDecalageSansHeure(d) ? ' · Sans heure' : ''}
            </div>
            <div>
              <strong>État :</strong> {etatLabel}
            </div>
            <div>
              <strong>Qui a envoyé :</strong> {expediteur}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              <strong>Message :</strong> {message}
            </div>
            <div>
              <strong>Date création :</strong> {dateCreation}
            </div>
            {showActions && (
              <div
                className="fiche-etat-decalage-demande-actions"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginTop: '10px',
                }}
              >
                <button
                  type="button"
                  className="fiche-etat-decalage-btn fiche-etat-decalage-btn--accept"
                  disabled={isLoading}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    actions.onAccept?.(d);
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: '#16a34a',
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor: isLoading ? 'wait' : 'pointer',
                  }}
                >
                  Acceptée
                </button>
                <button
                  type="button"
                  className="fiche-etat-decalage-btn fiche-etat-decalage-btn--refuse"
                  disabled={isLoading}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    actions.onRefuse?.(d);
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: '#dc2626',
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor: isLoading ? 'wait' : 'pointer',
                  }}
                >
                  Refusée
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Date d'appel exploitable pour affichage (détails fiche, pas historique). */
function parseFicheDateAppel(fiche) {
  if (!fiche) return null;
  if (fiche.date_appel_time) {
    const d = new Date(fiche.date_appel_time);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (fiche.date_appel_date) {
    const d = new Date(fiche.date_appel_date);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (fiche.date_appel != null && fiche.date_appel !== '') {
    const n = Number(fiche.date_appel);
    if (!Number.isNaN(n)) {
      const d = new Date(n * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  return null;
}

/** Texte relatif après la date absolue : « Jeudi, il y a 1 mois et 1 semaine » (détails fiche uniquement). */
function formatDateAppelRelativeDescription(date) {
  if (!date || Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const weekday = format(date, 'EEEE', { locale: frLocale });
  const dayLabel = weekday ? `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ` : '';

  if (date > now) {
    return `${dayLabel}dans le futur`;
  }

  const diffMins = differenceInMinutes(now, date);
  const diffHrs = differenceInHours(now, date);
  const diffDays = differenceInDays(now, date);

  if (diffDays >= 365) {
    return `${dayLabel}il y a longtemps`;
  }

  if (diffMins < 1) {
    return `${dayLabel}à l'instant`;
  }
  if (diffMins < 60) {
    return `${dayLabel}il y a ${diffMins} minute${diffMins > 1 ? 's' : ''}`;
  }
  if (diffHrs < 24) {
    return `${dayLabel}il y a ${diffHrs} heure${diffHrs > 1 ? 's' : ''}`;
  }
  if (diffDays < 7) {
    return `${dayLabel}il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
  }
  if (diffDays < 30) {
    const w = Math.floor(diffDays / 7);
    const d = diffDays % 7;
    let rel = `il y a ${w} semaine${w > 1 ? 's' : ''}`;
    if (d > 0) rel += `, ${d} jour${d > 1 ? 's' : ''}`;
    return `${dayLabel}${rel}`;
  }
  // ≥ 30 jours et &lt; 1 an : mois calendaires + reste en semaines (et jours si besoin)
  const fullMonths = differenceInMonths(now, date);
  if (fullMonths >= 1) {
    const afterMonths = addMonths(date, fullMonths);
    const remDays = Math.max(0, differenceInDays(now, afterMonths));
    const weeks = Math.floor(remDays / 7);
    const days = remDays % 7;
    let rel = `il y a ${fullMonths} mois`;
    if (weeks > 0) {
      rel += ` et ${weeks} semaine${weeks > 1 ? 's' : ''}`;
    } else if (days > 0) {
      rel += ` et ${days} jour${days > 1 ? 's' : ''}`;
    }
    return `${dayLabel}${rel}`;
  }
  const w = Math.floor(diffDays / 7);
  const d = diffDays % 7;
  let rel = `il y a ${w} semaine${w > 1 ? 's' : ''}`;
  if (d > 0) rel += `, ${d} jour${d > 1 ? 's' : ''}`;
  return `${dayLabel}${rel}`;
}

/** La fiche est passée au moins une fois par « honoré à suivre » (état 9) suite à un compte rendu : ligne d’historique ou CR approuvé. */
function ficheHonoreASuivreViaCompteRendu(fiche) {
  if (!fiche) return false;
  const hist = Array.isArray(fiche.historique) ? fiche.historique : [];
  const fromHisto = hist.some(
    (h) =>
      Number(h.id_etat) === 9 &&
      (h.from_compte_rendu === true || h.from_compte_rendu === 1)
  );
  if (fromHisto) return true;
  const crs = Array.isArray(fiche.comptes_rendus) ? fiche.comptes_rendus : [];
  return crs.some(
    (cr) => Number(cr.id_etat_final) === 9 && cr.statut === 'approved'
  );
}

const EMPTY_CONF_FORM_BASE = {
  produit: '',
  id_confirmateur: '',
  id_confirmateur_2: '',
  id_confirmateur_3: '',
  conf_rdv_date: '',
  conf_rdv_time: '',
  conf_rdv_avec: '',
  situation_conjugale: '',
  conf_appel_tunisie_avec: '',
  conf_deja_etude: '',
  conf_deja_fait_etude: '',
  conf_details_etude: '',
  conf_profession_monsieur: '',
  conf_type_contrat_mr: '',
  conf_profession_madame: '',
  conf_type_contrat_madame: '',
  conf_revenu: '',
  conf_credit: '',
  conf_mode_chauffage: '',
  conf_complement_chauffage: '',
  conf_consommation_electricite: '',
  conf_consommation_chauffage: '',
  conf_rdv_annule_precedent: '',
  conf_presence_couple: '',
  conf_orientation_toiture: '',
  conf_zones_ombres: '',
  conf_site_classe: '',
  nb_pans: '',
  annee_systeme_chauffage: '',
  surface_chauffee: '',
  consommation_chauffage: '',
  surface_habitable: '',
  conf_commentaire_produit: '',
  id_commercial_2: '',
  is_r2: '',
  date_r1: '',
  id_commercial_r1: '',
  commentaire_r1: '',
};

/** Normalise is_r2 → 0 | 1 | null (Buffer MySQL, OUI/NON, 0/1, …) */
function normalizeIsR2Value(v) {
  return normalizeIsR2Field(v);
}

/** datetime-local → MySQL DATETIME */
function dateR1ToMysql(value) {
  if (value == null || String(value).trim() === '') return null;
  const s = String(value).trim().replace('T', ' ');
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(s)) return `${s}:00`;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s} 00:00:00`;
  return s;
}

function mysqlToDatetimeLocal(value) {
  if (value == null || String(value).trim() === '') return '';
  const s = String(value).trim().replace(' ', 'T');
  const m = s.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
  return m ? m[1] : '';
}

/**
 * Valide + construit le payload R2 pour création RDV / confirmation.
 * @returns {{ ok: true, data: object } | { ok: false, message: string }}
 */
function buildR2PayloadFromForm(form) {
  const isR2 = normalizeIsR2Value(form?.is_r2);
  if (isR2 === null) {
    return { ok: false, message: 'Veuillez indiquer si le RDV est un R2 (OUI / NON).' };
  }
  if (isR2 === 0) {
    return {
      ok: true,
      data: {
        is_r2: 0,
        date_r1: null,
        id_commercial_r1: null,
        commentaire_r1: null,
      },
    };
  }
  const dateR1 = dateR1ToMysql(form?.date_r1);
  const idComR1 =
    form?.id_commercial_r1 && String(form.id_commercial_r1).trim() !== ''
      ? parseInt(form.id_commercial_r1, 10)
      : null;
  const commentaireR1 = (form?.commentaire_r1 || '').trim();
  if (!dateR1) {
    return { ok: false, message: 'Date R1 obligatoire lorsque R2 = OUI.' };
  }
  if (!idComR1 || !Number.isFinite(idComR1)) {
    return { ok: false, message: 'Commercial R1 obligatoire lorsque R2 = OUI.' };
  }
  if (!commentaireR1) {
    return { ok: false, message: 'Commentaire R1 obligatoire lorsque R2 = OUI.' };
  }
  return {
    ok: true,
    data: {
      is_r2: 1,
      date_r1: dateR1,
      id_commercial_r1: idComR1,
      commentaire_r1: commentaireR1,
    },
  };
}

/** Normalise OUI/NON pour les selects de confirmation / RDV. */
function normalizeOuiNonSelect(value) {
  if (value == null || String(value).trim() === '') return '';
  const u = String(value).trim().toUpperCase();
  if (u === 'OUI' || u === 'YES' || u === '1' || u === 'TRUE') return 'OUI';
  if (u === 'NON' || u === 'NO' || u === '0' || u === 'FALSE') return 'NON';
  return String(value).trim();
}

/** Normalise MR / MME / AUTRE pour conf_rdv_avec et conf_appel_tunisie_avec. */
function normalizeMrMmeSelect(value) {
  if (value == null || String(value).trim() === '') return '';
  const s = String(value).trim();
  const u = s.toUpperCase();
  if (u === 'MR' || u === 'MONSIEUR' || u === 'M.' || u === 'M') return 'MR';
  if (u === 'MME' || u === 'MADAME' || u === 'MLLE' || u === 'MLE') return 'MME';
  if (u === 'AUTRE') return 'AUTRE';
  return s;
}

/**
 * Commentaire de la dernière confirmation (état 7) :
 * priorité à l'historique CONFIRMER, puis champ fiche si encore confirmée.
 */
function getLastConfirmationCommentaire(ficheData) {
  if (!ficheData) return '';
  const hist = Array.isArray(ficheData.historique) ? ficheData.historique : [];
  for (let i = hist.length - 1; i >= 0; i -= 1) {
    const h = hist[i];
    if (Number(h?.id_etat) !== 7) continue;
    const c = String(h?.conf_commentaire_produit || '').trim();
    if (c) return c;
  }
  const fromFiche = String(ficheData.conf_commentaire_produit || '').trim();
  if (fromFiche) return fromFiche;
  return String(ficheData.commentaire || '').trim();
}

/** Dernière ligne d'historique CONFIRMER (état 7), pour préremplir une reconfirmation. */
function getLastConfirmationHisto(ficheData) {
  const hist = Array.isArray(ficheData?.historique) ? ficheData.historique : [];
  for (let i = hist.length - 1; i >= 0; i -= 1) {
    if (Number(hist[i]?.id_etat) === 7) return hist[i];
  }
  return null;
}

function pickConfPrefillValue(...candidates) {
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') return c;
  }
  return '';
}

/**
 * Formulaire « Informations de confirmation » (état 7) prérempli depuis la fiche
 * (+ dernière confirmation historisée si la fiche a déjà été confirmée).
 * Sessions : admin, backoffice, RE/RP confirmation, confirmateur.
 */
function buildConfFormStateFromFiche(ficheData, user) {
  const now = new Date();
  const defaultDate = now.toISOString().split('T')[0];
  const defaultTime = now.toTimeString().split(' ')[0].substring(0, 5);

  if (!ficheData) {
    return { ...EMPTY_CONF_FORM_BASE, conf_rdv_date: defaultDate, conf_rdv_time: defaultTime };
  }

  const lastConf = getLastConfirmationHisto(ficheData);

  let rdvDate = defaultDate;
  let rdvTime = defaultTime;
  const rdvSource = pickConfPrefillValue(ficheData.date_rdv_time, lastConf?.date_rdv_time, lastConf?.histo_date_rdv_time);
  if (rdvSource) {
    const parts = String(rdvSource).split(/[\sT]/);
    if (parts[0]) rdvDate = parts[0];
    if (parts[1]) rdvTime = parts[1].substring(0, 5);
  }

  const histoConf = Array.isArray(ficheData.confirmateurs_from_histo) && ficheData.confirmateurs_from_histo.length > 0
    ? ficheData.confirmateurs_from_histo.map((id) => String(id))
    : null;
  let idConf1 = '';
  let idConf2 = '';
  let idConf3 = '';
  // Session confirmateur (6) : conf1 = soi-même uniquement (prérempli) ; 2/3 manuels via +.
  // Autres sessions (admin, BO, RE, RP) : préremplir depuis la fiche / histo.
  if (Number(user?.fonction) === 6) {
    idConf1 = user?.id ? String(user.id) : '';
    if (!isNouvelleConfirmationConf1Seul(ficheData)) {
      const a = ficheData.id_confirmateur ? String(ficheData.id_confirmateur) : (lastConf?.id_confirmateur ? String(lastConf.id_confirmateur) : '');
      const b = ficheData.id_confirmateur_2 ? String(ficheData.id_confirmateur_2) : (lastConf?.id_confirmateur_2 ? String(lastConf.id_confirmateur_2) : '');
      const c = ficheData.id_confirmateur_3 ? String(ficheData.id_confirmateur_3) : (lastConf?.id_confirmateur_3 ? String(lastConf.id_confirmateur_3) : '');
      const prev = [a, b, c].filter((id) => id && id !== idConf1);
      idConf2 = prev[0] || '';
      idConf3 = prev[1] || '';
    }
  } else {
    idConf1 = pickConfPrefillValue(
      ficheData.id_confirmateur,
      lastConf?.id_confirmateur,
      histoConf ? histoConf[0] : ''
    );
    idConf2 = pickConfPrefillValue(
      ficheData.id_confirmateur_2,
      lastConf?.id_confirmateur_2,
      histoConf ? histoConf[1] : ''
    );
    idConf3 = pickConfPrefillValue(
      ficheData.id_confirmateur_3,
      lastConf?.id_confirmateur_3,
      histoConf ? histoConf[2] : ''
    );
    if (isNouvelleConfirmationConf1Seul(ficheData)) {
      idConf2 = '';
      idConf3 = '';
    }
  }

  const confAppelTunisie = (() => {
    const raw = pickConfPrefillValue(
      ficheData.conf_appel_tunisie_avec,
      lastConf?.conf_appel_tunisie_avec,
      ficheData.entretien
    );
    return raw ? normalizeMrMmeSelect(raw) : '';
  })();

  const produit = String(
    pickConfPrefillValue(
      ficheData.produit,
      ficheData.conf_produit,
      lastConf?.conf_produit,
      lastConf?.produit
    )
  );

  const dejaEtude = normalizeOuiNonSelect(
    pickConfPrefillValue(
      ficheData.conf_deja_etude,
      ficheData.conf_deja_fait_etude,
      lastConf?.conf_deja_etude,
      lastConf?.conf_deja_fait_etude,
      ficheData.etude
    )
  );
  const presenceCoupleRaw = pickConfPrefillValue(
    ficheData.conf_presence_couple,
    lastConf?.conf_presence_couple
  );
  const presenceCoupleNorm = getRdvSeulRawLabel({ conf_presence_couple: presenceCoupleRaw }) || presenceCoupleRaw;

  const strOrEmpty = (v) => (v != null && String(v).trim() !== '' ? String(v) : '');

  return {
    produit,
    id_confirmateur: idConf1 ? String(idConf1) : '',
    id_confirmateur_2: idConf2 ? String(idConf2) : '',
    id_confirmateur_3: idConf3 ? String(idConf3) : '',
    conf_rdv_date: rdvDate,
    conf_rdv_time: rdvTime,
    conf_rdv_avec: normalizeMrMmeSelect(
      pickConfPrefillValue(ficheData.conf_rdv_avec, lastConf?.conf_rdv_avec)
    ),
    situation_conjugale: normalizeSituationConjugaleForm(ficheData.situation_conjugale),
    conf_appel_tunisie_avec: confAppelTunisie,
    conf_deja_etude: dejaEtude,
    conf_deja_fait_etude: dejaEtude,
    conf_details_etude: pickConfPrefillValue(
      ficheData.conf_details_etude,
      lastConf?.conf_details_etude,
      ficheData.details_etude
    ),
    conf_profession_monsieur: strOrEmpty(
      pickConfPrefillValue(
        ficheData.conf_profession_monsieur,
        lastConf?.conf_profession_monsieur,
        ficheData.profession_mr
      )
    ),
    conf_type_contrat_mr: strOrEmpty(
      pickConfPrefillValue(
        ficheData.conf_type_contrat_mr,
        lastConf?.conf_type_contrat_mr,
        ficheData.type_contrat_mr
      )
    ),
    conf_profession_madame: strOrEmpty(
      pickConfPrefillValue(
        ficheData.conf_profession_madame,
        lastConf?.conf_profession_madame,
        ficheData.profession_madame
      )
    ),
    conf_type_contrat_madame: strOrEmpty(
      pickConfPrefillValue(
        ficheData.conf_type_contrat_madame,
        lastConf?.conf_type_contrat_madame,
        ficheData.type_contrat_madame
      )
    ),
    conf_revenu: pickConfPrefillValue(ficheData.conf_revenu, lastConf?.conf_revenu, ficheData.revenu_foyer),
    conf_credit: pickConfPrefillValue(ficheData.conf_credit, lastConf?.conf_credit, ficheData.credit_foyer),
    conf_mode_chauffage: strOrEmpty(
      pickConfPrefillValue(
        ficheData.conf_mode_chauffage,
        lastConf?.conf_mode_chauffage,
        ficheData.mode_chauffage
      )
    ),
    conf_complement_chauffage: pickConfPrefillValue(
      ficheData.conf_complement_chauffage,
      lastConf?.conf_complement_chauffage
    ),
    conf_consommation_electricite: strOrEmpty(
      pickConfPrefillValue(
        ficheData.conf_consommation_electricite,
        lastConf?.conf_consommation_electricite,
        ficheData.consommation_electricite
      )
    ),
    conf_consommation_chauffage: strOrEmpty(
      pickConfPrefillValue(
        ficheData.conf_consommation_chauffage,
        lastConf?.conf_consommation_chauffage,
        ficheData.consommation_chauffage
      )
    ),
    conf_rdv_annule_precedent: normalizeOuiNonSelect(
      pickConfPrefillValue(ficheData.conf_rdv_annule_precedent, lastConf?.conf_rdv_annule_precedent)
    ),
    conf_presence_couple: presenceCoupleNorm || '',
    conf_orientation_toiture: strOrEmpty(
      pickConfPrefillValue(
        ficheData.conf_orientation_toiture,
        lastConf?.conf_orientation_toiture,
        ficheData.orientation_toiture
      )
    ),
    conf_zones_ombres: strOrEmpty(
      pickConfPrefillValue(ficheData.conf_zones_ombres, lastConf?.conf_zones_ombres, ficheData.zones_ombres)
    ),
    conf_site_classe: strOrEmpty(
      pickConfPrefillValue(ficheData.conf_site_classe, lastConf?.conf_site_classe, ficheData.site_classe)
    ),
    nb_pans: strOrEmpty(pickConfPrefillValue(ficheData.nb_pans, lastConf?.nb_pans)),
    annee_systeme_chauffage: strOrEmpty(
      pickConfPrefillValue(ficheData.annee_systeme_chauffage, lastConf?.annee_systeme_chauffage)
    ),
    surface_chauffee: strOrEmpty(pickConfPrefillValue(ficheData.surface_chauffee, lastConf?.surface_chauffee)),
    consommation_chauffage: pickConfPrefillValue(
      ficheData.consommation_chauffage,
      lastConf?.consommation_chauffage
    ),
    surface_habitable: strOrEmpty(pickConfPrefillValue(ficheData.surface_habitable, lastConf?.surface_habitable)),
    conf_commentaire_produit: getLastConfirmationCommentaire(ficheData),
    id_commercial_2: strOrEmpty(
      pickConfPrefillValue(
        ficheData.id_commercial_2 != null && Number(ficheData.id_commercial_2) > 0
          ? ficheData.id_commercial_2
          : '',
        lastConf?.id_commercial_2 != null && Number(lastConf.id_commercial_2) > 0
          ? lastConf.id_commercial_2
          : ''
      )
    ),
    is_r2: (() => {
      const fromFiche = normalizeIsR2Field(ficheData.is_r2);
      if (fromFiche === 1) return 'OUI';
      if (fromFiche === 0) return 'NON';
      const fromHisto = normalizeIsR2Field(lastConf?.is_r2);
      if (fromHisto === 1) return 'OUI';
      if (fromHisto === 0) return 'NON';
      return '';
    })(),
    date_r1: mysqlToDatetimeLocal(ficheData.date_r1 || lastConf?.date_r1),
    id_commercial_r1: strOrEmpty(
      pickConfPrefillValue(
        ficheData.id_commercial_r1 != null && Number(ficheData.id_commercial_r1) > 0
          ? ficheData.id_commercial_r1
          : '',
        lastConf?.id_commercial_r1 != null && Number(lastConf.id_commercial_r1) > 0
          ? lastConf.id_commercial_r1
          : ''
      )
    ),
    commentaire_r1: pickConfPrefillValue(ficheData.commentaire_r1, lastConf?.commentaire_r1),
  };
}

/**
 * Formulaire création RDV prérempli depuis une fiche déjà confirmée (tous champs conf + commentaire confirmation).
 */
function buildRdvFormStateFromFiche(ficheData, user, { dateStr, timeStr } = {}) {
  const conf = buildConfFormStateFromFiche(ficheData, user);
  const datePart = dateStr || conf.conf_rdv_date || '';
  const timePart = timeStr || conf.conf_rdv_time || '';
  return {
    ...conf,
    date_rdv_time: datePart && timePart ? `${datePart} ${timePart}` : '',
    id_etat_final: 7,
    is_urgent:
      ficheData?.rdv_urgent === 1 ||
      ficheData?.rdv_urgent === true ||
      ficheData?.qualification_code === 'RDV_URGENT',
  };
}

// Créneaux horaires
const TIME_SLOTS = [
  { hour: '09:00:00', name: '9H ( 9h uniquement )' },
  { hour: '11:00:00', name: '11H ( 11h à 12h )' },
  { hour: '13:00:00', name: '13H ( 13h à 14h30 )' },
  { hour: '16:00:00', name: '16H ( 15h à 17h )' },
  { hour: '18:00:00', name: '18H ( 18h à 19h )' },
  { hour: '19:30:00', name: '20H ( 19h30 à 20h )' }
];

// États sans transition possible (aligné sur backend management.routes.js) : pas de « nouvel état »
const ETATS_SANS_NOUVEL_ETAT = [22, 25, 26, 34, 35]; // ANNULER 2 FOIS, REFUSER 2 FOIS, RDV ANNULER 2 FOIS, HHC FINANCEMENT, HHC TECHNIQUE

/** Admin (1, 7), Backoffice (11), RE Confirmation (14), RP Confirmation (13) : changement d'état autorisé même depuis un état définitif. */
const FONCTIONS_BYPASS_ETAT_DEFINITIF = [1, 7, 11, 13, 14];

function canBypassEtatDefinitif(fonction) {
  return FONCTIONS_BYPASS_ETAT_DEFINITIF.includes(Number(fonction));
}
const ETATS_AUTORISES_VERS_CONFIRMER_PLANNING = [1, 2, 5, 8, 9, 11, 12, 16, 19];

/** REFUSER / SIGNER RETRACTER (+ variantes 2×) : à la (re)confirmation, uniquement le confirmateur 1 (créateur du RDV). */
const ETATS_NOUVELLE_CONFIRMATION_CONF1_SEUL = [12, 16, 25, 38];

function isNouvelleConfirmationConf1Seul(ficheOrEtatId) {
  const id =
    typeof ficheOrEtatId === 'object' && ficheOrEtatId != null
      ? Number(ficheOrEtatId.id_etat_final)
      : Number(ficheOrEtatId);
  if (ETATS_NOUVELLE_CONFIRMATION_CONF1_SEUL.includes(id)) return true;
  if (typeof ficheOrEtatId === 'object' && ficheOrEtatId) {
    const t = String(ficheOrEtatId.etat_titre || ficheOrEtatId.etat_final || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
    if (t === 'refuser' || t.startsWith('refuser ')) return true;
    if (t.includes('signer') && t.includes('retract')) return true;
  }
  return false;
}

/**
 * Slots confirmateur actuels de la fiche (pas l'ordre chronologique histo).
 * Reprise : nouveau = conf1 ; ancien conf1 → conf2 ; ancien conf2 → conf3.
 * REFUSER / SIGNER RETRACTER : conf1 seul.
 */
function applyConfirmateurRepriseFromFiche(userId, ficheData) {
  const uid = userId != null ? String(userId) : '';
  if (!uid) {
    return { id_confirmateur: '', id_confirmateur_2: '', id_confirmateur_3: '' };
  }
  if (isNouvelleConfirmationConf1Seul(ficheData)) {
    return { id_confirmateur: uid, id_confirmateur_2: '', id_confirmateur_3: '' };
  }
  const a = ficheData?.id_confirmateur ? String(ficheData.id_confirmateur) : '';
  const b = ficheData?.id_confirmateur_2 ? String(ficheData.id_confirmateur_2) : '';
  const c = ficheData?.id_confirmateur_3 ? String(ficheData.id_confirmateur_3) : '';
  const histoLen = Array.isArray(ficheData?.confirmateurs_from_histo)
    ? ficheData.confirmateurs_from_histo.length
    : 0;
  const hasPrior = !!(a || b || c || histoLen > 0 || Number(ficheData?.id_etat_final) === 7);
  if (!hasPrior) {
    return { id_confirmateur: uid, id_confirmateur_2: '', id_confirmateur_3: '' };
  }
  if (a === uid) {
    const id2 = b && b !== uid ? b : '';
    const id3 = c && c !== uid && c !== id2 ? c : '';
    return { id_confirmateur: uid, id_confirmateur_2: id2, id_confirmateur_3: id3 };
  }
  const prev = [a, b, c].filter((id) => id && id !== uid);
  return {
    id_confirmateur: uid,
    id_confirmateur_2: prev[0] || '',
    id_confirmateur_3: prev[1] || ''
  };
}

/** Commentaire (motif_qualif) en détails fiche : annuler, annuler 2×, RDV annuler, RDV annuler 2×, refuser, refuser 2×, hors cible air air, âge/doublon/locataire, financement, HC confirmateur, HHC financement à vérifier. */
const ETATS_AVEC_COMMENTAIRE_MOTIF = [5, 6, 11, 12, 22, 23, 24, 25, 26, 29, 34];
/** À la validation du passage d'état : commentaire obligatoire (motif_qualif). */
const ETATS_MOTIF_QUALIF_REQUIS = [5, 6, 11, 12, 22, 24, 25, 26, 29];

/**
 * Préremplissage commentaire depuis le titre du sous-état.
 * Exception : sous-état « AUTRE » → laisser le commentaire vide (saisie libre).
 */
function getCommentPrefillFromSousEtatTitre(titre) {
  const t = String(titre || '').trim();
  if (!t) return '';
  if (t.toUpperCase() === 'AUTRE') return '';
  return t;
}

// Helper pour calculer le timeKey à partir d'une heure (HH:MM:SS)
// Évite les problèmes de fuseau horaire en calculant directement les secondes depuis minuit UTC
function hourToTimeKey(hour) {
  const [hours, minutes, seconds] = hour.split(':').map(Number);
  return hours * 3600 + minutes * 60 + (seconds || 0);
}

// Helper pour formater une date en YYYY-MM-DD en heure locale (évite le décalage UTC)
function formatDateLocal(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Numéro de semaine ISO (lundi = début de semaine)
function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// Associe une heure "HH:MM" ou "HH:MM:SS" au créneau planning (hour key backend)
function timeToSlotHour(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const parts = timeStr.trim().split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) || 0;
  if (isNaN(h)) return null;
  const minutes = h * 60 + m;
  if (minutes >= 540 && minutes <= 659) return '09:00:00';
  if (minutes >= 660 && minutes <= 779) return '11:00:00';
  if (minutes >= 780 && minutes <= 899) return '13:00:00'; // 13h-14h59
  if (minutes >= 900 && minutes <= 1079) return '16:00:00'; // 15h-17h59
  if (minutes >= 1080 && minutes <= 1169) return '18:00:00';
  if (minutes >= 1170 && minutes <= 1200) return '19:30:00';
  return null;
}

function lookupAvailabilityCell(availMap, dateStr, slotHour) {
  if (!availMap || !dateStr) return null;
  const dayMap = availMap[dateStr] || availMap[String(dateStr).slice(0, 10)];
  if (!dayMap || typeof dayMap !== 'object') return null;
  if (slotHour && dayMap[slotHour] != null) return dayMap[slotHour];
  const wanted = String(slotHour || '').slice(0, 8);
  if (!wanted) return null;
  const key = Object.keys(dayMap).find((k) => String(k).slice(0, 8) === wanted);
  return key ? dayMap[key] : null;
}

function isClosedAvailabilityCell(cell) {
  if (!cell) return false;
  return (
    Number(cell.is_closed) === 1 ||
    cell.is_closed === true ||
    cell.is_closed === '1' ||
    Number(cell.is_policy_closed) === 1 ||
    cell.is_policy_closed === true ||
    cell.is_policy_closed === '1'
  );
}

function isPolicyClosedAvailabilityCell(cell, dep, dateStr, hour) {
  if (cell && (Number(cell.is_policy_closed) === 1 || cell.is_policy_closed === true || cell.is_policy_closed === '1')) {
    return true;
  }
  return isPolicyClosedSlot(dep, dateStr, hour);
}

function isZeroAvailabilityCell(cell) {
  if (!cell) return false;
  return cell.nbr_com === 0 || cell.nbr_com === '0';
}

/** Créneau sans disponibilité saisie (null / vide) — ni 0 ni > 0 */
function isSansDispoAvailabilityCell(cell) {
  if (!cell) return false;
  return cell.nbr_com === null || cell.nbr_com === undefined || cell.nbr_com === '';
}

/** Créneau blindé / surplus : plein exact (2/2) ou dépassé (3/2) → warning. */
function isBlindOrSurplusSlot(planningWeekData, dateStr, slotHour, availCell) {
  if (!dateStr || !slotHour) return false;
  const timeKey = hourToTimeKey(slotHour);
  const dayPlanning = planningWeekData?.[dateStr]?.time?.[timeKey];
  const rdvCount = Array.isArray(dayPlanning?.planning) ? dayPlanning.planning.length : 0;
  const rawAv =
    dayPlanning?.av !== undefined && dayPlanning?.av !== null
      ? dayPlanning.av
      : availCell?.nbr_com;
  if (rawAv === undefined || rawAv === null || rawAv === '') return false;
  const avail = Number(rawAv);
  if (!Number.isFinite(avail)) return false;
  if (avail <= 0) return rdvCount > 0; // 0 dispo déjà couvert par zero
  // Blindé dès le plein (2/2), pas seulement après dépassement (3/2)
  return rdvCount >= avail;
}

function isPlanningSlotClosedError(error) {
  const data = error?.response?.data;
  if (data?.code === 'PLANNING_SLOT_CLOSED') return true;
  const msg = String(data?.message || error?.message || '');
  return /cr[eé]neau/i.test(msg) && /ferm[eé]/i.test(msg);
}

/** Plage horaire alignée sur les buckets planning (voir timeToSlotMinuteRanges) pour filtrer date_rdv_time sur le dashboard. */
function getDashboardTimeRangeForPlanningSlot(slotHour) {
  const map = {
    '09:00:00': { time_debut: '09:00:00', time_fin: '10:59:59' },
    '11:00:00': { time_debut: '11:00:00', time_fin: '12:59:59' },
    '13:00:00': { time_debut: '13:00:00', time_fin: '14:59:59' },
    '16:00:00': { time_debut: '15:00:00', time_fin: '17:59:59' },
    '18:00:00': { time_debut: '18:00:00', time_fin: '19:29:59' },
    '19:30:00': { time_debut: '19:30:00', time_fin: '20:00:00' }
  };
  return map[slotHour] || { time_debut: '00:00:00', time_fin: '23:59:59' };
}

/** Ouvre le dashboard avec filtres : RDV confirmés (état 7), jour + créneau + département (CP). */
function buildDashboardUrlForPlanningSlot({ dep, date, slotHour }) {
  const range = getDashboardTimeRangeForPlanningSlot(slotHour);
  const params = new URLSearchParams({
    fiche_search: '1',
    page: '1',
    limit: '999999',
    date_champ: 'date_rdv_time',
    date_debut: date,
    date_fin: date,
    time_debut: range.time_debut,
    time_fin: range.time_fin,
    id_etat_final: '7'
  });
  if (dep != null && String(dep).trim() !== '') {
    params.set('cp', String(dep).trim());
  }
  return `/dashboard?${params.toString()}`;
}

const FicheDetail = ({
  ficheHash,
  onClose,
  isModal = false,
  initialFocusHistoriqueEtats = false,
  initialTab = null,
}) => {
  // En mode modal, utiliser le contexte personnalisé, sinon utiliser useParams
  const routeParams = useRouteParams();
  const routerParams = useParams();
  const routerNavigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const params = isModal && routeParams.params ? routeParams.params : routerParams;
  const navigate = isModal && routeParams.navigate ? routeParams.navigate : routerNavigate;
  
  const hashFromParams = params?.id;
  const hash = ficheHash || hashFromParams; // Utiliser le prop si fourni, sinon utiliser les params
  const { user, hasPermission, permissions } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('fiches'); // 'fiches', 'modifica', 'planning', 'sms', 'pdf'

  useEffect(() => {
    if (isModal) return;
    const tab = searchParams.get('tab');
    if (tab === 'pdf') setActiveTab('pdf');
    if (tab === 'sms') setActiveTab('sms');
  }, [isModal, searchParams]);

  // Vérifier si l'utilisateur est qualité qualification (fonction 2, 8, 12)
  const userFonction = user?.fonction != null ? Number(user.fonction) : null;
  const isQualiteQualif = userFonction === 2 || userFonction === 8 || userFonction === 12;
  const isQualiteConfirmation = userFonction === 4;
  const isREConfirmation = userFonction === 14;
  const isRPConfirmation = userFonction === 13;
  /** Session partenaire : fonction 9, ou libellé fonction/centre « Partenaire ». */
  const isPartenaireProfil =
    Number(user?.fonction) === 9 ||
    (typeof user?.fonction_titre === 'string' && /partenaire/i.test(user.fonction_titre)) ||
    (typeof user?.centre_titre === 'string' && /partenaire/i.test(user.centre_titre));
  /** Modal détail fiche partenaire : onglets limités, sauf si « fixer RDV » est autorisé. */
  const isPartenaireModalLimitedTabs = Boolean(isModal && isPartenaireProfil);

  const { data: partenaireFixerRdvEnabled = false } = useQuery(
    ['partenaire-fixer-rdv-me', user?.id],
    async () => {
      const res = await api.get('/management/global-settings/partenaire-fixer-rdv/me');
      return !!res.data?.data?.enabled;
    },
    { enabled: isPartenaireProfil, staleTime: 60 * 1000 }
  );
  const partenaireCanFixerRdv = isPartenaireProfil && partenaireFixerRdvEnabled;

  useEffect(() => {
    if (!isModal || !initialTab) return;
    const allowed = isPartenaireModalLimitedTabs
      ? (partenaireCanFixerRdv ? ['fiches', 'planning'] : ['fiches'])
      : ['fiches', 'modifica', 'affectation', 'planning', 'sms', 'pdf'];
    if (allowed.includes(initialTab)) setActiveTab(initialTab);
  }, [isModal, initialTab, isPartenaireModalLimitedTabs, partenaireCanFixerRdv]);
  const isConfirmateurSession = userFonction === 6;
  const getConfirmateurLabel = (id) => {
    if (!id) return '';
    const found = confirmateurs?.find(c => String(c.id) === String(id));
    return found?.pseudo || `ID: ${id}`;
  };
  
  // Rediriger vers l'onglet fiches si l'utilisateur qualité qualification est sur un onglet masqué
  useEffect(() => {
    if (isQualiteQualif && (activeTab === 'planning' || activeTab === 'sms')) {
      setActiveTab('fiches');
    }
  }, [isQualiteQualif, activeTab]);

  // Modal partenaire : Fiches seul, ou Fiches + Planning si fixer RDV autorisé
  useEffect(() => {
    if (!isPartenaireModalLimitedTabs) return;
    const allowed = partenaireCanFixerRdv ? ['fiches', 'planning'] : ['fiches'];
    if (!allowed.includes(activeTab)) {
      setActiveTab('fiches');
    }
  }, [isPartenaireModalLimitedTabs, partenaireCanFixerRdv, activeTab]);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [bulkEditingEtude, setBulkEditingEtude] = useState(false);
  const [bulkEditValues, setBulkEditValues] = useState({});
  const [bulkEditInitial, setBulkEditInitial] = useState({});
  const [bulkEditSaving, setBulkEditSaving] = useState(false);
  const etudeBulkFieldsRef = useRef({});
  const [planningWeek, setPlanningWeek] = useState(null);
  const [planningYear, setPlanningYear] = useState(null);
  const [planningDep, setPlanningDep] = useState(null);
  
  // État pour le modal de création de RDV
  const [showRdvModal, setShowRdvModal] = useState(false);
  const [rdvSubmitting, setRdvSubmitting] = useState(false);
  const [etatSubmitting, setEtatSubmitting] = useState(false);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const confirmSubmittingRef = useRef(false);
  const [selectedSlot, setSelectedSlot] = useState(null); // { date, hour }
  const [slotCodeModal, setSlotCodeModal] = useState(null);
  const slotCodeVerifiedRef = useRef(false);
  const pendingAfterSlotCodeRef = useRef(null);
  const pendingKnownSlotStatusRef = useRef(null);
  const slotBypassContextRef = useRef(null);
  const [showConfirmConfFields, setShowConfirmConfFields] = useState(true);
  const [rdvFormData, setRdvFormData] = useState({
    date_rdv_time: '',
    id_etat_final: 7, // CONFIRMER par défaut
    is_urgent: false, // RDV_URGENT
    id_confirmateur: '',
    id_confirmateur_2: '',
    id_confirmateur_3: '',
    produit: '',
    conf_rdv_avec: '',
    conf_appel_tunisie_avec: '',
    conf_deja_etude: '',
    conf_deja_fait_etude: '',
    conf_details_etude: '',
    conf_profession_monsieur: '',
    conf_type_contrat_mr: '',
    conf_profession_madame: '',
    conf_type_contrat_madame: '',
    conf_revenu: '',
    conf_credit: '',
    conf_mode_chauffage: '',
    conf_complement_chauffage: '',
    conf_consommation_electricite: '',
    conf_consommation_chauffage: '',
    conf_rdv_annule_precedent: '',
    conf_presence_couple: '',
    // Champs spécifiques PV
    surface_habitable: '',
    conf_orientation_toiture: '',
    conf_zones_ombres: '',
    conf_site_classe: '',
    nb_pans: '',
    // Champs spécifiques PAC
    surface_chauffee: '',
    consommation_chauffage: '',
    annee_systeme_chauffage: '',
    conf_commentaire_produit: '',
    id_commercial_2: '',
    is_r2: '',
    date_r1: '',
    id_commercial_r1: '',
    commentaire_r1: '',
  });

  // État pour le formulaire de confirmation
  const [selectedEtat, setSelectedEtat] = useState(null);
  const [histoConfirmateurId, setHistoConfirmateurId] = useState('');
  const [histoConfirmateur2Id, setHistoConfirmateur2Id] = useState('');
  const [histoConfirmateur3Id, setHistoConfirmateur3Id] = useState('');
  const [showHistoConfirmateur2, setShowHistoConfirmateur2] = useState(false);
  const [showHistoConfirmateur3, setShowHistoConfirmateur3] = useState(false);
  const [showConfConfirmateur2, setShowConfConfirmateur2] = useState(false);
  const [showConfConfirmateur3, setShowConfConfirmateur3] = useState(false);
  const [showRdvConfirmateur2, setShowRdvConfirmateur2] = useState(false);
  const [showRdvConfirmateur3, setShowRdvConfirmateur3] = useState(false);
  const [decalageAcceptModal, setDecalageAcceptModal] = useState(null); // { decalage, id_etat, step }
  const [decalageAcceptDate, setDecalageAcceptDate] = useState('');
  const [decalageAcceptTime, setDecalageAcceptTime] = useState('');
  const [confFormData, setConfFormData] = useState({
    ...EMPTY_CONF_FORM_BASE,
    conf_orientation_toiture: '',
    conf_zones_ombres: '',
    conf_site_classe: '',
    nb_pans: '',
    // Champs techniques PAC
    annee_systeme_chauffage: '',
    surface_chauffee: '',
    consommation_chauffage: '',
    conf_commentaire_produit: '',
    id_commercial_2: '',
    is_r2: '',
    date_r1: '',
    id_commercial_r1: '',
    commentaire_r1: '',
  });
  /** Si l’utilisateur choisit Confirmer avant que ficheData soit chargé : réhydrater dès que la fiche arrive. */
  const confFormHydratePendingRef = useRef(false);

  useEffect(() => {
    if (selectedEtat === 7) {
      setShowConfirmConfFields(true);
    }
  }, [selectedEtat]);

  // État pour le formulaire NRP
  const [nrpFormData, setNrpFormData] = useState({
    date_appel_date: '',
    date_appel_time: '',
    id_sous_etat: '',
    conf_commentaire_produit: ''
  });

  // État pour les formulaires d'autres états (8, 13, 16, 19, 44, 45)
  const [etatFormData, setEtatFormData] = useState({
    // Pour état 8 (ANNULER À REPROGRAMMER)
    conf_rdv_date: '',
    conf_rdv_time: '',
    id_sous_etat: '',
    conf_rdv_avec: '',
    conf_commentaire_produit: '',
    motif_qualif: '',
    // Pour état 19 (RAPPEL POUR BUREAU)
    date_rappel_date: '',
    date_rappel_time: '',
    auto_saisie: '',
    // Pour états 13, 44, 45 (SIGNER)
    date_sign_date: '',
    date_sign_time: '',
    produit: '',
    id_commercial: '',
    id_commercial_2: '',
    pseudo: '',
    ph3_pac: 'reau',
    ph3_rr_model: '',
    ph3_puissance: '',
    ph3_puissance_pv: '',
    ph3_ballon: '',
    ph3_marque_ballon: '',
    ph3_alimentation: '',
    ph3_type: '',
    ph3_prix: '',
    ph3_installateur: '',
    conf_consommations: '',
    ph3_bonus_30: '',
    valeur_mensualite: '',
    ph3_mensualite: '',
    ph3_attente: '',
    nbr_annee_finance: '',
    credit_immobilier: '',
    credit_autre: ''
  });

  // État pour le formulaire de décalage
  const [decalageFormData, setDecalageFormData] = useState({
    select_minutes: '0',
    id_confirmateur: '',
    message: '',
    date_prevu: '',
    nouvelle_date: ''
  });

  // État pour le compte rendu commercial
  const [compteRenduOption, setCompteRenduOption] = useState('');
  const [editingCompteRendu, setEditingCompteRendu] = useState(null);
  const [earlyCrVerified, setEarlyCrVerified] = useState(false);

  // État pour l'onglet Affectation (un seul commercial)
  const [affectationCommercial, setAffectationCommercial] = useState('');
  const [affectationSaving, setAffectationSaving] = useState(false);

  const saveAffectationFromTab = async () => {
    const ficheId = ficheData?.id != null ? Number(ficheData.id) : null;
    if (!ficheId) {
      alert('Fiche non chargée. Réessayez dans quelques instants.');
      return;
    }
    setAffectationSaving(true);
    try {
      if (!affectationCommercial) {
        const res = await api.post('/affectations/desaffecter', { fiches_ids: [ficheId] });
        const errs = res.data?.data?.erreurs;
        if (errs?.length) {
          throw new Error(errs.map((e) => e.error || 'Erreur').join('\n'));
        }
      } else {
        const res = await api.post('/affectations/affecter', {
          fiches_ids: [ficheId],
          id_commercial: parseInt(affectationCommercial, 10),
          allow_any_etat: true,
        });
        const errs = res.data?.data?.erreurs;
        if (errs?.length) {
          throw new Error(errs.map((e) => e.error || 'Erreur').join('\n'));
        }
      }
      queryClient.invalidateQueries(['fiche', hash]);
      queryClient.invalidateQueries(['fiches']);
      queryClient.invalidateQueries(['modifica', hash]);
      alert('Affectation enregistrée.');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Erreur lors de l\'enregistrement de l\'affectation');
    } finally {
      setAffectationSaving(false);
    }
  };
  
  // États pour le formulaire de validation
  const [confRdvAvecValue, setConfRdvAvecValue] = useState('');
  const [confPresenceCoupleValue, setConfPresenceCoupleValue] = useState('');
  // Édition inline de Date RDV dans le bloc « État Actuel CONFIRMER »
  const [dateRdvInlineEdit, setDateRdvInlineEdit] = useState('');
  const [showDateRdvHistory, setShowDateRdvHistory] = useState(false);
  /** Édition inline du commentaire dans la carte État actuel (double-clic) */
  const [etatActuelCommentEdit, setEtatActuelCommentEdit] = useState(null); // { field, value } | null
  // Liste déroulante de validation RDV (état actuel CONFIRMER)
  const [validationDropdownOpen, setValidationDropdownOpen] = useState(false);
  // Référence vers le bouton qui ouvre la liste pour calculer la position du menu (rendu via portail
  // pour ne pas être tronqué par un parent en overflow:hidden et pour éviter de déborder de la fenêtre).
  const validationButtonRef = useRef(null);
  const [validationDropdownPosition, setValidationDropdownPosition] = useState(null);
  const [rdvSeulDropdownOpen, setRdvSeulDropdownOpen] = useState(false);
  const [showAbsenceCoupleHistory, setShowAbsenceCoupleHistory] = useState(false);

  const updateValidationDropdownPosition = () => {
    if (!validationButtonRef.current) return;
    const rect = validationButtonRef.current.getBoundingClientRect();
    const DROPDOWN_WIDTH = 240;
    const DROPDOWN_HEIGHT = 156; // ~3 boutons * ~52px
    const GAP = 6;
    const PADDING = 8;
    const viewportH = typeof window !== 'undefined' ? window.innerHeight : 0;
    const viewportW = typeof window !== 'undefined' ? window.innerWidth : 0;
    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < DROPDOWN_HEIGHT + GAP + PADDING && spaceAbove > spaceBelow;
    const top = openUpward
      ? Math.max(PADDING, rect.top - GAP - DROPDOWN_HEIGHT)
      : Math.min(viewportH - DROPDOWN_HEIGHT - PADDING, rect.bottom + GAP);
    const left = Math.min(
      Math.max(PADDING, rect.left),
      Math.max(PADDING, viewportW - DROPDOWN_WIDTH - PADDING)
    );
    setValidationDropdownPosition({ top, left, width: DROPDOWN_WIDTH });
  };

  useEffect(() => {
    if (!validationDropdownOpen) {
      setValidationDropdownPosition(null);
      return undefined;
    }
    updateValidationDropdownPosition();
    const onResize = () => updateValidationDropdownPosition();
    const onScroll = () => updateValidationDropdownPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [validationDropdownOpen]);
  const [showHistorique, setShowHistorique] = useState(false); // État pour contrôler l'affichage de l'historique
  const [showEtatActuel, setShowEtatActuel] = useState(true); // État actuel rétractable au clic
  const historiqueEtatsAnchorRef = useRef(null);
  const etatChangeSectionRef = useRef(null);
  /** Évite de régénérer le PDF plusieurs fois pour la même URL ?tab=pdf */
  const autoPdfFromQueryRef = useRef(false);

  // Contrôle Qualité (états signer) : formulaire par fiche (clé = hash)
  const [cqFormByHash, setCqFormByHash] = useState({});

  // Récupérer les données de référence
  const { data: centres } = useQuery('centres', async () => {
    const res = await api.get('/management/centres');
    return res.data.data || [];
  });

  const { data: agents } = useQuery('agents', async () => {
    const res = await api.get('/management/utilisateurs');
    return (res.data.data || []).filter(u => u.fonction === 3);
  });

  const { data: commerciaux } = useQuery('commerciaux', async () => {
    const res = await api.get('/management/utilisateurs');
    return (res.data.data || []).filter(u => u.fonction === 5);
  });

  const { data: confirmateurs } = useQuery('confirmateurs', async () => {
    const res = await api.get('/management/utilisateurs');
    return (res.data.data || []).filter(u => u.fonction === 6 && (u.etat > 0 || u.etat == null));
  });

  // Admin (1, 7), Backoffice (11), RP Confirmation (13), RE Confirmation (14)
  // Confirmateur (6) : pas de picker histo — auto (soi) sauf formulaire CONFIRMER
  const showHistoConfirmateurDropdown = [1, 7, 11, 13, 14].includes(Number(user?.fonction));

  // Session confirmateur : conf1 = uniquement soi ; conf2/3 = liste complète
  const confirmateur1SelectOptions = useMemo(() => {
    if (isConfirmateurSession && user?.id) {
      const self = (confirmateurs || []).find((c) => Number(c.id) === Number(user.id));
      return self
        ? [self]
        : [{ id: user.id, pseudo: user.pseudo || `ID: ${user.id}` }];
    }
    return confirmateurs || [];
  }, [isConfirmateurSession, user?.id, user?.pseudo, confirmateurs]);

  const { data: professions } = useQuery('professions', async () => {
    const res = await api.get('/management/professions');
    return res.data.data || [];
  });

  const { data: typeContrat } = useQuery('type-contrat', async () => {
    const res = await api.get('/management/type-contrat');
    return res.data.data || [];
  });

  const applyConfTypeContratChange = useCallback((field, value) => {
    setConfFormData((prev) => {
      const next = { ...prev, [field]: value };
      next.conf_revenu = resolveConfRevenuAfterTypeContratChange(
        prev.conf_revenu,
        next.conf_type_contrat_mr,
        next.conf_type_contrat_madame,
        typeContrat
      );
      return next;
    });
  }, [typeContrat]);

  const { data: produits } = useQuery('produits', async () => {
    try {
      const res = await api.get('/management/produits');
      return res.data.data || [];
    } catch (error) {
      console.warn('Impossible de charger les produits:', error);
      return [];
    }
  });

  const { data: installateurs } = useQuery('installateurs', async () => {
    try {
      const res = await api.get('/management/installateurs');
      return res.data.data || [];
    } catch (error) {
      console.warn('Impossible de charger les installateurs:', error);
      return [];
    }
  });

  const { data: typesFinancement = [] } = useQuery('financement', async () => {
    try {
      const res = await api.get('/management/financement');
      return res.data?.data ?? [];
    } catch (error) {
      console.warn('Impossible de charger les types de financement:', error);
      return [];
    }
  });

  // Récupérer les qualifications (peut être vide si la table n'existe pas)
  const { data: qualifications = [] } = useQuery('qualifications', async () => {
    try {
      const res = await api.get('/management/qualifications');
      return res.data.data || [];
    } catch (error) {
      console.warn('Impossible de charger les qualifications:', error);
      return [];
    }
  });

  // Sous-états chargés depuis l'API pour ces états (11 RDV ANNULER, 12 REFUSER : liste affichée, sélection facultative)
  // 55 = HC (motifs via sous_etat)
  const etatsAvecListeSousEtats = [2, 8, 11, 12, 13, 16, 19, 44, 45, 55];
  // Sous-état obligatoire seulement pour ces états lorsque la liste existe (pas 11 ni 12)
  const etatsSousEtatObligatoire = [2, 8, 16, 19, 55];
  const { data: sousEtats = [] } = useQuery(
    ['sous-etat', selectedEtat],
    async () => {
      try {
        const res = await api.get(`/management/sous-etat/${selectedEtat}`);
        return res.data.data || [];
      } catch (error) {
        console.warn('Impossible de charger les sous-états:', error);
        return [];
      }
    },
    { enabled: selectedEtat !== null && etatsAvecListeSousEtats.includes(selectedEtat) }
  );

  // Admin session (1 admin, 11 backoffice, 13 RP confirmation, 14 RE confirmation) : formulaire honoré à suivre étendu
  const isAdminSessionHonoreSuivre = [1, 11, 13, 14].includes(Number(user?.fonction));

  // Pré-remplir A Rappeler le (J+2 jours ouvrés) quand l'état 9 (Honoré à suivre) est sélectionné
  useEffect(() => {
    const addWorkingDays = (date, days) => {
      const result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        const dow = result.getDay();
        if (dow !== 0 && dow !== 6) added++;
      }
      return result;
    };
    if (selectedEtat === 9) {
      const dateRappel = addWorkingDays(new Date(), 2);
      const dateRappelStr = dateRappel.toISOString().split('T')[0];
      setEtatFormData(prev => ({
        ...prev,
        date_rappel_date: prev.date_rappel_date || dateRappelStr,
        date_rappel_time: prev.date_rappel_time || '09:00'
      }));
    }
  }, [selectedEtat]);

  // Pré-remplir "A rappeler le" avec la date/heure actuelles pour l'état 8 (Annuler à reprogrammer)
  useEffect(() => {
    if (selectedEtat !== 8) return;
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const today = `${yyyy}-${mm}-${dd}`;
    const currentTime = `${hh}:${min}`;

    setEtatFormData((prev) => ({
      ...prev,
      conf_rdv_date: prev.conf_rdv_date || today,
      conf_rdv_time: prev.conf_rdv_time || currentTime
    }));
  }, [selectedEtat]);

  // Pré-remplir "A Rappeler le" avec la date/heure actuelles pour l'état 19 (RAPPEL POUR BUREAU)
  useEffect(() => {
    if (selectedEtat !== 19) return;
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const today = `${yyyy}-${mm}-${dd}`;
    const currentTime = `${hh}:${min}`;

    setEtatFormData((prev) => ({
      ...prev,
      date_rappel_date: prev.date_rappel_date || today,
      date_rappel_time: prev.date_rappel_time || currentTime
    }));
  }, [selectedEtat]);

  // Pré-remplir les sous-états selon l'option de compte rendu sélectionnée
  useEffect(() => {
    if (compteRenduOption && sousEtats.length > 0 && selectedEtat) {
      let sousEtatToSelect = null;
      
      if (compteRenduOption === 'deballé_réfléchir' && selectedEtat === 19) {
        // Trouver le sous-état "DÉBALLÉ DOIT RÉFLÉCHIR"
        sousEtatToSelect = sousEtats.find(se => se.titre === 'DÉBALLÉ DOIT RÉFLÉCHIR');
      } else if (isCommercialAnnulerReproSimpleOption(compteRenduOption) && selectedEtat === 8) {
        // Annuler à reprogrammer (commercial) : sous-état / dates saisis plus tard par le backoffice
      }
      
      // Ne pas présélectionner automatiquement le sous-état pour garder "Sélectionner" par défaut.
      if (sousEtatToSelect && !etatFormData.id_sous_etat && selectedEtat !== 19 && selectedEtat !== 2) {
        setEtatFormData({ ...etatFormData, id_sous_etat: String(sousEtatToSelect.id) });
      }
    }
  }, [sousEtats, compteRenduOption, selectedEtat]);

  // NRP (2) : sous-état par défaut = "Répondeur"
  useEffect(() => {
    if (selectedEtat !== 2 || !Array.isArray(sousEtats) || sousEtats.length === 0) return;

    const normalize = (s) =>
      String(s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    const defaultSousEtat = sousEtats.find((se) =>
      normalize(se?.titre).includes('repondeur')
    );

    if (!defaultSousEtat) return;

    setNrpFormData((prev) => {
      // Ne pas écraser un choix déjà fait
      if ((prev.id_sous_etat || '').toString().trim()) return prev;

      return {
        ...prev,
        id_sous_etat: String(defaultSousEtat.id),
      };
    });
  }, [selectedEtat, sousEtats]);

  // Récupérer la fiche (utiliser le hash au lieu de l'ID) avec rafraîchissement automatique toutes les 5 secondes
  const { data: ficheData, isLoading } = useQuery(
    ['fiche', hash],
    async () => {
      const res = await api.get(`/fiches/${hash}`);
      return res.data.data;
    },
    { 
      enabled: !!hash,
      // Rafraîchissement automatique toutes les 5 secondes dans le modal
      refetchInterval: isModal ? 5000 : false, // Uniquement si c'est un modal
      refetchOnWindowFocus: isModal, // Rafraîchir quand la fenêtre redevient active (modal uniquement)
      refetchOnReconnect: isModal // Rafraîchir quand la connexion est rétablie (modal uniquement)
    }
  );

  const requiresEarlyCrGate = useMemo(() => {
    if (Number(user?.fonction) !== 5) return false;
    if (!ficheData?.date_rdv_time) return false;
    return isBeforeRdvDateTime(ficheData.date_rdv_time);
  }, [user?.fonction, ficheData?.date_rdv_time]);

  const canAccessCompteRenduChoices = !requiresEarlyCrGate || earlyCrVerified;

  useEffect(() => {
    setEarlyCrVerified(false);
  }, [ficheData?.id, ficheData?.date_rdv_time]);

  useEffect(() => {
    slotCodeVerifiedRef.current = false;
  }, [confFormData.conf_rdv_date, confFormData.conf_rdv_time, rdvFormData.date_rdv_time]);

  const isBackofficeSession = userFonction === 11;
  const showCompletudeSection =
    isQualiteConfirmation ||
    isREConfirmation ||
    isRPConfirmation ||
    isConfirmateurSession ||
    isBackofficeSession;
  const canTreatCompletudeDetail =
    isREConfirmation || isRPConfirmation || isConfirmateurSession || isBackofficeSession;
  const showCompletudeTabBadge =
    isConfirmateurSession || isREConfirmation || isRPConfirmation || isBackofficeSession;
  /** Badge DECALAGE (demande en attente) : admin, backoffice, RE/RP confirmation, confirmateur */
  const showDecalageTabBadge = [1, 6, 7, 11, 13, 14].includes(Number(userFonction));

  const { data: completudeIndicatorList = [] } = useQuery(
    ['fiche-completude-indicator', hash],
    async () => {
      const res = await api.get(`/fiches/${hash}/completude`);
      return res.data.data || [];
    },
    {
      enabled: !!hash && showCompletudeTabBadge,
      refetchInterval: isModal ? 5000 : false,
      refetchOnWindowFocus: isModal
    }
  );

  const hasCompletudeTabAlert = useMemo(() => {
    const list = Array.isArray(completudeIndicatorList) ? completudeIndicatorList : [];
    return list.some((item) => item.statut === 'en_attente');
  }, [completudeIndicatorList]);

  const scrollToCompletudeSection = () => {
    setActiveTab('fiches');
    window.setTimeout(() => {
      document
        .querySelector('.fiche-completude-section')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const scrollToDecalageSection = () => {
    setActiveTab('fiches');
    setShowEtatActuel(true);
    window.setTimeout(() => {
      document
        .querySelector('.etat-actuel-wrapper')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const { data: modificaData = [] } = useQuery(
    ['modifica', hash],
    async () => {
      const res = await api.get(`/fiches/${hash}/modifica`);
      return res.data.success ? (res.data.data || []) : [];
    },
    {
      enabled: !!hash,
      retry: 1,
    }
  );

  const dateRdvModifications = useMemo(
    () => (Array.isArray(modificaData) ? modificaData : [])
      .filter((mod) => String(mod.type || mod.champ || '').trim() === 'date_rdv_time'),
    [modificaData]
  );

  /** Historique bouton « Signaler l'absence du couple » (rdv_seul / conf_presence_couple). */
  const absenceCoupleModifications = useMemo(() => {
    const allowed = new Set(['rdv_seul', 'conf_presence_couple']);
    return (Array.isArray(modificaData) ? modificaData : [])
      .filter((mod) => allowed.has(String(mod.type || mod.champ || '').trim()))
      .slice()
      .sort((a, b) => {
        const ta = a.date_modif_time ? new Date(a.date_modif_time).getTime() : 0;
        const tb = b.date_modif_time ? new Date(b.date_modif_time).getTime() : 0;
        return tb - ta;
      });
  }, [modificaData]);

  useEffect(() => {
    if (selectedEtat !== 7 || !confFormHydratePendingRef.current || !ficheData?.id) return;
    const nextConf = buildConfFormStateFromFiche(ficheData, user);
    setConfFormData(nextConf);
    setShowConfConfirmateur2(!!nextConf.id_confirmateur_2);
    setShowConfConfirmateur3(!!nextConf.id_confirmateur_3);
    confFormHydratePendingRef.current = false;
  }, [selectedEtat, ficheData, user]);

  // Modal (ex. menu contextuel dashboard) : onglet Fiches, historique des états déplié, scroll vers la section
  useEffect(() => {
    if (!initialFocusHistoriqueEtats || !isModal || !ficheData) return;
    setActiveTab('fiches');
    setShowHistorique(true);
  }, [initialFocusHistoriqueEtats, isModal, ficheData?.id]);

  useEffect(() => {
    if (!initialFocusHistoriqueEtats || !isModal || !ficheData) return;
    if (activeTab !== 'fiches') return;
    const tid = window.setTimeout(() => {
      historiqueEtatsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 220);
    return () => window.clearTimeout(tid);
  }, [initialFocusHistoriqueEtats, isModal, ficheData?.id, activeTab, showHistorique]);

  useEffect(() => {
    autoPdfFromQueryRef.current = false;
  }, [hash]);

  // Page pleine : ?tab=pdf → téléchargement automatique du PDF (aligné menu contextuel dashboard)
  useEffect(() => {
    if (isModal) return;
    const tab = searchParams.get('tab');
    if (tab !== 'pdf') {
      autoPdfFromQueryRef.current = false;
      return;
    }
    if (isLoading || !ficheData) return;
    if (autoPdfFromQueryRef.current) return;
    autoPdfFromQueryRef.current = true;
    generateFicheClientPdf(ficheData, {
      professions: professions || [],
      typeContrat: typeContrat || [],
      centres: centres || [],
      agents: agents || [],
      commerciaux: commerciaux || [],
      confirmateurs: confirmateurs || [],
    });
  }, [
    isModal,
    searchParams,
    isLoading,
    ficheData,
    hash,
    professions,
    typeContrat,
    centres,
    agents,
    commerciaux,
    confirmateurs,
  ]);

  // États : confirmateur (6) et partenaire « fixer RDV » : même matrice selon l'état actuel
  const useConfirmateurEtatList = Number(user?.fonction) === 6 || partenaireCanFixerRdv;
  const { data: etats } = useQuery(
    useConfirmateurEtatList && ficheData?.id_etat_final
      ? ['etats', 'confirmateur', ficheData.id_etat_final, user?.id]
      : ['etats', user?.fonction],
    async () => {
      const params = useConfirmateurEtatList && ficheData?.id_etat_final
        ? { id_etat_fiche: ficheData.id_etat_final }
        : {};
      const res = await api.get('/management/etats', { params });
      return res.data.data || [];
    }
  );

  // États regroupés par phase (0,1,2,3), ordre BDD, pour les selects
  const etatsList = etats || [];
  const { phase0: etatsPhase0, phase1: etatsPhase1, phase2: etatsPhase2, phase3: etatsPhase3 } = getEtatsGroupedByPhase(etatsList);
  const currentEtatFinalId = ficheData?.id_etat_final != null ? Number(ficheData.id_etat_final) : null;
  const canSelectEtatConfirmerInChangeSection = etatsList.some((etat) => Number(etat.id) === 7);
  // Matrice de transitions vers "Confirmer" via le bouton + du planning :
  // - Confirmateur (fonction 6) : on applique strictement la matrice
  //   (sinon impossible de passer de "Confirmer" à "Confirmer", de "Annuler 2 fois" à "Confirmer", etc.).
  // - Autres rôles autorisés à voir le bouton + : aucune restriction d'état actuel.
  const isConfirmateurSessionForPlanning = Number(user?.fonction) === 6 || partenaireCanFixerRdv;
  const canOpenConfirmEtatFromPlanning = isConfirmateurSessionForPlanning
    ? (
        currentEtatFinalId != null &&
        ETATS_AUTORISES_VERS_CONFIRMER_PLANNING.includes(currentEtatFinalId) &&
        canSelectEtatConfirmerInChangeSection
      )
    : true;
  
  // Vérifier si c'est un commercial (fonction 5)
  const isCommercial = userFonction === 5;

  // Onglet Affectation : visible par administrateur (1), backoffice (11), RE confirmation (14), RP confirmation (13)
  const showAffectationTab = [1, 11, 13, 14].includes(Number(user?.fonction));
  const isAdminSession = [1, 2, 7].includes(Number(user?.fonction));
  /** RE / RP confirmation : pas de phase 0 dans la liste des états (admin voit aussi sans phase 0). */
  const excludeEtatsPhase0 = isREConfirmation || isRPConfirmation || isAdminSession;
  
  // Vérifier si c'est un R2 (deuxième commercial assigné)
  const isR2 = isCommercial && ficheData && Number(ficheData.id_commercial_2) === Number(user?.id);

  const canCommercialEditFiche =
    isCommercial &&
    hasPermission('fiches_edit') &&
    ficheData &&
    (Number(ficheData.id_commercial) === Number(user?.id) ||
      Number(ficheData.id_commercial_2) === Number(user?.id));

  const canCommercialValidateRdv =
    isCommercial &&
    ficheData &&
    (Number(ficheData.id_commercial) === Number(user?.id) ||
      Number(ficheData.id_commercial_2) === Number(user?.id));

  const getCommercialDisplayName = (id) => {
    if (!id) return '';
    // Priorité API détail (déjà nom||login), puis liste utilisateurs
    if (
      ficheData?.id_commercial != null &&
      String(ficheData.id_commercial) === String(id) &&
      ficheData.commercial_pseudo
    ) {
      return String(ficheData.commercial_pseudo).trim();
    }
    const found = (commerciaux || []).find((c) => String(c.id) === String(id));
    return formatCommercialDisplayName(found);
  };
  const commercialAffecteNom = getCommercialDisplayName(ficheData?.id_commercial || affectationCommercial);
  
  // Rediriger vers l'onglet fiches si un commercial est sur un onglet désactivé
  useEffect(() => {
    if (isCommercial && (activeTab === 'modifica' || activeTab === 'planning' || activeTab === 'sms')) {
      setActiveTab('fiches');
    }
  }, [isCommercial, activeTab, ficheData]);

  // Synchroniser l'état d'affectation avec la fiche (pour l'onglet Affectation)
  useEffect(() => {
    if (ficheData) {
      setAffectationCommercial(ficheData.id_commercial != null ? String(ficheData.id_commercial) : '');
    }
  }, [ficheData?.id, ficheData?.id_commercial]);

  const ID_ETAT_DECALAGE_EN_ATTENTE = 1;
  const ID_ETAT_DECALAGE_ANNULE = 6;
  const currentFicheId = ficheData?.id != null ? Number(ficheData.id) : null;
  const isCurrentFicheDecalage = (d) => {
    const decalageFicheId = Number(d?.id_fiche ?? d?.fiche_id);
    return Number.isFinite(currentFicheId) && currentFicheId > 0
      && Number.isFinite(decalageFicheId) && decalageFicheId === currentFicheId;
  };

  // Uniquement les décalages de la fiche ouverte (jamais ceux d'une autre fiche).
  const { data: decalagesData } = useQuery(
    ['decalages', 'fiche', currentFicheId],
    async () => {
      if (!Number.isFinite(currentFicheId) || currentFicheId <= 0) return [];
      const res = await api.get('/decalages', { params: { id_fiche: currentFicheId } });
      return (res.data.data || []).filter((d) => Number(d.id_fiche ?? d.fiche_id) === currentFicheId);
    },
    {
      enabled: Number.isFinite(currentFicheId) && currentFicheId > 0,
      refetchInterval: isCommercial || showDecalageTabBadge ? 15000 : false,
      refetchOnWindowFocus: true,
    }
  );

  const decalagesPourFiche = useMemo(() => {
    if (!Array.isArray(decalagesData)) return [];
    return decalagesData.filter(isCurrentFicheDecalage);
  }, [decalagesData, currentFicheId]);

  /** Ne plus afficher si état ≠ CONFIRMER ou jour RDV déjà passé (ex. le 13 → masquer le 12) */
  const shouldShowDecalageDemandes = shouldDisplayDecalageRequest(ficheData);
  const decalagesAffichables = useMemo(() => {
    if (!shouldShowDecalageDemandes) return [];
    return decalagesPourFiche.filter((d) => {
      // Jour calendaire de la demande (date prévue / RDV), sans l'heure
      const dateRef = d.date_prevu || d.date_nouvelle || ficheData?.date_rdv_time;
      return !isRdvDatePastByAtLeastOneDay(dateRef);
    });
  }, [shouldShowDecalageDemandes, decalagesPourFiche, ficheData?.date_rdv_time]);

  const hasPendingDecalageTabAlert = useMemo(() => {
    if (!showDecalageTabBadge || !shouldShowDecalageDemandes) return false;
    return decalagesAffichables.some(
      (d) => d.id_etat == null || Number(d.id_etat) === ID_ETAT_DECALAGE_EN_ATTENTE
    );
  }, [decalagesAffichables, showDecalageTabBadge, shouldShowDecalageDemandes]);

  const commercialPendingDecalage = useMemo(() => {
    if (!shouldShowDecalageDemandes || !isCommercial || decalagesAffichables.length === 0) return null;
    return decalagesAffichables.find(
      (d) =>
        Number(d.expediteur) === Number(user?.id) &&
        Number(d.id_etat) === ID_ETAT_DECALAGE_EN_ATTENTE
    ) || null;
  }, [decalagesAffichables, isCommercial, shouldShowDecalageDemandes, user?.id]);

  const hasCommercialPendingDecalage = !!commercialPendingDecalage;

  const commercialAlreadyCreatedDecalage = useMemo(() => {
    if (!isCommercial || decalagesPourFiche.length === 0) return false;
    return decalagesPourFiche.some((d) => Number(d.expediteur) === Number(user?.id));
  }, [decalagesPourFiche, isCommercial, user?.id]);

  const commercialCanCreateDecalage = !commercialAlreadyCreatedDecalage;

  const heureRdvAvantDecalage = useMemo(() => {
    if (!shouldShowDecalageDemandes || decalagesAffichables.length === 0) return null;
    const currentNorm = String(ficheData?.date_rdv_time || '').trim();
    if (!currentNorm) return null;

    const candidats = decalagesAffichables
      .filter((d) => {
        if (!d.date_prevu) return false;
        if (Number(d.id_etat) === ID_ETAT_DECALAGE_EN_ATTENTE) return false;
        return String(d.date_prevu).trim() !== currentNorm;
      })
      .sort((a, b) => {
        const ta = a.date_creation ? new Date(a.date_creation).getTime() : 0;
        const tb = b.date_creation ? new Date(b.date_creation).getTime() : 0;
        return tb - ta;
      });

    return candidats[0]?.date_prevu ?? null;
  }, [decalagesAffichables, ficheData?.date_rdv_time, shouldShowDecalageDemandes]);

  const decalageRdvHistorique = useMemo(() => {
    if (!shouldShowDecalageDemandes) return [];
    return [...decalagesAffichables]
      .filter((d) => d.date_prevu)
      .sort((a, b) => {
        const ta = a.date_creation ? new Date(a.date_creation).getTime() : 0;
        const tb = b.date_creation ? new Date(b.date_creation).getTime() : 0;
        return tb - ta;
      });
  }, [decalagesAffichables, shouldShowDecalageDemandes]);

  const prevDecalagesSignatureRef = useRef(null);
  useEffect(() => {
    prevDecalagesSignatureRef.current = null;
  }, [currentFicheId]);
  useEffect(() => {
    if (!hash || !Array.isArray(decalagesPourFiche)) return;
    const signature = JSON.stringify(
      decalagesPourFiche.map((d) => ({ id: d.id, id_etat: d.id_etat, date_nouvelle: d.date_nouvelle }))
    );
    if (prevDecalagesSignatureRef.current != null && prevDecalagesSignatureRef.current !== signature) {
      queryClient.invalidateQueries(['fiche', hash]);
      queryClient.invalidateQueries(['modifica', hash]);
      queryClient.invalidateQueries(['planning-commercial']);
    }
    prevDecalagesSignatureRef.current = signature;
  }, [decalagesPourFiche, hash, queryClient]);

  // Initialiser le formulaire de décalage avec la date du RDV de la fiche
  useEffect(() => {
    if (ficheData && ficheData.date_rdv_time) {
      setDecalageFormData(prev => ({
        ...prev,
        date_prevu: ficheData.date_rdv_time,
        id_confirmateur: ficheData.id_confirmateur ? String(ficheData.id_confirmateur) : prev.id_confirmateur
      }));
    }
  }, [ficheData, user?.fonction]);

  // Calculer la nouvelle date/heure en fonction du décalage sélectionné
  const calculateNouvelleDate = (datePrevu, minutes) => {
    if (!datePrevu || !minutes || minutes === '0') return '';
    
    try {
      const date = new Date(datePrevu);
      date.setMinutes(date.getMinutes() + parseInt(minutes));
      
      // Formater au format YYYY-MM-DD HH:MM:SS
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const mins = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${mins}:00`;
    } catch (error) {
      console.error('Erreur lors du calcul de la nouvelle date:', error);
      return '';
    }
  };

  // Réinitialiser seulement date_prevu quand la date RDV de la fiche change
  // Le calcul de nouvelle_date est géré directement dans le onChange du select
  useEffect(() => {
    if (ficheData?.date_rdv_time && ficheData.date_rdv_time !== decalageFormData.date_prevu) {
      setDecalageFormData(prev => ({
        ...prev,
        date_prevu: ficheData.date_rdv_time
      }));
    }
  }, [ficheData?.date_rdv_time]);

  const canAnnulerDecalageEnAttente = (decalage) => {
    if (!decalage?.id) return false;
    if (Number(decalage.id_etat) !== ID_ETAT_DECALAGE_EN_ATTENTE) return false;
    const fn = Number(user?.fonction);
    if (fn === 5) return Number(decalage.expediteur) === Number(user?.id);
    if ([1, 2, 7, 11].includes(fn)) return true;
    return false;
  };

  const cancelDecalageMutation = useMutation(
    async (decalageId) => {
      const res = await api.put(`/decalages/${decalageId}/statut`, { id_etat: ID_ETAT_DECALAGE_ANNULE });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['decalages', 'fiche', currentFicheId]);
        queryClient.invalidateQueries(['decalages']);
        queryClient.invalidateQueries(['decalages-pending-count']);
        queryClient.invalidateQueries(['fiche', hash]);
      },
      onError: (err) => {
        alert(err.response?.data?.message || 'Impossible d\'annuler le décalage.');
      }
    }
  );

  const { data: etatsDecalageFiche = [] } = useQuery(
    'etats-decalage',
    async () => {
      const res = await api.get('/management/etat-decalage');
      return res.data.data || [];
    }
  );

  const resolveDecalageEtatId = useCallback(
    (kind) => {
      const list = Array.isArray(etatsDecalageFiche) ? etatsDecalageFiche : [];
      const norm = (t) =>
        String(t || '')
          .toUpperCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
      if (kind === 'accept') {
        const found = list.find((e) => {
          const t = norm(e.titre);
          return Number(e.id) === 2 || t.includes('ACCEPT') || t.includes('VALID');
        });
        return found ? Number(found.id) : 2;
      }
      const found = list.find((e) => {
        const t = norm(e.titre);
        return Number(e.id) === 3 || Number(e.id) === 4 || t.includes('REFUS');
      });
      return found ? Number(found.id) : 3;
    },
    [etatsDecalageFiche]
  );

  const updateDecalageStatutMutation = useMutation(
    async ({ id, id_etat, date_nouvelle, appliquer_fiche }) => {
      const payload = { id_etat };
      if (date_nouvelle) payload.date_nouvelle = date_nouvelle;
      if (appliquer_fiche !== undefined) payload.appliquer_fiche = appliquer_fiche;
      const res = await api.put(`/decalages/${id}/statut`, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        setDecalageAcceptModal(null);
        setDecalageAcceptDate('');
        setDecalageAcceptTime('');
        queryClient.invalidateQueries(['decalages', 'fiche', currentFicheId]);
        queryClient.invalidateQueries(['decalages']);
        queryClient.invalidateQueries(['decalages-pending-count']);
        queryClient.invalidateQueries(['fiche', hash]);
        queryClient.invalidateQueries(['modifica', hash]);
        queryClient.invalidateQueries(['planning-commercial']);
      },
      onError: (err) => {
        alert(err.response?.data?.message || 'Impossible de mettre à jour le décalage.');
      },
    }
  );

  const canTreatDecalageFromFiche = [1, 2, 6, 7, 11, 13, 14].includes(Number(userFonction));

  const buildDecalageAcceptedDateNouvelle = () => {
    if (!decalageAcceptDate || !decalageAcceptTime) {
      alert('Veuillez saisir la date et l\'heure du RDV accepté par le client.');
      return null;
    }
    const date_nouvelle = `${decalageAcceptDate} ${decalageAcceptTime}:00`;
    if (isRdvDateBeforeToday(date_nouvelle)) {
      alert('La date du RDV ne peut pas être antérieure à aujourd\'hui.');
      return null;
    }
    return date_nouvelle;
  };

  const handleRefuseDecalageFromFiche = (decalage) => {
    if (!decalage?.id || !isDecalagePending(decalage)) return;
    if (!window.confirm('Refuser cette demande de décalage ?')) return;
    updateDecalageStatutMutation.mutate({
      id: decalage.id,
      id_etat: resolveDecalageEtatId('refuse'),
    });
  };

  const handleAcceptDecalageFromFiche = (decalage) => {
    if (!decalage?.id || !isDecalagePending(decalage)) return;
    const idAccept = resolveDecalageEtatId('accept');
    if (isDecalageSansHeure(decalage)) {
      setDecalageAcceptModal({ decalage, id_etat: idAccept, step: 'saisie' });
      setDecalageAcceptDate(parseDecalageDatePart(decalage.date_prevu) || formatLocalYmd());
      setDecalageAcceptTime('');
      return;
    }
    if (!window.confirm('Accepter cette demande de décalage ?')) return;
    updateDecalageStatutMutation.mutate({
      id: decalage.id,
      id_etat: idAccept,
      appliquer_fiche: true,
    });
  };

  const submitDecalageAcceptModal = (e) => {
    e?.preventDefault?.();
    if (!decalageAcceptModal?.decalage?.id) return;
    if (!buildDecalageAcceptedDateNouvelle()) return;
    setDecalageAcceptModal((prev) => (prev ? { ...prev, step: 'appliquer' } : prev));
  };

  const confirmDecalageAcceptModal = (appliquerFiche) => {
    if (!decalageAcceptModal?.decalage?.id) return;
    const date_nouvelle = buildDecalageAcceptedDateNouvelle();
    if (!date_nouvelle) {
      setDecalageAcceptModal((prev) => (prev ? { ...prev, step: 'saisie' } : prev));
      return;
    }
    updateDecalageStatutMutation.mutate({
      id: decalageAcceptModal.decalage.id,
      id_etat: decalageAcceptModal.id_etat,
      date_nouvelle,
      appliquer_fiche: !!appliquerFiche,
    });
  };

  const decalageDemandesActions = useMemo(
    () => ({
      canTreat: canTreatDecalageFromFiche,
      onAccept: handleAcceptDecalageFromFiche,
      onRefuse: handleRefuseDecalageFromFiche,
      isLoading: updateDecalageStatutMutation.isLoading,
    }),
    // handlers stables via closures ; re-render quand loading / droits / etats changent
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canTreatDecalageFromFiche, updateDecalageStatutMutation.isLoading, etatsDecalageFiche]
  );

  // Mutation pour créer/mettre à jour un décalage
  const decalageMutation = useMutation(
    async (data) => {
      const res = await api.post('/decalages', data);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['decalages', 'fiche', currentFicheId]);
        queryClient.invalidateQueries(['decalages']);
        queryClient.invalidateQueries(['decalages-pending-count']);
        queryClient.invalidateQueries(['fiche', hash]);
        alert('Décalage créé avec succès');
        setDecalageFormData({
          select_minutes: '0',
          id_confirmateur: ficheData?.id_confirmateur ? String(ficheData.id_confirmateur) : '',
          message: '',
          date_prevu: ficheData?.date_rdv_time || '',
          nouvelle_date: ''
        });
      },
      onError: (error) => {
        console.error('Erreur lors de la création du décalage:', error);
        console.error('Détails de l\'erreur:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          data: error.response?.data
        });
        const errorMessage = error.response?.data?.message || error.message || 'Erreur inconnue';
        alert('Erreur lors de la création du décalage: ' + errorMessage);
      }
    }
  );

  // Soumettre le formulaire de décalage
  const handleDecalageSubmit = async () => {
    // Utiliser ficheData au lieu de fiche car fiche est défini plus bas dans le render
    if (!ficheData) {
      console.error('Erreur : ficheData est null ou undefined');
      alert('Erreur : fiche non trouvée. Veuillez recharger la page.');
      return;
    }

    // Vérifier que l'ID de la fiche est bien défini et valide
    const ficheId = ficheData.id;
    if (!ficheId || ficheId === null || ficheId === undefined) {
      console.error('Erreur : ficheData.id est null ou undefined', ficheData);
      alert('Erreur : ID de fiche non trouvé. Veuillez recharger la page.');
      return;
    }

    // S'assurer que l'ID est un nombre
    const idFicheNum = parseInt(ficheId, 10);
    if (isNaN(idFicheNum) || idFicheNum <= 0) {
      console.error('Erreur : ficheData.id n\'est pas un nombre valide', ficheId);
      alert('Erreur : ID de fiche invalide. Veuillez recharger la page.');
      return;
    }

    if (!ficheData.date_rdv_time) {
      alert('Aucune date de RDV disponible pour créer un décalage');
      return;
    }

    if (user.fonction === 5 && commercialAlreadyCreatedDecalage) {
      alert('Une demande de décalage a déjà été créée pour cette fiche.');
      return;
    }

    const adminBackoffice = isAdminOrBackofficeFonction(user.fonction);
    const sameDateMode = isSameDateDecalageSelection(decalageFormData.select_minutes);

    if (!adminBackoffice && sameDateMode) {
      alert('Veuillez sélectionner une durée de décalage');
      return;
    }

    // Déterminer le destinataire selon la fonction
    let destination = null;
    
    if (user.fonction === 5) {
      // Commerciaux : utiliser le confirmateur de la fiche (obligatoire)
      destination = ficheData?.id_confirmateur;
      if (!destination) {
        alert('Cette fiche n\'a pas de confirmateur assigné. Veuillez assigner un confirmateur avant de créer un décalage.');
        return;
      }
    } else if (user.fonction === 6) {
      // Confirmateurs : utiliser leur propre ID
      destination = user.id;
    } else if (adminBackoffice) {
      // Admins / backoffice : utiliser le confirmateur sélectionné dans la liste déroulante
      destination = decalageFormData.id_confirmateur || ficheData?.id_confirmateur;
      if (!destination) {
        alert('Veuillez sélectionner un confirmateur depuis la liste déroulante');
        return;
      }
    } else {
      alert('Vous n\'avez pas la permission de créer un décalage');
      return;
    }

    // Récupérer la date RDV originale de la fiche
    const dateRdvOriginale = ficheData?.date_rdv_time || decalageFormData.date_prevu || '';
    if (!dateRdvOriginale) {
      alert('Erreur : la date de rendez-vous originale n\'a pas été trouvée.');
      return;
    }

    let dateNouvelle = '';
    if (adminBackoffice && sameDateMode) {
      dateNouvelle = addMinutesToDateTimeString(dateRdvOriginale, 0) || dateRdvOriginale;
    } else {
      if (!decalageFormData.nouvelle_date || sameDateMode) {
        alert('Veuillez sélectionner une durée de décalage (10 minutes, 1 heure, etc.)');
        return;
      }
      dateNouvelle = decalageFormData.nouvelle_date;
    }
    
    // S'assurer que date_nouvelle est au bon format (YYYY-MM-DD HH:MM:SS)
    if (dateNouvelle.includes('T')) {
      // Si c'est au format ISO, convertir en format MySQL
      const date = new Date(dateNouvelle);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const mins = String(date.getMinutes()).padStart(2, '0');
      const secs = String(date.getSeconds()).padStart(2, '0');
      dateNouvelle = `${year}-${month}-${day} ${hours}:${mins}:${secs}`;
    }

    const message = decalageFormData.message.trim();

    decalageMutation.mutate({
      id_fiche: idFicheNum,
      destination: parseInt(destination, 10),
      message,
      date_prevu: dateRdvOriginale, // Date RDV originale
      date_nouvelle: dateNouvelle    // Nouvelle date après décalage (identique si sans durée)
    });
  };

  // Initialiser les données NRP si la fiche est déjà en état NRP.
  // On utilise la date/heure ACTUELLES (et non celles déjà enregistrées en BDD)
  // afin qu'un changement d'état vers NRP horodate toujours l'action en cours.
  useEffect(() => {
    if (ficheData && ficheData.id_etat_final === 2 && (selectedEtat === null || selectedEtat === 2)) {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const hh = String(now.getHours()).padStart(2, '0');
      const min = String(now.getMinutes()).padStart(2, '0');
      setNrpFormData({
        date_appel_date: `${yyyy}-${mm}-${dd}`,
        date_appel_time: `${hh}:${min}`,
        id_sous_etat: '',
        conf_commentaire_produit: '',
      });
    }
  }, [ficheData, selectedEtat]);

  // Mutation pour valider une fiche
  const validateMutation = useMutation(
    async ({ type_valid, conf_rdv_avec, conf_presence_couple }) => {
      const res = await api.post(`/fiches/${hash}/valider`, {
        type_valid,
        conf_rdv_avec,
        conf_presence_couple
      });
      return res.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['fiche', hash]);
        queryClient.invalidateQueries(['modifica', hash]);
        queryClient.invalidateQueries(['planning-commercial']); // Mettre à jour le planning commercial
        alert(data.message || 'Fiche validée avec succès');
      },
      onError: (error) => {
        alert('Erreur lors de la validation: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  // Mutation pour mettre à jour un champ
  const updateFieldMutation = useMutation(
    async ({ field, value }) => {
      const res = await api.patch(`/fiches/${hash}/field`, { field, value });
      return res.data;
    },
    {
      onSuccess: (data, variables) => {
        // Vérifier si un compte rendu a été créé (pour les commerciaux)
        if (data.data?.id_compte_rendu) {
          queryClient.invalidateQueries(['planning-commercial']); // Mettre à jour le planning commercial
          alert('Compte rendu créé avec succès. Les modifications sont en attente d\'approbation de l\'administrateur.');
        } else {
          queryClient.invalidateQueries(['fiche', hash]);
          queryClient.invalidateQueries(['planning-commercial']); // Mettre à jour le planning commercial
          queryClient.invalidateQueries(['fiches']); // Invalider la liste des fiches aussi
          queryClient.invalidateQueries(['modifica', hash]); // Invalider les modifications
          
          // Si la date du RDV a été modifiée, invalider et recharger toutes les queries de planning
          if (variables.field === 'date_rdv_time') {
            queryClient.invalidateQueries(['planning-week']);
            queryClient.invalidateQueries(['planning-availability']);
            queryClient.invalidateQueries(['planning-modal']);
            queryClient.invalidateQueries(['availability-modal']);
            // Forcer le refetch immédiat de toutes les queries de planning actives
            queryClient.refetchQueries(['planning-modal'], { active: true });
            queryClient.refetchQueries(['availability-modal'], { active: true });
          }
        }
        
        setEditingField(null);
        setEditValue('');
        // Si l'état final a été modifié, réinitialiser selectedEtat
        if (editingField === 'id_etat_final') {
          setSelectedEtat(null);
        }
      },
      onError: (error) => {
        console.error('Erreur lors de la mise à jour du champ:', error);
        console.error('Détails de l\'erreur:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          message: error.response?.data?.message,
          url: error.config?.url,
          method: error.config?.method,
          hash: hash,
          field: error.config?.data ? JSON.parse(error.config.data)?.field : 'unknown'
        });
        
        let errorMessage = 'Erreur lors de la mise à jour du champ';
        if (error.response?.status === 404) {
          errorMessage = 'Route non trouvée. Vérifiez que le hash de la fiche est valide.';
        } else if (error.response?.status === 400) {
          errorMessage = error.response?.data?.message || 'Données invalides';
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        alert(errorMessage);
      }
    }
  );

  // Mutation Contrôle Qualité (CQ ETAT, CQ DOSSIER, OBSERVATIONS) pour états signer — champ dédié observations_cq
  const controleQualiteMutation = useMutation(
    async ({ cq_etat, cq_dossier, observations_cq }) => {
      const res = await api.put(`/fiches/${hash}/controle-qualite`, {
        cq_etat: cq_etat || null,
        cq_dossier: cq_dossier || null,
        observations_cq: observations_cq || null
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fiche', hash]);
        queryClient.invalidateQueries(['fiches']);
        setCqFormByHash(prev => {
          const next = { ...prev };
          delete next[hash];
          return next;
        });
        alert('Contrôle qualité enregistré avec succès.');
      },
      onError: (error) => {
        const msg = error.response?.data?.message || error.message || 'Erreur lors de l\'enregistrement du contrôle qualité';
        alert(msg);
      }
    }
  );

  // Récupérer les utilisateurs pour les couleurs
  const { data: usersData } = useQuery('users', async () => {
    const res = await api.get('/management/utilisateurs');
    return res.data.data || [];
  });

  // Récupérer le planning si nécessaire (même format que la page Planning)
  const { data: planningResponse, isLoading: isLoadingPlanning } = useQuery(
    ['planning-week', planningWeek, planningYear, planningDep],
    async () => {
      const res = await api.get('/planning/week', {
        params: { w: planningWeek, y: planningYear, dp: planningDep }
      });
      return res.data;
    },
    { enabled: false } // Les requêtes de planning sont gérées dans PlanningTab
  );

  const { data: availabilityResponse } = useQuery(
    ['planning-availability', planningWeek, planningYear, planningDep],
    async () => {
      const res = await api.get('/planning/availability', {
        params: { w: planningWeek, y: planningYear, dp: planningDep }
      });
      return res.data;
    },
    { enabled: false } // Les requêtes de planning sont gérées dans PlanningTab
  );

  // Extraire les données du planning et de la disponibilité
  const planningData = planningResponse?.data || {};
  const availabilityData = availabilityResponse?.data || {};

  // Calculer le département à partir du code postal (2 premiers chiffres)
  useEffect(() => {
    if (ficheData?.cp) {
      // Extraire les 2 premiers chiffres du code postal
      const cpStr = String(ficheData.cp).trim();
      let dep = '';
      
      // Si le code postal commence par des chiffres, prendre les 2 premiers
      if (/^\d/.test(cpStr)) {
        dep = cpStr.substring(0, 2);
      } else {
        // Sinon, essayer de trouver les 2 premiers chiffres dans la chaîne
        const match = cpStr.match(/\d{2}/);
        if (match) {
          dep = match[0];
        }
      }
      
      // S'assurer que le département est valide (2 chiffres)
      if (dep && dep.length === 2 && /^\d{2}$/.test(dep)) {
        setPlanningDep(dep);
        
        // Calculer la semaine actuelle si pas déjà définie
        if (!planningWeek || !planningYear) {
          const now = new Date();
          const getWeekNumber = (date) => {
            const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            const dayNum = d.getUTCDay() || 7;
            d.setUTCDate(d.getUTCDate() + 4 - dayNum);
            const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
            return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
          };
          setPlanningWeek(getWeekNumber(now));
          setPlanningYear(now.getFullYear());
        }
      }
    }
  }, [ficheData?.cp]);

  // Prefetch du planning dès que dep/semaine/année sont connus (affichage instantané au clic onglet Planning)
  useEffect(() => {
    if (!planningDep || !planningWeek || !planningYear) return;
    queryClient.prefetchQuery(
      ['planning-modal', planningWeek, planningYear, planningDep],
      async () => {
        const res = await api.get('/planning/week', { params: { w: planningWeek, y: planningYear, dp: planningDep } });
        return res.data;
      },
      { staleTime: 60000 }
    );
    queryClient.prefetchQuery(
      ['availability-modal', planningWeek, planningYear, planningDep],
      async () => {
        const res = await api.get('/planning/availability', { params: { w: planningWeek, y: planningYear, dp: planningDep } });
        return res.data;
      },
      { staleTime: 60000 }
    );
  }, [planningDep, planningWeek, planningYear, queryClient]);

  // Mettre à jour automatiquement is_urgent si la date du RDV est aujourd'hui ou demain
  useEffect(() => {
    if (rdvFormData.date_rdv_time && showRdvModal) {
      const rdvDateStr = rdvFormData.date_rdv_time.split(' ')[0];
      if (rdvDateStr) {
        try {
          const rdvDate = new Date(rdvDateStr + 'T00:00:00');
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          const rdvDateOnly = new Date(rdvDate);
          rdvDateOnly.setHours(0, 0, 0, 0);
          const isTodayOrTomorrow = rdvDateOnly.getTime() === today.getTime() || rdvDateOnly.getTime() === tomorrow.getTime();
          
          // Mettre à jour is_urgent si la date est aujourd'hui ou demain
          if (isTodayOrTomorrow) {
            setRdvFormData(prev => ({ ...prev, is_urgent: true }));
          }
        } catch (e) {
          console.error('Erreur lors de la vérification de la date:', e);
        }
      }
    }
  }, [rdvFormData.date_rdv_time, showRdvModal]);

  const handleEditField = (field, currentValue) => {
    if (Number(user?.fonction) === 5 && COMMERCIAL_SESSION_READONLY_FIELDS.has(field)) {
      return;
    }
    if (bulkEditingEtude) return;
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  const handleSaveField = async (field) => {
    if (Number(user?.fonction) === 5 && COMMERCIAL_SESSION_READONLY_FIELDS.has(field)) {
      handleCancelEdit();
      return;
    }
    await updateFieldMutation.mutateAsync({ field, value: editValue });
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleStartBulkEditEtude = () => {
    if (bulkEditingEtude) {
      setBulkEditingEtude(false);
      setBulkEditValues({});
      setBulkEditInitial({});
      return;
    }
    handleCancelEdit();
    const initials = { ...etudeBulkFieldsRef.current };
    setBulkEditInitial(initials);
    setBulkEditValues(initials);
    setBulkEditingEtude(true);
  };

  const handleSaveBulkEditEtude = async () => {
    if (bulkEditSaving) return;
    const changed = Object.keys(bulkEditValues).filter(
      (field) => String(bulkEditValues[field] ?? '') !== String(bulkEditInitial[field] ?? '')
    );
    if (changed.length === 0) {
      setBulkEditingEtude(false);
      setBulkEditValues({});
      setBulkEditInitial({});
      return;
    }
    setBulkEditSaving(true);
    try {
      for (const field of changed) {
        await api.patch(`/fiches/${hash}/field`, { field, value: bulkEditValues[field] });
      }
      queryClient.invalidateQueries(['fiche', hash]);
      queryClient.invalidateQueries(['planning-commercial']);
      queryClient.invalidateQueries(['fiches']);
      queryClient.invalidateQueries(['modifica', hash]);
      setBulkEditingEtude(false);
      setBulkEditValues({});
      setBulkEditInitial({});
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement groupé:', error);
      alert('Erreur lors de l\'enregistrement: ' + (error.response?.data?.message || error.message));
    } finally {
      setBulkEditSaving(false);
    }
  };

  const handleSelectPlanningSlot = async (date, hour, rdvId = null, availabilityData = null) => {
    // Ouvrir le modal avec le formulaire
    // Vérifier et formater correctement la date et l'heure
    let dateStr = date;
    let timeStr = hour;
    
    // Si hour est au format "HH:MM:SS", extraire seulement "HH:MM"
    if (timeStr && timeStr.includes(':')) {
      const timeParts = timeStr.split(':');
      timeStr = `${timeParts[0]}:${timeParts[1] || '00'}`;
    } else if (!timeStr) {
      timeStr = '00:00';
    }
    
    // S'assurer que date est au format YYYY-MM-DD
    if (!dateStr || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Essayer de parser la date si elle n'est pas au bon format
      try {
        const parsedDate = new Date(dateStr);
        if (!isNaN(parsedDate.getTime())) {
          dateStr = formatDateLocal(parsedDate);
        } else {
          console.error('Date invalide:', dateStr);
          alert('Erreur: Date invalide');
          return;
        }
      } catch (e) {
        console.error('Erreur lors du parsing de la date:', e);
        alert('Erreur: Format de date invalide');
        return;
      }
    }

    if (isRdvDateBeforeToday(dateStr)) {
      alert('Impossible de créer un RDV à une date antérieure à aujourd\'hui.');
      return;
    }

    const slotHour = timeToSlotHour(timeStr) || timeStr;
    const slotCell = lookupAvailabilityCell(availabilityData, dateStr, timeStr)
      || lookupAvailabilityCell(availabilityData, dateStr, slotHour);
    const planningCached = getPlanningWeekDataCached();
    const policyClosed = isPolicyClosedAvailabilityCell(slotCell, planningDep, dateStr, slotHour);
    pendingKnownSlotStatusRef.current = {
      date: dateStr,
      hour: slotHour,
      closed: isClosedAvailabilityCell(slotCell) || policyClosed,
      // Catégorie « 0 disponibilité » : 0 ou null
      zero: isZeroAvailabilityCell(slotCell) || isSansDispoAvailabilityCell(slotCell),
      sansDispo: isSansDispoAvailabilityCell(slotCell),
      policyClosed,
      surplus: isBlindOrSurplusSlot(planningCached, dateStr, slotHour, slotCell),
    };
    
    // Initialiser le formulaire avec toutes les données de confirmation déjà saisies
    const nextRdvFormData = buildRdvFormStateFromFiche(ficheData, user, { dateStr, timeStr });

    setRdvFormData(nextRdvFormData);
    setShowRdvConfirmateur2(!!nextRdvFormData.id_confirmateur_2);
    setShowRdvConfirmateur3(!!nextRdvFormData.id_confirmateur_3);
    
    setSelectedSlot({ date, hour });
    setShowRdvModal(true);
  };

  const handleOpenConfirmEtatFromPlanningSlot = (date, hour) => {
    if (!canOpenConfirmEtatFromPlanning) {
      return;
    }

    let dateStr = date;
    let timeStr = hour;

    if (timeStr && timeStr.includes(':')) {
      const timeParts = timeStr.split(':');
      timeStr = `${timeParts[0]}:${timeParts[1] || '00'}`;
    } else if (!timeStr) {
      timeStr = '00:00';
    }

    if (!dateStr || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      try {
        const parsedDate = new Date(dateStr);
        if (!isNaN(parsedDate.getTime())) {
          dateStr = formatDateLocal(parsedDate);
        } else {
          console.error('Date invalide:', dateStr);
          alert('Erreur: Date invalide');
          return;
        }
      } catch (e) {
        console.error('Erreur lors du parsing de la date:', e);
        alert('Erreur: Format de date invalide');
        return;
      }
    }

    if (isRdvDateBeforeToday(dateStr)) {
      alert('Impossible de créer un RDV à une date antérieure à aujourd\'hui.');
      return;
    }

    const slotHour = timeToSlotHour(timeStr) || timeStr;
    const cachedAvail = queryClient.getQueryData(['availability-modal', planningWeek, planningYear, planningDep]);
    const slotCell = lookupAvailabilityCell(cachedAvail?.data, dateStr, slotHour);
    const policyClosed = isPolicyClosedAvailabilityCell(slotCell, planningDep, dateStr, slotHour);
    if (policyClosed && !canCreateRdvOnPolicyClosedSlot(user?.fonction)) {
      alert('Ce créneau est fermé pour ce département. Vous ne pouvez pas y créer de RDV.');
      return;
    }
    const planningCached = getPlanningWeekDataCached();
    pendingKnownSlotStatusRef.current = {
      date: dateStr,
      hour: slotHour,
      closed: isClosedAvailabilityCell(slotCell) || policyClosed,
      zero: isZeroAvailabilityCell(slotCell) || isSansDispoAvailabilityCell(slotCell),
      sansDispo: isSansDispoAvailabilityCell(slotCell),
      policyClosed,
      surplus: isBlindOrSurplusSlot(planningCached, dateStr, slotHour, slotCell)
    };

    setSelectedSlot({ date: dateStr, hour });
    setShowRdvModal(false);
    setActiveTab('fiches');
    setSelectedEtat(7);
    const nextConf = {
      ...buildConfFormStateFromFiche(ficheData, user),
      conf_rdv_date: dateStr,
      conf_rdv_time: timeStr
    };
    setConfFormData(nextConf);
    setShowConfConfirmateur2(!!nextConf.id_confirmateur_2);
    setShowConfConfirmateur3(!!nextConf.id_confirmateur_3);
    confFormHydratePendingRef.current = false;

    setTimeout(() => {
      etatChangeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const resolvePlanningDepFromFiche = () => {
    let dep = planningDep;
    if (!dep && ficheData?.cp) {
      const cpStr = String(ficheData.cp).trim();
      if (/^\d/.test(cpStr)) dep = cpStr.substring(0, 2);
      else {
        const m = cpStr.match(/\d{2}/);
        if (m) dep = m[0];
      }
    }
    if (dep && String(dep).length === 2 && /^\d{2}$/.test(String(dep))) return String(dep);
    return null;
  };

  const getPlanningWeekDataCached = () => {
    const keys = [
      ['planning-modal', planningWeek, planningYear, planningDep],
      ['planning-week', planningWeek, planningYear, planningDep],
      ['planning', planningWeek, planningYear, planningDep],
    ];
    for (const key of keys) {
      if (!key[1] || !key[2] || !key[3]) continue;
      const cached = queryClient.getQueryData(key);
      if (cached?.data) return cached.data;
      if (cached && typeof cached === 'object' && !cached.data) {
        const sample = Object.values(cached)[0];
        if (sample && sample.time) return cached;
      }
    }
    return null;
  };

  const getPlanningSlotStatus = async (dateStr, timeStr) => {
    const dep = resolvePlanningDepFromFiche();
    const slotHour = timeToSlotHour(timeStr)
      || (selectedSlot?.hour ? String(selectedSlot.hour) : null);
    const policyClosedFallback = isPolicyClosedSlot(dep, dateStr, slotHour);
    const empty = {
      closed: policyClosedFallback,
      zero: false,
      sansDispo: false,
      surplus: false,
      date: dateStr,
      hour: slotHour,
      dep,
      policyClosed: policyClosedFallback
    };
    if (!dateStr) return empty;

    const known = pendingKnownSlotStatusRef.current;
    if (known && known.date === dateStr && (known.closed || known.zero || known.sansDispo || known.policyClosed || known.surplus)) {
      const policyClosed = known.policyClosed || isPolicyClosedSlot(dep, dateStr, slotHour || known.hour);
      return {
        ...known,
        hour: slotHour || known.hour,
        dep,
        closed: known.closed || policyClosed,
        policyClosed,
        surplus: !!known.surplus,
        sansDispo: !!known.sansDispo,
      };
    }

    let planningWeekData = getPlanningWeekDataCached();
    const readCell = (map) => lookupAvailabilityCell(map, dateStr, slotHour);
    const buildStatus = (cell, weekData = planningWeekData) => {
      const policyClosed = isPolicyClosedAvailabilityCell(cell, dep, dateStr, slotHour);
      const sansDispo = isSansDispoAvailabilityCell(cell);
      return {
        closed: isClosedAvailabilityCell(cell) || policyClosed,
        // Catégorie « 0 disponibilité »
        zero: isZeroAvailabilityCell(cell) || sansDispo,
        sansDispo,
        surplus: isBlindOrSurplusSlot(weekData, dateStr, slotHour, cell),
        date: dateStr,
        hour: slotHour,
        dep,
        policyClosed
      };
    };
    const statusNeedsWarning = (st) =>
      !!(st && (st.closed || st.zero || st.policyClosed || st.surplus));

    const cachedKeys = [
      ['availability-modal', planningWeek, planningYear, planningDep],
      ['planning-availability', planningWeek, planningYear, planningDep]
    ];
    for (const key of cachedKeys) {
      if (!key[1] || !key[2] || !key[3]) continue;
      const cached = queryClient.getQueryData(key);
      const cell = readCell(cached?.data);
      if (cell) {
        const st = buildStatus(cell);
        if (statusNeedsWarning(st)) return st;
      }
    }

    try {
      const allCached = queryClient.getQueriesData({ queryKey: ['availability-modal'] });
      for (const [, cached] of allCached) {
        const cell = readCell(cached?.data);
        if (cell) {
          const st = buildStatus(cell);
          if (statusNeedsWarning(st)) return st;
        }
      }
    } catch (err) {
      /* ignore cache scan */
    }

    if (policyClosedFallback) return empty;
    if (!dep || !slotHour) return empty;
    try {
      const rdvDate = new Date(`${dateStr}T12:00:00`);
      const week = getWeekNumber(rdvDate);
      const isoThursday = new Date(Date.UTC(rdvDate.getFullYear(), rdvDate.getMonth(), rdvDate.getDate()));
      const dayNum = isoThursday.getUTCDay() || 7;
      isoThursday.setUTCDate(isoThursday.getUTCDate() + 4 - dayNum);
      const yearsToTry = [...new Set([isoThursday.getUTCFullYear(), rdvDate.getFullYear(), planningYear].filter(Boolean))];
      for (const year of yearsToTry) {
        const [availRes, weekRes] = await Promise.all([
          api.get('/planning/availability', { params: { w: week, y: year, dp: dep } }),
          api.get('/planning/week', { params: { w: week, y: year, dp: dep } }).catch(() => null),
        ]);
        if (weekRes?.data?.data) planningWeekData = weekRes.data.data;
        const cell = readCell(availRes.data?.data);
        if (cell || planningWeekData) {
          const st = buildStatus(cell, planningWeekData);
          if (statusNeedsWarning(st) || cell) return st;
        }
      }
      return empty;
    } catch (err) {
      console.error('Erreur lors de la lecture du créneau planning:', err);
      return empty;
    }
  };

  const openSlotCodeModalIfNeeded = async (dateStr, timeStr, thenFn) => {
    if (slotCodeVerifiedRef.current) return true;
    const status = await getPlanningSlotStatus(dateStr, timeStr);
    const depForPolicy = status.dep || resolvePlanningDepFromFiche();
    const slotHour = timeToSlotHour(timeStr) || timeStr;
    const policyClosed = status.policyClosed || isPolicyClosedSlot(depForPolicy, dateStr, slotHour);
    if (policyClosed && !canCreateRdvOnPolicyClosedSlot(user?.fonction)) {
      alert('Ce créneau est fermé pour ce département. Vous ne pouvez pas y créer de RDV.');
      return false;
    }
    // Toutes sessions : warning + code si 0 dispo / null / surplus (ou créneau fermé)
    const needsWarning = !!(
      status.zero ||
      status.sansDispo ||
      status.surplus ||
      status.closed ||
      policyClosed
    );
    if (!needsWarning) return true;
    pendingAfterSlotCodeRef.current = thenFn;
    const bypassCtx = {
      closed: !!(status.closed || policyClosed),
      zero: !!(status.zero || status.sansDispo),
      sansDispo: !!status.sansDispo,
      policyClosed: !!policyClosed,
      surplus: !!status.surplus
    };
    slotBypassContextRef.current = bypassCtx;
    setSlotCodeModal({
      ...status,
      ...bypassCtx
    });
    return false;
  };

  const handleSlotCodeVerified = () => {
    slotCodeVerifiedRef.current = true;
    setSlotCodeModal(null);
    const next = pendingAfterSlotCodeRef.current;
    pendingAfterSlotCodeRef.current = null;
    if (typeof next === 'function') next();
  };

  // Fonction pour créer le RDV depuis le formulaire (formData optionnel = données avec professions résolues)
  const handleCreateRdvFromForm = async (formData) => {
    const data = formData || rdvFormData;

    const createValidation = validateCreateRdvForm({
      formData: data,
      produits,
      requireConfirmateur: true,
      mode: 'create',
    });
    if (!createValidation.valid) {
      alertCreateRdvValidation(createValidation.missing);
      return;
    }

    if (!data.date_rdv_time) {
      alert('Veuillez remplir la date et l\'heure du RDV');
      return;
    }

    const dateRdvNormalized = data.date_rdv_time.includes(':')
      ? data.date_rdv_time
      : `${data.date_rdv_time}:00`;
    if (isRdvDateBeforeToday(dateRdvNormalized)) {
      alert('Impossible de créer un RDV à une date antérieure à aujourd\'hui.');
      return;
    }
    if (!isBeforeRdvDateTime(dateRdvNormalized)) {
      alert('La date du RDV est dépassée. Impossible de créer un RDV à une date passée.');
      return;
    }

    const datePart = String(dateRdvNormalized).split(/[T\s]/)[0];
    const timePart = String(dateRdvNormalized).split(/[T\s]/)[1] || '';
    const canProceedSlot = await openSlotCodeModalIfNeeded(datePart, timePart, () => handleCreateRdvFromForm(data));
    if (!canProceedSlot) return;

    setRdvSubmitting(true);
    try {
      // Vérifier la disponibilité du créneau
      let needsApproval = false;
      let availabilityCount = null;
      let availabilityFromPlanning = null;
      let rdvCountInSlot = 0;

      // Récupérer le nombre de RDV confirmés et la disponibilité depuis le planning
      if (planningWeek && planningYear && planningDep && selectedSlot) {
        try {
          const planningRes = await api.get('/planning/week', {
            params: { w: planningWeek, y: planningYear, dp: planningDep }
          });
          const planningData = planningRes.data?.data || {};
          const timeKey = hourToTimeKey(selectedSlot.hour);
          const slotPlanning = resolveDayPlanningTimeSlot(planningData, selectedSlot.date, timeKey);
          if (slotPlanning) {
            availabilityFromPlanning = slotPlanning.av !== undefined ? slotPlanning.av : null;
            if (slotPlanning.planning) {
              // Créneau « plein » = tous les RDV renvoyés par l’API (cohérent avec le badge X / Y)
              rdvCountInSlot = slotPlanning.planning.length;
            }
          }
        } catch (err) {
          console.error('Erreur lors de la récupération du planning:', err);
        }
      }

      if (availabilityFromPlanning !== null && availabilityFromPlanning !== undefined) {
        availabilityCount = availabilityFromPlanning;
      }

      // Surplus uniquement si on dépasse la capacité (ex. 3/2), pas à égalité (2/2)
      if (availabilityCount !== null && availabilityCount !== undefined) {
        if (Number(availabilityCount) > 0 && rdvCountInSlot > Number(availabilityCount)) {
          needsApproval = true;
        }
      }

      // Vérifier la permission pour créer un RDV sans disponibilité
      const canCreateWithoutAvailability = permissions && permissions['CREATE_RDV_NO_AVAILABILITY'] === true;
      
      if (needsApproval && canCreateWithoutAvailability) {
        needsApproval = false;
      }

      // Préparer les données de mise à jour
      // RDV_URGENT doit être en état CONFIRMER (7) avec la qualification RDV_URGENT
      const toConfId = (v) => {
        if (v === '' || v == null) return null;
        const n = parseInt(v, 10);
        return Number.isFinite(n) && n > 0 ? n : null;
      };
      const conf1Id =
        Number(user?.fonction) === 6 && user?.id
          ? toConfId(user.id)
          : toConfId(data.id_confirmateur);
      const conf2Id = isNouvelleConfirmationConf1Seul(ficheData)
        ? null
        : toConfId(data.id_confirmateur_2);
      const conf3Id = isNouvelleConfirmationConf1Seul(ficheData)
        ? null
        : toConfId(data.id_confirmateur_3);
      const updateData = {
        date_rdv_time: data.date_rdv_time.includes(':') 
          ? data.date_rdv_time 
          : `${data.date_rdv_time}:00`,
        id_etat_final: 7, // Toujours CONFIRMER (7) - RDV_URGENT est géré via id_qualif
        // Retirer espaces avant/après le CP (évite ex. " 77600" hors planning)
        ...(ficheData?.cp != null && String(ficheData.cp).trim() !== ''
          ? { cp: String(ficheData.cp).trim() }
          : {}),
        produit: data.produit ? parseInt(data.produit) : null,
        conf_produit: data.produit ? parseInt(data.produit) : null,
        // Affectation confirmateurs → fiches + fiches_histo (via histo_id_*)
        id_confirmateur: conf1Id,
        id_confirmateur_2: conf2Id,
        id_confirmateur_3: conf3Id,
        histo_id_confirmateur: conf1Id,
        histo_id_confirmateur_2: conf2Id,
        histo_id_confirmateur_3: conf3Id,
        conf_rdv_avec: data.conf_rdv_avec || null,
        conf_appel_tunisie_avec: data.conf_appel_tunisie_avec || null,
        conf_deja_etude: data.conf_deja_etude || data.conf_deja_fait_etude || null,
        conf_deja_fait_etude: data.conf_deja_fait_etude || data.conf_deja_etude || null,
        conf_details_etude:
          (data.conf_deja_etude === 'OUI' || data.conf_deja_fait_etude === 'OUI')
            ? (data.conf_details_etude || data.details_etude || null)
            : null,
        conf_revenu: data.conf_revenu || null,
        conf_credit: data.conf_credit || null,
        conf_mode_chauffage: data.conf_mode_chauffage?.trim() ? data.conf_mode_chauffage.trim() : null,
        conf_complement_chauffage: data.conf_complement_chauffage?.trim() ? data.conf_complement_chauffage.trim() : null,
        conf_consommation_electricite: data.conf_consommation_electricite || null,
        conf_consommation_chauffage: data.conf_consommation_chauffage || null,
        conf_rdv_annule_precedent: data.conf_rdv_annule_precedent || null,
        conf_presence_couple: data.conf_presence_couple || null,
        // Champs spécifiques PV
        surface_habitable: data.surface_habitable ? parseFloat(data.surface_habitable) : null,
        conf_orientation_toiture: data.conf_orientation_toiture || null,
        conf_zones_ombres: data.conf_zones_ombres || null,
        conf_site_classe: data.conf_site_classe || null,
        nb_pans: data.nb_pans ? parseInt(data.nb_pans) : null,
        // Champs spécifiques PAC
        surface_chauffee: data.surface_chauffee || null,
        consommation_chauffage: data.consommation_chauffage || null,
        mode_chauffage: data.conf_mode_chauffage?.trim() ? data.conf_mode_chauffage.trim() : null,
        annee_systeme_chauffage: data.annee_systeme_chauffage ? parseInt(data.annee_systeme_chauffage) : null,
        conf_commentaire_produit: data.conf_commentaire_produit || null
      };

      // RDV modal : professions toujours affichées — n'écraser la BDD que si une valeur est saisie
      if (String(data.conf_profession_monsieur || '').trim() !== '') {
        updateData.conf_profession_monsieur = data.conf_profession_monsieur;
      }
      if (data.conf_type_contrat_mr) {
        updateData.conf_type_contrat_mr = parseInt(data.conf_type_contrat_mr, 10);
      }
      if (String(data.conf_profession_madame || '').trim() !== '') {
        updateData.conf_profession_madame = data.conf_profession_madame;
      }
      if (data.conf_type_contrat_madame) {
        updateData.conf_type_contrat_madame = parseInt(data.conf_type_contrat_madame, 10);
      }

      // Commercial 2 retiré de la création RDV (toutes sessions) — R2 / champs R1 conservés
      updateData.id_commercial_2 = null;

      const r2Payload = buildR2PayloadFromForm(data);
      if (!r2Payload.ok) {
        alert(r2Payload.message);
        setRdvSubmitting(false);
        return;
      }
      Object.assign(updateData, r2Payload.data);

      if (slotCodeVerifiedRef.current) {
        updateData.allow_unavailable_slot = true;
        const known = slotBypassContextRef.current || pendingKnownSlotStatusRef.current;
        if (known) {
          updateData.slot_bypass_context = {
            surplus: !!known.surplus,
            closed: !!known.closed,
            zero: !!known.zero,
            sansDispo: !!known.sansDispo,
            policyClosed: !!known.policyClosed
          };
        }
      }

      // Vérifier si le RDV est pour aujourd'hui ou demain
      const rdvDate = new Date(updateData.date_rdv_time);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const rdvDateOnly = new Date(rdvDate);
      rdvDateOnly.setHours(0, 0, 0, 0);
      
      // Si le RDV est pour aujourd'hui ou demain, le marquer comme urgent automatiquement
      const isTodayOrTomorrow = rdvDateOnly.getTime() === today.getTime() || rdvDateOnly.getTime() === tomorrow.getTime();
      
      // Gérer rdv_urgent (nouveau champ booléen)
      // Si c'est pour aujourd'hui ou demain, ou si l'utilisateur a coché la case, mettre à 1
      updateData.rdv_urgent = (data.is_urgent || isTodayOrTomorrow) ? 1 : 0;
      
      // Pour rétrocompatibilité, mettre à jour aussi id_qualif si nécessaire
      if (updateData.rdv_urgent === 1) {
        const urgentQualif = qualifications?.find(q => q.code === 'RDV_URGENT');
        if (urgentQualif) {
          updateData.id_qualif = urgentQualif.id;
        } else {
          // Si la table qualif n'existe pas, essayer de trouver par code directement
          updateData.id_qualif = 'RDV_URGENT';
        }
      } else {
        // Si pas urgent, s'assurer que id_qualif est null
        updateData.id_qualif = null;
      }

      // Mettre à jour la fiche
      const res = await api.put(`/fiches/${hash}`, updateData);

      // Vérifier si un compte rendu a été créé (pour les commerciaux)
      if (res.data.data?.id_compte_rendu) {
        alert('Compte rendu créé avec succès. Les modifications sont en attente d\'approbation de l\'administrateur.');
        setShowRdvModal(false);
        setRdvFormData({
          date_rdv_time: '',
          id_etat_final: 7,
          is_urgent: false,
          id_confirmateur: '',
          id_confirmateur_2: '',
          id_confirmateur_3: '',
          produit: '',
          conf_rdv_avec: '',
          conf_appel_tunisie_avec: '',
          conf_deja_etude: '',
          conf_deja_fait_etude: '',
          conf_details_etude: '',
          conf_profession_monsieur: '',
          conf_type_contrat_mr: '',
          conf_profession_madame: '',
          conf_type_contrat_madame: '',
          conf_revenu: '',
          conf_credit: '',
          conf_mode_chauffage: '',
          conf_complement_chauffage: '',
          conf_consommation_electricite: '',
          conf_consommation_chauffage: '',
          conf_rdv_annule_precedent: '',
          conf_presence_couple: '',
          surface_habitable: '',
          conf_orientation_toiture: '',
          conf_zones_ombres: '',
          conf_site_classe: '',
          nb_pans: '',
          surface_chauffee: '',
          consommation_chauffage: '',
          annee_systeme_chauffage: '',
          conf_commentaire_produit: '',
          id_commercial_2: '',
          is_r2: '',
          date_r1: '',
          id_commercial_r1: '',
          commentaire_r1: '',
        });
        return;
      }

      if (needsApproval) {
        alert(`RDV créé en PRE-CONFIRMER. Les notifications d'approbation automatiques sont désactivées.`);
      } else {
        alert(`Rendez-vous créé avec succès${data.is_urgent ? ' (RDV URGENT)' : ' (CONFIRMER)'}`);
      }

      // Fermer le modal
      setShowRdvModal(false);
      setSelectedSlot(null);
      slotCodeVerifiedRef.current = false;
      pendingKnownSlotStatusRef.current = null;
      slotBypassContextRef.current = null;

      // Recharger les données
      queryClient.invalidateQueries(['fiche', hash]);
      queryClient.invalidateQueries(['planning-week']);
      queryClient.invalidateQueries(['planning-availability']);
      queryClient.invalidateQueries(['planning-modal']);
      queryClient.invalidateQueries(['availability-modal']);
      
      if (planningWeek && planningYear && planningDep) {
        await Promise.all([
          queryClient.refetchQueries(['planning-modal', planningWeek, planningYear, planningDep], { active: true }),
          queryClient.refetchQueries(['availability-modal', planningWeek, planningYear, planningDep], { active: true })
        ]);
      }
    } catch (error) {
      console.error('Erreur lors de la création du RDV:', error);
      if (isPlanningSlotClosedError(error) && !slotCodeVerifiedRef.current) {
        pendingAfterSlotCodeRef.current = () => handleCreateRdvFromForm(data);
        setSlotCodeModal({ closed: true, zero: false });
        return;
      }
      alert('Erreur lors de la création du rendez-vous: ' + (error.response?.data?.message || error.message));
    } finally {
      setRdvSubmitting(false);
    }
  };

  // Helper functions pour le planning (comme dans Planning.jsx)
  const getUserColor = (userId) => {
    if (!userId || !usersData) return '#cccccc';
    const user = usersData.find(u => u.id === userId);
    return user?.color || '#cccccc';
  };

  const getUserName = (userId) => {
    if (!userId || !usersData) return '';
    const user = usersData.find(u => u.id === userId);
    return user?.pseudo || '';
  };

  const getAvailabilityColor = (planningCount, availability) => {
    if (availability === 0) return '#cccccc';
    if (planningCount > availability && planningCount > 0) return '#f44336'; // Rouge = surplus (ex. 3/2)
    if (planningCount === availability && planningCount > 0) return '#ff9800'; // Orange = plein exact (2/2)
    if (planningCount < availability && planningCount > 0) return '#f7a219'; // Orange clair = partiel
    return '#8BC34A'; // Vert
  };

  // Obtenir les jours de la semaine
  const getDaysFromPlanning = () => {
    if (!planningWeek || !planningYear) return [];
    
    // Calculer le lundi de la semaine (plus robuste pour les transitions d'année)
    const getMondayOfWeek = (year, week) => {
      // Trouver le 4 janvier de l'année (toujours dans la semaine 1 ISO)
      const simple = new Date(year, 0, 4);
      // Obtenir le jour de la semaine (0 = dimanche, 6 = samedi)
      // En ISO, lundi = 1, donc on ajuste
      const jan4Day = simple.getDay() || 7; // Convertir dimanche (0) en 7
      // Le lundi de la semaine 1 est le 4 janvier moins (jour - 1) jours
      const week1Monday = new Date(year, 0, 4 - (jan4Day - 1));
      // Ajouter (week - 1) semaines pour obtenir le lundi de la semaine demandée
      const targetMonday = new Date(week1Monday);
      targetMonday.setDate(week1Monday.getDate() + (week - 1) * 7);
      return targetMonday;
    };
    
    const monday = getMondayOfWeek(planningYear, planningWeek);
    const daysFr = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
    const days = [];
    
    // Extraire les composants du lundi pour éviter les problèmes de fuseau horaire
    const mondayYear = monday.getFullYear();
    const mondayMonth = monday.getMonth();
    const mondayDay = monday.getDate();
    
    for (let i = 0; i < 5; i++) {
      // Créer la date directement avec les composants (évite les problèmes de fuseau horaire)
      const date = new Date(mondayYear, mondayMonth, mondayDay + i);
      days.push({
        date: formatDateLocal(date),
        dayName: daysFr[i]
      });
    }
    
    return days;
  };

  // Navigation entre les semaines
  const handlePrevWeek = () => {
    if (planningWeek === 1) {
      setPlanningYear(planningYear - 1);
      setPlanningWeek(52);
    } else {
      setPlanningWeek(planningWeek - 1);
    }
  };

  const handleNextWeek = () => {
    if (planningWeek === 52) {
      setPlanningYear(planningYear + 1);
      setPlanningWeek(1);
    } else {
      setPlanningWeek(planningWeek + 1);
    }
  };

  // Formater la date pour l'affichage
  const formatWeekRange = () => {
    const days = getDaysFromPlanning();
    if (days.length === 0) return '';
    // Parser les dates en heure locale pour éviter les problèmes de fuseau horaire
    const parseDateLocal = (dateStr) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    const start = parseDateLocal(days[0].date);
    const end = parseDateLocal(days[days.length - 1].date);
    return `${start.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} au ${end.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
  };

  // Gérer le changement d'état
  const handleEtatChange = (newEtatId) => {
    setSelectedEtat(newEtatId);
    // Warning créneau fermé / 0 dispo : uniquement pour Confirmer — ne pas conserver le statut d’un créneau planning
    if (newEtatId !== 7) {
      pendingKnownSlotStatusRef.current = null;
      slotBypassContextRef.current = null;
      slotCodeVerifiedRef.current = false;
      setSlotCodeModal(null);
      pendingAfterSlotCodeRef.current = null;
    }
    if ([5, 6, 11, 12, 22, 23, 24, 25, 26, 29, 34, 55].includes(newEtatId)) {
      setEtatFormData(prev => ({ ...prev, motif_qualif: '', id_sous_etat: '' }));
    }
    // État 19 (RAPPEL POUR BUREAU) : toujours date/heure actuelles (comme NRP)
    if (newEtatId === 19) {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const hh = String(now.getHours()).padStart(2, '0');
      const min = String(now.getMinutes()).padStart(2, '0');
      setEtatFormData(prev => ({
        ...prev,
        date_rappel_date: `${yyyy}-${mm}-${dd}`,
        date_rappel_time: `${hh}:${min}`,
        id_sous_etat: '',
        conf_commentaire_produit: '',
        motif_qualif: ''
      }));
    }
    // NRP (2) : date/heure d'appel TOUJOURS forcées à « maintenant » lors d'un
    // changement d'état vers NRP (on n'utilise jamais la valeur précédente afin
    // que la fiche enregistre l'heure exacte de l'action en cours).
    if (newEtatId === 2) {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const hh = String(now.getHours()).padStart(2, '0');
      const min = String(now.getMinutes()).padStart(2, '0');
      setNrpFormData((prev) => ({
        ...prev,
        date_appel_date: `${yyyy}-${mm}-${dd}`,
        date_appel_time: `${hh}:${min}`,
        id_sous_etat: '',
        conf_commentaire_produit: '',
      }));
    }
    // Si l'état est 7 (confirmer), initialiser les valeurs du formulaire depuis la fiche (tous les conf_*)
    // + dernière confirmation historisée si la fiche a déjà été confirmée (admin, BO, RE, RP, confirmateur).
    if (newEtatId === 7) {
      const nextConf = buildConfFormStateFromFiche(ficheData, user);
      setConfFormData(nextConf);
      setShowConfConfirmateur2(!!nextConf.id_confirmateur_2);
      setShowConfConfirmateur3(!!nextConf.id_confirmateur_3);
      setShowConfirmConfFields(true);
      confFormHydratePendingRef.current = !ficheData?.id;
    } else {
      setConfFormData({ ...EMPTY_CONF_FORM_BASE });
      setShowConfConfirmateur2(false);
      setShowConfConfirmateur3(false);
      setConfProfMrDisplay('');
      setConfProfMmeDisplay('');
      setShowSuggestionsMr(false);
      setShowSuggestionsMme(false);
    }
  };

  // Soumettre la confirmation (état 7)
  const handleConfirmSubmit = async () => {
    if (confirmSubmittingRef.current) return;
    confirmSubmittingRef.current = true;
    setConfirmSubmitting(true);
    try {
      const confValidation = validateCreateRdvForm({
        formData: {
          ...confFormData,
          // Session confirmateur : conf1 = utilisateur connecté même si le select est figé
          id_confirmateur:
            isConfirmateurSession && user?.id
              ? String(user.id)
              : confFormData.id_confirmateur,
        },
        produits,
        requireConfirmateur: true,
        mode: 'confirm',
      });
      if (!confValidation.valid) {
        setShowConfirmConfFields(true);
        alertCreateRdvValidation(confValidation.missing);
        return;
      }

      // Construire la date/heure du RDV
      const dateRdvTime = confFormData.conf_rdv_date && confFormData.conf_rdv_time 
        ? `${confFormData.conf_rdv_date} ${confFormData.conf_rdv_time}:00`
        : null;

      if (dateRdvTime && isRdvDateBeforeToday(dateRdvTime)) {
        alert('Impossible de créer un RDV à une date antérieure à aujourd\'hui.');
        return;
      }
      if (dateRdvTime && !isBeforeRdvDateTime(dateRdvTime)) {
        alert('La date du RDV est dépassée. Impossible de créer un RDV à une date passée.');
        return;
      }

      if (confFormData.conf_rdv_date && confFormData.conf_rdv_time) {
        const canProceedSlot = await openSlotCodeModalIfNeeded(
          confFormData.conf_rdv_date,
          confFormData.conf_rdv_time,
          () => handleConfirmSubmit()
        );
        if (!canProceedSlot) return;
      }

      // Préparer les données à envoyer
      const toConfId = (v) => {
        if (v === '' || v == null) return null;
        const n = parseInt(v, 10);
        return Number.isFinite(n) && n > 0 ? n : null;
      };
      const conf1Id = isConfirmateurSession && user?.id
        ? toConfId(user.id)
        : toConfId(confFormData.id_confirmateur);
      const conf2Id = isNouvelleConfirmationConf1Seul(ficheData)
        ? null
        : toConfId(confFormData.id_confirmateur_2);
      const conf3Id = isNouvelleConfirmationConf1Seul(ficheData)
        ? null
        : toConfId(confFormData.id_confirmateur_3);
      const updateData = {
        id_etat_final: 7,
        produit: confFormData.produit ? parseInt(confFormData.produit) : null,
        // Retirer espaces avant/après le CP (évite ex. " 77600" hors planning)
        ...(ficheData?.cp != null && String(ficheData.cp).trim() !== ''
          ? { cp: String(ficheData.cp).trim() }
          : {}),
        // Affectation confirmateurs → fiches + fiches_histo (via histo_id_*)
        id_confirmateur: conf1Id,
        id_confirmateur_2: conf2Id,
        id_confirmateur_3: conf3Id,
        histo_id_confirmateur: conf1Id,
        histo_id_confirmateur_2: conf2Id,
        histo_id_confirmateur_3: conf3Id,
        date_rdv_time: dateRdvTime,
        conf_rdv_avec: confFormData.conf_rdv_avec || null,
        situation_conjugale: confFormData.situation_conjugale || null,
        conf_appel_tunisie_avec: confFormData.conf_appel_tunisie_avec || null,
        conf_deja_etude: confFormData.conf_deja_etude || confFormData.conf_deja_fait_etude || null,
        conf_deja_fait_etude: confFormData.conf_deja_fait_etude || confFormData.conf_deja_etude || null,
        conf_details_etude:
          (confFormData.conf_deja_fait_etude || confFormData.conf_deja_etude) === 'OUI'
            ? (confFormData.conf_details_etude || null)
            : null,
        conf_revenu: confFormData.conf_revenu || null,
        conf_credit: confFormData.conf_credit || null,
        conf_mode_chauffage: confFormData.conf_mode_chauffage?.trim() ? confFormData.conf_mode_chauffage.trim() : null,
        conf_complement_chauffage: confFormData.conf_complement_chauffage?.trim() ? confFormData.conf_complement_chauffage.trim() : null,
        conf_consommation_electricite: confFormData.conf_consommation_electricite || null,
        conf_consommation_chauffage: confFormData.conf_consommation_chauffage || null,
        conf_rdv_annule_precedent: confFormData.conf_rdv_annule_precedent || null,
        conf_presence_couple: confFormData.conf_presence_couple || null,
        conf_orientation_toiture: confFormData.conf_orientation_toiture || null,
        conf_zones_ombres: confFormData.conf_zones_ombres || null,
        conf_site_classe: confFormData.conf_site_classe || null,
        nb_pans: confFormData.nb_pans ? parseInt(confFormData.nb_pans) : null,
        mode_chauffage: confFormData.conf_mode_chauffage?.trim() ? confFormData.conf_mode_chauffage.trim() : null,
        annee_systeme_chauffage: confFormData.annee_systeme_chauffage ? parseInt(confFormData.annee_systeme_chauffage) : null,
        surface_chauffee: confFormData.surface_chauffee ? parseFloat(confFormData.surface_chauffee) : null,
        consommation_chauffage: confFormData.consommation_chauffage || null,
        conf_commentaire_produit: confFormData.conf_commentaire_produit || null
      };

      // Profession / type contrat : n'envoyer que les champs visibles (sinon null écrasait la BDD)
      if (showConfProfessionMrFields(confFormData.situation_conjugale, confFormData.conf_rdv_avec)) {
        updateData.conf_profession_monsieur = confFormData.conf_profession_monsieur || null;
        updateData.conf_type_contrat_mr = confFormData.conf_type_contrat_mr
          ? parseInt(confFormData.conf_type_contrat_mr, 10)
          : null;
      }
      if (showConfProfessionMmeFields(confFormData.situation_conjugale, confFormData.conf_rdv_avec)) {
        updateData.conf_profession_madame = confFormData.conf_profession_madame || null;
        updateData.conf_type_contrat_madame = confFormData.conf_type_contrat_madame
          ? parseInt(confFormData.conf_type_contrat_madame, 10)
          : null;
      }

      // Commercial 2 retiré de la confirmation RDV (toutes sessions) — R2 / champs R1 conservés
      updateData.id_commercial_2 = null;

      const r2Payload = buildR2PayloadFromForm(confFormData);
      if (!r2Payload.ok) {
        alert(r2Payload.message);
        return;
      }
      Object.assign(updateData, r2Payload.data);

      if (slotCodeVerifiedRef.current) {
        updateData.allow_unavailable_slot = true;
        const known = slotBypassContextRef.current || pendingKnownSlotStatusRef.current;
        if (known) {
          updateData.slot_bypass_context = {
            surplus: !!known.surplus,
            closed: !!known.closed,
            zero: !!known.zero,
            sansDispo: !!known.sansDispo,
            policyClosed: !!known.policyClosed
          };
        }
      }

      // Appeler l'API pour mettre à jour
      const res = await api.put(`/fiches/${hash}`, updateData);
      
      if (res.data.success) {
        // Recharger les données
        queryClient.invalidateQueries(['fiche', hash]);
        queryClient.invalidateQueries(['fiches']);
        queryClient.invalidateQueries(['modifica', hash]); // Invalider les modifications
        queryClient.invalidateQueries(['planning-commercial']); // Mettre à jour le planning commercial
        setSelectedEtat(null);
        setCompteRenduOption('');
        setEditingCompteRendu(null);
        setConfFormData({ ...EMPTY_CONF_FORM_BASE });
        alert('Fiche confirmée avec succès');
        slotCodeVerifiedRef.current = false;
        pendingKnownSlotStatusRef.current = null;
        slotBypassContextRef.current = null;
      }
    } catch (error) {
      console.error('Erreur lors de la confirmation:', error);
      if (isPlanningSlotClosedError(error) && !slotCodeVerifiedRef.current) {
        pendingAfterSlotCodeRef.current = () => handleConfirmSubmit();
        setSlotCodeModal({ closed: true, zero: false });
        return;
      }
      alert('Erreur lors de la confirmation de la fiche: ' + (error.response?.data?.message || error.message));
    } finally {
      confirmSubmittingRef.current = false;
      setConfirmSubmitting(false);
    }
  };

  // Mutation pour modifier un compte rendu
  const updateCompteRenduMutation = useMutation(
    async ({ crId, data }) => {
      const res = await api.put(`/compte-rendu/${crId}`, data);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fiche', hash]);
        queryClient.invalidateQueries(['planning-commercial']); // Mettre à jour le planning commercial
        setEditingCompteRendu(null);
        setCompteRenduOption('');
        setSelectedEtat(null);
        alert('Compte rendu modifié avec succès');
      },
      onError: (error) => {
        alert('Erreur lors de la modification du compte rendu: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  // Soumettre le changement d'état pour les autres états
  const handleCompteRenduOptionChange = (value) => {
    setCompteRenduOption(value);
    applyCompteRenduOptionChange(value, {
      etatFormData,
      ficheData,
      splitDateTimeForInput,
      setSelectedEtat,
      setEtatFormData,
    });
  };

  const handleEtatSubmit = async () => {
    try {
      if (!selectedEtat) {
        alert('Veuillez sélectionner un état');
        return;
      }

      if (
        ficheData?.id_etat_final != null &&
        ETATS_SANS_NOUVEL_ETAT.includes(Number(ficheData.id_etat_final)) &&
        !canBypassEtatDefinitif(user?.fonction)
      ) {
        alert('Impossible de modifier l\'état : la fiche est dans un état définitif (annuler ou refus 2 fois, RDV annuler 2 fois, HHC financement à vérifier ou HHC technique).');
        return;
      }

      const isCommercialAnnulerReproSimple =
        Number(user?.fonction) === 5 &&
        isCommercialAnnulerReproSimpleOption(compteRenduOption) &&
        Number(selectedEtat) === 8;

      // Validation : sous-état obligatoire si la liste existe (sauf états 11 et 12 où il est facultatif)
      if (
        etatsSousEtatObligatoire.includes(selectedEtat) &&
        sousEtats &&
        sousEtats.length > 0 &&
        !(selectedEtat === 8 && isCommercialAnnulerReproSimple)
      ) {
        const idSousEtat =
          selectedEtat === 2
            ? (nrpFormData.id_sous_etat || '').toString().trim()
            : (etatFormData.id_sous_etat || '').toString().trim();
        if (!idSousEtat) {
          alert('Veuillez sélectionner un sous-état.');
          return;
        }
      }

      if (ETATS_MOTIF_QUALIF_REQUIS.includes(selectedEtat)) {
        const motifCommentaire = (etatFormData.motif_qualif || etatFormData.conf_commentaire_produit || '').trim();
        if (!motifCommentaire) {
          alert('Veuillez saisir un commentaire.');
          return;
        }
      }

      // HC (55) : sous-état (motif) obligatoire ; commentaire obligatoire si motif AUTRE
      if (Number(selectedEtat) === 55) {
        const idSousEtatHc = (etatFormData.id_sous_etat || '').toString().trim();
        if (!idSousEtatHc) {
          alert('Veuillez sélectionner un sous-état HC.');
          return;
        }
        const selectedHcSousEtat = (sousEtats || []).find(
          (se) => String(se.id) === String(idSousEtatHc)
        );
        const isHcAutre = String(selectedHcSousEtat?.titre || '').trim().toUpperCase() === 'AUTRE';
        const commentaireHc = (etatFormData.motif_qualif || etatFormData.conf_commentaire_produit || '').trim();
        if (isHcAutre && !commentaireHc) {
          alert('Veuillez préciser le motif (commentaire obligatoire pour AUTRE).');
          return;
        }
      }

      // Validation : commentaire obligatoire lorsque le formulaire le propose
      // (11 et 12 : commentaire facultatif ; NRP (2) : commentaire facultatif)
      // Session commercial : le champ « Compte rendu » alimente conf_commentaire_produit
      const etatsAvecCommentaire = [8, 9, 19, 23, 34, 13, 44, 45, 16, 38, 35];
      if (etatsAvecCommentaire.includes(selectedEtat)) {
        const comment =
          (
            etatFormData.conf_commentaire_produit ||
            etatFormData.motif_qualif ||
            ''
          ).trim();
        if (!comment) {
          alert('Veuillez saisir un commentaire.');
          return;
        }
      }

      // HHC FINANCEMENT A VERIFIER (34) : "Appel avec qui" obligatoire (hors session commercial)
      if (
        selectedEtat === 34 &&
        Number(user?.fonction) !== 5 &&
        !(etatFormData.conf_rdv_avec || '').trim()
      ) {
        alert('Veuillez renseigner "Appel avec qui".');
        return;
      }

      // SIGNER (13, 44, 45) : tous les champs obligatoires (sauf Commercial 2)
      if ([13, 44, 45].includes(selectedEtat)) {
        const signerValidation = validateSignerCompteRenduForm({
          etatFormData,
          produits,
          sousEtats,
          requireCommercial: [1, 2, 7].includes(Number(user?.fonction)),
        });
        if (!signerValidation.valid) {
          alertSignerCompteRenduValidation(signerValidation.missing);
          return;
        }
      }

      if (requiresEarlyCrGate && !earlyCrVerified) {
        alert('Veuillez valider le code à 4 chiffres pour accéder au compte rendu avant le rendez-vous.');
        return;
      }

      // Si on modifie un compte rendu existant
      if (editingCompteRendu) {
        const crToEdit = ficheData?.comptes_rendus?.find(cr => cr.id === editingCompteRendu);
        if (!crToEdit) {
          alert('Compte rendu non trouvé');
          return;
        }

        // Construire les données de modification selon le type d'état
        const modifications = {};
        if (compteRenduOption) {
          modifications.compte_rendu_option = resolveOptionKey(compteRenduOption);
        }
        const commentaireEtat =
          selectedEtat === 2
            ? (nrpFormData.conf_commentaire_produit || '')
            : (
                etatFormData.conf_commentaire_produit ||
                etatFormData.motif_qualif ||
                ''
              );

        const updateData = {
          id_etat_final: selectedEtat,
          commentaire: commentaireEtat
        };
        // Pour les options « Annuler à reprogrammer » côté commercial, seul le commentaire est saisi ici.
        if (!isCommercialAnnulerReproSimple) {
          updateData.id_sous_etat = selectedEtat === 2
            ? (nrpFormData.id_sous_etat ? parseInt(nrpFormData.id_sous_etat) : null)
            : (etatFormData.id_sous_etat ? parseInt(etatFormData.id_sous_etat) : null);
        }

        // Pour SIGNER, ajouter les champs Phase 3
        if ([13, 44, 45].includes(selectedEtat)) {
          if (etatFormData.date_sign_date && etatFormData.date_sign_time) {
            const dateSignStr = `${etatFormData.date_sign_date} ${etatFormData.date_sign_time}:00`;
            modifications.date_sign_time = dateSignStr;
          }
          if (etatFormData.produit) {
            modifications.produit = parseInt(etatFormData.produit);
            updateData.produit = parseInt(etatFormData.produit, 10);
          }
          if ([1, 2, 7].includes(Number(user?.fonction))) {
            modifications.id_commercial =
              etatFormData.id_commercial && String(etatFormData.id_commercial).trim() !== ''
                ? parseInt(etatFormData.id_commercial, 10)
                : null;
            modifications.id_commercial_2 =
              etatFormData.id_commercial_2 && String(etatFormData.id_commercial_2).trim() !== ''
                ? parseInt(etatFormData.id_commercial_2, 10)
                : null;
          } else if (user?.fonction !== 5 && etatFormData.id_commercial) {
            modifications.id_commercial = parseInt(etatFormData.id_commercial, 10);
            if (etatFormData.id_commercial_2) {
              modifications.id_commercial_2 = parseInt(etatFormData.id_commercial_2, 10);
            }
          }
          if (etatFormData.pseudo) {
            modifications.pseudo = etatFormData.pseudo;
            updateData.pseudo = etatFormData.pseudo;
          }
          if (etatFormData.conf_consommations) {
            modifications.conf_consommations = etatFormData.conf_consommations;
            updateData.conf_consommations = etatFormData.conf_consommations;
          }
          if (etatFormData.valeur_mensualite) {
            modifications.valeur_mensualite = etatFormData.valeur_mensualite;
            updateData.valeur_mensualite = etatFormData.valeur_mensualite;
          }
          
          updateData.ph3_pac = etatFormData.ph3_pac || null;
          updateData.ph3_rr_model = etatFormData.ph3_rr_model || null;
          updateData.ph3_puissance = etatFormData.ph3_puissance || null;
          updateData.ph3_ballon = etatFormData.ph3_ballon || null;
          updateData.ph3_marque_ballon = etatFormData.ph3_marque_ballon || null;
          updateData.ph3_alimentation = etatFormData.ph3_alimentation || null;
          updateData.ph3_type = etatFormData.ph3_type || null;
          updateData.ph3_prix = etatFormData.ph3_prix || null;
          updateData.ph3_installateur = etatFormData.ph3_installateur ? parseInt(etatFormData.ph3_installateur) : null;
          updateData.ph3_bonus_30 = etatFormData.ph3_bonus_30 || null;
          updateData.ph3_mensualite = etatFormData.ph3_mensualite || null;
          updateData.ph3_attente = etatFormData.ph3_attente || null;
          updateData.nbr_annee_finance = etatFormData.nbr_annee_finance || null;
          updateData.credit_immobilier = etatFormData.credit_immobilier || null;
          updateData.credit_autre = etatFormData.credit_autre || null;
        } else if (selectedEtat === 8 && !isCommercialAnnulerReproSimple) {
          // ANNULER À REPROGRAMMER
          if (etatFormData.conf_rdv_date && etatFormData.conf_rdv_time) {
            modifications.conf_rdv_date = etatFormData.conf_rdv_date;
            modifications.conf_rdv_time = etatFormData.conf_rdv_time;
          }
          if (etatFormData.conf_rdv_avec) {
            modifications.conf_rdv_avec = etatFormData.conf_rdv_avec;
          }
        }

        if (Object.keys(modifications).length > 0) {
          updateData.modifications = modifications;
        }

        setEtatSubmitting(true);
        updateCompteRenduMutation.mutate({ crId: editingCompteRendu, data: updateData }, { onSettled: () => setEtatSubmitting(false) });
        return;
      }

      setEtatSubmitting(true);
      const updateData = {
        id_etat_final: parseInt(selectedEtat)
      };
      if (Number(user?.fonction) === 5 && compteRenduOption) {
        updateData.compte_rendu_option = resolveOptionKey(compteRenduOption);
      }
      if (
        showHistoConfirmateurDropdown &&
        ![13, 44, 45].includes(Number(selectedEtat))
      ) {
        // Toujours envoyer les IDs sélectionnés (y compris vide → null côté API) :
        // ne pas laisser le backend retomber sur l'utilisateur connecté.
        // Écriture dans fiches_histo (histo_id_*) et fiches (id_confirmateur*) quand conf1 est choisi.
        const conf1 =
          histoConfirmateurId !== '' && histoConfirmateurId != null
            ? parseInt(histoConfirmateurId, 10)
            : null;
        const conf2 =
          showHistoConfirmateur2 && histoConfirmateur2Id !== '' && histoConfirmateur2Id != null
            ? parseInt(histoConfirmateur2Id, 10)
            : null;
        const conf3 =
          showHistoConfirmateur3 && histoConfirmateur3Id !== '' && histoConfirmateur3Id != null
            ? parseInt(histoConfirmateur3Id, 10)
            : null;
        const c1 = Number.isFinite(conf1) && conf1 > 0 ? conf1 : null;
        const c2 = Number.isFinite(conf2) && conf2 > 0 ? conf2 : null;
        const c3 = Number.isFinite(conf3) && conf3 > 0 ? conf3 : null;
        updateData.histo_id_confirmateur = c1;
        updateData.histo_id_confirmateur_2 = c2;
        updateData.histo_id_confirmateur_3 = c3;
        if (c1 != null) {
          updateData.id_confirmateur = c1;
          updateData.id_confirmateur_2 = c2;
          updateData.id_confirmateur_3 = c3;
        }
      }

      // Ajouter les champs spécifiques selon l'état sélectionné
      if (selectedEtat === 2) {
        // NRP - date d'appel saisie: utilisée aussi pour "A rappeler le" dans fiches_histo
        if (nrpFormData.date_appel_date) {
          updateData.date_appel_time = `${nrpFormData.date_appel_date} ${nrpFormData.date_appel_time || '00:00'}:00`;
        }
        if (nrpFormData.id_sous_etat) {
          updateData.id_sous_etat = parseInt(nrpFormData.id_sous_etat);
        }
        if (nrpFormData.conf_commentaire_produit) {
          updateData.conf_commentaire_produit = nrpFormData.conf_commentaire_produit;
        }
      } else if (selectedEtat === 8) {
        // ANNULER À REPROGRAMMER
        // Pour "Porte / Téléphone / NRP / RDV non faisable" côté commercial, on ne saisit que le commentaire.
        if (!isCommercialAnnulerReproSimple) {
          if (etatFormData.conf_rdv_date) {
            const dateRdvStr = `${etatFormData.conf_rdv_date} ${etatFormData.conf_rdv_time || '00:00'}:00`;
            updateData.date_rdv_time = dateRdvStr;
          }
          if (etatFormData.id_sous_etat) {
            updateData.id_sous_etat = parseInt(etatFormData.id_sous_etat);
          }
          if (etatFormData.conf_rdv_avec) {
            updateData.conf_rdv_avec = etatFormData.conf_rdv_avec;
          }
        }
        if (etatFormData.conf_commentaire_produit) {
          updateData.conf_commentaire_produit = etatFormData.conf_commentaire_produit;
        }
      } else if (selectedEtat === 19) {
        // RAPPEL POUR BUREAU
        // Enregistrer la date de rappel choisie dans date_rdv_time
        // pour affichage cohérent dans l'historique et l'état actuel.
        if (etatFormData.date_rappel_date) {
          const dateRappelStr = `${etatFormData.date_rappel_date} ${etatFormData.date_rappel_time || '09:00'}:00`;
          updateData.date_rdv_time = dateRappelStr;
        }
        if (etatFormData.id_sous_etat) {
          updateData.id_sous_etat = parseInt(etatFormData.id_sous_etat);
        }
        if (etatFormData.motif_qualif) {
          updateData.motif_qualif = etatFormData.motif_qualif;
        }
      } else if (Number(selectedEtat) === 55) {
        // HC (état 55) : sous-état obligatoire + commentaire optionnel (obligatoire si AUTRE)
        updateData.id_sous_etat = etatFormData.id_sous_etat
          ? parseInt(etatFormData.id_sous_etat, 10)
          : null;
        updateData.hc = 1;
        updateData.ko = 0;
        const motifCommentaire = (etatFormData.motif_qualif || etatFormData.conf_commentaire_produit || '').trim();
        if (motifCommentaire) {
          updateData.motif_qualif = motifCommentaire;
          updateData.commentaire_qualite = motifCommentaire;
        }
      } else if ([5, 6, 9, 11, 12, 22, 23, 24, 25, 26, 29, 34, 35].includes(selectedEtat)) {
        // CLIENT HONORE A SUIVRE (9), RDV ANNULER (11), REFUSER (12), HORS CIBLE (23), HHC FINANCEMENT (34), HCC TECHNIQUE (35), ANNULER (5), HC ÂGE/DBL/LOC (6), ANNULER 2× (22), HC FINANCEMENT (24), REFUSER 2× (25), RDV ANNULER 2× (26), HC AIR AIR (29)
        if (selectedEtat === 9 && etatFormData.conf_commentaire_produit) {
          updateData.conf_commentaire_produit = etatFormData.conf_commentaire_produit;
        }
        if ([11, 12].includes(selectedEtat)) {
          if (etatFormData.id_sous_etat) {
            updateData.id_sous_etat = parseInt(etatFormData.id_sous_etat, 10);
          }
          const motifCommentaire = (etatFormData.motif_qualif || etatFormData.conf_commentaire_produit || '').trim();
          if (motifCommentaire) updateData.motif_qualif = motifCommentaire;
        } else if ([5, 6, 22, 24, 25, 26, 29].includes(selectedEtat) && etatFormData.motif_qualif) {
          updateData.motif_qualif = etatFormData.motif_qualif;
        } else if ([23, 34, 35].includes(selectedEtat)) {
          const motifCommentaire = (etatFormData.motif_qualif || etatFormData.conf_commentaire_produit || '').trim();
          if (motifCommentaire) updateData.motif_qualif = motifCommentaire;
        }
        if (selectedEtat === 34 && etatFormData.conf_rdv_avec) {
          updateData.conf_rdv_avec = etatFormData.conf_rdv_avec;
        }
        // Pour Honoré à suivre (9), ajouter A Rappeler le (toutes sessions)
        if (selectedEtat === 9) {
          if (etatFormData.date_rappel_date) {
            const dateRappelStr = `${etatFormData.date_rappel_date} ${etatFormData.date_rappel_time || '09:00'}:00`;
            updateData.date_rdv_time = dateRappelStr;
          }
          // Session admin/backoffice/RP/RE: possibilité d'assigner le commercial
          if (etatFormData.id_commercial) {
            if (isAdminSessionHonoreSuivre) {
              updateData.id_commercial = parseInt(etatFormData.id_commercial);
            }
          }
        }
      } else if ([13, 44, 45].includes(selectedEtat)) {
        // SIGNER, SIGNER PM, SIGNER COMPLET
        if (etatFormData.date_sign_date) {
          const dateSignStr = `${etatFormData.date_sign_date} ${etatFormData.date_sign_time || '00:00'}:00`;
          updateData.date_sign_time = dateSignStr;
        }
        if (etatFormData.produit) {
          updateData.produit = parseInt(etatFormData.produit);
        }
        if (etatFormData.id_sous_etat) {
          updateData.id_sous_etat = parseInt(etatFormData.id_sous_etat);
        }
        // Admin/qualité/superviseur : commercial choisi → body (snapshot fiches_histo côté API)
        if ([1, 2, 7].includes(Number(user?.fonction))) {
          updateData.id_commercial =
            etatFormData.id_commercial && String(etatFormData.id_commercial).trim() !== ''
              ? parseInt(etatFormData.id_commercial, 10)
              : null;
          updateData.id_commercial_2 =
            etatFormData.id_commercial_2 && String(etatFormData.id_commercial_2).trim() !== ''
              ? parseInt(etatFormData.id_commercial_2, 10)
              : null;
        } else if (user?.fonction !== 5 && etatFormData.id_commercial) {
          updateData.id_commercial = parseInt(etatFormData.id_commercial, 10);
          if (etatFormData.id_commercial_2) {
            updateData.id_commercial_2 = parseInt(etatFormData.id_commercial_2, 10);
          }
        }
        if (etatFormData.pseudo) {
          updateData.pseudo = etatFormData.pseudo;
        }
        if (etatFormData.ph3_pac) {
          updateData.ph3_pac = etatFormData.ph3_pac;
        }
        if (etatFormData.ph3_rr_model) {
          updateData.ph3_rr_model = etatFormData.ph3_rr_model;
        }
        if (etatFormData.ph3_puissance) {
          updateData.ph3_puissance = etatFormData.ph3_puissance;
        }
        if (etatFormData.ph3_puissance_pv) {
          updateData.ph3_puissance_pv = etatFormData.ph3_puissance_pv;
        }
        if (etatFormData.ph3_ballon) {
          updateData.ph3_ballon = etatFormData.ph3_ballon;
        }
        if (etatFormData.ph3_marque_ballon) {
          updateData.ph3_marque_ballon = etatFormData.ph3_marque_ballon;
        }
        if (etatFormData.ph3_alimentation) {
          updateData.ph3_alimentation = etatFormData.ph3_alimentation;
        }
        if (etatFormData.ph3_type) {
          updateData.ph3_type = etatFormData.ph3_type;
        }
        if (etatFormData.ph3_prix) {
          updateData.ph3_prix = parseFloat(etatFormData.ph3_prix);
        }
        if (etatFormData.ph3_installateur) {
          updateData.ph3_installateur = parseInt(etatFormData.ph3_installateur);
        }
        if (etatFormData.conf_consommations) {
          updateData.conf_consommations = parseFloat(etatFormData.conf_consommations);
        }
        if (etatFormData.ph3_bonus_30) {
          updateData.ph3_bonus_30 = etatFormData.ph3_bonus_30;
        }
        if (etatFormData.valeur_mensualite) {
          updateData.valeur_mensualite = parseFloat(etatFormData.valeur_mensualite);
        }
        if (etatFormData.ph3_mensualite) {
          updateData.ph3_mensualite = parseFloat(etatFormData.ph3_mensualite);
        }
        if (etatFormData.ph3_attente) {
          updateData.ph3_attente = etatFormData.ph3_attente;
        }
        if (etatFormData.nbr_annee_finance) {
          updateData.nbr_annee_finance = parseInt(etatFormData.nbr_annee_finance);
        }
        if (etatFormData.credit_immobilier) {
          updateData.credit_immobilier = parseFloat(etatFormData.credit_immobilier);
        }
        if (etatFormData.credit_autre) {
          updateData.credit_autre = parseFloat(etatFormData.credit_autre);
        }
        if (etatFormData.conf_commentaire_produit) {
          updateData.conf_commentaire_produit = etatFormData.conf_commentaire_produit;
        }
      } else if ([16, 38].includes(selectedEtat)) {
        // SIGNER RETRACTER
        if ([1, 2, 7].includes(Number(user?.fonction))) {
          updateData.id_commercial =
            etatFormData.id_commercial && String(etatFormData.id_commercial).trim() !== ''
              ? parseInt(etatFormData.id_commercial, 10)
              : null;
          updateData.id_commercial_2 =
            etatFormData.id_commercial_2 && String(etatFormData.id_commercial_2).trim() !== ''
              ? parseInt(etatFormData.id_commercial_2, 10)
              : null;
        } else if (user?.fonction !== 5 && etatFormData.id_commercial) {
          updateData.id_commercial = parseInt(etatFormData.id_commercial, 10);
          if (etatFormData.id_commercial_2) {
            updateData.id_commercial_2 = parseInt(etatFormData.id_commercial_2, 10);
          }
        }
        if (etatFormData.conf_commentaire_produit) {
          updateData.conf_commentaire_produit = etatFormData.conf_commentaire_produit;
        }
      }

      // Appeler l'API pour mettre à jour
      const res = await api.put(`/fiches/${hash}`, updateData);
      
      if (res.data.success) {
        // Vérifier si un compte rendu a été créé (pour les commerciaux)
        if (res.data.data?.id_compte_rendu) {
          queryClient.invalidateQueries(['planning-commercial']); // Mettre à jour le planning commercial
          alert('Compte rendu créé avec succès. Les modifications sont en attente d\'approbation de l\'administrateur.');
        } else {
          // Recharger les données seulement si les modifications ont été appliquées directement
          queryClient.invalidateQueries(['fiche', hash]);
          queryClient.invalidateQueries(['fiches']);
          queryClient.invalidateQueries(['modifica', hash]); // Invalider les modifications
          queryClient.invalidateQueries(['planning-commercial']); // Mettre à jour le planning commercial
          // Invalider aussi les queries de planning au cas où
          queryClient.invalidateQueries(['planning-week']);
          queryClient.invalidateQueries(['planning-availability']);
          queryClient.invalidateQueries(['planning-modal']);
        }
        queryClient.invalidateQueries(['availability-modal']);
        setSelectedEtat(null);
        setCompteRenduOption('');
        setNrpFormData({
          date_appel_date: '',
          date_appel_time: '',
          id_sous_etat: '',
          conf_commentaire_produit: ''
        });
        // Pas d'alerte de succès ici pour éviter la popup lors de la validation de l'état.
      }
      setEtatSubmitting(false);
    } catch (error) {
      console.error('Erreur lors du changement d\'état:', error);
      alert('Erreur lors du changement d\'état: ' + (error.response?.data?.message || error.message));
      setEtatSubmitting(false);
    }
  };

  const renderField = (label, field, value, type = 'text', options = null, readOnly = false, valueForEdit = undefined, bulkEditable = false) => {
    const isEditing = editingField === field;
    const rawEditValue = valueForEdit !== undefined ? valueForEdit : value;
    const normalizedEditValue = rawEditValue === '-' || rawEditValue == null ? '' : rawEditValue;

    if (bulkEditable) {
      etudeBulkFieldsRef.current[field] = normalizedEditValue;
    }

    // Permissions d'édition :
    // - Admins (1, 2, 7) : peuvent tout modifier
    // - Agents (3) : peuvent modifier les fiches de leur centre
    // - Commerciaux (5) : peuvent modifier leurs propres fiches (avec permission fiches_edit)
    // - Confirmateurs (6) : peuvent modifier les fiches qui leur sont assignées
    if (!ficheData || !user) {
      return (
        <tr>
          <td className="field-label">{label}</td>
          <td className="field-value">
            <span className="field-display">{value || '-'}</span>
          </td>
          <td className="field-actions"></td>
        </tr>
      );
    }
    
    // Permissions d'édition :
    // - Admins (1, 2, 7) : peuvent tout modifier
    // - Superviseur Qualification (2) : peuvent modifier les fiches des agents sous leur responsabilité
    // - RP Qualification (12) : peuvent modifier les fiches des agents sous la responsabilité de leurs superviseurs
    // - Agents (3) : peuvent modifier les fiches de leur centre
    // - Commerciaux (5) : peuvent modifier leurs propres fiches (avec permission fiches_edit)
    // - Confirmateurs (6) : peuvent modifier toutes les fiches
    // Convertir la fonction en nombre pour la comparaison (peut être string ou number)
    // Utiliser == au lieu de === pour gérer les comparaisons string/number
    const userFonctionRaw = user.fonction;
    const userFonction = userFonctionRaw != null ? Number(userFonctionRaw) : null;
    
    // Permissions d'édition pour qualité qualification (fonction 2, 8 et 12)
    // Utiliser == pour gérer les comparaisons string/number
    const isQualiteQualif = userFonction == 2 || userFonction == 8 || userFonction == 12;
    const isAdmin = userFonction == 1 || userFonction == 7;
    const isAgent = userFonction == 3 && user.centre === ficheData.id_centre;
    const isCommercialSession = userFonction == 5;
    const isCommercialEditor = canCommercialEditFiche;
    const isConfirmateur = userFonction == 6;
    const isREConfirmation = userFonction == 14; // RE confirmation : modification rapide
    const isRPConfirmation = userFonction == 13; // RP confirmation : modification rapide
    const isBackoffice = userFonction == 11; // Backoffice : modification rapide
    const canEditModificationRapide = isREConfirmation || isRPConfirmation || isBackoffice || (typeof hasPermission === 'function' && hasPermission('fiche_quick_edit'));

    // Session commercial : ces champs sont toujours en lecture seule (pas de bouton Modifier)
    const isCommercialFieldBlocked =
      isCommercialSession && COMMERCIAL_SESSION_READONLY_FIELDS.has(field);
    const effectiveReadOnly =
      isCommercialFieldBlocked || ((readOnly || false) && !canEditModificationRapide);
    const canEditField =
      !effectiveReadOnly &&
      userFonction != null &&
      (isAdmin || isQualiteQualif || isAgent || isCommercialEditor || isConfirmateur || (!isCommercialSession && canEditModificationRapide));

    // Si un commercial était déjà en édition sur un champ bloqué, forcer l'affichage lecture seule
    const showBulkEditControls =
      bulkEditingEtude && bulkEditable && canEditField && !isCommercialFieldBlocked;
    const showEditControls = !bulkEditingEtude && isEditing && canEditField && !isCommercialFieldBlocked;
    const bulkValue = Object.prototype.hasOwnProperty.call(bulkEditValues, field)
      ? (bulkEditValues[field] ?? '')
      : normalizedEditValue;

    const renderInputControl = (currentValue, onValueChange, { autoFocus = false, onEnterSave = null } = {}) => {
      if (type === 'select' && options) {
        return (
          <select
            value={currentValue}
            onChange={(e) => onValueChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && onEnterSave) { e.preventDefault(); onEnterSave(); } }}
            className="form-control"
            autoFocus={autoFocus}
          >
            <option value="">Sélectionner</option>
            {options.map(opt => (
              <option key={opt.id || opt.value} value={opt.id || opt.value}>
                {opt.nom || opt.titre || opt.label}
              </option>
            ))}
          </select>
        );
      }
      if (type === 'textarea') {
        return (
          <textarea
            value={currentValue}
            onChange={(e) => onValueChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey && onEnterSave) { e.preventDefault(); onEnterSave(); } }}
            className="form-control"
            autoFocus={autoFocus}
            rows={4}
            title="Ctrl+Entrée pour enregistrer"
          />
        );
      }
      return (
        <input
          type={type}
          value={currentValue}
          onChange={(e) => onValueChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && onEnterSave) { e.preventDefault(); onEnterSave(); } }}
          className="form-control"
          autoFocus={autoFocus}
        />
      );
    };

    return (
      <tr>
        <td className="field-label">{label}</td>
        <td className="field-value">
          {showBulkEditControls ? (
            <div className="edit-controls edit-controls-bulk">
              {renderInputControl(
                bulkValue,
                (next) => setBulkEditValues((prev) => ({ ...prev, [field]: next }))
              )}
            </div>
          ) : showEditControls ? (
            <div className="edit-controls">
              {renderInputControl(
                editValue,
                setEditValue,
                { autoFocus: true, onEnterSave: () => handleSaveField(field) }
              )}
              <button
                className="btn-save"
                onClick={() => handleSaveField(field)}
                disabled={updateFieldMutation.isLoading}
              >
                <FaCheck />
              </button>
              <button
                className="btn-cancel"
                onClick={handleCancelEdit}
              >
                <FaTimes />
              </button>
            </div>
          ) : (
            <span className="field-display">
              {value || '-'}
            </span>
          )}
        </td>
        <td className="field-actions">
          {canEditField && !isEditing && !isCommercialFieldBlocked && !bulkEditingEtude && (
            <button
              className="btn-edit"
              onClick={() => handleEditField(field, normalizedEditValue)}
              title="Modifier"
            >
              <FaEdit />
            </button>
          )}
        </td>
      </tr>
    );
  };

  if (isLoading) {
    return <div className="loading">Chargement...</div>;
  }

  if (!ficheData) {
    return <div className="error">Fiche non trouvée</div>;
  }

  const fiche = ficheData;

  const displayAppelTunisieAvec = (f) => {
    const c = f?.conf_appel_tunisie_avec;
    if (c != null && String(c).trim() !== '') return String(c).trim();
    const e = f?.entretien;
    if (e != null && String(e).trim() !== '') return String(e).trim();
    return '-';
  };

  const isChangementEtatBloque =
    fiche.id_etat_final != null &&
    ETATS_SANS_NOUVEL_ETAT.includes(Number(fiche.id_etat_final)) &&
    !canBypassEtatDefinitif(user?.fonction);

  const userFonctionTop = user ? Number(user.fonction) : null;
  const canEditModificationRapideTop = userFonctionTop === 14 || userFonctionTop === 13 || userFonctionTop === 11 || (typeof hasPermission === 'function' && hasPermission('fiche_quick_edit'));
  const canBulkEditEtude =
    !!ficheData &&
    userFonctionTop != null &&
    (
      userFonctionTop === 1 ||
      userFonctionTop === 7 ||
      userFonctionTop === 2 ||
      userFonctionTop === 8 ||
      userFonctionTop === 12 ||
      (userFonctionTop === 3 && user?.centre === ficheData.id_centre) ||
      canCommercialEditFiche ||
      userFonctionTop === 6 ||
      canEditModificationRapideTop
    );
  const getEtatColor = () => {
    if (fiche.etat_final_color) {
      return fiche.etat_final_color;
    }
    if (etats && fiche.id_etat_final != null && fiche.id_etat_final !== '') {
      const etat = etats.find((e) => Number(e.id) === Number(fiche.id_etat_final));
      return etat?.color || '#3498db';
    }
    return '#3498db';
  };

  const etatColor = getEtatColor();
  const textColorOnEtatBackground = (bg) => {
    const c = String(bg || '').trim().toLowerCase();
    if (c === '#ffffff' || c === '#fff' || c === 'white') return '#000';
    return '#fff';
  };
  const etatHeaderForeground = textColorOnEtatBackground(etatColor);

  const etatActuelHeaderTitre = fiche.id_etat_final
    ? (
        fiche.etat_final_titre ||
        etats?.find((e) => Number(e.id) === Number(fiche.id_etat_final))?.titre ||
        'État inconnu'
      )
    : null;
  const etatActuelHeaderSous = fiche.sous_etat_titre || null;

  const generatePDF = () =>
    generateFicheClientPdf(fiche, {
      professions,
      typeContrat,
      centres,
      agents,
      commerciaux,
      confirmateurs,
    });

  return (
    <div 
      className={`fiche-detail${!isModal ? ' fiche-detail-page' : ''}`}
      style={{
        border: `8px solid ${etatColor}`,
        borderRadius: '8px',
        padding: '20px',
        backgroundColor: '#fff'
      }}
    >
      <div className="fiche-detail-header">
        <div className="fiche-detail-header-title">
          <div className="fiche-type-badge" style={{ 
            backgroundColor: fiche.produit_color || (fiche.produit === 1 ? '#66D5D4' : '#818cf8'),
            color: fiche.produit === 1 ? 'white' : 'black'
          }}>
            {fiche.produit_nom || (fiche.produit === 1 ? 'PAC' : 'PV')}
          </div>
          <h1>
            <FaInfoCircle /> Détails de la fiche
            {isR2 && <span style={{ 
              marginLeft: '10px', 
              fontSize: '0.7em', 
              fontWeight: 'normal',
              color: '#e74c3c',
              backgroundColor: '#fff',
              padding: '4px 8px',
              borderRadius: '4px',
              border: '1px solid #e74c3c'
            }}>R2</span>}
          </h1>
        </div>
        <div className="fiche-detail-etat-actuel">
          <span
            className="fiche-detail-etat-pill"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '6px',
              backgroundColor: etatColor,
              color: etatHeaderForeground,
              fontSize: '15px',
              lineHeight: 1.35,
              maxWidth: '100%',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12)',
            }}
          >
            <span className="fiche-detail-etat-label">État actuel :</span>
            <span className="fiche-detail-etat-value">
              {etatActuelHeaderTitre
                ? `${etatActuelHeaderTitre}${etatActuelHeaderSous ? ` · ${etatActuelHeaderSous}` : ''}`
                : '—'}
            </span>
          </span>
        </div>
      </div>

      {/* Onglets */}
      <div className="fiche-tabs">
        <div className="fiche-tabs-items">
        <button
          className={`fiche-tab ${activeTab === 'fiches' ? 'active' : ''}`}
          onClick={() => setActiveTab('fiches')}
        >
          <FaFileAlt /> Fiches
        </button>
        {/* Désactiver l'onglet Modifica pour les commerciaux et le modal partenaire */}
        {!isCommercial && !isPartenaireModalLimitedTabs && (
          <button
            className={`fiche-tab ${activeTab === 'modifica' ? 'active' : ''}`}
            onClick={() => setActiveTab('modifica')}
          >
            <FaListAlt /> Modifica
          </button>
        )}
        {/* Onglet Affectation : visible par administrateur, backoffice, RE confirmation, RP confirmation */}
        {showAffectationTab && !isPartenaireModalLimitedTabs && (
          <button
            className={`fiche-tab ${activeTab === 'affectation' ? 'active' : ''}`}
            onClick={() => setActiveTab('affectation')}
          >
            <FaUserPlus /> {commercialAffecteNom ? `${commercialAffecteNom} | ` : ''}Affectation
          </button>
        )}
        {/* Planning : aussi pour un partenaire autorisé à fixer un RDV. SMS reste masqué pour le partenaire. */}
        {!isQualiteQualif && !isCommercial && (!isPartenaireModalLimitedTabs || partenaireCanFixerRdv) && (
          <button
            className={`fiche-tab ${activeTab === 'planning' ? 'active' : ''}`}
            onClick={() => setActiveTab('planning')}
          >
            <FaCalendar /> Planning
          </button>
        )}
        {!isQualiteQualif && !isCommercial && !isPartenaireModalLimitedTabs && (
          <button
            className={`fiche-tab ${activeTab === 'sms' ? 'active' : ''}`}
            onClick={() => setActiveTab('sms')}
          >
            <FaSms /> SMS
          </button>
        )}
        {!isPartenaireModalLimitedTabs && (
          <button
            className={`fiche-tab ${activeTab === 'pdf' ? 'active' : ''}`}
            onClick={() => setActiveTab('pdf')}
          >
            <FaFilePdf /> PDF
          </button>
        )}
        </div>
        <div className="fiche-tabs-alerts">
          {showDecalageTabBadge && hasPendingDecalageTabAlert && (
            <button
              type="button"
              className="fiche-tab-completude-alert fiche-tab-decalage-alert"
              onClick={scrollToDecalageSection}
              title="Demande de décalage en attente — cliquer pour afficher l'état actuel"
            >
              <span className="fiche-tab-completude-alert-inner">
                <FaInfoCircle aria-hidden />
                <span>DECALAGE</span>
              </span>
            </button>
          )}
          {showCompletudeTabBadge && hasCompletudeTabAlert && (
            <button
              type="button"
              className="fiche-tab-completude-alert"
              onClick={scrollToCompletudeSection}
              title="Complétude en attente — cliquer pour afficher"
            >
              <span className="fiche-tab-completude-alert-inner">
                <FaInfoCircle aria-hidden />
                <span>Complétude</span>
              </span>
            </button>
          )}
        </div>
      </div>


      {/* Contenu des onglets */}
      {activeTab === 'fiches' && (
        <>
          {/* Détails de la fiche */}
          <div className="fiche-sections">
        {ETATS_AVEC_COMMENTAIRE_MOTIF.includes(Number(fiche.id_etat_final)) && (
          <div className="fiche-section">
            <h2 className="section-title">Commentaire sur l&apos;état</h2>
            <table className="fiche-details-table">
              <tbody>
                {renderField('Commentaire', 'motif_qualif', fiche.motif_qualif || '-', 'textarea')}
              </tbody>
            </table>
          </div>
        )}
        {/* Section Données personnelles */}
        <div className="fiche-section">
          <h2 className="section-title">Données personnelles</h2>
          <table className="fiche-details-table">
            <tbody>
              {renderField('Civilité', 'civ', fiche.civ, 'select', [
                { value: 'MR', label: 'MR' },
                { value: 'MME', label: 'MME' }
              ])}
              {renderField('Nom', 'nom', fiche.nom)}
              {renderField('Prénom', 'prenom', fiche.prenom)}
              {renderField('Téléphone', 'tel', fiche.tel, 'tel')}
              {renderField('GSM1', 'gsm1', fiche.gsm1, 'tel')}
              {renderField('GSM2', 'gsm2', fiche.gsm2, 'tel')}
              {renderField('Adresse', 'adresse', fiche.adresse, 'textarea')}
              {renderField('Code postal', 'cp', fiche.cp)}
              {renderField('Ville', 'ville', fiche.ville)}
            </tbody>
          </table>
        </div>

        {/* Section étude : titre = VALIDE par pseudo agent qualité (id_qualite) */}
        <div className="fiche-section">
          <h2 className={`section-title section-title-with-actions${canBulkEditEtude ? ' has-section-actions' : ''}`}>
            <span className="section-title-text">
              {fiche.id_qualite && (fiche.qualite_pseudo || '').trim()
                ? `VALIDE par ${String(fiche.qualite_pseudo).trim()}`
                : fiche.id_qualite
                  ? "VALIDE par —"
                  : "Détails de l'étude"}
            </span>
            {canBulkEditEtude && (
              <span className="section-title-actions">
                <button
                  type="button"
                  className="btn-section-edit"
                  onClick={handleStartBulkEditEtude}
                  disabled={bulkEditSaving}
                  title={bulkEditingEtude ? 'Annuler la modification' : 'Modifier tous les champs'}
                >
                  <FaEdit /> Modifier
                </button>
                {bulkEditingEtude && (
                  <button
                    type="button"
                    className="btn-section-save"
                    onClick={handleSaveBulkEditEtude}
                    disabled={bulkEditSaving}
                    title="Enregistrer les modifications"
                  >
                    <FaCheck /> {bulkEditSaving ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                )}
              </span>
            )}
          </h2>
          <table className="fiche-details-table">
            <tbody>
              {(() => { etudeBulkFieldsRef.current = {}; return null; })()}
              {renderField(
                'Étude à faire pour',
                'conf_produit',
                (fiche.conf_produit != null && fiche.conf_produit !== '')
                  ? (fiche.conf_produit === 1 || fiche.conf_produit === '1' ? 'PAC' : fiche.conf_produit === 2 || fiche.conf_produit === '2' ? 'PV' : String(fiche.conf_produit))
                  : (fiche.produit_nom || (fiche.produit === 1 ? 'PAC' : fiche.produit === 2 ? 'PV' : '-')),
                'select',
                [
                  { id: 1, nom: 'PAC' },
                  { id: 2, nom: 'PV' }
                ],
                false,
                String(
                  fiche.conf_produit != null && String(fiche.conf_produit).trim() !== ''
                    ? fiche.conf_produit
                    : (fiche.produit ?? '')
                ),
                true
              )}
              {hasConfValue(fiche.conf_commentaire_produit)
                ? renderField('Commentaire', 'conf_commentaire_produit', fiche.conf_commentaire_produit || '-', 'textarea', null, false, undefined, true)
                : renderField('Commentaire', 'commentaire_qualite', fiche.commentaire_qualite || '-', 'textarea', null, false, undefined, true)}
              {hasConfValue(fiche.conf_deja_etude) || hasConfValue(fiche.conf_deja_fait_etude)
                ? renderField('A déjà fait une étude', 'conf_deja_fait_etude', fiche.conf_deja_fait_etude || fiche.conf_deja_etude || 'NON', 'select', [
                    { value: 'OUI', label: 'Oui' },
                    { value: 'NON', label: 'Non' }
                  ], false, undefined, true)
                : renderField('A déjà fait une étude', 'etude', fiche.etude || 'NON', 'select', [
                    { value: 'OUI', label: 'Oui' },
                    { value: 'NON', label: 'Non' }
                  ], false, undefined, true)}
              {/* Visible pour toutes les sessions (modal / page détail fiche) */}
              {renderField(
                'Détails étude',
                'details_etude',
                resolveDetailsEtudeForFiche(fiche) || '-',
                'textarea',
                null,
                false,
                undefined,
                true
              )}
              {renderField(
                'Mode de chauffage',
                'conf_mode_chauffage',
                modeChauffageAffiche(fiche.conf_mode_chauffage, fiche.mode_chauffage) || '-',
                'text',
                null,
                false,
                modeChauffageAffiche(fiche.conf_mode_chauffage, fiche.mode_chauffage) || '',
                true
              )}
              {renderField('Année de système de chauffage', 'annee_systeme_chauffage', fiche.annee_systeme_chauffage || '-', 'number', null, false, undefined, true)}
              {renderField('Consommation chauffage', 'consommation_chauffage',
                (fiche.conf_consommations != null && fiche.conf_consommations !== '') ? String(fiche.conf_consommations) : (fiche.consommation_chauffage || '-'),
                'text', null, false, undefined, true)}
              {renderField('Surface chauffée en M²', 'surface_chauffee', fiche.surface_chauffee || '-', 'number', null, false, undefined, true)}
              {fiche.surface_chauffee && (fiche.conf_consommations != null && fiche.conf_consommations !== '' ? fiche.conf_consommations : fiche.consommation_chauffage) && parseFloat(fiche.surface_chauffee) > 0 && parseFloat(String(fiche.conf_consommations != null && fiche.conf_consommations !== '' ? fiche.conf_consommations : fiche.consommation_chauffage).replace(/[^\d.,]/g, '').replace(',', '.')) > 0 ? (
                renderField('Consommation en M²', 'conso',
                  (parseFloat(String(fiche.conf_consommations != null && fiche.conf_consommations !== '' ? fiche.conf_consommations : fiche.consommation_chauffage).replace(/[^\d.,]/g, '').replace(',', '.')) / parseFloat(fiche.surface_chauffee)).toFixed(2) + ' €/m²',
                  'text', null, true)
              ) : (
                renderField('Consommation en M²', 'conso', '-', 'text', null, true)
              )}
              {renderField('Propriétaire de la maison', 'proprietaire_maison', fiche.proprietaire_maison || '-', 'select', [
                { value: 'MR', label: 'Mr' },
                { value: 'MME', label: 'Mme' },
                { value: 'LES DEUX', label: 'LES DEUX' }
              ], false, undefined, true)}
              {renderField('Orientation de la toiture', 'orientation_toiture',
                (fiche.conf_orientation_toiture != null && String(fiche.conf_orientation_toiture).trim() !== '') ? fiche.conf_orientation_toiture : (fiche.orientation_toiture || '-'), 'text', null, false, undefined, true)}
              {renderField('Zones d\'ombres', 'zones_ombres',
                (fiche.conf_zones_ombres != null && String(fiche.conf_zones_ombres).trim() !== '') ? fiche.conf_zones_ombres : (fiche.zones_ombres || '-'),
                'text', null, false, undefined, true
              )}
              {hasConfValue(fiche.conf_site_classe)
                ? renderField('Proche d\'un site classé', 'conf_site_classe', fiche.conf_site_classe || '-', 'text', null, false, undefined, true)
                : renderField('Proche d\'un site classé', 'site_classe', fiche.site_classe || '-', 'text', null, false, undefined, true)}
              {renderField('Âge du MR', 'age_mr', fiche.age_mr || '-', 'number', null, false, undefined, true)}
              {renderField('Âge du Madame', 'age_madame', fiche.age_madame || '-', 'number', null, false, undefined, true)}
              {renderField('Consommation électricité', 'consommation_electricite',
                (fiche.conf_consommation_electricite != null && String(fiche.conf_consommation_electricite).trim() !== '') ? fiche.conf_consommation_electricite : (fiche.consommation_electricite || '-'),
                'text', null, false, undefined, true
              )}
              {hasConfValue(fiche.conf_revenu)
                ? renderField('Revenu du foyer', 'conf_revenu', fiche.conf_revenu || '-', 'number', null, false, undefined, true)
                : renderField('Revenu du foyer', 'revenu_foyer', fiche.revenu_foyer || '-', 'number', null, false, undefined, true)}
              {hasConfValue(fiche.conf_credit)
                ? renderField('Crédit du foyer', 'conf_credit', fiche.conf_credit || '-', 'number', null, false, undefined, true)
                : renderField('Crédit du foyer', 'credit_foyer', fiche.credit_foyer || '-', 'number', null, false, undefined, true)}
              {renderField('Situation Conjugale', 'situation_conjugale', fiche.situation_conjugale || '-', 'select', [
                { value: 'COUPLE', label: 'Couple' },
                { value: 'CELIBATAIRE', label: 'Célibataire' },
                { value: 'MARIE', label: 'Marié' },
                { value: 'CONCUBINAGE', label: 'Concubinage' },
                { value: 'VEUF/VEUVE', label: 'Veuf/Veuve' },
                { value: 'DIVORCE', label: 'Divorcé' },
                { value: 'PAXE', label: 'Pacsé' }
              ], false, undefined, true)}
              {renderField('Nombre d\'enfants en Charges', 'nb_enfants', fiche.nb_enfants || '-', 'number', null, false, undefined, true)}
              {(() => {
                const rawMr = pickProfessionOrContratValue(fiche.conf_profession_monsieur, fiche.profession_mr);
                const labelMr = rawMr != null
                  ? (professions?.find(p => p.id == rawMr || (p.nom && String(p.nom).toLowerCase() === String(rawMr).toLowerCase()))?.nom || String(rawMr))
                  : '';
                return renderField('Profession Du MR', 'profession_mr', labelMr || '-', 'text', null, false, labelMr, true);
              })()}
              {(() => {
                const rawTypeMr = pickProfessionOrContratValue(fiche.conf_type_contrat_mr, fiche.type_contrat_mr);
                return renderField(
                  'Type de Contrat MR',
                  'type_contrat_mr',
                  rawTypeMr != null
                    ? (typeContrat?.find(t => String(t.id) === String(rawTypeMr))?.nom || rawTypeMr || '-')
                    : '-',
                  'select',
                  typeContrat,
                  false,
                  rawTypeMr ?? '',
                  true
                );
              })()}
              {(() => {
                const rawMme = pickProfessionOrContratValue(fiche.conf_profession_madame, fiche.profession_madame);
                const labelMme = rawMme != null
                  ? (professions?.find(p => p.id == rawMme || (p.nom && String(p.nom).toLowerCase() === String(rawMme).toLowerCase()))?.nom || String(rawMme))
                  : '';
                return renderField('Profession Du Madame', 'profession_madame', labelMme || '-', 'text', null, false, labelMme, true);
              })()}
              {(() => {
                const rawTypeMme = pickProfessionOrContratValue(fiche.conf_type_contrat_madame, fiche.type_contrat_madame);
                return renderField(
                  'Type de Contrat MME',
                  'type_contrat_madame',
                  rawTypeMme != null
                    ? (typeContrat?.find(t => String(t.id) === String(rawTypeMme))?.nom || rawTypeMme || '-')
                    : '-',
                  'select',
                  typeContrat,
                  false,
                  rawTypeMme ?? '',
                  true
                );
              })()}
              {/* Exclus du mode édition groupée : date/heure d'appel, agent, centre */}
              {!isCommercial && renderField('Date & Heure d\'appel', 'date_appel_time', 
                (() => {
                  const dAppel = parseFicheDateAppel(fiche);
                  if (!dAppel) return '-';
                  const absolute = fiche.date_appel_time
                    ? dAppel.toLocaleString('fr-FR')
                    : fiche.date_appel_date
                      ? dAppel.toLocaleDateString('fr-FR')
                      : dAppel.toLocaleString('fr-FR');
                  return `${absolute} — ${formatDateAppelRelativeDescription(dAppel)}`;
                })(),
                null, null, true)}
              {!isCommercial && renderField(
                'Entretien en tunisie avec',
                'conf_appel_tunisie_avec',
                displayAppelTunisieAvec(fiche),
                'select',
                [
                  { value: 'MR', label: 'Mr' },
                  { value: 'MME', label: 'Mme' }
                ],
                false,
                fiche.conf_appel_tunisie_avec || '',
                true
              )}
              {/* Champ Agent : visible uniquement pour Admin (1, 7), Qualité Qualification (2, 8, 12),
                  Qualité Confirmation (4), Backoffice (11), RP Confirmation (13) et RE Confirmation (14).
                  Affiché au-dessus de Centre, en lecture seule. Exclu du mode édition groupée. */}
              {[1, 2, 4, 7, 8, 11, 12, 13, 14].includes(Number(user?.fonction)) &&
                renderField(
                  'Agent',
                  'id_agent',
                  getUserName(fiche.id_agent) || fiche.agent_pseudo || '-',
                  'text',
                  null,
                  true
                )}
              {!isCommercial && renderField('Centre', 'id_centre',
                centres?.find(c => c.id === fiche.id_centre)?.titre || fiche.centre_titre || '-',
                'select', centres)}
              {renderField('Présence du couple', 'rdv_seul',
                (() => {
                  const raw = (fiche.conf_presence_couple !== undefined && fiche.conf_presence_couple !== null && String(fiche.conf_presence_couple).trim() !== '')
                    ? String(fiche.conf_presence_couple).toUpperCase()
                    : (fiche.conf_rdv_avec || fiche.rdv_avec)
                      ? (fiche.conf_rdv_avec === 'SEUL' || fiche.rdv_avec === 'SEUL' ? 'MR SEUL SANS MME' : 'RAS PRESENCE CLIENT(S)')
                      : null;
                  return raw || '-';
                })(),
                'select', [
                  { value: 'RAS PRESENCE CLIENT(S)', label: 'RAS PRESENCE CLIENT(S)' },
                  { value: 'MME SEULE SANS MR', label: 'MME SEULE SANS MR' },
                  { value: 'MR SEUL SANS MME', label: 'MR SEUL SANS MME' }
                ], false, undefined, true)}
            </tbody>
          </table>
        </div>

        {/* Formulaire de décalage de RDV */}
        {/* Conditions d'affichage selon l'ancienne application :
            - Pas pour fonction 6 (confirmateur)
            - Pas pour fonction 3 (agent)
            - Pas pour certains états finaux (13, 16, 38, 45, 44)
            - Pas si commercial (5) avec compte rendu existant
            - Doit avoir la permission decalage_create
            - Doit avoir une date de RDV */}
        {false && hasPermission('decalage_create') && 
         user.fonction === 5 &&
         user.fonction !== 6 && 
         user.fonction !== 3 && 
         ficheData && !([13, 16, 38, 45, 44].includes(ficheData.id_etat_final)) &&
         ficheData.date_rdv_time && (
          <div className="fiche-section decalage-form" style={{ order: 9999 }}>
            <h2 className="section-title" style={{ 
              background: '#9cbfc8', 
              color: '#fff', 
              padding: '10px', 
              textAlign: 'center',
              marginBottom: '0',
              fontSize: '13.6px',
              fontWeight: 'bold'
            }}>
              Demande de décalage
            </h2>
            
            {/* Afficher les décalages existants pour cette fiche */}
            {decalagesAffichables.length > 0 && (
              <div className="decalage-existing-list" style={{ 
                border: '1px solid #e0e0e0', 
                borderTop: 'none', 
                padding: '15px',
                background: '#f9f9f9',
                marginBottom: '10px'
              }}>
                <h3 style={{ marginTop: '0', marginBottom: '10px', fontSize: '11.9px', fontWeight: 'bold' }}>
                  Demande de décalage ({decalagesAffichables.length})
                </h3>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {decalagesAffichables.map((decalage, index) => (
                    <div key={decalage.id || index} className="decalage-existing-item" style={{ 
                      background: '#fff', 
                      padding: '10px', 
                      marginBottom: '8px', 
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}>
                      <div style={{ fontSize: '10.2px', color: '#666' }}>
                        <strong>Demande #{index + 1}</strong> - 
                        Créée le: {decalage.date_creation ? new Date(decalage.date_creation).toLocaleString('fr-FR') : 'N/A'}
                      </div>
                      <div style={{ fontSize: '10.2px', marginTop: '5px' }}>
                        <strong>Nouvelle date:</strong> {decalage.date_nouvelle ? formatRdvDateTime(decalage.date_nouvelle) : (decalage.date_prevu ? formatRdvDateTime(decalage.date_prevu) : 'N/A')}
                      </div>
                      {decalage.message && (
                        <div style={{ fontSize: '10.2px', marginTop: '5px', fontStyle: 'italic', color: '#555' }}>
                          "{decalage.message}"
                        </div>
                      )}
                      {decalage.etat_dec && (
                        <div style={{ fontSize: '10.2px', marginTop: '5px' }}>
                          <strong>État:</strong> {decalage.etat_dec}
                        </div>
                      )}
                      {canAnnulerDecalageEnAttente(decalage) && (
                        <div style={{ marginTop: '8px' }}>
                          <button
                            type="button"
                            className="btn-secondary decalage-annuler-demande-btn"
                            onClick={() => {
                              if (!window.confirm('Supprimer définitivement cette demande de décalage ?')) return;
                              cancelDecalageMutation.mutate(decalage.id);
                            }}
                            disabled={cancelDecalageMutation.isLoading}
                          >
                            {cancelDecalageMutation.isLoading ? 'Annulation…' : 'Annuler la demande'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: '10px', fontSize: '10.2px', color: '#666', fontStyle: 'italic' }}>
                  Vous pouvez créer une nouvelle demande de décalage ci-dessous.
                </div>
              </div>
            )}
            
            <div className="decalage-new-form" style={{ 
              border: '1px solid #e0e0e0', 
              borderTop: 'none', 
              padding: '15px',
              background: '#fff'
            }}>
              <div className="form-group">
                <label htmlFor="select_minutes">Décalage de :</label>
                <select
                  id="select_minutes"
                  className="form-control"
                  value={decalageFormData.select_minutes}
                  onChange={(e) => {
                    const minutes = parseInt(e.target.value);
                    // Toujours utiliser la date de la fiche en priorité, sinon celle du formulaire
                    const dateRdvOriginale = ficheData?.date_rdv_time || decalageFormData.date_prevu || '';
                    
                    if (minutes > 0 && dateRdvOriginale) {
                      try {
                        const formattedNewDate = addMinutesToDateTimeString(dateRdvOriginale, minutes);
                        if (!formattedNewDate) {
                          console.error('Date RDV originale invalide:', dateRdvOriginale);
                          alert('Erreur : la date de rendez-vous originale est invalide.');
                          return;
                        }
                        
                        // Utiliser la forme fonctionnelle de setState pour garantir la cohérence
                        setDecalageFormData(prev => ({
                          ...prev,
                          select_minutes: e.target.value,
                          nouvelle_date: formattedNewDate,
                          date_prevu: dateRdvOriginale // Garder la date RDV originale
                        }));
                      } catch (error) {
                        console.error('Erreur lors du calcul de la nouvelle date:', error);
                        alert('Erreur lors du calcul de la nouvelle date. Veuillez réessayer.');
                      }
                    } else {
                      // Utiliser la forme fonctionnelle de setState pour garantir la cohérence
                      setDecalageFormData(prev => ({
                        ...prev,
                        select_minutes: e.target.value,
                        nouvelle_date: '',
                        date_prevu: dateRdvOriginale // Garder la date RDV originale
                      }));
                    }
                  }}
                >
                  <option value="0">SÉLECTIONNER</option>
                  <option value="10">10 MIN</option>
                  <option value="15">15 MIN</option>
                  <option value="20">20 MIN</option>
                  <option value="25">25 MIN</option>
                  <option value="30">30 MIN</option>
                  <option value="35">35 MIN</option>
                  <option value="40">40 MIN</option>
                  <option value="45">45 MIN</option>
                  <option value="50">50 MIN</option>
                  <option value="55">55 MIN</option>
                  <option value="60">1 HEURE</option>
                  <option value="75">1H15</option>
                  <option value="90">1H30</option>
                  <option value="105">1H45</option>
                  <option value="120">2 HEURES</option>
                </select>
              </div>

              {decalageFormData.nouvelle_date && (
                <div className="form-group decalage-date-preview" style={{ 
                  background: '#e8f5e9', 
                  padding: '10px', 
                  borderRadius: '4px',
                  marginBottom: '15px',
                  border: '2px solid #4caf50'
                }}>
                  <strong>📅 Nouvelle date/heure :</strong> 
                  <span style={{ 
                    display: 'block', 
                    marginTop: '5px', 
                    fontSize: '13.6px', 
                    fontWeight: 'bold',
                    color: '#2e7d32'
                  }}>
                    {formatRdvDateTime(decalageFormData.nouvelle_date)}
                  </span>
                  {ficheData?.date_rdv_time && (
                    <div className="decalage-date-originale" style={{ 
                      marginTop: '8px', 
                      fontSize: '10.2px', 
                      color: '#666',
                      fontStyle: 'italic'
                    }}>
                      Date originale : {formatRdvDateTime(ficheData.date_rdv_time)}
                    </div>
                  )}
                </div>
              )}

              {/* Champ confirmateur */}
              {/* Pour commerciaux : afficher le confirmateur de la fiche (non modifiable) */}
              {user.fonction === 5 && (
                <div className="form-group">
                  <label htmlFor="id_confirmateur_dec">Confirmateur :</label>
                  {ficheData?.id_confirmateur ? (
                    <div className="decalage-readonly-value" style={{ 
                      background: '#f0f0f0', 
                      padding: '10px', 
                      borderRadius: '4px',
                      fontWeight: 'bold'
                    }}>
                      {confirmateurs?.find(c => c.id === ficheData.id_confirmateur)?.pseudo || `ID: ${ficheData.id_confirmateur}`}
                    </div>
                  ) : (
                    <div className="decalage-warning-text" style={{ color: 'red', fontStyle: 'italic' }}>
                      Aucun confirmateur assigné à cette fiche. Veuillez assigner un confirmateur avant de créer un décalage.
                    </div>
                  )}
                </div>
              )}
              
              {/* Pour admins : liste déroulante pour sélectionner le confirmateur */}
              {([1, 2, 7].includes(user.fonction)) && (
                <div className="form-group">
                  <label htmlFor="id_confirmateur_dec">Confirmateur :</label>
                  <select
                    id="id_confirmateur_dec"
                    className="form-control"
                    value={decalageFormData.id_confirmateur || (fiche?.id_confirmateur ? String(fiche.id_confirmateur) : '')}
                    onChange={(e) => {
                      setDecalageFormData({...decalageFormData, id_confirmateur: e.target.value});
                    }}
                    required
                  >
                    <option value="">SÉLECTIONNER UN CONFIRMATEUR</option>
                    {confirmateurs?.map(conf => (
                      <option key={conf.id} value={conf.id}>
                        {conf.pseudo}
                      </option>
                    ))}
                  </select>
                  {!decalageFormData.id_confirmateur && !fiche?.id_confirmateur && (
                    <small style={{ color: '#666', fontStyle: 'italic' }}>
                      Sélectionnez un confirmateur depuis la liste
                    </small>
                  )}
                </div>
              )}
              
              {/* Pour confirmateurs : afficher leur propre ID (non modifiable) */}
              {user.fonction === 6 && (
                <div className="form-group">
                  <label htmlFor="id_confirmateur_dec">Confirmateur :</label>
                  <div className="decalage-readonly-value" style={{ 
                    background: '#f0f0f0', 
                    padding: '10px', 
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}>
                    {user.pseudo || `ID: ${user.id}`}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="message_dec">Message du décalage :</label>
                <textarea
                  id="message_dec"
                  className="form-control"
                  rows="4"
                  value={decalageFormData.message}
                  onChange={(e) => {
                    setDecalageFormData({...decalageFormData, message: e.target.value});
                  }}
                  placeholder="Saisissez le message expliquant le décalage..."
                />
              </div>

              <div className="form-actions" style={{ textAlign: 'center', marginTop: '15px' }}>
                <button
                  className="btn-confirm"
                  onClick={handleDecalageSubmit}
                  disabled={decalageMutation.isLoading}
                  style={{
                    display: 'table',
                    width: 'max-content',
                    margin: '0 auto',
                    borderRadius: '7px',
                    fontWeight: 'bold',
                    padding: '10px 20px'
                  }}
                >
                  {decalageMutation.isLoading ? 'Envoi...' : 'Demande de décalage'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* État actuel et Historique */}
        {(fiche.id_etat_final || (fiche.historique && fiche.historique.length > 0)) && (
          <div
            ref={
              user?.fonction === 5 || !(fiche.historique && fiche.historique.length > 0)
                ? historiqueEtatsAnchorRef
                : undefined
            }
            className="fiche-section etat-current-history-section"
          >
            {/* Fonction réutilisable pour afficher les détails selon l'état */}
            {(() => {
              const showQualiteConfirmationBackoffice = canShowQualiteConfirmationBackofficePanel(user);
              const renderQualiteConfirmationBackofficePanel = () => {
                if (!showQualiteConfirmationBackoffice) return null;
                const idQc = fiche?.id_qualite_confirmation;
                if (idQc == null || idQc === '' || Number(idQc) <= 0) return null;
                const auditeurLabel =
                  fiche?.qualite_confirmation_pseudo?.trim() ||
                  (fiche?.id_qualite_confirmation ? `Agent #${fiche.id_qualite_confirmation}` : null);
                const observationText = String(fiche?.observation_qualite ?? '').trim();
                return (
                  <div className="qualite-confirmation-backoffice-panel">
                    <div className="qualite-confirmation-backoffice-panel__title">Qualité confirmation</div>
                    <div className="qualite-confirmation-backoffice-panel__row">
                      <span className="qualite-confirmation-backoffice-panel__label">Audité par :</span>
                      <span className="qualite-confirmation-backoffice-panel__value">
                        {auditeurLabel || '—'}
                      </span>
                    </div>
                    <div className="qualite-confirmation-backoffice-panel__row">
                      <span className="qualite-confirmation-backoffice-panel__label">Observation :</span>
                      <span className="qualite-confirmation-backoffice-panel__value">
                        {observationText || '—'}
                      </span>
                    </div>
                  </div>
                );
              };

              // 13 SIGNER, 44 SIGNER PM, 45 SIGNER COMPLET, 16/38 SIGNER RETRACTER
              const SIGNER_ETAT_IDS = [13, 16, 38, 44, 45];
              const isEtatSigner = (id) => SIGNER_ETAT_IDS.includes(Number(id));
              // État SIGNER (13) : seul état qui porte le bloc Contrôle Qualité (affichage CQ + formulaire).
              const isEtatSigner13 = (id) => Number(id) === 13;
              const cleanObservationCQ = (value) => {
                if (value == null) return '';
                const cleaned = String(value)
                  .split(/\r?\n/)
                  .map((line) => {
                    const normalized = line
                      .trim()
                      .toLowerCase()
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '');
                    if (
                      normalized.startsWith('cq etat') ||
                      normalized.startsWith('cq dossier') ||
                      normalized.startsWith('controle qualite')
                    ) {
                      const observationOnly = line.match(/observations?\s*:?\s*(.*)$/i)?.[1]?.trim();
                      return observationOnly || '';
                    }
                    return line.replace(/^\s*observations?\s*:?\s*/i, '');
                  })
                  .filter((line) => String(line).trim() !== '')
                  .join('\n');
                return cleaned.trim() === '' ? '' : cleaned;
              };

              // Formulaire Contrôle Qualité réutilisable :
              //   - utilisé dans la carte « État actuel » quand id_etat_final === 13
              //   - utilisé dans l'entrée d'historique état 13 quand l'état actuel n'est pas 13
              // Les données (cq_etat, cq_dossier, observations_cq) sont stockées sur la fiche (table fiches).
              const isControleQualiteDone = (ficheRow) => {
                const etat = ficheRow?.cq_etat;
                const dossier = ficheRow?.cq_dossier;
                return (
                  etat != null && etat !== '' && Number(etat) > 0 &&
                  dossier != null && dossier !== '' && Number(dossier) > 0
                );
              };

              const resolveControleQualiteDate = (ficheRow) => {
                if (ficheRow?.cq_date_modif) return ficheRow.cq_date_modif;
                const cqTypes = new Set(['cq_etat', 'cq_dossier', 'observations_cq']);
                const mods = Array.isArray(modificaData) ? modificaData : [];
                let best = null;
                for (const m of mods) {
                  const field = m.type || m.champ;
                  if (!cqTypes.has(field)) continue;
                  const d = m.date_modif_time || m.date;
                  if (!d) continue;
                  const ts = new Date(d).getTime();
                  if (Number.isNaN(ts)) continue;
                  if (best == null || ts > new Date(best).getTime()) best = d;
                }
                return best;
              };

              const formatControleQualiteDate = (value) => {
                if (value == null || value === '') return null;
                const d = new Date(value);
                if (Number.isNaN(d.getTime())) return String(value);
                return d.toLocaleString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                });
              };

              const renderControleQualiteForm = ({ embedded = false, keySuffix = '' } = {}) => {
                const ficheHash = fiche.hash || hash;
                const form = cqFormByHash[ficheHash] || {};
                const vCqEtat = form.cq_etat !== undefined ? form.cq_etat : String(fiche.cq_etat ?? '');
                const vCqDossier = form.cq_dossier !== undefined ? form.cq_dossier : String(fiche.cq_dossier ?? '');
                const vObs = form.observations !== undefined
                  ? form.observations
                  : cleanObservationCQ(fiche.observations_cq);
                const idCqEtat = `cq_etat_signer${keySuffix}`;
                const idCqDossier = `cq_dossier_signer${keySuffix}`;
                const idObs = `cq_observations_signer${keySuffix}`;
                const cqDone = isControleQualiteDone(fiche);
                const cqDoneDateLabel =
                  embedded && cqDone ? formatControleQualiteDate(resolveControleQualiteDate(fiche)) : null;
                return (
                  <div className={`controle-qualite-card${embedded ? ' controle-qualite-card--embedded' : ''}`}>
                    <div className="controle-qualite-card__header">CONTROLE QUALITE:</div>
                    <div className="controle-qualite-card__body">
                      <div className="controle-qualite-row">
                        <label htmlFor={idCqEtat}>CQ ETAT :</label>
                        <select
                          id={idCqEtat}
                          className="form-control"
                          value={vCqEtat}
                          onChange={(e) => setCqFormByHash(prev => ({
                            ...prev,
                            [ficheHash]: { ...(prev[ficheHash] || {}), cq_etat: e.target.value }
                          }))}
                        >
                          <option value="">--SELECTIONNEZ--</option>
                          <option value="1">NRP / INJOIGNABLE</option>
                          <option value="2">RAS</option>
                          <option value="3">NEGATIF</option>
                        </select>
                      </div>
                      <div className="controle-qualite-row">
                        <label htmlFor={idCqDossier}>CQ DOSSIER :</label>
                        <select
                          id={idCqDossier}
                          className="form-control"
                          value={vCqDossier}
                          onChange={(e) => setCqFormByHash(prev => ({
                            ...prev,
                            [ficheHash]: { ...(prev[ficheHash] || {}), cq_dossier: e.target.value }
                          }))}
                        >
                          <option value="">--SELECTIONNEZ--</option>
                          <option value="1">COMPLET</option>
                          <option value="2">INCOMPLET</option>
                        </select>
                      </div>
                      <div className="controle-qualite-row">
                        <label htmlFor={idObs}>OBSERVATIONS :</label>
                        <textarea
                          id={idObs}
                          className="form-control"
                          rows={3}
                          value={vObs}
                          onChange={(e) => setCqFormByHash(prev => ({
                            ...prev,
                            [ficheHash]: { ...(prev[ficheHash] || {}), observations: e.target.value }
                          }))}
                          placeholder="Commentaires..."
                        />
                      </div>
                      {cqDoneDateLabel && (
                        <p className="controle-qualite-card__done-date">
                          Contrôle qualité effectué le {cqDoneDateLabel}
                        </p>
                      )}
                      <div className="controle-qualite-actions">
                        <button
                          type="button"
                          className="btn-confirm"
                          disabled={controleQualiteMutation.isLoading}
                          onClick={() => controleQualiteMutation.mutate({
                            cq_etat: vCqEtat || null,
                            cq_dossier: vCqDossier || null,
                            observations_cq: cleanObservationCQ(vObs) || null
                          })}
                        >
                          {controleQualiteMutation.isLoading ? 'Enregistrement...' : 'VALIDER'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              };

              // Une seule entrée d'historique « état 13 » porte le formulaire CQ (la plus récente).
              // Sert quand l'état actuel n'est pas 13 (sinon le formulaire reste dans la carte État actuel).
              const histoEtat13Id = (() => {
                if (isEtatSigner13(fiche.id_etat_final)) return null;
                const list = Array.isArray(fiche.historique) ? fiche.historique : [];
                let pick = null;
                for (const h of list) {
                  if (Number(h.id_etat) === 13) pick = h; // dernière occurrence (boucle ascendante)
                }
                return pick ? pick.id : null;
              })();
              // Format date+heure SANS secondes pour les blocs « État actuel » et « Historique ».
              const formatDateNoSeconds = (value) => {
                if (value == null || value === '') return '-';
                const d = new Date(value);
                if (Number.isNaN(d.getTime())) return String(value);
                return d.toLocaleString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                });
              };
              const renderEtatDetails = (etatData, { isCurrent = false } = {}) => {
                const etatId = etatData.id_etat;
                const confirmateursList = [
                  etatData.confirmateur_pseudo,
                  etatData.confirmateur_2_pseudo,
                  etatData.confirmateur_3_pseudo
                ].filter(Boolean).join(', ') || '-';
                const auteurChangementEtat =
                  etatData.histo_confirmateur_pseudo != null && String(etatData.histo_confirmateur_pseudo).trim() !== ''
                    ? String(etatData.histo_confirmateur_pseudo).trim()
                    : '';
                // Qualité qualification (2 superviseur, 8 qualité, 12 RP) → libellé « Audité par » au lieu de « Confirmateur »
                const isAuteurQualiteQualif = [2, 8, 12].includes(Number(etatData.histo_confirmateur_fonction));
                const labelAuteurEtat = isAuteurQualiteQualif ? 'Audité par' : 'Confirmateur';
                const isHonoreASuivreCompteRendu =
                  Number(etatId) === 9 &&
                  (etatData.from_compte_rendu === true || etatData.from_compte_rendu === 1);
                /** Historique : un seul confirmateur (auteur), sauf CONFIRMER / SIGNER / Honoré à suivre (CR) → conf 1/2/3 depuis fiches_histo. */
                const showConfirmateurs123 =
                  Number(etatId) === 7 ||
                  isEtatSigner(etatId) ||
                  isHonoreASuivreCompteRendu ||
                  (isCurrent && [9, 13, 16, 44, 45].includes(Number(etatId)));
                /** Libellé « Confirmateur » : priorité fiches_histo (histo_confirmateur_pseudo / confirmateur_pseudo). */
                const valeurConfirmateurAffichee =
                  auteurChangementEtat !== ''
                    ? auteurChangementEtat
                    : (etatData.confirmateur_pseudo
                      ? String(etatData.confirmateur_pseudo)
                      : '-');
                const valeurConfirmateurEtatListe = showConfirmateurs123
                  ? (confirmateursList !== '-' ? confirmateursList : valeurConfirmateurAffichee)
                  : valeurConfirmateurAffichee;
                const hasAnyConfirmateurAssigne = showConfirmateurs123
                  ? !!(etatData.confirmateur_pseudo || etatData.confirmateur_2_pseudo || etatData.confirmateur_3_pseudo || auteurChangementEtat)
                  : !!(auteurChangementEtat || etatData.confirmateur_pseudo);

                /** Commentaire affiché : pour l'historique, uniquement fiches_histo.conf_commentaire_produit (ou CR matching). */
                const pushCommentaireEtat = (items, {
                  histoLabel = 'Commentaire',
                  commercialLabel = 'Commentaire commercial',
                  allowQualiteOnCurrent = false,
                } = {}) => {
                  const confC =
                    etatData.conf_commentaire_produit != null && String(etatData.conf_commentaire_produit).trim() !== ''
                      ? String(etatData.conf_commentaire_produit).trim()
                      : '';
                  const fromCr = etatData.from_compte_rendu === true || etatData.from_compte_rendu === 1;
                  const crC =
                    etatData.commentaire_commercial != null && String(etatData.commentaire_commercial).trim() !== ''
                      ? String(etatData.commentaire_commercial).trim()
                      : '';
                  const qualC =
                    etatData.commentaire_qualite != null && String(etatData.commentaire_qualite).trim() !== ''
                      ? String(etatData.commentaire_qualite).trim()
                      : '';

                  if (!isCurrent) {
                    if (confC) {
                      items.push({ label: histoLabel, value: confC, fullWidth: true });
                    } else if (fromCr && crC) {
                      items.push({ label: commercialLabel, value: crC, fullWidth: true });
                    }
                    return;
                  }

                  if (fromCr && crC) {
                    items.push({ label: commercialLabel, value: crC, fullWidth: true });
                  } else if (confC) {
                    items.push({ label: histoLabel, value: confC, fullWidth: true });
                  } else if (allowQualiteOnCurrent && qualC) {
                    items.push({ label: 'Commentaire', value: qualC, fullWidth: true });
                  } else if (crC) {
                    items.push({ label: commercialLabel, value: crC, fullWidth: true });
                  }
                };
                
                const items = [];

                if (etatData.pseudo != null && String(etatData.pseudo).trim() !== '') {
                  items.push({ label: 'Pseudo', value: String(etatData.pseudo).trim() });
                }

                if (etatData.from_compte_rendu && !isEtatSigner(etatId)) {
                  const nomCommercial =
                    etatData.cr_commercial_pseudo || etatData.commercial_pseudo;
                  const affiche =
                    nomCommercial && String(nomCommercial).trim() !== ''
                      ? String(nomCommercial).trim()
                      : '—';
                  items.push({ label: 'Commercial', value: affiche });
                }
                if (Number(etatId) === 7 && etatData.commercial_2_pseudo) {
                  // Ne pas dupliquer si commercial 1 == commercial 2 (même utilisateur saisi deux fois)
                  const c1 = String(etatData.commercial_pseudo || '').trim().toLowerCase();
                  const c2 = String(etatData.commercial_2_pseudo || '').trim().toLowerCase();
                  const sameById =
                    etatData.id_commercial != null &&
                    etatData.id_commercial_2 != null &&
                    Number(etatData.id_commercial) === Number(etatData.id_commercial_2);
                  if (!sameById && (c1 === '' || c1 !== c2)) {
                    items.push({ label: 'Commercial', value: etatData.commercial_2_pseudo });
                  }
                }

                // NRP (2) — Le commentaire n'est PAS affiché dans la carte « État actuel »
                // (demande explicite). Il reste visible dans l'historique des passages NRP.
                if (etatId === 2) {
                  if (etatData.sous_etat_titre) items.push({ label: 'Sous-état', value: etatData.sous_etat_titre });
                  if (etatData.confirmateur_pseudo || etatData.histo_confirmateur_pseudo) items.push({ label: labelAuteurEtat, value: valeurConfirmateurAffichee });
                  if (!isCurrent) {
                    pushCommentaireEtat(items);
                  }
                  if (etatData.histo_date_rdv_time) items.push({ label: 'A rappeler le', value: formatRdvDateTime(etatData.histo_date_rdv_time) });
                  if (etatData.date_appel_time || etatData.date_creation) items.push({ label: 'Date d\'appel', value: formatDateNoSeconds(etatData.date_appel_time || etatData.date_creation) });
                }
                // RAPPEL POUR BUREAU (19)
                else if (etatId === 19) {
                  if (etatData.confirmateur_pseudo || etatData.histo_confirmateur_pseudo) items.push({ label: labelAuteurEtat, value: valeurConfirmateurAffichee });
                  pushCommentaireEtat(items);
                  if (etatData.date_rdv_time) items.push({ label: 'A rappeler le', value: formatRdvDateTime(etatData.date_rdv_time) });
                  if (etatData.date_appel_time || etatData.date_creation) items.push({ label: 'Date d\'appel', value: formatDateNoSeconds(etatData.date_appel_time || etatData.date_creation) });
                }
                // ANNULER ET A REPROGRAMMER (8)
                else if (etatId === 8) {
                  if (etatData.confirmateur_pseudo || etatData.histo_confirmateur_pseudo) items.push({ label: labelAuteurEtat, value: valeurConfirmateurAffichee });
                  pushCommentaireEtat(items);
                  const rappelEtat8 = etatData.histo_date_rdv_time || etatData.date_rdv_time;
                  if (rappelEtat8) items.push({ label: 'A rappeler le', value: formatRdvDateTime(rappelEtat8) });
                  if (etatData.date_appel_time || etatData.date_creation) items.push({ label: 'Date d\'appel', value: formatDateNoSeconds(etatData.date_appel_time || etatData.date_creation) });
                }
                // CLIENT HONORE A SUIVRE (9)
                else if (etatId === 9) {
                  if (hasAnyConfirmateurAssigne || etatData.histo_confirmateur_pseudo) items.push({ label: labelAuteurEtat, value: valeurConfirmateurEtatListe });
                  pushCommentaireEtat(items);
                  if (etatData.date_rdv_time) items.push({ label: 'A rappeler le', value: formatRdvDateOnly(etatData.date_rdv_time) });
                  if (etatData.date_appel_time || etatData.date_creation) items.push({ label: 'Date d\'appel', value: formatDateNoSeconds(etatData.date_appel_time || etatData.date_creation) });
                }
                // RDV ANNULER (11)
                else if (etatId === 11) {
                  if (etatData.confirmateur_pseudo || etatData.histo_confirmateur_pseudo) items.push({ label: labelAuteurEtat, value: valeurConfirmateurAffichee });
                  pushCommentaireEtat(items);
                  if (etatData.conf_rdv_avec) items.push({ label: 'Appel avec qui', value: etatData.conf_rdv_avec });
                }
                // RDV ANNULER 2 FOIS (26)
                else if (etatId === 26) {
                  if (etatData.confirmateur_pseudo || etatData.histo_confirmateur_pseudo) items.push({ label: labelAuteurEtat, value: valeurConfirmateurAffichee });
                  pushCommentaireEtat(items);
                  if (etatData.conf_rdv_avec) items.push({ label: 'Appel avec qui', value: etatData.conf_rdv_avec });
                }
                // REFUSER (12)
                else if (etatId === 12) {
                  if (etatData.confirmateur_pseudo || etatData.histo_confirmateur_pseudo) items.push({ label: labelAuteurEtat, value: valeurConfirmateurAffichee });
                  pushCommentaireEtat(items);
                  if (etatData.date_appel_time || etatData.date_creation) items.push({ label: 'Date d\'appel', value: formatDateNoSeconds(etatData.date_appel_time || etatData.date_creation) });
                }
                // HHC FINANCEMENT A VERIFIER (34)
                else if (etatId === 34) {
                  if (etatData.confirmateur_pseudo || etatData.histo_confirmateur_pseudo) items.push({ label: labelAuteurEtat, value: valeurConfirmateurAffichee });
                  pushCommentaireEtat(items);
                  if (etatData.date_appel_time || etatData.date_creation) items.push({ label: 'Date d\'appel', value: formatDateNoSeconds(etatData.date_appel_time || etatData.date_creation) });
                }
                // HCC TECHNIQUE (35)
                else if (etatId === 35) {
                  if (etatData.confirmateur_pseudo || etatData.histo_confirmateur_pseudo) items.push({ label: labelAuteurEtat, value: valeurConfirmateurAffichee });
                  pushCommentaireEtat(items);
                  if (etatData.commercial_pseudo) items.push({ label: 'Commercial', value: etatData.commercial_pseudo });
                  if (etatData.date_appel_time || etatData.date_creation) items.push({ label: 'Date d\'appel', value: formatDateNoSeconds(etatData.date_appel_time || etatData.date_creation) });
                }
                // SIGNER, SIGNER RETRACTER, SIGNER COMPLET, SIGNER PM (13, 16, 45, 44) - Phase 3
                else if (isEtatSigner(etatId)) {
                  if (etatData.sous_etat_titre) items.push({ label: 'SOUS ETAT', value: etatData.sous_etat_titre });
                  // Confirmateur(s) : si >= 2, afficher chaque pseudo sur sa propre ligne (« Confirmateur 1 », « Confirmateur 2 », « Confirmateur 3 »).
                  {
                    const confsSigner = [
                      etatData.confirmateur_pseudo,
                      etatData.confirmateur_2_pseudo,
                      etatData.confirmateur_3_pseudo,
                    ]
                      .map((p) => (p != null ? String(p).trim() : ''))
                      .filter((p) => p !== '');
                    if (confsSigner.length >= 2) {
                      confsSigner.forEach((pseudo, idx) => {
                        const labelConf =
                          idx === 0 && isAuteurQualiteQualif
                            ? 'Audité par'
                            : `Confirmateur ${idx + 1}`;
                        items.push({ label: labelConf, value: pseudo });
                      });
                    } else if (confsSigner.length === 1) {
                      items.push({ label: labelAuteurEtat, value: confsSigner[0] });
                    } else if (etatData.histo_confirmateur_pseudo) {
                      items.push({ label: labelAuteurEtat, value: valeurConfirmateurAffichee });
                    }
                  }
                  // Commercial(s) : si >= 2, afficher chaque pseudo sur sa propre ligne (« Commercial 1 », « Commercial 2 »).
                  // Si commercial 1 == commercial 2 (même utilisateur), n'afficher qu'une seule ligne « Commercial ».
                  {
                    const sameCommercialById =
                      etatData.id_commercial != null &&
                      etatData.id_commercial_2 != null &&
                      Number(etatData.id_commercial) === Number(etatData.id_commercial_2);
                    const rawComs = [
                      etatData.commercial_pseudo,
                      etatData.commercial_2_pseudo,
                    ]
                      .map((p) => (p != null ? String(p).trim() : ''))
                      .filter((p) => p !== '');
                    // Déduplication par pseudo (insensible à la casse) — couvre aussi le cas où
                    // l'id n'est pas dispo dans l'objet courant (historique enrichi côté backend).
                    const seen = new Set();
                    const comsSigner = sameCommercialById
                      ? rawComs.slice(0, 1)
                      : rawComs.filter((p) => {
                          const key = p.toLowerCase();
                          if (seen.has(key)) return false;
                          seen.add(key);
                          return true;
                        });
                    if (comsSigner.length >= 2) {
                      comsSigner.forEach((pseudo, idx) => {
                        items.push({ label: `Commercial ${idx + 1}`, value: pseudo });
                      });
                    } else if (comsSigner.length === 1) {
                      items.push({ label: 'Commercial', value: comsSigner[0] });
                    } else if (etatData.from_compte_rendu && etatData.cr_commercial_pseudo) {
                      items.push({ label: 'Commercial', value: String(etatData.cr_commercial_pseudo).trim() });
                    }
                  }
                  pushCommentaireEtat(items);
                  // Date signature = date_heure RDV de la ligne fiches_histo correspondante (pas fiches.date_sign_time)
                  {
                    const dateSignatureRaw =
                      etatData.histo_date_rdv_time ||
                      etatData.date_rdv_time ||
                      null;
                    if (dateSignatureRaw) {
                      items.push({
                        label: 'Date signature',
                        value: formatRdvDateTime(dateSignatureRaw) || formatDateNoSeconds(dateSignatureRaw),
                      });
                    }
                  }
                  if (etatData.ph3_pac) {
                    const pacValue = etatData.ph3_pac === 'reau' || etatData.ph3_pac === 'R/EAU' ? 'R/EAU' : 
                                     etatData.ph3_pac === 'rr' || etatData.ph3_pac === 'R/R' ? 'R/R' : etatData.ph3_pac;
                    items.push({ label: 'PAC', value: pacValue });
                  }
                  if (etatData.ph3_attente) items.push({ label: 'Financement', value: etatData.ph3_attente });
                  if (etatData.ph3_type) items.push({ label: 'Type', value: etatData.ph3_type });
                  if (etatData.ph3_prix) items.push({ label: 'Prix', value: etatData.ph3_prix });
                  if (etatData.credit_immobilier) items.push({ label: 'Crédit immobilier', value: etatData.credit_immobilier });
                  if (etatData.credit_autre) items.push({ label: 'Autre crédit', value: etatData.credit_autre });
                  if (etatData.ph3_puissance) items.push({ label: 'Puissance', value: etatData.ph3_puissance });
                  if (etatData.ph3_ballon) {
                    const ballonValue = etatData.ph3_ballon === 'OUI' || etatData.ph3_ballon === 1 || etatData.ph3_ballon === '1' ? 'OUI' :
                                        etatData.ph3_ballon === 'NON' || etatData.ph3_ballon === 0 || etatData.ph3_ballon === '0' ? 'NON' : etatData.ph3_ballon;
                    items.push({ label: 'Ballon', value: ballonValue });
                  }
                  if (etatData.installeur_nom) items.push({ label: 'Installateur', value: etatData.installeur_nom });
                  if (etatData.ph3_consommation) items.push({ label: 'Consommation annuelle ancien système', value: etatData.ph3_consommation });
                  if (etatData.valeur_mensualite || etatData.ph3_mensualite) items.push({ label: 'Partie à financer du client', value: etatData.valeur_mensualite || etatData.ph3_mensualite });
                  if (etatData.ph3_bonus_30) items.push({ label: 'Bonus annoncé', value: etatData.ph3_bonus_30 });
                  if (etatData.ph3_mensualite) items.push({ label: 'Mensualité du crédit', value: etatData.ph3_mensualite });
                  if (etatData.ph3_nbr_annee_finance) items.push({ label: 'Nombre de mois du crédit', value: etatData.ph3_nbr_annee_finance });
                  if (etatData.ph3_alimentation) items.push({ label: 'Alimentation', value: etatData.ph3_alimentation });
                  // CQ ETAT / CQ DOSSIER / Observation : uniquement dans le formulaire CONTROLE QUALITE, pas dans les détails signature.
                }
                // CONFIRMER (7) — confirmateur(s) depuis fiches_histo ; champs conf_ remplis
                else if (Number(etatId) === 7) {
                  const confsConfirmer = [
                    etatData.confirmateur_pseudo || etatData.histo_confirmateur_pseudo,
                    etatData.confirmateur_2_pseudo,
                    etatData.confirmateur_3_pseudo,
                  ].filter((p) => p != null && String(p).trim() !== '');
                  if (confsConfirmer.length >= 2) {
                    confsConfirmer.forEach((pseudo, idx) => {
                      const labelConf =
                        idx === 0 && isAuteurQualiteQualif
                          ? 'Audité par'
                          : `Confirmateur ${idx + 1}`;
                      items.push({ label: labelConf, value: pseudo });
                    });
                  } else if (confsConfirmer.length === 1) {
                    items.push({ label: labelAuteurEtat, value: confsConfirmer[0] });
                  } else if (hasAnyConfirmateurAssigne || etatData.histo_confirmateur_pseudo) {
                    items.push({ label: labelAuteurEtat, value: valeurConfirmateurEtatListe });
                  }
                  pushCommentaireEtat(items, {
                    histoLabel: 'Commentaire confirmateur',
                    commercialLabel: 'Commentaire commercial (compte rendu)',
                  });
                  if (etatData.date_rdv_time) {
                    items.push({
                      label: 'Date RDV',
                      value: formatRdvDateTime(etatData.date_rdv_time),
                      ...(etatData.heure_rdv_avant_decalage
                        ? { heure_rdv_avant_decalage: etatData.heure_rdv_avant_decalage }
                        : {}),
                    });
                  }
                  if (etatData.date_appel_time || etatData.date_creation) items.push({ label: 'Date d\'appel', value: formatDateNoSeconds(etatData.date_appel_time || etatData.date_creation) });
                  // Champs conf_ (affichés uniquement si non vides)
                  if (etatData.conf_rdv_avec) items.push({ label: 'RDV pris avec', value: etatData.conf_rdv_avec });
                  if (etatData.situation_conjugale != null && String(etatData.situation_conjugale).trim() !== '') {
                    const sitRaw = String(etatData.situation_conjugale).trim();
                    const sitUp = sitRaw.toUpperCase();
                    const sitLabel =
                      sitUp === 'COUPLE' ? 'Couple'
                        : sitUp.startsWith('CELIB') ? 'Célibataire'
                          : sitRaw;
                    items.push({ label: 'Situation conjugale', value: sitLabel });
                  }
                  if (etatData.conf_deja_etude || etatData.conf_deja_fait_etude) {
                    items.push({ label: 'A déjà fait une étude', value: etatData.conf_deja_fait_etude || etatData.conf_deja_etude });
                  }
                  if (etatData.conf_details_etude != null && String(etatData.conf_details_etude).trim() !== '') {
                    items.push({ label: 'Détails étude', value: etatData.conf_details_etude, fullWidth: true });
                  }
                  if (etatData.conf_rdv_annule_precedent) items.push({ label: 'RDV déjà annulé précédemment', value: etatData.conf_rdv_annule_precedent });
                  if (etatData.conf_presence_couple) items.push({ label: 'Présence du couple ou célibataire', value: etatData.conf_presence_couple });
                  const profMrId = pickProfessionOrContratValue(etatData.conf_profession_monsieur, etatData.profession_mr);
                  if (profMrId != null) {
                    const professionMr = professions?.find(p => p.id == profMrId);
                    items.push({ label: 'Profession MR', value: professionMr ? professionMr.nom : String(profMrId) });
                  }
                  const typeContratMrId = pickProfessionOrContratValue(etatData.conf_type_contrat_mr, etatData.type_contrat_mr);
                  if (typeContratMrId != null) {
                    const typeContratMr = typeContrat?.find(t => String(t.id) === String(typeContratMrId));
                    items.push({ label: 'Type de contrat MR', value: typeContratMr ? typeContratMr.nom : String(typeContratMrId) });
                  }
                  const profMmeId = pickProfessionOrContratValue(etatData.conf_profession_madame, etatData.profession_madame);
                  if (profMmeId != null) {
                    const professionMme = professions?.find(p => p.id == profMmeId);
                    items.push({ label: 'Profession MME', value: professionMme ? professionMme.nom : String(profMmeId) });
                  }
                  const typeContratMmeId = pickProfessionOrContratValue(etatData.conf_type_contrat_madame, etatData.type_contrat_madame);
                  if (typeContratMmeId != null) {
                    const typeContratMme = typeContrat?.find(t => String(t.id) === String(typeContratMmeId));
                    items.push({ label: 'Type de contrat MME', value: typeContratMme ? typeContratMme.nom : String(typeContratMmeId) });
                  }
                  if (etatData.conf_revenu || etatData.revenu_foyer) items.push({ label: 'Revenu', value: etatData.conf_revenu || etatData.revenu_foyer || '-' });
                  if (etatData.conf_credit || etatData.credit_foyer) items.push({ label: 'Crédit', value: etatData.conf_credit || etatData.credit_foyer || '-' });
                  const modeChauffageText = modeChauffageAffiche(etatData.conf_mode_chauffage, etatData.mode_chauffage);
                  if (modeChauffageText) {
                    items.push({ label: 'Mode de chauffage', value: modeChauffageText });
                  }
                  if (etatData.conf_consommation_electricite) items.push({ label: 'Consommations électrique', value: etatData.conf_consommation_electricite });
                  if (etatData.conf_consommation_chauffage) items.push({ label: 'Consommations chauffage', value: etatData.conf_consommation_chauffage });
                  if (etatData.conf_orientation_toiture != null && etatData.conf_orientation_toiture !== '') items.push({ label: 'Orientation toiture', value: String(etatData.conf_orientation_toiture) });
                  if (etatData.conf_zones_ombres != null && etatData.conf_zones_ombres !== '') items.push({ label: 'Zones d\'ombres', value: String(etatData.conf_zones_ombres) });
                  if (etatData.conf_site_classe != null && etatData.conf_site_classe !== '') items.push({ label: 'Proche d\'un site classé', value: String(etatData.conf_site_classe) });
                  if (etatData.produit) {
                    const produitText = produits?.find(p => p.id == etatData.produit)?.nom || (etatData.produit === 1 ? 'PAC' : etatData.produit === 2 ? 'PV' : etatData.produit);
                    items.push({ label: 'Produit', value: produitText });
                  }
                }
                // Par défaut
                else {
                  if (etatData.confirmateur_pseudo || etatData.histo_confirmateur_pseudo) items.push({ label: labelAuteurEtat, value: valeurConfirmateurAffichee });
                  pushCommentaireEtat(items, { allowQualiteOnCurrent: true });
                  if (etatData.date_appel_time || etatData.date_creation) items.push({ label: 'Date d\'appel', value: formatDateNoSeconds(etatData.date_appel_time || etatData.date_creation) });
                }
                
                return items;
              };
              
              // Dernière entrée historique = passage à l'état actuel (pour date_creation et from_compte_rendu)
              const lastHisto = fiche.historique && fiche.historique.length > 0 ? fiche.historique[fiche.historique.length - 1] : null;
              const lastHistoEtatActuel = (lastHisto && lastHisto.id_etat === fiche.id_etat_final) ? lastHisto : null;

              /** Liste affichée sous « Historique des états » : sans la ligne qui duplique l'état actuel (déjà montrée au-dessus). */
              const historiqueListeSansEtatActuel = (() => {
                const hist = Array.isArray(fiche.historique) ? fiche.historique : [];
                if (hist.length === 0) return [];
                const last = hist[hist.length - 1];
                if (last && Number(last.id_etat) === Number(fiche.id_etat_final)) {
                  return hist.slice(0, -1);
                }
                return hist;
              })();

              const dateCreationEtatActuel = (lastHisto && lastHisto.id_etat === fiche.id_etat_final) ? lastHisto.date_creation : (fiche.date_modif_time || fiche.date_insert_time || null);
              
              // Date affichée état actuel / bandeau : priorité à fiches_histo.date_appel_time
              const currentDateAppelTime = (lastHisto && lastHisto.id_etat === fiche.id_etat_final && lastHisto.date_appel_time)
                ? lastHisto.date_appel_time
                : null;
              const dateAfficheeEtatActuel = currentDateAppelTime || dateCreationEtatActuel;
              // Pour NRP « A rappeler le » : on utilise exclusivement la valeur historisée
              // dans fiches_histo (alias `histo_date_rdv_time`, déjà sérialisée en chaîne locale
               // par le backend), pour éviter tout décalage horaire dû à la conversion UTC d'un Date JS.
              const currentHistoDateRappelTime = lastHistoEtatActuel?.histo_date_rdv_time || null;
              const currentDateRappelTime = (lastHisto && lastHisto.id_etat === fiche.id_etat_final && lastHisto.date_rdv_time)
                ? lastHisto.date_rdv_time
                : fiche.date_rdv_time;

              const etatActuelBaseTitre = fiche.etat_final_titre || etats?.find((e) => Number(e.id) === Number(fiche.id_etat_final))?.titre || 'État inconnu';
              const etatActuelBaseColor = fiche.etat_final_color || etats?.find((e) => Number(e.id) === Number(fiche.id_etat_final))?.color || '#3498db';
              const etatActuel = {
                id_etat: fiche.id_etat_final,
                etat_titre: etatActuelBaseTitre,
                etat_color: etatActuelBaseColor,
                sous_etat_titre: fiche.sous_etat_titre || null,
                // Pseudo utilisateur ayant enregistré le passage à cet état (colonne id_confirmateur de la ligne fiches_histo)
                histo_confirmateur_pseudo:
                  lastHistoEtatActuel?.histo_confirmateur_pseudo ||
                  lastHistoEtatActuel?.confirmateur_pseudo ||
                  null,
                histo_confirmateur_fonction:
                  lastHistoEtatActuel?.histo_confirmateur_fonction != null
                    ? Number(lastHistoEtatActuel.histo_confirmateur_fonction)
                    : null,
                // Confirmateurs : priorité à la ligne fiches_histo de l'état actuel, sinon slots fiche
                confirmateur_pseudo:
                  lastHistoEtatActuel?.confirmateur_pseudo ||
                  lastHistoEtatActuel?.histo_confirmateur_pseudo ||
                  confirmateurs?.find(c => Number(c.id) === Number(fiche.id_confirmateur))?.pseudo ||
                  null,
                confirmateur_2_pseudo:
                  lastHistoEtatActuel?.confirmateur_2_pseudo ||
                  confirmateurs?.find(c => Number(c.id) === Number(fiche.id_confirmateur_2))?.pseudo ||
                  null,
                confirmateur_3_pseudo:
                  lastHistoEtatActuel?.confirmateur_3_pseudo ||
                  confirmateurs?.find(c => Number(c.id) === Number(fiche.id_confirmateur_3))?.pseudo ||
                  null,
                // Pas de fallback fiche : uniquement le commentaire historisé du passage d'état
                conf_commentaire_produit:
                  lastHistoEtatActuel?.conf_commentaire_produit != null &&
                  String(lastHistoEtatActuel.conf_commentaire_produit).trim() !== ''
                    ? lastHistoEtatActuel.conf_commentaire_produit
                    : null,
                commentaire_qualite: fiche.commentaire_qualite || null,
                commentaire_commercial: fiche.commentaire_commercial || null,
                conf_rdv_avec: fiche.conf_rdv_avec || null,
                situation_conjugale: fiche.situation_conjugale || null,
                conf_appel_tunisie_avec: fiche.conf_appel_tunisie_avec || null,
                conf_deja_etude: fiche.conf_deja_etude || fiche.conf_deja_fait_etude || null,
                conf_deja_fait_etude: fiche.conf_deja_fait_etude || fiche.conf_deja_etude || null,
                conf_details_etude:
                  lastHistoEtatActuel?.conf_details_etude != null && String(lastHistoEtatActuel.conf_details_etude).trim() !== ''
                    ? lastHistoEtatActuel.conf_details_etude
                    : (fiche.conf_details_etude != null && String(fiche.conf_details_etude).trim() !== ''
                      ? fiche.conf_details_etude
                      : null),
                conf_profession_monsieur: pickProfessionOrContratValue(fiche.conf_profession_monsieur, fiche.profession_mr),
                conf_type_contrat_mr: pickProfessionOrContratValue(fiche.conf_type_contrat_mr, fiche.type_contrat_mr),
                conf_profession_madame: pickProfessionOrContratValue(fiche.conf_profession_madame, fiche.profession_madame),
                conf_type_contrat_madame: pickProfessionOrContratValue(fiche.conf_type_contrat_madame, fiche.type_contrat_madame),
                conf_revenu: fiche.conf_revenu || null,
                conf_credit: fiche.conf_credit || null,
                conf_mode_chauffage: fiche.conf_mode_chauffage ?? fiche.mode_chauffage ?? null,
                conf_consommation_electricite: fiche.conf_consommation_electricite || null,
                conf_consommation_chauffage: fiche.conf_consommation_chauffage || null,
                conf_rdv_annule_precedent: fiche.conf_rdv_annule_precedent || null,
                conf_presence_couple: fiche.conf_presence_couple || null,
                conf_orientation_toiture: fiche.conf_orientation_toiture ?? fiche.orientation_toiture ?? null,
                conf_zones_ombres: fiche.conf_zones_ombres ?? fiche.zones_ombres ?? null,
                conf_site_classe: fiche.conf_site_classe ?? fiche.site_classe ?? null,
                histo_date_rdv_time: currentHistoDateRappelTime,
                heure_rdv_avant_decalage: heureRdvAvantDecalage,
                date_rdv_time: currentDateRappelTime || null,
                date_appel_time: currentDateAppelTime || null,
                date_sign_time: fiche.date_sign_time || null,
                commercial_pseudo:
                  fiche.commercial_pseudo ||
                  formatCommercialDisplayName(commerciaux?.find((c) => c.id === fiche.id_commercial)) ||
                  null,
                commercial_2_pseudo:
                  formatCommercialDisplayName(commerciaux?.find((c) => c.id === fiche.id_commercial_2)) ||
                  (normalizeIsR2Value(fiche.is_r2) === 1
                    ? formatCommercialDisplayName(
                        commerciaux?.find((c) => c.id === fiche.id_commercial_r1)
                      )
                    : null) ||
                  null,
                id_commercial: fiche.id_commercial != null ? fiche.id_commercial : null,
                id_commercial_2:
                  fiche.id_commercial_2 != null && Number(fiche.id_commercial_2) > 0
                    ? fiche.id_commercial_2
                    : (normalizeIsR2Value(fiche.is_r2) === 1 &&
                      fiche.id_commercial_r1 != null &&
                      Number(fiche.id_commercial_r1) > 0
                        ? fiche.id_commercial_r1
                        : null),
                id_commercial_r1: fiche.id_commercial_r1 != null ? fiche.id_commercial_r1 : null,
                installeur_nom: installateurs?.find(i => i.id === fiche.ph3_installateur)?.nom || null,
                // Phase 3
                ph3_pac: fiche.ph3_pac || null,
                ph3_type: fiche.ph3_type || null,
                ph3_prix: fiche.ph3_prix || null,
                ph3_puissance: fiche.ph3_puissance || fiche.ph3_puissance_pv || null,
                ph3_consommation: fiche.conf_consommations || null,
                ph3_bonus_30: fiche.ph3_bonus_30 || null,
                ph3_mensualite: fiche.ph3_mensualite || null,
                ph3_nbr_annee_finance: fiche.nbr_annee_finance || null,
                ph3_ballon: fiche.ph3_ballon || null,
                ph3_alimentation: fiche.ph3_alimentation || null,
                credit_immobilier: fiche.credit_immobilier || null,
                credit_autre: fiche.credit_autre || null,
                valeur_mensualite: fiche.valeur_mensualite || null,
                // Autres
                profession_mr: fiche.profession_mr || null,
                profession_madame: fiche.profession_madame || null,
                type_contrat_mr: fiche.type_contrat_mr || null,
                type_contrat_madame: fiche.type_contrat_madame || null,
                revenu_foyer: fiche.revenu_foyer || null,
                credit_foyer: fiche.credit_foyer || null,
                mode_chauffage: fiche.mode_chauffage || null,
                produit: fiche.produit || null,
                cq_etat: fiche.cq_etat || null,
                cq_dossier: fiche.cq_dossier || null,
                observations_cq: fiche.observations_cq || null,
                date_creation: dateCreationEtatActuel,
                from_compte_rendu: !!(lastHisto && lastHisto.id_etat === fiche.id_etat_final && lastHisto.from_compte_rendu),
                cr_commercial_pseudo:
                  lastHistoEtatActuel?.cr_commercial_pseudo || fiche.compte_rendu_commercial_pseudo || null,
                pseudo: lastHistoEtatActuel?.pseudo || null
              };

              // Afficher <CR> dans l'état actuel uniquement si cet état vient d'un compte rendu (dernière entrée historio = état actuel et from_compte_rendu)
              const isCurrentStateFromCR = lastHisto && lastHisto.id_etat === fiche.id_etat_final && lastHisto.from_compte_rendu;
              const crPseudoEtatActuel = isCurrentStateFromCR ? (lastHisto.cr_commercial_pseudo || '') : '';
              
              const detailItemsActuelRaw = renderEtatDetails(etatActuel, { isCurrent: true });
              // Sessions confirmateur / RE / RP / backoffice / admin : permettre d'éditer même sans commentaire affiché
              const canEditEtatActuelComment = [1, 6, 7, 11, 13, 14].includes(Number(user?.fonction));
              const detailItemsActuel = (() => {
                let items = [...detailItemsActuelRaw];
                // Session commercial : Date RDV + Situation conjugale (+ Date signature si SIGNER)
                if (isCommercial) {
                  const allowed = new Set(['Date RDV', 'Situation conjugale', 'Date signature', 'Signé le']);
                  let filtered = items.filter((it) => allowed.has(String(it.label || '')));
                  if (!filtered.some((it) => String(it.label || '') === 'Date RDV')) {
                    const dt = etatActuel.date_rdv_time || fiche.date_rdv_time;
                    if (dt && !isEtatSigner(etatActuel.id_etat)) {
                      const heureAvant =
                        etatActuel.heure_rdv_avant_decalage || fiche.heure_rdv_avant_decalage;
                      filtered = [
                        {
                          label: 'Date RDV',
                          value: formatRdvDateTime(dt),
                          ...(heureAvant ? { heure_rdv_avant_decalage: heureAvant } : {}),
                        },
                        ...filtered,
                      ];
                    }
                  }
                  if (!filtered.some((it) => String(it.label || '') === 'Situation conjugale')) {
                    const sitRaw = String(
                      etatActuel.situation_conjugale || fiche.situation_conjugale || ''
                    ).trim();
                    if (sitRaw) {
                      const sitUp = sitRaw.toUpperCase();
                      const sitLabel =
                        sitUp === 'COUPLE' ? 'Couple'
                          : sitUp.startsWith('CELIB') ? 'Célibataire'
                            : sitRaw;
                      filtered = [...filtered, { label: 'Situation conjugale', value: sitLabel }];
                    }
                  }
                  if (
                    isEtatSigner(etatActuel.id_etat) &&
                    !filtered.some((it) =>
                      ['Date signature', 'Signé le'].includes(String(it.label || ''))
                    )
                  ) {
                    const dateSignatureRaw =
                      etatActuel.histo_date_rdv_time || etatActuel.date_rdv_time || null;
                    if (dateSignatureRaw) {
                      filtered = [
                        ...filtered,
                        {
                          label: 'Date signature',
                          value: formatRdvDateTime(dateSignatureRaw) || formatDateNoSeconds(dateSignatureRaw),
                        },
                      ];
                    }
                  }
                  return filtered;
                }
                if (!canEditEtatActuelComment) return items;
                // NRP : commentaire volontairement masqué dans l'état actuel
                if (Number(etatActuel.id_etat) === 2) return items;
                const hasCommentItem = items.some((it) =>
                  /commentaire/i.test(String(it.label || ''))
                );
                if (!hasCommentItem) {
                  items.push({
                    label: 'Commentaire',
                    value: etatActuel.conf_commentaire_produit || '',
                    fullWidth: true,
                  });
                }
                return items;
              })();

              /** Texte à préremplir dans le textarea d'édition du commentaire (État actuel). */
              const resolveEtatActuelCommentForEdit = (commentField, itemValue) => {
                const pick = (...candidates) => {
                  for (const c of candidates) {
                    if (c == null) continue;
                    const s = String(c);
                    if (s.trim() !== '' && s !== '-' && s !== '—') return s;
                  }
                  return '';
                };
                if (commentField === 'commentaire_commercial') {
                  return pick(
                    itemValue,
                    etatActuel.commentaire_commercial,
                    fiche.commentaire_commercial
                  );
                }
                return pick(
                  itemValue,
                  etatActuel.conf_commentaire_produit,
                  lastHistoEtatActuel?.conf_commentaire_produit
                );
              };
              const normalizeEtatTitle = (v) =>
                String(v || '')
                  .toLowerCase()
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .trim();

              const historiquePriorityStates = (() => {
                const hist = Array.isArray(fiche.historique) ? fiche.historique : [];
                if (hist.length === 0) return [];

                // Bannière : uniquement fiches_histo.id_commercial (pas de fallback CR / fiche)
                const resolveCommercialFromHisto = (h) => {
                  if (!h) return { pseudo: '', tel: '' };
                  const comId =
                    h.id_commercial != null && Number(h.id_commercial) > 0
                      ? Number(h.id_commercial)
                      : null;
                  if (!comId) return { pseudo: '', tel: '' };
                  // Historique API : commercial_pseudo = nom||login ; sinon users
                  const fromHisto = h.commercial_pseudo != null ? String(h.commercial_pseudo).trim() : '';
                  if (fromHisto) {
                    const found = (commerciaux || []).find((c) => Number(c.id) === comId);
                    const tel = found?.tel != null ? String(found.tel).trim() : '';
                    return { pseudo: fromHisto, tel };
                  }
                  const found = (commerciaux || []).find((c) => Number(c.id) === comId);
                  if (!found) return { pseudo: '', tel: '' };
                  return {
                    pseudo: formatCommercialDisplayName(found),
                    tel: found.tel != null ? String(found.tel).trim() : '',
                  };
                };

                const hasSigned = (h) => {
                  const id = Number(h?.id_etat);
                  const t = normalizeEtatTitle(h?.etat_titre);
                  return [13, 44, 45, 16].includes(id) || t.includes('sign');
                };
                const hasASuivre = (h) => {
                  const id = Number(h?.id_etat);
                  const t = normalizeEtatTitle(h?.etat_titre);
                  return id === 9 || (t.includes('honore') && t.includes('suivr'));
                };
                const hasRefuser = (h) => {
                  const id = Number(h?.id_etat);
                  const t = normalizeEtatTitle(h?.etat_titre);
                  return [12, 25].includes(id) || t.includes('refus');
                };

                const banners = [];
                // Ordre: Signé, Refuser, Honoré à suivre
                const signedState = hist.find(hasSigned);
                if (signedState) {
                  const com = resolveCommercialFromHisto(signedState);
                  banners.push({
                    key: 'signer',
                    label: 'Signé',
                    color: signedState.etat_color || '#4CAF50',
                    commercialPseudo: com.pseudo,
                    commercialTel: com.tel,
                  });
                }
                const refuserState = hist.find(hasRefuser);
                if (refuserState) {
                  const com = resolveCommercialFromHisto(refuserState);
                  banners.push({
                    key: 'refuser',
                    label: 'Refuser',
                    color: refuserState.etat_color || '#e74c3c',
                    commercialPseudo: com.pseudo,
                    commercialTel: com.tel,
                  });
                }
                const aSuivreState = hist.find(hasASuivre);
                if (aSuivreState) {
                  const com = resolveCommercialFromHisto(aSuivreState);
                  banners.push({
                    key: 'honore',
                    label: 'Honoré à suivre',
                    color: aSuivreState.etat_color || '#f7a219',
                    commercialPseudo: com.pseudo,
                    commercialTel: com.tel,
                  });
                }
                return banners;
              })();
              
              return (
                <>
                  {/* 1) Bannières Signé / Refuser / Honoré à suivre */}
                  {user?.fonction !== 5 && historiquePriorityStates.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      {historiquePriorityStates.map((banner) => (
                        <div
                          key={banner.key}
                          style={{
                            width: '100%',
                            marginBottom: '10px',
                            padding: '10px 12px',
                            borderRadius: '6px',
                            backgroundColor: banner.color,
                            color: banner.color === '#ffffff' || banner.color === '#fff' ? '#000' : '#fff',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            fontSize: '16px',
                          }}
                        >
                          {banner.label}
                          {banner.commercialPseudo ? (
                            <>
                              {' — Commercial : '}
                              <span
                                className="fiche-detail-etat-confirmer-val--commercial"
                                data-confirmer-hl="commercial"
                              >
                                {formatAgentPseudoDisplay(banner.commercialPseudo)}
                              </span>
                              {banner.commercialTel ? (
                                <>
                                  {' — Tél : '}
                                  <span>{banner.commercialTel}</span>
                                </>
                              ) : null}
                            </>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Section Historique - Pliable (masquée en session commercial ; visible partenaire si fixer RDV) */}
                  {(!isPartenaireModalLimitedTabs || partenaireCanFixerRdv) && user?.fonction !== 5 && fiche.historique && fiche.historique.length > 0 && (
                    <>
                      {historiqueListeSansEtatActuel.length > 0 && (
                      <>
                      <div 
                        ref={historiqueEtatsAnchorRef}
                        className="section-title historique-title-bar" 
                        style={{ 
                          cursor: 'pointer', 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          userSelect: 'none',
                          padding: '12px 15px',
                          backgroundColor: '#000000',
                          color: '#ffffff',
                          borderRadius: '6px',
                          marginBottom: '15px',
                          border: '1px solid #000000',
                          WebkitTextFillColor: '#ffffff'
                        }}
                        onClick={() => setShowHistorique(!showHistorique)}
                      >
                        <span style={{ fontWeight: 'bold', fontSize: '15px', color: '#ffffff', WebkitTextFillColor: '#ffffff' }}>
                          <FaHistory style={{ marginRight: '8px', color: '#ffffff', fill: '#ffffff' }} />
                          Historique des états ({historiqueListeSansEtatActuel.length} entrée{historiqueListeSansEtatActuel.length > 1 ? 's' : ''})
                        </span>
                        {showHistorique
                          ? <FaChevronUp style={{ color: '#ffffff', fill: '#ffffff' }} />
                          : <FaChevronDown style={{ color: '#ffffff', fill: '#ffffff' }} />}
                      </div>
                      
                      {showHistorique && (
                        <div className="historique-list" style={{ marginTop: '10px' }}>
                          {historiqueListeSansEtatActuel.map((histo) => {
                            const detailItems = renderEtatDetails(histo);
                            const histoEtatColor = histo.etat_color
                              || etats?.find((e) => Number(e.id) === Number(histo.id_etat))?.color
                              || '#3498db';
                            const histoEtatTitre = histo.etat_titre
                              || etats?.find((e) => Number(e.id) === Number(histo.id_etat))?.titre
                              || 'État inconnu';
                            const histoSousEtatTitre = histo.sous_etat_titre;
                            const histoHeaderTextColor = histoEtatColor === '#ffffff' || histoEtatColor === '#fff' ? '#000000' : '#ffffff';
                            
                            return (
                              <div
                                key={histo.id}
                                className="historique-item etat-historique-card"
                                style={{
                                  width: '100%',
                                  maxWidth: '760px',
                                  margin: '0 auto 15px auto',
                                  alignSelf: 'center',
                                  boxSizing: 'border-box',
                                  backgroundColor: '#3a3a3a',
                                  color: '#ffffff',
                                  fontWeight: 'bold',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                  border: '1px solid rgba(0,0,0,0.25)'
                                }}
                              >
                                <div
                                  className="historique-header"
                                  style={{
                                    backgroundColor: histoEtatColor,
                                    color: histoHeaderTextColor,
                                    padding: '8px 14px',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <span
                                      className="historique-etat"
                                      style={{
                                        color: histoHeaderTextColor,
                                        fontWeight: 'bold',
                                        fontSize: '15px',
                                        textTransform: 'uppercase',
                                        flex: '0 1 auto'
                                      }}
                                    >
                                      {histo.from_compte_rendu && (
                                        <span style={{ marginRight: '6px' }}>
                                          &lt;CR&gt;
                                          {histo.cr_commercial_pseudo ? (
                                            <>
                                              {' '}
                                              <span className="fiche-detail-cr-commercial-pseudo">
                                                {formatAgentPseudoDisplay(histo.cr_commercial_pseudo)}
                                              </span>
                                            </>
                                          ) : null}
                                        </span>
                                      )}
                                      {histoEtatTitre}
                                    </span>
                                    {histoSousEtatTitre && (
                                      <span
                                        style={{
                                          padding: '3px 9px',
                                          borderRadius: '4px',
                                          backgroundColor: 'rgba(255,255,255,0.22)',
                                          color: histoHeaderTextColor,
                                          fontSize: '12px',
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        {histoSousEtatTitre}
                                      </span>
                                    )}
                                    <span
                                      className="historique-date"
                                      style={{
                                        color: histoHeaderTextColor,
                                        WebkitTextFillColor: histoHeaderTextColor,
                                        fontSize: '13px',
                                        marginLeft: 'auto',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      {histo.date_appel_time
                                        ? formatDateNoSeconds(histo.date_appel_time)
                                        : (histo.date_creation ? formatDateNoSeconds(histo.date_creation) : '-')}
                                    </span>
                                  </div>
                                </div>
                                
                                {detailItems.length > 0 && (
                                  <div style={{ padding: '15px', fontSize: '13px', color: '#ffffff' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                      {detailItems.map((item, idx) => {
                                        const hi = getConfirmerDetailHighlight(
                                          histo.id_etat,
                                          histo.etat_titre,
                                          item.label
                                        );
                                        const showDecalageUnderRdv =
                                          item.label === 'Date RDV' &&
                                          isEtatConfirmerLike(histo.id_etat, histoEtatTitre);
                                        return (
                                          <div key={idx} style={{ width: '100%', lineHeight: 1.45, color: '#ffffff' }}>
                                            <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#ffffff' }}>
                                              <span className="etat-detail-label" style={{ color: '#ffffff', fontWeight: 500 }}>{item.label}:</span>{' '}
                                              <span
                                                className={`etat-detail-value${hi?.className ? ` ${hi.className}` : ''}`}
                                                style={{
                                                  color: '#ffffff',
                                                  fontWeight: 700,
                                                  ...(hi?.style || {}),
                                                }}
                                                data-confirmer-hl={hi?.dataHl || undefined}
                                              >
                                                {item.value || '-'}
                                              </span>
                                            </span>
                                            {showDecalageUnderRdv && renderDecalageDemandesUnderRdv(decalagesAffichables, decalageDemandesActions)}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                {isEtatConfirmerLike(histo.id_etat, histoEtatTitre) &&
                                  renderQualiteConfirmationBackofficePanel()}
                                {/* Formulaire Contrôle Qualité dans l'historique : uniquement si l'état actuel n'est PAS SIGNER (13)
                                    et que cette entrée correspond à la dernière occurrence de l'état 13. */}
                                {histoEtat13Id && histo.id === histoEtat13Id && (
                                  <div style={{ padding: '0 15px 15px 15px' }}>
                                    {renderControleQualiteForm({ embedded: true, keySuffix: `_histo_${histo.id}` })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      </>
                      )}
                    </>
                  )}
                  {/* Section État Actuel — rétractable au clic */}
                  {fiche.id_etat_final && (
                    <>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowEtatActuel((v) => !v)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setShowEtatActuel((v) => !v);
                          }
                        }}
                        style={{
                          backgroundColor: '#f97316',
                          color: '#ffffff',
                          padding: '10px 18px',
                          borderRadius: '8px',
                          fontWeight: 'bold',
                          fontSize: '15px',
                          letterSpacing: '1px',
                          textTransform: 'uppercase',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '10px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                          marginTop: '12px',
                          marginBottom: showEtatActuel ? '12px' : '0',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                        title={showEtatActuel ? "Réduire l'état actuel" : "Afficher l'état actuel"}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                          <FaInfoCircle />
                          <span>ETAT ACTUEL</span>
                        </span>
                        {showEtatActuel
                          ? <FaChevronUp style={{ color: '#ffffff', fill: '#ffffff' }} />
                          : <FaChevronDown style={{ color: '#ffffff', fill: '#ffffff' }} />}
                      </div>

                      {showEtatActuel && (
                      <div
                        className="etat-actuel-wrapper"
                        style={{
                          maxWidth: '760px',
                          margin: '0 auto 20px auto',
                        }}
                      >
                    <div 
                      className="etat-actuel-card"
                      style={{
                        padding: '20px',
                        border: '6px solid #000000',
                        borderRadius: '8px',
                        backgroundColor: '#3a3a3a',
                        color: '#ffffff',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        position: 'relative'
                      }}
                    >
                      {/* Badge de l'état — pleine largeur de la carte, couleur de l'état */}
                      <span
                        className={
                          Number(etatActuel.id_etat) === 1 || Number(etatActuel.id_etat) === 2
                            ? 'etat-actuel-badge etat-actuel-badge--dark-text'
                            : 'etat-actuel-badge'
                        }
                        style={{
                          padding: '8px 18px',
                          borderRadius: '6px',
                          backgroundColor: etatActuel.etat_color || '#3498db',
                          color:
                            Number(etatActuel.id_etat) === 2 || Number(etatActuel.id_etat) === 1
                              ? '#333333'
                              : (etatActuel.etat_color === '#ffffff' || etatActuel.etat_color === '#fff' ? '#000' : '#fff'),
                          fontWeight: 'bold',
                          fontSize: '16px',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          flexWrap: 'wrap',
                          width: '100%',
                          boxSizing: 'border-box',
                          marginBottom: '16px',
                        }}
                      >
                        <span style={{ flex: '1 1 auto' }}>
                          {isCurrentStateFromCR ? (
                            <>
                              <span style={{ marginRight: '6px' }}>&lt;CR&gt;</span>
                              {crPseudoEtatActuel && (
                                <span
                                  className="fiche-detail-cr-commercial-pseudo"
                                  style={{ marginRight: '6px' }}
                                >
                                  {formatAgentPseudoDisplay(crPseudoEtatActuel)} –
                                </span>
                              )}
                            </>
                          ) : null}
                          {etatActuel.etat_titre}
                        </span>
                        {etatActuel.sous_etat_titre && (
                          <span
                            style={{
                              padding: '3px 10px',
                              borderRadius: '4px',
                              backgroundColor: 'rgba(255,255,255,0.25)',
                              fontSize: '12px',
                              fontWeight: 'bold',
                            }}
                          >
                            {etatActuel.sous_etat_titre}
                          </span>
                        )}
                        <span
                          className="etat-actuel-badge-date"
                          style={{
                            marginLeft: 'auto',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            letterSpacing: 0,
                            color:
                              Number(etatActuel.id_etat) === 1 || Number(etatActuel.id_etat) === 2
                                ? '#333333'
                                : '#ffffff',
                            WebkitTextFillColor:
                              Number(etatActuel.id_etat) === 1 || Number(etatActuel.id_etat) === 2
                                ? '#333333'
                                : '#ffffff',
                          }}
                        >
                          {dateAfficheeEtatActuel
                            ? formatDateNoSeconds(dateAfficheeEtatActuel)
                            : '-'}
                        </span>
                      </span>

                      {/* Détails de l'état actuel (même présentation que le bloc détails de l'historique) */}
                      {detailItemsActuel.length > 0 && (
                        <div
                          style={{
                            fontSize: '13px',
                            color: '#ffffff'
                          }}
                        >
                          <h4
                            style={{
                              marginTop: 0,
                              marginBottom: '10px',
                              fontSize: '15px',
                              fontWeight: 'bold',
                              color: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                          >
                            <FaInfoCircle style={{ color: etatActuel.etat_color }} />
                            Détails de l'état actuel
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {detailItemsActuel.map((item, idx) => {
                              const hi = getConfirmerDetailHighlight(
                                etatActuel.id_etat,
                                etatActuel.etat_titre,
                                item.label
                              );
                              const isEditableDateRdvInline =
                                !isCommercial &&
                                (!isPartenaireProfil || partenaireCanFixerRdv) &&
                                isEtatConfirmerLike(etatActuel.id_etat, etatActuel.etat_titre) &&
                                item.label === 'Date RDV' &&
                                !!etatActuel.date_rdv_time;
                              const currentInlineRdvTime = formatRdvTimeOnly(etatActuel.date_rdv_time);
                              const saveInlineRdvTime = async () => {
                                if (!dateRdvInlineEdit || dateRdvInlineEdit === currentInlineRdvTime) {
                                  setDateRdvInlineEdit('');
                                  return;
                                }
                                const datePart = String(etatActuel.date_rdv_time).match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
                                if (!datePart) {
                                  setDateRdvInlineEdit('');
                                  return;
                                }
                                const dateLabel = formatRdvDateOnly(etatActuel.date_rdv_time);
                                if (!window.confirm(`Confirmer la nouvelle heure du RDV : ${dateLabel} à ${dateRdvInlineEdit} ?`)) {
                                  return;
                                }
                                try {
                                  await updateFieldMutation.mutateAsync({
                                    field: 'date_rdv_time',
                                    value: `${datePart} ${dateRdvInlineEdit}:00`,
                                  });
                                } finally {
                                  setDateRdvInlineEdit('');
                                }
                              };
                              const heureRdvAvantDecalage =
                                item.heure_rdv_avant_decalage || etatActuel.heure_rdv_avant_decalage;
                              return (
                                <div key={idx} style={{ width: '100%', lineHeight: 1.45, color: '#ffffff' }}>
                                  <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#ffffff' }}>
                                    <span className="etat-detail-label" style={{ color: '#ffffff', fontWeight: 500 }}>{item.label}:</span>{' '}
                                    {isEditableDateRdvInline ? (
                                      <>
                                        <span
                                          className={`${hi?.className || ''} etat-actuel-date-rdv-badge`.trim()}
                                          data-confirmer-hl={hi?.dataHl || undefined}
                                          style={{
                                            ...hi?.style,
                                            backgroundColor: '#4a4a4a',
                                            color: '#ffffff',
                                            padding: '6px 14px',
                                            fontSize: '17px',
                                            fontWeight: 800,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            whiteSpace: 'nowrap',
                                            flexWrap: 'nowrap',
                                            wordBreak: 'keep-all',
                                            maxWidth: 'none',
                                          }}
                                        >
                                          <span style={{ whiteSpace: 'nowrap', color: '#ffffff', fontWeight: 800 }}>
                                            {formatRdvDateOnly(etatActuel.date_rdv_time)}
                                          </span>
                                          <input
                                            type="time"
                                            disabled={updateFieldMutation.isLoading}
                                            value={
                                              dateRdvInlineEdit !== ''
                                                ? dateRdvInlineEdit
                                                : currentInlineRdvTime
                                            }
                                            onChange={(e) => setDateRdvInlineEdit(e.target.value)}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                e.preventDefault();
                                                saveInlineRdvTime();
                                              } else if (e.key === 'Escape') {
                                                e.preventDefault();
                                                setDateRdvInlineEdit('');
                                                e.currentTarget.blur();
                                              }
                                            }}
                                            style={{
                                              fontSize: '17px',
                                              fontWeight: 800,
                                              fontFamily: 'inherit',
                                              color: '#15803d',
                                              backgroundColor: '#ffffff',
                                              border: '1px solid #15803d',
                                              borderRadius: '4px',
                                              padding: '2px 6px',
                                              cursor: updateFieldMutation.isLoading ? 'wait' : 'pointer',
                                            }}
                                            title="Modifier l'heure du RDV puis cliquer sur le bouton de validation"
                                          />
                                        </span>
                                        {item.label === 'Date RDV' && renderHeureRdvAvantBadge(heureRdvAvantDecalage)}
                                        <button
                                          type="button"
                                          onClick={saveInlineRdvTime}
                                          disabled={
                                            updateFieldMutation.isLoading ||
                                            !dateRdvInlineEdit ||
                                            dateRdvInlineEdit === currentInlineRdvTime
                                          }
                                          style={{
                                            marginLeft: '8px',
                                            width: '30px',
                                            height: '30px',
                                            borderRadius: '50%',
                                            border: '2px solid #15803d',
                                            backgroundColor: '#dcfce7',
                                            color: '#15803d',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: updateFieldMutation.isLoading ? 'wait' : 'pointer',
                                            verticalAlign: 'middle',
                                          }}
                                          title="Valider la nouvelle heure du RDV"
                                        >
                                          <FaCheck />
                                        </button>
                                        {(!isPartenaireProfil || partenaireCanFixerRdv) && (() => {
                                          const sinceTs = etatActuel.date_creation
                                            ? new Date(etatActuel.date_creation).getTime()
                                            : null;
                                          const histoSinceConfirmer = dateRdvModifications.filter((mod) => {
                                            if (!sinceTs) return true;
                                            const d = mod.date_modif_time ? new Date(mod.date_modif_time).getTime() : null;
                                            return d != null && !Number.isNaN(d) && d >= sinceTs;
                                          });
                                          if (histoSinceConfirmer.length === 0) return null;
                                          return (
                                            <div style={{ marginTop: '10px', width: '100%' }}>
                                              <button
                                                type="button"
                                                onClick={() => setShowDateRdvHistory((prev) => !prev)}
                                                title="Voir l'historique des modifications de l'heure du RDV"
                                                style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: '6px',
                                                  background: 'transparent',
                                                  border: '1px solid #ffffff',
                                                  color: '#ffffff',
                                                  borderRadius: '4px',
                                                  padding: '4px 10px',
                                                  fontSize: '12px',
                                                  fontWeight: 700,
                                                  cursor: 'pointer'
                                                }}
                                              >
                                                <FaHistory />
                                                Historique des modifications ({histoSinceConfirmer.length})
                                              </button>
                                              {showDateRdvHistory && (
                                                <div
                                                  className="etat-actuel-rdv-heure-historique"
                                                  style={{
                                                    marginTop: '8px',
                                                    background: '#ffffff',
                                                    color: '#1f2937',
                                                    borderRadius: '6px',
                                                    padding: '8px',
                                                    border: '1px solid #d1d5db',
                                                    maxWidth: '720px'
                                                  }}
                                                >
                                                  <table
                                                    className="etat-actuel-rdv-heure-historique-table"
                                                    style={{
                                                      width: '100%',
                                                      borderCollapse: 'collapse',
                                                      fontSize: '12px',
                                                      color: '#1f2937'
                                                    }}
                                                  >
                                                    <thead>
                                                      <tr style={{ background: '#f3f4f6' }}>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db', color: '#111827' }}>Quand</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db', color: '#111827' }}>Qui</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db', color: '#111827' }}>Ancienne valeur</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db', color: '#111827' }}>Nouvelle valeur</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {histoSinceConfirmer
                                                        .slice()
                                                        .sort((a, b) => {
                                                          const ta = a.date_modif_time ? new Date(a.date_modif_time).getTime() : 0;
                                                          const tb = b.date_modif_time ? new Date(b.date_modif_time).getTime() : 0;
                                                          return tb - ta;
                                                        })
                                                        .map((mod) => (
                                                          <tr key={mod.id}>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap', color: '#1f2937' }}>
                                                              {mod.date_modif_time
                                                                ? new Date(mod.date_modif_time).toLocaleString('fr-FR')
                                                                : '-'}
                                                            </td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: '#1f2937' }}>
                                                              {mod.user_pseudo || '-'}
                                                            </td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: '#1f2937' }}>
                                                              {modificaValueDisplay(
                                                                mod.ancien_valeur,
                                                                mod.type || 'date_rdv_time',
                                                                mod.ancien_valeur_label
                                                              )}
                                                            </td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: '#1f2937' }}>
                                                              {modificaValueDisplay(
                                                                mod.nouvelle_valeur,
                                                                mod.type || 'date_rdv_time',
                                                                mod.nouvelle_valeur_label
                                                              )}
                                                            </td>
                                                          </tr>
                                                        ))}
                                                    </tbody>
                                                  </table>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })()}
                                        {item.label === 'Date RDV' &&
                                          isEtatConfirmerLike(etatActuel.id_etat, etatActuel.etat_titre) &&
                                          renderDecalageDemandesUnderRdv(decalagesAffichables, decalageDemandesActions)}
                                      </>
                                    ) : (
                                      <>
                                      {(() => {
                                        const isCommentItem = /commentaire/i.test(String(item.label || ''));
                                        const commentField =
                                          /commentaire commercial/i.test(String(item.label || ''))
                                            ? 'commentaire_commercial'
                                            : 'conf_commentaire_produit';
                                        const isEditingComment =
                                          canEditEtatActuelComment &&
                                          isCommentItem &&
                                          etatActuelCommentEdit?.field === commentField;

                                        if (isEditingComment) {
                                          const saveComment = async () => {
                                            const next = etatActuelCommentEdit?.value ?? '';
                                            const prev = item.value || '';
                                            if (String(next) === String(prev)) {
                                              setEtatActuelCommentEdit(null);
                                              return;
                                            }
                                            try {
                                              await updateFieldMutation.mutateAsync({
                                                field: commentField,
                                                value: next,
                                              });
                                              setEtatActuelCommentEdit(null);
                                            } catch {
                                              /* erreur déjà gérée par la mutation */
                                            }
                                          };
                                          return (
                                            <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '8px', width: '100%', maxWidth: '100%' }}>
                                              <textarea
                                                autoFocus
                                                className="etat-actuel-comment-edit-textarea"
                                                value={etatActuelCommentEdit.value ?? ''}
                                                disabled={updateFieldMutation.isLoading}
                                                onChange={(e) =>
                                                  setEtatActuelCommentEdit({
                                                    field: commentField,
                                                    value: e.target.value,
                                                  })
                                                }
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Escape') {
                                                    e.preventDefault();
                                                    setEtatActuelCommentEdit(null);
                                                  } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                                    e.preventDefault();
                                                    saveComment();
                                                  }
                                                }}
                                                rows={3}
                                                style={{
                                                  width: '100%',
                                                  boxSizing: 'border-box',
                                                  fontSize: '14px',
                                                  fontFamily: 'inherit',
                                                  padding: '8px 10px',
                                                  borderRadius: '6px',
                                                  border: '1px solid #9cbfc8',
                                                  background: '#fff',
                                                  resize: 'vertical',
                                                }}
                                              />
                                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                <button
                                                  type="button"
                                                  onClick={saveComment}
                                                  disabled={updateFieldMutation.isLoading}
                                                  title="Valider"
                                                  style={{
                                                    width: '28px',
                                                    height: '28px',
                                                    borderRadius: '50%',
                                                    border: '2px solid #15803d',
                                                    backgroundColor: '#dcfce7',
                                                    color: '#15803d',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: updateFieldMutation.isLoading ? 'wait' : 'pointer',
                                                    padding: 0,
                                                    fontSize: '13px',
                                                  }}
                                                >
                                                  <FaCheck />
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => setEtatActuelCommentEdit(null)}
                                                  disabled={updateFieldMutation.isLoading}
                                                  title="Annuler"
                                                  style={{
                                                    width: '28px',
                                                    height: '28px',
                                                    borderRadius: '50%',
                                                    border: '2px solid #b91c1c',
                                                    backgroundColor: '#fee2e2',
                                                    color: '#b91c1c',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: updateFieldMutation.isLoading ? 'wait' : 'pointer',
                                                    padding: 0,
                                                    fontSize: '13px',
                                                  }}
                                                >
                                                  <FaTimes />
                                                </button>
                                              </span>
                                            </span>
                                          );
                                        }

                                        return (
                                          <>
                                      <span
                                        className={`etat-detail-value${hi?.className ? ` ${hi.className}` : ''}`}
                                        style={{
                                          color: '#ffffff',
                                          fontWeight: 700,
                                          ...(hi?.style || {}),
                                          ...(canEditEtatActuelComment && isCommentItem
                                            ? { cursor: 'text', borderBottom: '1px dashed rgba(255,255,255,0.45)' }
                                            : {}),
                                        }}
                                        data-confirmer-hl={hi?.dataHl || undefined}
                                        title={
                                          canEditEtatActuelComment && isCommentItem
                                            ? 'Double-clic pour modifier le commentaire'
                                            : undefined
                                        }
                                        onDoubleClick={(e) => {
                                          if (!canEditEtatActuelComment || !isCommentItem) return;
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setEtatActuelCommentEdit({
                                            field: commentField,
                                            value: resolveEtatActuelCommentForEdit(commentField, item.value),
                                          });
                                        }}
                                      >
                                        {item.value || (canEditEtatActuelComment && isCommentItem ? '—' : '-')}
                                      </span>
                                      {item.label === 'Date RDV' && renderHeureRdvAvantBadge(heureRdvAvantDecalage)}
                                      {item.label === 'Date RDV' &&
                                        isEtatConfirmerLike(etatActuel.id_etat, etatActuel.etat_titre) &&
                                        renderDecalageDemandesUnderRdv(decalagesAffichables, decalageDemandesActions)}
                                      {item.label === 'Date RDV' &&
                                        (!isPartenaireProfil || partenaireCanFixerRdv) &&
                                        (() => {
                                          const sinceTs = etatActuel.date_creation
                                            ? new Date(etatActuel.date_creation).getTime()
                                            : null;
                                          const histoSinceConfirmer = dateRdvModifications.filter((mod) => {
                                            if (!sinceTs) return true;
                                            const d = mod.date_modif_time ? new Date(mod.date_modif_time).getTime() : null;
                                            return d != null && !Number.isNaN(d) && d >= sinceTs;
                                          });
                                          if (histoSinceConfirmer.length === 0) return null;
                                          return (
                                            <div style={{ marginTop: '10px', width: '100%' }}>
                                              <button
                                                type="button"
                                                onClick={() => setShowDateRdvHistory((prev) => !prev)}
                                                title="Voir l'historique des modifications de l'heure du RDV"
                                                style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: '6px',
                                                  background: 'transparent',
                                                  border: '1px solid #ffffff',
                                                  color: '#ffffff',
                                                  borderRadius: '4px',
                                                  padding: '4px 10px',
                                                  fontSize: '12px',
                                                  fontWeight: 700,
                                                  cursor: 'pointer'
                                                }}
                                              >
                                                <FaHistory />
                                                Historique des modifications ({histoSinceConfirmer.length})
                                              </button>
                                              {showDateRdvHistory && (
                                                <div
                                                  className="etat-actuel-rdv-heure-historique"
                                                  style={{
                                                    marginTop: '8px',
                                                    background: '#ffffff',
                                                    color: '#1f2937',
                                                    borderRadius: '6px',
                                                    padding: '8px',
                                                    border: '1px solid #d1d5db',
                                                    maxWidth: '720px'
                                                  }}
                                                >
                                                  <table
                                                    className="etat-actuel-rdv-heure-historique-table"
                                                    style={{
                                                      width: '100%',
                                                      borderCollapse: 'collapse',
                                                      fontSize: '12px',
                                                      color: '#1f2937'
                                                    }}
                                                  >
                                                    <thead>
                                                      <tr style={{ background: '#f3f4f6' }}>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db', color: '#111827' }}>Quand</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db', color: '#111827' }}>Qui</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db', color: '#111827' }}>Ancienne valeur</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db', color: '#111827' }}>Nouvelle valeur</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {histoSinceConfirmer
                                                        .slice()
                                                        .sort((a, b) => {
                                                          const ta = a.date_modif_time ? new Date(a.date_modif_time).getTime() : 0;
                                                          const tb = b.date_modif_time ? new Date(b.date_modif_time).getTime() : 0;
                                                          return tb - ta;
                                                        })
                                                        .map((mod) => (
                                                          <tr key={mod.id}>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap', color: '#1f2937' }}>
                                                              {mod.date_modif_time
                                                                ? new Date(mod.date_modif_time).toLocaleString('fr-FR')
                                                                : '-'}
                                                            </td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: '#1f2937' }}>
                                                              {mod.user_pseudo || '-'}
                                                            </td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: '#1f2937' }}>
                                                              {modificaValueDisplay(
                                                                mod.ancien_valeur,
                                                                mod.type || 'date_rdv_time',
                                                                mod.ancien_valeur_label
                                                              )}
                                                            </td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: '#1f2937' }}>
                                                              {modificaValueDisplay(
                                                                mod.nouvelle_valeur,
                                                                mod.type || 'date_rdv_time',
                                                                mod.nouvelle_valeur_label
                                                              )}
                                                            </td>
                                                          </tr>
                                                        ))}
                                                    </tbody>
                                                  </table>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })()}
                                          </>
                                        );
                                      })()}
                                      </>
                                    )}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {isEtatConfirmerLike(etatActuel.id_etat, etatActuel.etat_titre) && !isCommercial &&
                        renderQualiteConfirmationBackofficePanel()}

                      {isEtatConfirmerLike(etatActuel.id_etat, etatActuel.etat_titre) &&
                        (!isCommercial || getRdvSeulRawLabel(fiche) || absenceCoupleModifications.length > 0) && (
                        <div className="etat-actuel-rdv-seul-row">
                          {getRdvSeulRawLabel(fiche) && (
                            <span className="etat-actuel-rdv-seul-label">
                              RDV seul : {getRdvSeulRawLabel(fiche)}
                            </span>
                          )}
                          {!isCommercial && (!isPartenaireProfil || partenaireCanFixerRdv) && (
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button
                              type="button"
                              className={getRdvSeulRawLabel(fiche) ? 'etat-actuel-modifier-validation-btn' : 'etat-actuel-signaler-absence-btn'}
                              onClick={() => setRdvSeulDropdownOpen((prev) => !prev)}
                              disabled={updateFieldMutation.isLoading}
                              title={getRdvSeulRawLabel(fiche) ? 'Modifier la présence' : "Signaler l'absence du couple"}
                            >
                              <FaEdit />
                              <span>{getRdvSeulRawLabel(fiche) ? 'Modifier' : "Signaler l'absence du couple"}</span>
                              <FaChevronDown size={11} />
                            </button>
                            {rdvSeulDropdownOpen && (
                              <>
                                <div
                                  onClick={() => setRdvSeulDropdownOpen(false)}
                                  style={{ position: 'fixed', inset: 0, zIndex: 99998, background: 'transparent' }}
                                />
                                <div className="etat-actuel-rdv-seul-dropdown">
                                  {RDV_SEUL_PRESENCE_OPTIONS.map((opt) => (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      disabled={updateFieldMutation.isLoading}
                                      onClick={async () => {
                                        setRdvSeulDropdownOpen(false);
                                        if (String(fiche.conf_presence_couple || '').toUpperCase() === opt.value) return;
                                        await updateFieldMutation.mutateAsync({
                                          field: 'rdv_seul',
                                          value: opt.value,
                                        });
                                      }}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                          )}
                          {absenceCoupleModifications.length > 0 && (
                            <button
                              type="button"
                              className="etat-actuel-absence-couple-histo-btn"
                              onClick={() => setShowAbsenceCoupleHistory((prev) => !prev)}
                              title="Voir l'historique des signalements d'absence du couple"
                            >
                              <FaHistory />
                              <span>
                                Historique
                                {showAbsenceCoupleHistory
                                  ? ''
                                  : ` (${absenceCoupleModifications.length})`}
                              </span>
                            </button>
                          )}
                          {showAbsenceCoupleHistory && absenceCoupleModifications.length > 0 && (
                            <div className="etat-actuel-absence-couple-historique">
                              <table className="etat-actuel-absence-couple-historique-table">
                                <thead>
                                  <tr>
                                    <th>Quand</th>
                                    <th>Qui</th>
                                    <th>Ancienne valeur</th>
                                    <th>Nouvelle valeur</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {absenceCoupleModifications.map((mod) => (
                                    <tr key={mod.id || `${mod.date_modif_time}-${mod.nouvelle_valeur}`}>
                                      <td>
                                        {mod.date_modif_time
                                          ? new Date(mod.date_modif_time).toLocaleString('fr-FR')
                                          : '-'}
                                      </td>
                                      <td>{mod.user_pseudo || '-'}</td>
                                      <td>
                                        {modificaValueDisplay(
                                          mod.ancien_valeur,
                                          mod.type || mod.champ || 'rdv_seul',
                                          mod.ancien_valeur_label
                                        )}
                                      </td>
                                      <td>
                                        {modificaValueDisplay(
                                          mod.nouvelle_valeur,
                                          mod.type || mod.champ || 'rdv_seul',
                                          mod.nouvelle_valeur_label
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}

                      {(hasPermission('fiche_validate') || canCommercialValidateRdv) && Number(fiche.id_etat_final) === 7 && (
                        <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }}>
                          {fiche.valider > 0 && (
                            <div
                              className="etat-actuel-validation-badge"
                              style={{
                                padding: '8px 14px',
                                backgroundColor: '#4a4a4a',
                                border: '2px solid #16a34a',
                                borderRadius: '6px',
                                color: '#ffffff',
                                fontWeight: 700,
                                fontSize: '14px',
                                alignSelf: 'flex-start',
                              }}
                            >
                              {fiche.validation_date_time ? (
                                <>
                                  Le {formatRdvDateTime(fiche.validation_date_time)}
                                  {' '}Valider{fiche.validation_conf_rdv_avec ? ` avec ${String(fiche.validation_conf_rdv_avec).toUpperCase()}` : ''}
                                  {fiche.validateur_pseudo ? ` par ${String(fiche.validateur_pseudo).toUpperCase()}` : ''}
                                </>
                              ) : (
                                <>Validé</>
                              )}
                            </div>
                          )}

                          <div style={{ position: 'relative', display: 'inline-block', alignSelf: 'flex-start' }}>
                          {fiche.valider > 0 ? (
                            <button
                              ref={validationButtonRef}
                              type="button"
                              onClick={() => setValidationDropdownOpen((prev) => !prev)}
                              disabled={validateMutation.isLoading}
                              className="etat-actuel-modifier-validation-btn"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 14px',
                                background: '#ffffff',
                                color: '#000000',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontWeight: 700,
                                fontSize: '13px',
                                cursor: validateMutation.isLoading ? 'wait' : 'pointer',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                              }}
                              title="Modifier la validation"
                            >
                              <FaEdit style={{ color: '#000000' }} />
                              <span style={{ color: '#000000' }}>Modifier</span>
                              <FaChevronDown size={11} style={{ color: '#000000' }} />
                            </button>
                          ) : (
                            <button
                              ref={validationButtonRef}
                              type="button"
                              onClick={() => setValidationDropdownOpen((prev) => !prev)}
                              disabled={validateMutation.isLoading}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 18px',
                                background: '#16a34a',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                fontWeight: 700,
                                fontSize: '15px',
                                cursor: validateMutation.isLoading ? 'wait' : 'pointer',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                              }}
                              title="Validation RDV"
                            >
                              <FaCheck />
                              <span>Validation RDV</span>
                              <FaChevronDown size={12} />
                            </button>
                          )}

                          {validationDropdownOpen && validationDropdownPosition && createPortal(
                            <>
                              <div
                                onClick={() => setValidationDropdownOpen(false)}
                                style={{
                                  position: 'fixed',
                                  inset: 0,
                                  zIndex: 99999,
                                  background: 'transparent',
                                }}
                              />
                              <div
                                style={{
                                  position: 'fixed',
                                  top: validationDropdownPosition.top,
                                  left: validationDropdownPosition.left,
                                  background: '#ffffff',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '8px',
                                  width: Math.max(validationDropdownPosition.width || 0, 220),
                                  boxShadow: '0 6px 18px rgba(0,0,0,0.14)',
                                  zIndex: 100000,
                                  overflow: 'hidden',
                                }}
                              >
                                <button
                                  type="button"
                                  disabled={validateMutation.isLoading}
                                  onClick={() => {
                                    if (window.confirm('Valider le RDV avec MR ?')) {
                                      validateMutation.mutate({
                                        type_valid: '1-MR',
                                        conf_rdv_avec: 'MR',
                                        conf_presence_couple: null,
                                      });
                                      setValidationDropdownOpen(false);
                                    }
                                  }}
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '12px 16px',
                                    textAlign: 'left',
                                    background: 'transparent',
                                    border: 'none',
                                    borderBottom: '1px solid #e5e7eb',
                                    cursor: validateMutation.isLoading ? 'wait' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#111827',
                                  }}
                                >
                                  {isCommercial ? 'VALIDE avec MR' : 'VALIDÉ AVEC MR'}
                                </button>
                                <button
                                  type="button"
                                  disabled={validateMutation.isLoading}
                                  onClick={() => {
                                    if (window.confirm('Valider le RDV avec MME ?')) {
                                      validateMutation.mutate({
                                        type_valid: '1-MME',
                                        conf_rdv_avec: 'MME',
                                        conf_presence_couple: null,
                                      });
                                      setValidationDropdownOpen(false);
                                    }
                                  }}
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '12px 16px',
                                    textAlign: 'left',
                                    background: 'transparent',
                                    border: 'none',
                                    borderBottom: '1px solid #e5e7eb',
                                    cursor: validateMutation.isLoading ? 'wait' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#111827',
                                  }}
                                >
                                  {isCommercial ? 'VALIDE avec MME' : 'VALIDÉ AVEC MME'}
                                </button>
                                <button
                                  type="button"
                                  disabled={validateMutation.isLoading}
                                  onClick={() => {
                                    const message = isCommercial
                                      ? 'Marquer cette fiche comme NRP (non validé) ?'
                                      : (fiche.valider > 0
                                        ? 'Annuler la validation de cette fiche ?'
                                        : 'Marquer cette fiche comme NON VALIDÉ ?');
                                    if (window.confirm(message)) {
                                      validateMutation.mutate({
                                        type_valid: '0',
                                        conf_rdv_avec: null,
                                        conf_presence_couple: null,
                                      });
                                      setValidationDropdownOpen(false);
                                    }
                                  }}
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '12px 16px',
                                    textAlign: 'left',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: validateMutation.isLoading ? 'wait' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#111827',
                                  }}
                                >
                                  {isCommercial ? 'NRP (non validé)' : 'NON VALIDÉ'}
                                </button>
                              </div>
                            </>,
                            document.body
                          )}
                          </div>
                        </div>
                      )}

                      {/* Cadre R2 / R1 — uniquement si is_r2 = 1 (masqué si 0 / NON / null) */}
                      {(() => {
                        const canSeeR2R1Box = [1, 5, 7, 11, 13, 14].includes(Number(user?.fonction));
                        const isConfirmerActuel = isEtatConfirmerLike(etatActuel.id_etat, etatActuel.etat_titre);
                        // Strict : n'afficher que lorsque le flag fiche est explicitement OUI (1)
                        const isR2Oui = normalizeIsR2Field(fiche?.is_r2) === 1;
                        if (!canSeeR2R1Box || !isConfirmerActuel || !isR2Oui) return null;

                        const hasColor =
                          (etats || []).find((e) => Number(e.id) === 9)?.color || '#f7a219';
                        const comR1 = (commerciaux || []).find(
                          (c) => Number(c.id) === Number(fiche.id_commercial_r1)
                        );
                        const comR1Pseudo = comR1?.pseudo
                          ? formatAgentPseudoDisplay(comR1.pseudo)
                          : '';
                        const comR1Tel = comR1?.tel != null ? String(comR1.tel).trim() : '';
                        const telHref = comR1Tel.replace(/[^\d+]/g, '');
                        const dateR1Label = fiche.date_r1 ? formatRdvDateTime(fiche.date_r1) : '';
                        const commentaireR1 =
                          fiche.commentaire_r1 != null ? String(fiche.commentaire_r1).trim() : '';

                        return (
                          <div
                            className="etat-actuel-r2-r1-box"
                            style={{
                              ['--r2-r1-border']: hasColor,
                              marginTop: '16px',
                              padding: '14px 16px',
                              border: `3px solid ${hasColor}`,
                              borderRadius: '8px',
                              backgroundColor: 'rgba(247, 162, 25, 0.12)',
                            }}
                          >
                            <div className="etat-actuel-r2-r1-box__title">R2 — Infos R1</div>
                            <div className="etat-actuel-r2-r1-box__grid">
                              <div>
                                <span className="etat-actuel-r2-r1-box__label">Commercial R1 :</span>{' '}
                                <strong>{comR1Pseudo || '—'}</strong>
                              </div>
                              <div>
                                <span className="etat-actuel-r2-r1-box__label">Date R1 :</span>{' '}
                                <strong>{dateR1Label || '—'}</strong>
                              </div>
                              <div>
                                <span className="etat-actuel-r2-r1-box__label">Commentaire R1 :</span>{' '}
                                <strong className="etat-actuel-r2-r1-box__comment">
                                  {commentaireR1 || '—'}
                                </strong>
                              </div>
                              <div>
                                <span className="etat-actuel-r2-r1-box__label">Contact commercial R1 :</span>{' '}
                                {comR1Tel && telHref ? (
                                  <a
                                    href={`tel:${telHref}`}
                                    className="etat-actuel-r2-r1-tel"
                                    title={`Appeler ${comR1Tel}`}
                                  >
                                    {comR1Tel}
                                  </a>
                                ) : (
                                  <strong>—</strong>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Contrôle Qualité — affiché uniquement si l'état actuel est SIGNER (13).
                          Si l'état actuel n'est pas 13, le formulaire est rendu dans l'entrée d'historique état 13 (voir plus bas). */}
                      {!isCommercial && isEtatSigner13(fiche.id_etat_final) && renderControleQualiteForm({ embedded: true })}
                    </div>
                    </div>
                      )}
                    </>
                  )}

                  

                </>
              );
            })()}
          </div>
        )}

        {/* Formulaire de décalage de RDV (session commercial) — sous État actuel */} 
        {hasPermission('decalage_create') && 
         user.fonction === 5 && 
         user.fonction !== 6 && 
         user.fonction !== 3 && 
         ficheData && !([13, 16, 38, 45, 44].includes(ficheData.id_etat_final)) &&
         ficheData.date_rdv_time && (
          <div className="fiche-section decalage-form" style={{ marginTop: '24px' }}>
            <h2 className="section-title" style={{ 
              background: '#9cbfc8', 
              color: '#fff', 
              padding: '10px', 
              textAlign: 'center',
              marginBottom: '0',
              fontSize: '13.6px',
              fontWeight: 'bold'
            }}>
              Demande de décalage
            </h2>

            {decalageRdvHistorique.length > 0 && (
              <div className="decalage-rdv-historique" style={{
                border: '1px solid #e0e0e0',
                borderTop: 'none',
                padding: '15px',
                background: '#fff',
                marginBottom: '0',
              }}>
                <h3 style={{ marginTop: 0, marginBottom: '10px', fontSize: '12px', fontWeight: 'bold', color: '#000000' }}>
                  Historique des heures RDV avant décalage
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table className="decalage-rdv-historique-table" style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '11px',
                    color: '#000000',
                  }}>
                    <thead>
                      <tr style={{ background: '#f3f4f6', color: '#000000' }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db', color: '#000000' }}>Date demande</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db', color: '#000000' }}>Heure RDV avant</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db', color: '#000000' }}>Nouvelle heure</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db', color: '#000000' }}>État</th>
                      </tr>
                    </thead>
                    <tbody>
                      {decalageRdvHistorique.map((decalage) => (
                        <tr key={decalage.id} style={{ color: '#000000' }}>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: '#000000' }}>
                            {decalage.date_creation
                              ? new Date(decalage.date_creation).toLocaleString('fr-FR')
                              : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', fontWeight: 600, color: '#000000' }}>
                            {formatRdvDateTime(decalage.date_prevu)}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: '#000000' }}>
                            {decalage.date_nouvelle
                              ? formatRdvDateTime(decalage.date_nouvelle)
                              : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: '#000000' }}>
                            {decalage.etat_dec || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {commercialPendingDecalage && (
              <div className="decalage-existing-list" style={{ 
                border: '1px solid #e0e0e0', 
                borderTop: decalageRdvHistorique.length > 0 ? '1px solid #e0e0e0' : 'none', 
                padding: '15px',
                background: '#fff8e1',
                marginBottom: '0'
              }}>
                <h3 style={{ marginTop: '0', marginBottom: '10px', fontSize: '11.9px', fontWeight: 'bold' }}>
                  Demande en attente
                </h3>
                <div className="decalage-existing-item" style={{ 
                  background: '#fff', 
                  padding: '10px', 
                  borderRadius: '4px',
                  border: '1px solid #f59e0b'
                }}>
                  <div style={{ fontSize: '10.2px', color: '#666' }}>
                    <strong>Créée le :</strong>{' '}
                    {commercialPendingDecalage.date_creation
                      ? new Date(commercialPendingDecalage.date_creation).toLocaleString('fr-FR')
                      : 'N/A'}
                  </div>
                  <div style={{ fontSize: '10.2px', marginTop: '5px' }}>
                    <strong>Heure RDV avant décalage :</strong>{' '}
                    {commercialPendingDecalage.date_prevu
                      ? formatRdvDateTime(commercialPendingDecalage.date_prevu)
                      : 'N/A'}
                  </div>
                  <div style={{ fontSize: '10.2px', marginTop: '5px' }}>
                    <strong>Nouvelle date :</strong>{' '}
                    {commercialPendingDecalage.date_nouvelle
                      ? formatRdvDateTime(commercialPendingDecalage.date_nouvelle)
                      : 'N/A'}
                  </div>
                  {commercialPendingDecalage.message && (
                    <div style={{ fontSize: '10.2px', marginTop: '5px', fontStyle: 'italic', color: '#555' }}>
                      &quot;{commercialPendingDecalage.message}&quot;
                    </div>
                  )}
                  {commercialPendingDecalage.etat_dec && (
                    <div style={{ fontSize: '10.2px', marginTop: '5px' }}>
                      <strong>État :</strong> {commercialPendingDecalage.etat_dec}
                    </div>
                  )}
                  {canAnnulerDecalageEnAttente(commercialPendingDecalage) && (
                    <div style={{ marginTop: '8px' }}>
                      <button
                        type="button"
                        className="btn-secondary decalage-annuler-demande-btn"
                        onClick={() => {
                          if (!window.confirm('Supprimer définitivement cette demande de décalage ?')) return;
                          cancelDecalageMutation.mutate(commercialPendingDecalage.id);
                        }}
                        disabled={cancelDecalageMutation.isLoading}
                      >
                        {cancelDecalageMutation.isLoading ? 'Annulation…' : 'Annuler la demande'}
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: '10px', fontSize: '10.2px', color: '#92400e', fontStyle: 'italic' }}>
                  Une seule demande de décalage est autorisée à la fois. Attendez la réponse du confirmateur ou annulez la demande.
                </div>
              </div>
            )}
            
            {commercialAlreadyCreatedDecalage && !hasCommercialPendingDecalage && (
              <div
                className="decalage-already-created-notice"
                style={{
                  border: '1px solid #e0e0e0',
                  borderTop: decalageRdvHistorique.length > 0 ? '1px solid #e0e0e0' : 'none',
                  padding: '15px',
                  background: '#f9fafb',
                  textAlign: 'center',
                }}
              >
                <p className="decalage-already-created-notice-text">
                  Une demande de décalage a déjà été créée pour cette fiche.
                </p>
                <button
                  type="button"
                  className="btn-confirm"
                  disabled
                  title="Une demande de décalage a déjà été créée pour cette fiche."
                  style={{
                    display: 'inline-block',
                    borderRadius: '7px',
                    fontWeight: 'bold',
                    padding: '10px 20px',
                    opacity: 0.55,
                    cursor: 'not-allowed',
                  }}
                >
                  Demande de décalage
                </button>
              </div>
            )}

            {commercialCanCreateDecalage && (
            <div className="decalage-new-form" style={{ 
              border: '1px solid #e0e0e0', 
              borderTop: (decalageRdvHistorique.length > 0 || commercialPendingDecalage || (commercialAlreadyCreatedDecalage && !hasCommercialPendingDecalage)) ? '1px solid #e0e0e0' : 'none', 
              padding: '15px',
              background: '#fff'
            }}>
              <div className="form-group">
                <label htmlFor="select_minutes_commercial_after_state">Décalage de :</label>
                <select
                  id="select_minutes_commercial_after_state"
                  className="form-control"
                  value={decalageFormData.select_minutes}
                  onChange={(e) => {
                    const minutes = parseInt(e.target.value);
                    const dateRdvOriginale = ficheData?.date_rdv_time || decalageFormData.date_prevu || '';
                    if (minutes > 0 && dateRdvOriginale) {
                      try {
                        const formattedNewDate = addMinutesToDateTimeString(dateRdvOriginale, minutes);
                        if (!formattedNewDate) {
                          alert('Erreur : la date de rendez-vous originale est invalide.');
                          return;
                        }
                        setDecalageFormData(prev => ({
                          ...prev,
                          select_minutes: e.target.value,
                          nouvelle_date: formattedNewDate,
                          date_prevu: dateRdvOriginale
                        }));
                      } catch (error) {
                        alert('Erreur lors du calcul de la nouvelle date. Veuillez réessayer.');
                      }
                    } else {
                      setDecalageFormData(prev => ({
                        ...prev,
                        select_minutes: e.target.value,
                        nouvelle_date: '',
                        date_prevu: dateRdvOriginale
                      }));
                    }
                  }}
                >
                  <option value="0">SÉLECTIONNER</option>
                  <option value="10">10 MIN</option>
                  <option value="15">15 MIN</option>
                  <option value="20">20 MIN</option>
                  <option value="25">25 MIN</option>
                  <option value="30">30 MIN</option>
                  <option value="35">35 MIN</option>
                  <option value="40">40 MIN</option>
                  <option value="45">45 MIN</option>
                  <option value="50">50 MIN</option>
                  <option value="55">55 MIN</option>
                  <option value="60">1 HEURE</option>
                  <option value="75">1H15</option>
                  <option value="90">1H30</option>
                  <option value="105">1H45</option>
                  <option value="120">2 HEURES</option>
                </select>
              </div>

              {decalageFormData.nouvelle_date && (
                <div className="form-group decalage-date-preview" style={{ 
                  background: '#e8f5e9', 
                  padding: '10px', 
                  borderRadius: '4px',
                  marginBottom: '15px',
                  border: '2px solid #4caf50'
                }}>
                  <strong>📅 Nouvelle date/heure :</strong> 
                  <span style={{ display: 'block', marginTop: '5px', fontSize: '13.6px', fontWeight: 'bold', color: '#2e7d32' }}>
                    {formatRdvDateTime(decalageFormData.nouvelle_date)}
                  </span>
                  {ficheData?.date_rdv_time && (
                    <div className="decalage-date-originale" style={{ marginTop: '8px', fontSize: '10.2px', color: '#666', fontStyle: 'italic' }}>
                      Date originale : {formatRdvDateTime(ficheData.date_rdv_time)}
                    </div>
                  )}
                </div>
              )}

              {user.fonction === 5 && (
                <div className="form-group">
                  <label htmlFor="id_confirmateur_dec_commercial_after_state">Confirmateur :</label>
                  {ficheData?.id_confirmateur ? (
                    <div className="decalage-readonly-value" style={{ background: '#f0f0f0', padding: '10px', borderRadius: '4px', fontWeight: 'bold' }}>
                      {confirmateurs?.find(c => c.id === ficheData.id_confirmateur)?.pseudo || `ID: ${ficheData.id_confirmateur}`}
                    </div>
                  ) : (
                    <div className="decalage-warning-text" style={{ color: 'red', fontStyle: 'italic' }}>
                      Aucun confirmateur assigné à cette fiche. Veuillez assigner un confirmateur avant de créer un décalage.
                    </div>
                  )}
                </div>
              )}

              {([1, 2, 7].includes(user.fonction)) && (
                <div className="form-group">
                  <label htmlFor="id_confirmateur_dec_commercial_after_state_select">Confirmateur :</label>
                  <select
                    id="id_confirmateur_dec_commercial_after_state_select"
                    className="form-control"
                    value={decalageFormData.id_confirmateur || (fiche?.id_confirmateur ? String(fiche.id_confirmateur) : '')}
                    onChange={(e) => {
                      setDecalageFormData({...decalageFormData, id_confirmateur: e.target.value});
                    }}
                    required
                  >
                    <option value="">SÉLECTIONNER UN CONFIRMATEUR</option>
                    {confirmateurs?.map(conf => (
                      <option key={conf.id} value={conf.id}>
                        {conf.pseudo}
                      </option>
                    ))}
                  </select>
                  {!decalageFormData.id_confirmateur && !fiche?.id_confirmateur && (
                    <small style={{ color: '#666', fontStyle: 'italic' }}>
                      Sélectionnez un confirmateur depuis la liste
                    </small>
                  )}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="message_dec_commercial_after_state">Message du décalage :</label>
                <textarea
                  id="message_dec_commercial_after_state"
                  className="form-control"
                  rows="4"
                  value={decalageFormData.message}
                  onChange={(e) => {
                    setDecalageFormData({...decalageFormData, message: e.target.value});
                  }}
                  placeholder="Saisissez le message expliquant le décalage..."
                />
              </div>

              <div className="form-actions" style={{ textAlign: 'center', marginTop: '15px' }}>
                <button
                  className="btn-confirm"
                  onClick={handleDecalageSubmit}
                  disabled={decalageMutation.isLoading}
                  style={{
                    display: 'table',
                    width: 'max-content',
                    margin: '0 auto',
                    borderRadius: '7px',
                    fontWeight: 'bold',
                    padding: '10px 20px'
                  }}
                >
                  {decalageMutation.isLoading ? 'Envoi...' : 'Demande de décalage'}
                </button>
              </div>
            </div>
            )}
          </div>
        )}

        {/* Section Changement d'état - En bas de la page */}
        {/* Permissions : 
            - Admins (1, 2, 7) et Agents (3) : peuvent changer vers tous les états
            - Commerciaux (5) : peuvent changer uniquement vers les états de Phase 3 (groupe = 3) ou CONFIRMER (état 7)
            - Confirmateurs (6) : peuvent changer l'état des fiches qui leur sont assignées
        */}
        {/* Section Compte rendu : visible uniquement en session commercial propriétaire */}
        {(user?.fonction === 5 && (Number(ficheData?.id_commercial) === Number(user?.id) || Number(ficheData?.id_commercial_2) === Number(user?.id))) && (
          <>
            {/* Afficher les comptes rendu en attente uniquement (masquer après approbation/rejet) */}
            {ficheData?.comptes_rendus && ficheData.comptes_rendus.some((cr) => cr.statut === 'pending') && (
              <div className="fiche-section compte-rendu-section">
                <h2 className="section-title">Comptes rendu en attente</h2>
                {ficheData.comptes_rendus.filter((cr) => cr.statut === 'pending').map((cr) => {
                  const crOptionKey = resolveCompteRenduOptionFromCr(cr);
                  const crCommercialLabel =
                    getCompteRenduOptionLabel(crOptionKey, cr.id_etat_final) ||
                    cr.etat_titre ||
                    'N/A';

                  return (
                    <div key={cr.id} className="compte-rendu-item" style={{ 
                      marginBottom: '20px', 
                      padding: '15px', 
                      border: '1px solid #ddd', 
                      borderRadius: '5px',
                      backgroundColor: cr.statut === 'pending' ? '#fff3cd' : cr.statut === 'approved' ? '#d4edda' : '#f8d7da'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div>
                          <strong>État :</strong> {crCommercialLabel} | 
                          <strong> Statut :</strong> {cr.statut === 'pending' ? 'En attente' : cr.statut === 'approved' ? 'Approuvé' : 'Rejeté'} |
                          <strong> Date :</strong> {cr.date_creation ? new Date(cr.date_creation).toLocaleString('fr-FR') : 'N/A'}
                        </div>
                        {cr.statut === 'pending' && (
                          <button 
                            className="btn-edit" 
                            onClick={() => {
                              setEditingCompteRendu(cr.id);
                              const optionKey = resolveCompteRenduOptionFromCr(cr);
                              setCompteRenduOption(optionKey);
                              setSelectedEtat(cr.id_etat_final);
                              if ([13, 44, 45].includes(cr.id_etat_final)) {
                                const crMods = cr.modifications || {};
                                const pickCrValue = (modKey, topKey = modKey, fallback = '') => {
                                  const modVal = crMods?.[modKey];
                                  if (modVal !== undefined && modVal !== null && String(modVal) !== '') return modVal;
                                  const topVal = cr?.[topKey];
                                  if (topVal !== undefined && topVal !== null && String(topVal) !== '') return topVal;
                                  return fallback;
                                };
                                const fallbackSignDateTime = splitDateTimeForInput(ficheData?.date_rdv_time);
                                const rawDateSign = pickCrValue('date_sign_time', 'date_sign_time', '');
                                const { date: dateSignDate, time: dateSignTime } = splitDateTimeForInput(rawDateSign, fallbackSignDateTime);
                                setEtatFormData({
                                  date_sign_date: dateSignDate,
                                  date_sign_time: dateSignTime,
                                  produit: String(pickCrValue('produit', 'produit', ficheData?.produit ? String(ficheData.produit) : '')),
                                  id_sous_etat: cr.id_sous_etat ? String(cr.id_sous_etat) : '',
                                  id_commercial: String(pickCrValue('id_commercial', 'id_commercial', ficheData?.id_commercial ? String(ficheData.id_commercial) : '')),
                                  id_commercial_2: String(pickCrValue('id_commercial_2', 'id_commercial_2', '')),
                                  pseudo: String(pickCrValue('pseudo', 'pseudo', '')),
                                  ph3_pac: pickCrValue('ph3_pac', 'ph3_pac', 'reau'),
                                  ph3_rr_model: pickCrValue('ph3_rr_model', 'ph3_rr_model', ''),
                                  ph3_puissance: pickCrValue('ph3_puissance', 'ph3_puissance', ''),
                                  ph3_ballon: pickCrValue('ph3_ballon', 'ph3_ballon', ''),
                                  ph3_marque_ballon: pickCrValue('ph3_marque_ballon', 'ph3_marque_ballon', ''),
                                  ph3_alimentation: pickCrValue('ph3_alimentation', 'ph3_alimentation', ''),
                                  ph3_type: pickCrValue('ph3_type', 'ph3_type', ''),
                                  ph3_prix: pickCrValue('ph3_prix', 'ph3_prix', ''),
                                  ph3_installateur: cr.ph3_installateur ? String(cr.ph3_installateur) : '',
                                  conf_consommations: pickCrValue('conf_consommations', 'conf_consommations', ''),
                                  ph3_bonus_30: pickCrValue('ph3_bonus_30', 'ph3_bonus_30', ''),
                                  valeur_mensualite: pickCrValue('valeur_mensualite', 'valeur_mensualite', ''),
                                  ph3_mensualite: pickCrValue('ph3_mensualite', 'ph3_mensualite', ''),
                                  ph3_attente: pickCrValue('ph3_attente', 'ph3_attente', ''),
                                  nbr_annee_finance: pickCrValue('nbr_annee_finance', 'nbr_annee_finance', ''),
                                  credit_immobilier: pickCrValue('credit_immobilier', 'credit_immobilier', ''),
                                  credit_autre: pickCrValue('credit_autre', 'credit_autre', ''),
                                  conf_commentaire_produit: cr.commentaire || ''
                                });
                              } else if (cr.id_etat_final === 9) {
                                setEtatFormData({...etatFormData, conf_commentaire_produit: cr.commentaire || '', motif_qualif: ''});
                              } else if (cr.id_etat_final === 12) {
                                setEtatFormData({
                                  ...etatFormData,
                                  conf_commentaire_produit: cr.commentaire || '',
                                  motif_qualif: cr.commentaire || '',
                                });
                              } else if (cr.id_etat_final === 34) {
                                setEtatFormData({
                                  ...etatFormData,
                                  conf_commentaire_produit: cr.commentaire || '',
                                  motif_qualif: cr.commentaire || '',
                                  conf_rdv_avec: cr.modifications?.conf_rdv_avec || etatFormData.conf_rdv_avec || '',
                                });
                              } else if (cr.id_etat_final === 35) {
                                setEtatFormData({...etatFormData, conf_commentaire_produit: cr.commentaire || '', motif_qualif: ''});
                              } else if (cr.id_etat_final === 8) {
                                // Extraire date et heure si disponibles
                                let dateRdv = '';
                                let timeRdv = '';
                                if (cr.modifications?.conf_rdv_date) {
                                  dateRdv = cr.modifications.conf_rdv_date;
                                  timeRdv = cr.modifications.conf_rdv_time || '';
                                }
                                setEtatFormData({
                                  ...etatFormData,
                                  conf_rdv_date: dateRdv,
                                  conf_rdv_time: timeRdv,
                                  id_sous_etat: cr.id_sous_etat ? String(cr.id_sous_etat) : '',
                                  conf_rdv_avec: cr.modifications?.conf_rdv_avec || '',
                                  conf_commentaire_produit: cr.commentaire || ''
                                });
                              }
                            }}
                            style={{ padding: '5px 10px', fontSize: '0.9em' }}
                          >
                            Modifier
                          </button>
                        )}
                      </div>
                      {/* Afficher uniquement le compte rendu (commentaire) */}
                      {cr.commentaire && (
                        <div style={{ marginBottom: '10px' }}>
                          <strong>Compte rendu :</strong> {cr.commentaire}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Section pour créer un nouveau compte rendu (masquée si on édite un compte rendu ou s'il y a déjà un compte rendu en attente) */}
            {!editingCompteRendu && !(ficheData?.comptes_rendus && ficheData.comptes_rendus.some(cr => cr.statut === 'pending')) && (
              <div className="fiche-section compte-rendu-section">
                <h2 className="section-title">Compte rendu</h2>
                {requiresEarlyCrGate && !earlyCrVerified ? (
                  <CompteRenduEarlyVerification
                    key={`early-cr-gate-${ficheData?.id || hash}`}
                    rdvDateTime={ficheData?.date_rdv_time}
                    onVerified={() => setEarlyCrVerified(true)}
                  />
                ) : (
                  <div className="compte-rendu-form">
                    <div className="form-group">
                      <label htmlFor="compte_rendu_option">Type de compte rendu :</label>
                      <select
                        id="compte_rendu_option"
                        className="form-control"
                        value={resolveOptionKey(compteRenduOption)}
                        onChange={(e) => handleCompteRenduOptionChange(e.target.value)}
                      >
                        <option value="">Sélectionner une option</option>
                        {COMPTE_RENDU_COMMERCIAL_OPTIONS.map((opt) => (
                          <option key={opt.key} value={opt.key}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Section pour modifier un compte rendu - Afficher la liste déroulante */}
            {editingCompteRendu && (() => {
              const crToEdit = ficheData?.comptes_rendus?.find(cr => cr.id === editingCompteRendu);
              if (!crToEdit || crToEdit.statut !== 'pending') return null;

              if (requiresEarlyCrGate && !earlyCrVerified) {
                return (
                  <div className="fiche-section compte-rendu-section">
                    <h2 className="section-title">Modifier le compte rendu</h2>
                    <CompteRenduEarlyVerification
                      key={`early-cr-gate-edit-${ficheData?.id || hash}`}
                      rdvDateTime={ficheData?.date_rdv_time}
                      onVerified={() => setEarlyCrVerified(true)}
                    />
                  </div>
                );
              }
              
              return (
                <div className="fiche-section compte-rendu-section">
                  <h2 className="section-title">Modifier le compte rendu</h2>
                  <div className="compte-rendu-form">
                    <div className="form-group">
                      <label htmlFor="compte_rendu_option_edit">Type de compte rendu :</label>
                      <select
                        id="compte_rendu_option_edit"
                        className="form-control"
                        value={resolveOptionKey(compteRenduOption)}
                        onChange={(e) => handleCompteRenduOptionChange(e.target.value)}
                      >
                        <option value="">Sélectionner une option</option>
                        {COMPTE_RENDU_COMMERCIAL_OPTIONS.map((opt) => (
                          <option key={opt.key} value={opt.key}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ marginTop: '10px', textAlign: 'right' }}>
                      <button 
                        className="btn-cancel" 
                        onClick={() => {
                          setEditingCompteRendu(null);
                          setCompteRenduOption('');
                          setSelectedEtat(null);
                          setEtatFormData({
                            date_sign_date: '', date_sign_time: '', produit: '', id_sous_etat: '', id_commercial: '', 
                            id_commercial_2: '', pseudo: '', ph3_pac: 'reau', ph3_rr_model: '', ph3_puissance: '', 
                            ph3_ballon: '', ph3_marque_ballon: '', ph3_alimentation: '', ph3_type: '', ph3_prix: '', 
                            ph3_installateur: '', conf_consommations: '', ph3_bonus_30: '', valeur_mensualite: '', 
                            ph3_mensualite: '', ph3_attente: '', nbr_annee_finance: '', credit_immobilier: '', 
                            credit_autre: '', conf_commentaire_produit: '', conf_rdv_date: '', conf_rdv_time: '', conf_rdv_avec: '',
                          });
                        }}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Formulaire de compte rendu UNIFIÉ pour les commerciaux.
                - Pseudo et "Compte rendu" sont toujours visibles dès qu'une option est choisie.
                - Les autres champs (signature, sous-état, dates...) apparaissent selon l'option. */}
            {[8, 9, 12, 13, 23, 34, 35, 44, 45].includes(selectedEtat) && canAccessCompteRenduChoices && (editingCompteRendu || !(ficheData?.comptes_rendus && ficheData.comptes_rendus.some(cr => cr.statut === 'pending'))) && (
              <div className="fiche-section etat-change-section" style={{ marginTop: '20px' }}>
                <div className={`etat-form${[13, 44, 45].includes(selectedEtat) ? ' compte-rendu-signer-form' : ''}`}>

                  {/* Pseudo (toujours visible) */}
                  <div className="form-group">
                    <label htmlFor="compte_rendu_etat_pseudo">Pseudo :</label>
                    <input
                      type="text"
                      id="compte_rendu_etat_pseudo"
                      className="form-control"
                      value={etatFormData.pseudo}
                      onChange={(e) => setEtatFormData({ ...etatFormData, pseudo: e.target.value })}
                      required
                    />
                  </div>

                  {/* Champs spécifiques SIGNER (états 13, 44, 45) */}
                  {[13, 44, 45].includes(selectedEtat) && (
                    <>
                  <div className="form-group">
                    <label htmlFor="compte_rendu_etat_produit_signer">Signature pour :</label>
                    <select
                      id="compte_rendu_etat_produit_signer"
                      className="form-control"
                      value={etatFormData.produit}
                      onChange={(e) => setEtatFormData({...etatFormData, produit: e.target.value})}
                      required
                    >
                      <option value="">Sélectionner</option>
                      {produits?.map(prod => (
                        <option key={prod.id} value={prod.id}>
                          {prod.nom}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sous État SIGNER : on n'affiche QUE les sous-états COMPLETE et IMCOMPLETE
                      (les autres valeurs présentes en BDD sont ignorées ici). */}
                  {(() => {
                    const sousEtatsSigner = (sousEtats || []).filter((se) => {
                      const titre = (se?.titre || '').toString().trim().toUpperCase();
                      return titre === 'COMPLETE' || titre === 'IMCOMPLETE' || titre === 'INCOMPLETE';
                    });
                    if (sousEtatsSigner.length === 0) return null;
                    return (
                      <div className="form-group">
                        <label htmlFor="compte_rendu_etat_id_sous_etat_signer">Sous État :</label>
                        <select
                          id="compte_rendu_etat_id_sous_etat_signer"
                          className="form-control"
                          value={etatFormData.id_sous_etat}
                          onChange={(e) => setEtatFormData({ ...etatFormData, id_sous_etat: e.target.value })}
                          required
                        >
                          <option value="">Sélectionner</option>
                          {sousEtatsSigner.map((setat) => (
                            <option key={setat.id} value={setat.id}>
                              {setat.titre}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}

                  {([1, 2, 7].includes(user?.fonction)) && (
                    <div className="form-group">
                      <label htmlFor="compte_rendu_etat_id_commercial_signer">Commercial :</label>
                      <select
                        id="compte_rendu_etat_id_commercial_signer"
                        className="form-control"
                        value={etatFormData.id_commercial}
                        onChange={(e) => setEtatFormData({...etatFormData, id_commercial: e.target.value})}
                        required
                      >
                        <option value="">Sélectionner</option>
                        {commerciaux?.map(com => (
                          <option key={com.id} value={com.id}>
                            {formatCommercialDisplayName(com)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {([1, 2, 7].includes(user?.fonction)) && (
                    <div className="form-group">
                      <label htmlFor="compte_rendu_etat_id_commercial_2_signer">Commercial 2 (optionnel) :</label>
                      <select
                        id="compte_rendu_etat_id_commercial_2_signer"
                        className="form-control"
                        value={etatFormData.id_commercial_2}
                        onChange={(e) => setEtatFormData({...etatFormData, id_commercial_2: e.target.value})}
                      >
                        <option value="">Aucun</option>
                        {commerciaux?.map(com => (
                          <option key={com.id} value={com.id}>
                            {formatCommercialDisplayName(com)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {resolveSignerProduitKind(etatFormData.produit, produits) === 'pac' && (
                    <SignerPacSelect
                      idPrefix="compte_rendu_etat"
                      value={etatFormData.ph3_pac}
                      onChange={(e) => setEtatFormData({ ...etatFormData, ph3_pac: e.target.value })}
                      required
                    />
                  )}

                  <div className="form-group">
                    <label htmlFor="compte_rendu_etat_ph3_attente_signer">Financement :</label>
                    <select
                      id="compte_rendu_etat_ph3_attente_signer"
                      className="form-control"
                      value={etatFormData.ph3_attente}
                      onChange={(e) => setEtatFormData({...etatFormData, ph3_attente: e.target.value})}
                      required
                    >
                      <option value="">Sélectionner</option>
                      {typesFinancement.filter(t => t.etat !== 0).map(t => (
                        <option key={t.id} value={t.nom}>{t.nom}</option>
                      ))}
                    </select>
                  </div>

                  <SignerProduitPacPvFields
                    idPrefix="compte_rendu_etat"
                    kind={resolveSignerProduitKind(etatFormData.produit, produits)}
                    etatFormData={etatFormData}
                    setEtatFormData={setEtatFormData}
                    required
                  />

                  <div className="form-group">
                    <label htmlFor="compte_rendu_etat_ph3_prix_signer">Prix En € :</label>
                    <input
                      type="number"
                      id="compte_rendu_etat_ph3_prix_signer"
                      className="form-control"
                      value={etatFormData.ph3_prix}
                      onChange={(e) => setEtatFormData({...etatFormData, ph3_prix: e.target.value})}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="compte_rendu_etat_credit_immobilier_signer">Crédit immobilier :</label>
                    <input
                      type="number"
                      id="compte_rendu_etat_credit_immobilier_signer"
                      className="form-control"
                      value={etatFormData.credit_immobilier}
                      onChange={(e) => setEtatFormData({...etatFormData, credit_immobilier: e.target.value})}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="compte_rendu_etat_credit_autre_signer">Autre crédit :</label>
                    <input
                      type="number"
                      id="compte_rendu_etat_credit_autre_signer"
                      className="form-control"
                      value={etatFormData.credit_autre}
                      onChange={(e) => setEtatFormData({...etatFormData, credit_autre: e.target.value})}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="compte_rendu_etat_ph3_installateur_signer">Installateur :</label>
                    <select
                      id="compte_rendu_etat_ph3_installateur_signer"
                      className="form-control"
                      value={etatFormData.ph3_installateur}
                      onChange={(e) => setEtatFormData({...etatFormData, ph3_installateur: e.target.value})}
                      required
                    >
                      <option value="">Sélectionner</option>
                      {installateurs?.map(inst => (
                        <option key={inst.id} value={inst.id}>
                          {inst.nom}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="compte_rendu_etat_conf_consommations_signer">
                      Consommation annuelle
                      <br />
                      ancien système :
                    </label>
                    <input
                      type="number"
                      id="compte_rendu_etat_conf_consommations_signer"
                      className="form-control"
                      value={etatFormData.conf_consommations}
                      onChange={(e) => setEtatFormData({...etatFormData, conf_consommations: e.target.value})}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="compte_rendu_etat_valeur_mensualite_signer">Partie à financer du client :</label>
                    <input
                      type="number"
                      id="compte_rendu_etat_valeur_mensualite_signer"
                      className="form-control"
                      value={etatFormData.valeur_mensualite}
                      onChange={(e) => setEtatFormData({...etatFormData, valeur_mensualite: e.target.value})}
                      required
                    />
                  </div>

                  <SignerBonusAnnonceSelect
                    idPrefix="compte_rendu_etat"
                    kind={resolveSignerProduitKind(etatFormData.produit, produits)}
                    value={etatFormData.ph3_bonus_30}
                    onChange={(e) => setEtatFormData({ ...etatFormData, ph3_bonus_30: e.target.value })}
                    required
                  />

                  <div className="form-group">
                    <label htmlFor="compte_rendu_etat_ph3_mensualite_signer">Mensualité du Crédit :</label>
                    <input
                      type="number"
                      id="compte_rendu_etat_ph3_mensualite_signer"
                      className="form-control"
                      value={etatFormData.ph3_mensualite}
                      onChange={(e) => setEtatFormData({...etatFormData, ph3_mensualite: e.target.value})}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="compte_rendu_etat_nbr_annee_finance_signer">Nombre de mois du crédit :</label>
                    <input
                      type="number"
                      id="compte_rendu_etat_nbr_annee_finance_signer"
                      className="form-control"
                      value={etatFormData.nbr_annee_finance}
                      onChange={(e) => setEtatFormData({...etatFormData, nbr_annee_finance: e.target.value})}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="compte_rendu_etat_date_sign_datetime">Date et heure signature :</label>
                    <input
                      type="datetime-local"
                      id="compte_rendu_etat_date_sign_datetime"
                      className="form-control"
                      value={getDateTimeLocalValue(etatFormData.date_sign_date, etatFormData.date_sign_time)}
                      onChange={(e) => {
                        const [date = '', time = ''] = e.target.value.split('T');
                        setEtatFormData({ ...etatFormData, date_sign_date: date, date_sign_time: time });
                      }}
                      required
                    />
                  </div>
                    </>
                  )}

                  {/* Champs spécifiques ANNULER À REPROGRAMMER (état 8).
                      Pour Porte / Téléphone / NRP / RDV non faisable, le commercial ne remplit que le commentaire. */}
                  {selectedEtat === 8 && !(Number(user?.fonction) === 5 && isCommercialAnnulerReproSimpleOption(compteRenduOption)) && (
                    <>
                      {sousEtats.length > 0 && (
                        <div className="form-group">
                          <label htmlFor="compte_rendu_etat_id_sous_etat_8">Sous État :</label>
                          <select
                            id="compte_rendu_etat_id_sous_etat_8"
                            className="form-control"
                            value={etatFormData.id_sous_etat}
                            onChange={(e) => setEtatFormData({ ...etatFormData, id_sous_etat: e.target.value })}
                          >
                            <option value="">Sélectionner</option>
                            {sousEtats.map((setat) => (
                              <option key={setat.id} value={setat.id}>
                                {setat.titre}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div className="form-group">
                        <label htmlFor="compte_rendu_etat_conf_rdv_avec_8">Appel Avec :</label>
                        <select
                          id="compte_rendu_etat_conf_rdv_avec_8"
                          className="form-control"
                          value={etatFormData.conf_rdv_avec}
                          onChange={(e) => setEtatFormData({ ...etatFormData, conf_rdv_avec: e.target.value })}
                        >
                          <option value="">Sélectionner</option>
                          <option value="MR">MR</option>
                          <option value="MME">MME</option>
                          <option value="AUTRE">AUTRE</option>
                        </select>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="compte_rendu_etat_conf_rdv_date_8">A Rappeler Le :</label>
                          <input
                            type="date"
                            id="compte_rendu_etat_conf_rdv_date_8"
                            className="form-control"
                            value={etatFormData.conf_rdv_date}
                            onChange={(e) => setEtatFormData({ ...etatFormData, conf_rdv_date: e.target.value })}
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="compte_rendu_etat_conf_rdv_time_8">Heure :</label>
                          <input
                            type="time"
                            id="compte_rendu_etat_conf_rdv_time_8"
                            className="form-control"
                            value={etatFormData.conf_rdv_time}
                            onChange={(e) => setEtatFormData({ ...etatFormData, conf_rdv_time: e.target.value })}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Compte rendu (toujours visible) */}
                  <div className="form-group">
                    <label htmlFor="compte_rendu_etat_conf_commentaire">Compte rendu :</label>
                    <textarea
                      id="compte_rendu_etat_conf_commentaire"
                      className="form-control"
                      rows="4"
                      value={etatFormData.conf_commentaire_produit}
                      onChange={(e) => setEtatFormData({ ...etatFormData, conf_commentaire_produit: e.target.value })}
                      placeholder="Saisissez votre compte rendu commercial..."
                      required
                    />
                  </div>

                  <div className="form-actions">
                    <button className="btn-confirm" onClick={handleEtatSubmit} disabled={etatSubmitting || isChangementEtatBloque}>
                      {etatSubmitting ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                    <button className="btn-cancel" onClick={() => {
                      setSelectedEtat(null);
                      setCompteRenduOption('');
                      setEtatFormData({
                        ...etatFormData,
                        date_sign_date: '', date_sign_time: '', produit: '', id_sous_etat: '', id_commercial: '',
                        id_commercial_2: '', pseudo: '', ph3_pac: 'reau', ph3_rr_model: '', ph3_puissance: '',
                        ph3_ballon: '', ph3_marque_ballon: '', ph3_alimentation: '', ph3_type: '', ph3_prix: '',
                        ph3_installateur: '', conf_consommations: '', ph3_bonus_30: '', valeur_mensualite: '',
                        ph3_mensualite: '', ph3_attente: '', nbr_annee_finance: '', credit_immobilier: '',
                        credit_autre: '', conf_commentaire_produit: '',
                        conf_rdv_date: '', conf_rdv_time: '', conf_rdv_avec: '', motif_qualif: ''
                      });
                    }}>Annuler</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Permissions pour changer l'état :
            - Admins (1, 2, 7), Backoffice (11) : peuvent changer vers tous les états (y compris fiche confirmée)
            - Superviseur / Qualité / RP Qualification (2, 8, 12) : y compris depuis le modal détails (fiche verrouillée CQ / date antérieure)
            - Agents (3) : peuvent changer les fiches de leur centre
            - Confirmateurs (6), RE Confirmation (14), RP Confirmation (13) : peuvent changer l'état (y compris si fiche déjà confirmée) */}
        {((Number(user?.fonction) === 1 || Number(user?.fonction) === 2 || Number(user?.fonction) === 7 || Number(user?.fonction) === 8 || Number(user?.fonction) === 11 || Number(user?.fonction) === 12) ||
          (Number(user?.fonction) === 3 && user?.centre === ficheData?.id_centre) ||
          (Number(user?.fonction) === 6 || Number(user?.fonction) === 14 || Number(user?.fonction) === 13) ||
          partenaireCanFixerRdv) && (
          <div ref={etatChangeSectionRef} className="fiche-section etat-change-section">
            <h2 className="section-title">Changer l'état de la fiche</h2>
            <div className="etat-change-form">
              <div className="form-group">
                <label htmlFor="id_etat_final">Nouvel état :</label>
                {(() => {
                  const selectedEtatObj = selectedEtat != null
                    ? etats?.find((e) => Number(e.id) === Number(selectedEtat))
                    : null;
                  const selectStyle = selectedEtatObj
                    ? {
                        ...getEtatSelectOptionStyle(selectedEtatObj.color),
                        fontWeight: 700,
                      }
                    : undefined;
                  const renderEtatOption = (etat, labelSuffix = '') => (
                    <option
                      key={etat.id}
                      value={etat.id}
                      style={getEtatSelectOptionStyle(etat.color)}
                    >
                      {etat.titre}{labelSuffix}
                    </option>
                  );
                  return (
                <select
                  id="id_etat_final"
                  className="form-control etat-change-select"
                  value={selectedEtat != null ? String(selectedEtat) : ''}
                  disabled={isChangementEtatBloque}
                  onChange={(e) => handleEtatChange(e.target.value ? parseInt(e.target.value, 10) : null)}
                  style={selectStyle}
                >
                  <option value="">Choisissez un état</option>
                  {/* Afficher l'état actuel comme option visible dans la liste (surtout pour les confirmateurs) */}
                  {fiche.id_etat_final && (() => {
                    const etatActuel = etats?.find((e) => Number(e.id) === Number(fiche.id_etat_final));
                    if (!etatActuel) return null;
                    // Vérifier si l'état actuel n'est pas déjà dans les listes de phases
                    const isInPhases = [...etatsPhase0, ...etatsPhase1, ...etatsPhase2, ...etatsPhase3].some(e => e.id === etatActuel.id);
                    // Si l'état actuel n'est pas dans les phases, l'afficher dans un groupe séparé pour qu'il soit toujours visible
                    if (!isInPhases) {
                      return (
                        <optgroup label="État actuel">
                          {renderEtatOption(etatActuel, ' (actuel)')}
                        </optgroup>
                      );
                    }
                    return null; // Déjà affiché dans les phases
                  })()}
                  {!excludeEtatsPhase0 && etatsPhase0.length > 0 && (
                    <optgroup label="PHASE 0">
                      {etatsPhase0.map((etat) => renderEtatOption(etat))}
                    </optgroup>
                  )}
                  {etatsPhase1.length > 0 && (
                    <optgroup label="PHASE 1">
                      {etatsPhase1.map((etat) => renderEtatOption(etat))}
                    </optgroup>
                  )}
                  {etatsPhase2.length > 0 && (
                    <optgroup label="PHASE 2">
                      {etatsPhase2.map((etat) => renderEtatOption(etat))}
                    </optgroup>
                  )}
                  {etatsPhase3.length > 0 && (
                    <optgroup label="PHASE 3">
                      {etatsPhase3.map((etat) => renderEtatOption(etat))}
                    </optgroup>
                  )}
                </select>
                  );
                })()}
                {isChangementEtatBloque && (
                  <p style={{ marginTop: '10px', color: '#666', fontSize: '0.9em' }}>
                    Cette fiche est dans un état définitif : le changement d&apos;état n&apos;est plus possible.
                  </p>
                )}
            </div>

            {showHistoConfirmateurDropdown &&
              selectedEtat != null &&
              selectedEtat !== 7 &&
              ![13, 44, 45].includes(Number(selectedEtat)) && (
              <div className="form-group histo-confirmateurs-picker">
                <label htmlFor="histo_confirmateur">Confirmateur (historique)</label>
                <select
                  id="histo_confirmateur"
                  className="form-control"
                  value={histoConfirmateurId}
                  onChange={(e) => setHistoConfirmateurId(e.target.value)}
                  title="Confirmateur 1 enregistré dans l'historique lors du changement d'état"
                >
                  <option value="">— Sélectionner —</option>
                  {(confirmateurs || []).map(c => (
                    <option key={c.id} value={c.id}>{c.pseudo || `Utilisateur ${c.id}`}</option>
                  ))}
                </select>

                {showHistoConfirmateur2 && (
                  <div style={{ marginTop: '10px' }}>
                    <label htmlFor="histo_confirmateur_2">Confirmateur 2 (historique)</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        id="histo_confirmateur_2"
                        className="form-control"
                        value={histoConfirmateur2Id}
                        onChange={(e) => setHistoConfirmateur2Id(e.target.value)}
                        title="Confirmateur 2 enregistré dans l'historique"
                      >
                        <option value="">— Sélectionner —</option>
                        {(confirmateurs || []).map(c => (
                          <option key={c.id} value={c.id}>{c.pseudo || `Utilisateur ${c.id}`}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn-cancel"
                        title="Retirer le confirmateur 2"
                        onClick={() => {
                          setShowHistoConfirmateur2(false);
                          setHistoConfirmateur2Id('');
                          setShowHistoConfirmateur3(false);
                          setHistoConfirmateur3Id('');
                        }}
                        style={{ padding: '6px 10px', flexShrink: 0 }}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </div>
                )}

                {showHistoConfirmateur3 && (
                  <div style={{ marginTop: '10px' }}>
                    <label htmlFor="histo_confirmateur_3">Confirmateur 3 (historique)</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        id="histo_confirmateur_3"
                        className="form-control"
                        value={histoConfirmateur3Id}
                        onChange={(e) => setHistoConfirmateur3Id(e.target.value)}
                        title="Confirmateur 3 enregistré dans l'historique"
                      >
                        <option value="">— Sélectionner —</option>
                        {(confirmateurs || []).map(c => (
                          <option key={c.id} value={c.id}>{c.pseudo || `Utilisateur ${c.id}`}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn-cancel"
                        title="Retirer le confirmateur 3"
                        onClick={() => {
                          setShowHistoConfirmateur3(false);
                          setHistoConfirmateur3Id('');
                        }}
                        style={{ padding: '6px 10px', flexShrink: 0 }}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </div>
                )}

                {!showHistoConfirmateur3 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!showHistoConfirmateur2) {
                        setShowHistoConfirmateur2(true);
                      } else {
                        setShowHistoConfirmateur3(true);
                      }
                    }}
                    title={showHistoConfirmateur2 ? 'Ajouter un 3ᵉ confirmateur' : 'Ajouter un 2ᵉ confirmateur'}
                    style={{
                      marginTop: '8px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: 'transparent',
                      border: 'none',
                      color: '#0b6e99',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: '13px',
                      fontWeight: 600,
                    }}
                  >
                    <FaPlus size={12} />
                    {showHistoConfirmateur2 ? 'Ajouter un 3ᵉ confirmateur' : 'Ajouter un 2ᵉ confirmateur'}
                  </button>
                )}
              </div>
            )}

            {/* Formulaire de confirmation (état 7) */}
            {selectedEtat === 7 && (
              <div className="confirmation-form">
                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                  {!showConfirmConfFields ? (
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => {
                        setShowConfirmConfFields(true);
                        if (ficheData) {
                          const nextConf = buildConfFormStateFromFiche(ficheData, user);
                          setConfFormData(nextConf);
                          setShowConfConfirmateur2(!!nextConf.id_confirmateur_2);
                          setShowConfConfirmateur3(!!nextConf.id_confirmateur_3);
                        }
                      }}
                      title="Afficher les champs conf_"
                    >
                      <FaReplyAll size={20} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => setShowConfirmConfFields(false)}
                      title="Réduire les champs conf_"
                    >
                      <FaChevronUp size={20} />
                    </button>
                  )}
                </div>
                <table className="rdv-form-table">
                  <tbody>
                    <tr>
                      <td><label htmlFor="conf_produit">Étude à faire pour *</label></td>
                      <td>
                        <select
                          id="conf_produit"
                          className="form-control"
                          value={confFormData.produit}
                          onChange={(e) => setConfFormData({...confFormData, produit: e.target.value})}
                          required
                        >
                          <option value="">Sélectionner</option>
                          <option value="1">PAC</option>
                          <option value="2">PV</option>
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td><label htmlFor="conf_id_confirmateur">Confirmateur *</label></td>
                      <td>
                        <select
                          id="conf_id_confirmateur"
                          className="form-control"
                          value={confFormData.id_confirmateur}
                          onChange={(e) => setConfFormData({...confFormData, id_confirmateur: e.target.value})}
                          required
                        >
                          {!isConfirmateurSession && <option value="">Sélectionner</option>}
                          {confirmateur1SelectOptions.map(conf => (
                            <option key={conf.id} value={conf.id}>
                              {conf.pseudo}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                    {(showConfConfirmateur2 || confFormData.id_confirmateur_2) && (
                    <tr>
                      <td><label htmlFor="conf_id_confirmateur_2">Confirmateur 2 (optionnel) :</label></td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <select
                            id="conf_id_confirmateur_2"
                            className="form-control"
                            value={confFormData.id_confirmateur_2}
                            onChange={(e) => setConfFormData({...confFormData, id_confirmateur_2: e.target.value})}
                          >
                            <option value="">Aucun</option>
                            {confirmateurs?.map(conf => (
                              <option key={conf.id} value={conf.id}>
                                {conf.pseudo}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="btn-cancel"
                            title="Retirer le confirmateur 2"
                            onClick={() => {
                              setShowConfConfirmateur2(false);
                              setShowConfConfirmateur3(false);
                              setConfFormData((prev) => ({ ...prev, id_confirmateur_2: '', id_confirmateur_3: '' }));
                            }}
                            style={{ padding: '6px 10px', flexShrink: 0 }}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </td>
                    </tr>
                    )}
                    {(showConfConfirmateur3 || confFormData.id_confirmateur_3) && (
                    <tr>
                      <td><label htmlFor="conf_id_confirmateur_3">Confirmateur 3 (optionnel) :</label></td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <select
                            id="conf_id_confirmateur_3"
                            className="form-control"
                            value={confFormData.id_confirmateur_3}
                            onChange={(e) => setConfFormData({...confFormData, id_confirmateur_3: e.target.value})}
                          >
                            <option value="">Aucun</option>
                            {confirmateurs?.map(conf => (
                              <option key={conf.id} value={conf.id}>
                                {conf.pseudo}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="btn-cancel"
                            title="Retirer le confirmateur 3"
                            onClick={() => {
                              setShowConfConfirmateur3(false);
                              setConfFormData((prev) => ({ ...prev, id_confirmateur_3: '' }));
                            }}
                            style={{ padding: '6px 10px', flexShrink: 0 }}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </td>
                    </tr>
                    )}
                    {!showConfConfirmateur3 && (
                    <tr>
                      <td colSpan={2}>
                        <button
                          type="button"
                          onClick={() => {
                            if (!showConfConfirmateur2 && !confFormData.id_confirmateur_2) {
                              setShowConfConfirmateur2(true);
                            } else {
                              setShowConfConfirmateur3(true);
                            }
                          }}
                          title={
                            showConfConfirmateur2 || confFormData.id_confirmateur_2
                              ? 'Ajouter un 3ᵉ confirmateur'
                              : 'Ajouter un 2ᵉ confirmateur'
                          }
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'transparent',
                            border: 'none',
                            color: '#0b6e99',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: '13px',
                            fontWeight: 600,
                          }}
                        >
                          <FaPlus />
                          {showConfConfirmateur2 || confFormData.id_confirmateur_2
                            ? 'Ajouter un 3ᵉ confirmateur'
                            : 'Ajouter un 2ᵉ confirmateur'}
                        </button>
                      </td>
                    </tr>
                    )}
                    <tr>
                      <td><label htmlFor="conf_rdv_date">Date RDV *</label></td>
                      <td>
                        <input
                          type="date"
                          id="conf_rdv_date"
                          className="form-control"
                          min={formatLocalYmd()}
                          value={confFormData.conf_rdv_date}
                          onChange={(e) => setConfFormData({...confFormData, conf_rdv_date: e.target.value})}
                          required
                        />
                      </td>
                    </tr>
                    <tr>
                      <td><label htmlFor="conf_rdv_time">Heure RDV *</label></td>
                      <td>
                        <input
                          type="time"
                          id="conf_rdv_time"
                          className="form-control"
                          value={confFormData.conf_rdv_time}
                          onChange={(e) => setConfFormData({...confFormData, conf_rdv_time: e.target.value})}
                          required
                        />
                      </td>
                    </tr>
                    <tr>
                      <td><label htmlFor="conf_is_r2">R2 *</label></td>
                      <td>
                        <select
                          id="conf_is_r2"
                          className="form-control"
                          value={confFormData.is_r2 || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setConfFormData({
                              ...confFormData,
                              is_r2: v,
                              ...(v !== 'OUI'
                                ? { date_r1: '', id_commercial_r1: '', commentaire_r1: '' }
                                : {}),
                            });
                          }}
                          required
                        >
                          <option value="">Sélectionner</option>
                          <option value="OUI">OUI</option>
                          <option value="NON">NON</option>
                        </select>
                      </td>
                    </tr>
                    {confFormData.is_r2 === 'OUI' && (
                      <>
                        <tr>
                          <td><label htmlFor="conf_date_r1">Date R1 *</label></td>
                          <td>
                            <input
                              type="datetime-local"
                              id="conf_date_r1"
                              className="form-control"
                              value={confFormData.date_r1 || ''}
                              onChange={(e) => setConfFormData({ ...confFormData, date_r1: e.target.value })}
                              required
                            />
                          </td>
                        </tr>
                        <tr>
                          <td><label htmlFor="conf_id_commercial_r1">Commercial R1 *</label></td>
                          <td>
                            <select
                              id="conf_id_commercial_r1"
                              className="form-control"
                              value={confFormData.id_commercial_r1 || ''}
                              onChange={(e) =>
                                setConfFormData({ ...confFormData, id_commercial_r1: e.target.value })
                              }
                              required
                            >
                              <option value="">Sélectionner</option>
                              {(commerciaux || [])
                                .filter((u) => u.etat > 0 || u.etat == null)
                                .map((com) => (
                                  <option key={com.id} value={com.id}>
                                    {formatCommercialDisplayName(com)}
                                  </option>
                                ))}
                            </select>
                          </td>
                        </tr>
                        <tr>
                          <td><label htmlFor="conf_commentaire_r1">Commentaire R1 *</label></td>
                          <td>
                            <textarea
                              id="conf_commentaire_r1"
                              className="form-control"
                              rows="3"
                              value={confFormData.commentaire_r1 || ''}
                              onChange={(e) =>
                                setConfFormData({ ...confFormData, commentaire_r1: e.target.value })
                              }
                              required
                            />
                          </td>
                        </tr>
                      </>
                    )}
                    {showConfirmConfFields && (
                      <>
                    <tr>
                      <td><label htmlFor="conf_rdv_avec">RDV pris avec *</label></td>
                      <td>
                        <select
                          id="conf_rdv_avec"
                          className="form-control"
                          value={confFormData.conf_rdv_avec}
                          onChange={(e) => {
                            const next = { ...confFormData, conf_rdv_avec: e.target.value };
                            setConfFormData({ ...next, ...clearHiddenConfProfessionFields(next) });
                          }}
                          required
                        >
                          <option value="">Sélectionner</option>
                          <option value="MR">MR</option>
                          <option value="MME">MME</option>
                          <option value="AUTRE">AUTRE</option>
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td><label htmlFor="conf_situation_conjugale">Couple ou Célibataire *</label></td>
                      <td>
                        <select
                          id="conf_situation_conjugale"
                          className="form-control"
                          value={confFormData.situation_conjugale || ''}
                          onChange={(e) => {
                            const next = { ...confFormData, situation_conjugale: e.target.value };
                            setConfFormData({ ...next, ...clearHiddenConfProfessionFields(next) });
                          }}
                          required
                        >
                          <option value="">Sélectionner</option>
                          <option value="COUPLE">Couple</option>
                          <option value="CELIBATAIRE">Célibataire</option>
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td><label htmlFor="conf_appel_tunisie_avec">Appel en Tunisie avec *</label></td>
                      <td>
                        <select
                          id="conf_appel_tunisie_avec"
                          className="form-control"
                          value={confFormData.conf_appel_tunisie_avec}
                          onChange={(e) => setConfFormData({...confFormData, conf_appel_tunisie_avec: e.target.value})}
                          required
                        >
                          <option value="">Sélectionner</option>
                          <option value="MR">Mr</option>
                          <option value="MME">Mme</option>
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td><label htmlFor="conf_deja_etude">A déjà fait une étude *</label></td>
                      <td>
                        <select
                          id="conf_deja_etude"
                          className="form-control"
                          value={confFormData.conf_deja_etude}
                          onChange={(e) => {
                            const value = e.target.value;
                            setConfFormData({
                              ...confFormData,
                              conf_deja_etude: value,
                              conf_deja_fait_etude: value,
                              conf_details_etude: value === 'OUI' ? confFormData.conf_details_etude : ''
                            });
                          }}
                          required
                        >
                          <option value="">Sélectionner</option>
                          <option value="OUI">OUI</option>
                          <option value="NON">NON</option>
                        </select>
                      </td>
                    </tr>
                    {(confFormData.conf_deja_etude === 'OUI' || confFormData.conf_deja_fait_etude === 'OUI') && (
                    <tr>
                      <td><label htmlFor="conf_details_etude">Détails étude *</label></td>
                      <td>
                        <input
                          type="text"
                          id="conf_details_etude"
                          className="form-control"
                          value={confFormData.conf_details_etude || ''}
                          onChange={(e) => setConfFormData({ ...confFormData, conf_details_etude: e.target.value })}
                          required
                        />
                      </td>
                    </tr>
                    )}
                    <tr>
                      <td><label htmlFor="conf_rdv_annule_precedent">RDV déjà annulé précédemment *</label></td>
                      <td>
                        <select
                          id="conf_rdv_annule_precedent"
                          className="form-control"
                          value={confFormData.conf_rdv_annule_precedent}
                          onChange={(e) => setConfFormData({...confFormData, conf_rdv_annule_precedent: e.target.value})}
                          required
                        >
                          <option value="">Sélectionner</option>
                          <option value="OUI">OUI</option>
                          <option value="NON">NON</option>
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td><label htmlFor="conf_presence_couple">Présence du couple ou célibataire *</label></td>
                      <td>
                        <select
                          id="conf_presence_couple"
                          className="form-control"
                          value={confFormData.conf_presence_couple}
                          onChange={(e) => setConfFormData({...confFormData, conf_presence_couple: e.target.value})}
                          required
                        >
                          <option value="">Sélectionner</option>
                          <option value="RAS PRESENCE CLIENT(S)">RAS PRESENCE CLIENT(S)</option>
                          <option value="MME SEULE SANS MR">MME SEULE SANS MR</option>
                          <option value="MR SEUL SANS MME">MR SEUL SANS MME</option>
                        </select>
                      </td>
                    </tr>
                    {showConfProfessionMrFields(confFormData.situation_conjugale, confFormData.conf_rdv_avec) && (
                      <>
                    <tr>
                      <td><label>Profession MR *</label></td>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          autoComplete="off"
                          value={confFormData.conf_profession_monsieur || ''}
                          onChange={(e) => setConfFormData({ ...confFormData, conf_profession_monsieur: e.target.value })}
                          required
                        />
                      </td>
                    </tr>
                    <tr>
                      <td><label htmlFor="conf_type_contrat_mr">Type de Contrat MR *</label></td>
                      <td>
                        <select
                          id="conf_type_contrat_mr"
                          className="form-control"
                          value={confFormData.conf_type_contrat_mr}
                          onChange={(e) => applyConfTypeContratChange('conf_type_contrat_mr', e.target.value)}
                          required
                        >
                          <option value="">Sélectionner</option>
                          {(typeContrat || []).map(t => (
                            <option key={t.id} value={t.id}>{t.nom}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                      </>
                    )}
                    {showConfProfessionMmeFields(confFormData.situation_conjugale, confFormData.conf_rdv_avec) && (
                      <>
                    <tr>
                      <td><label>Profession MME *</label></td>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          autoComplete="off"
                          value={confFormData.conf_profession_madame || ''}
                          onChange={(e) => setConfFormData({ ...confFormData, conf_profession_madame: e.target.value })}
                          required
                        />
                      </td>
                    </tr>
                    <tr>
                      <td><label htmlFor="conf_type_contrat_madame">Type de Contrat MME *</label></td>
                      <td>
                        <select
                          id="conf_type_contrat_madame"
                          className="form-control"
                          value={confFormData.conf_type_contrat_madame}
                          onChange={(e) => applyConfTypeContratChange('conf_type_contrat_madame', e.target.value)}
                          required
                        >
                          <option value="">Sélectionner</option>
                          {(typeContrat || []).map(t => (
                            <option key={t.id} value={t.id}>{t.nom}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                      </>
                    )}
                    <tr>
                      <td><label htmlFor="conf_revenu">Revenu *</label></td>
                      <td>
                        <input
                          type="text"
                          id="conf_revenu"
                          className="form-control"
                          value={confFormData.conf_revenu}
                          onChange={(e) => setConfFormData({...confFormData, conf_revenu: e.target.value})}
                          required
                        />
                      </td>
                    </tr>
                    <tr>
                      <td><label htmlFor="conf_credit">Crédit *</label></td>
                      <td>
                        <input
                          type="text"
                          id="conf_credit"
                          className="form-control"
                          value={confFormData.conf_credit}
                          onChange={(e) => setConfFormData({...confFormData, conf_credit: e.target.value})}
                          required
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <label htmlFor="conf_consommation_electricite_gen">
                          Consommations électrique{String(confFormData.produit) === '1' ? '' : ' *'}
                        </label>
                      </td>
                      <td>
                        <input
                          type="text"
                          id="conf_consommation_electricite_gen"
                          className="form-control"
                          value={confFormData.conf_consommation_electricite}
                          onChange={(e) => setConfFormData({...confFormData, conf_consommation_electricite: e.target.value})}
                          required={String(confFormData.produit) !== '1'}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <label htmlFor="conf_consommation_chauffage_gen">
                          Consommations chauffage{String(confFormData.produit) === '2' ? '' : ' *'}
                        </label>
                      </td>
                      <td>
                        <input
                          type="text"
                          id="conf_consommation_chauffage_gen"
                          className="form-control"
                          value={confFormData.conf_consommation_chauffage}
                          onChange={(e) => setConfFormData({...confFormData, conf_consommation_chauffage: e.target.value})}
                          required={String(confFormData.produit) !== '2'}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td><label htmlFor="conf_mode_chauffage_gen">Mode de chauffage *</label></td>
                      <td>
                        <input
                          type="text"
                          id="conf_mode_chauffage_gen"
                          className="form-control"
                          value={confFormData.conf_mode_chauffage || ''}
                          onChange={(e) => setConfFormData({ ...confFormData, conf_mode_chauffage: e.target.value })}
                          autoComplete="off"
                          required
                        />
                      </td>
                    </tr>

                    {/* Champs spécifiques PAC */}
                    {confFormData.produit === '1' && (
                      <>
                        <tr>
                          <td><label htmlFor="conf_annee_systeme_chauffage">Année du système de chauffage *</label></td>
                          <td>
                            <input
                              type="number"
                              id="conf_annee_systeme_chauffage"
                              className="form-control"
                              value={confFormData.annee_systeme_chauffage}
                              onChange={(e) => setConfFormData({...confFormData, annee_systeme_chauffage: e.target.value})}
                              min="1970"
                              max={new Date().getFullYear()}
                              required
                            />
                          </td>
                        </tr>
                        <tr>
                          <td><label htmlFor="conf_surface_chauffee">Surface chauffée (m²) *</label></td>
                          <td>
                            <input
                              type="number"
                              id="conf_surface_chauffee"
                              className="form-control"
                              value={confFormData.surface_chauffee}
                              onChange={(e) => setConfFormData({...confFormData, surface_chauffee: e.target.value})}
                              min="0"
                              required
                            />
                          </td>
                        </tr>
                        <tr>
                          <td><label htmlFor="conf_consommation_chauffage">Consommation chauffage (€) *</label></td>
                          <td>
                            <input
                              type="text"
                              id="conf_consommation_chauffage"
                              className="form-control"
                              value={confFormData.consommation_chauffage}
                              onChange={(e) => setConfFormData({...confFormData, consommation_chauffage: e.target.value})}
                              required
                            />
                          </td>
                        </tr>
                      </>
                    )}

                    {/* Champs spécifiques PV */}
                    {confFormData.produit === '2' && (
                      <>
                        <tr>
                          <td><label htmlFor="conf_orientation_toiture">Orientation toiture *</label></td>
                          <td>
                            <input
                              type="text"
                              id="conf_orientation_toiture"
                              className="form-control"
                              value={confFormData.conf_orientation_toiture || ''}
                              onChange={(e) => setConfFormData({...confFormData, conf_orientation_toiture: e.target.value})}
                              required
                            />
                          </td>
                        </tr>
                        <tr>
                          <td><label htmlFor="conf_zones_ombres">Zones ombres *</label></td>
                          <td>
                            <input
                              type="text"
                              id="conf_zones_ombres"
                              className="form-control"
                              value={confFormData.conf_zones_ombres}
                              onChange={(e) => setConfFormData({...confFormData, conf_zones_ombres: e.target.value})}
                              required
                            />
                          </td>
                        </tr>
                        <tr>
                          <td><label htmlFor="conf_site_classe">Proche d'un site classé *</label></td>
                          <td>
                            <select
                              id="conf_site_classe"
                              className="form-control"
                              value={confFormData.conf_site_classe}
                              onChange={(e) => setConfFormData({...confFormData, conf_site_classe: e.target.value})}
                              required
                            >
                              <option value="">Sélectionner</option>
                              <option value="OUI">OUI</option>
                              <option value="NON">NON</option>
                            </select>
                          </td>
                        </tr>
                      </>
                    )}
                      </>
                    )}

                    <tr>
                      <td><label htmlFor="conf_commentaire_produit">Commentaire *</label></td>
                      <td>
                        <textarea
                          id="conf_commentaire_produit"
                          className="form-control"
                          rows={6}
                          value={confFormData.conf_commentaire_produit}
                          onChange={(e) => setConfFormData({...confFormData, conf_commentaire_produit: e.target.value})}
                          required
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="form-actions">
                  <button
                    className="btn-confirm"
                    onClick={handleConfirmSubmit}
                    disabled={confirmSubmitting}
                  >
                    {confirmSubmitting ? 'Confirmation…' : 'Confirmer'}
                  </button>
                  <button
                    className="btn-cancel"
                    onClick={() => {
                      setSelectedEtat(null);
                      setConfFormData({
                        produit: '',
                        id_confirmateur: '',
                        id_confirmateur_2: '',
                        id_confirmateur_3: '',
                        conf_rdv_date: '',
                        conf_rdv_time: '',
                        conf_rdv_avec: '',
                        conf_orientation_toiture: '',
                        conf_zones_ombres: '',
                        conf_site_classe: '',
                        conf_consommation_electricite: '',
                        conf_mode_chauffage: '',
                        conf_complement_chauffage: '',
                        annee_systeme_chauffage: '',
                        surface_chauffee: '',
                        consommation_chauffage: '',
                        nb_pans: '',
                        conf_commentaire_produit: ''
                      });
                    }}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Formulaire NRP (état 2) */}
            {selectedEtat === 2 && (
              <div className="nrp-form" style={{ marginTop: '20px' }}>
                <div className="form-group">
                  <label htmlFor="nrp_date_appel_datetime">Date et heure d&apos;appel :</label>
                  <input
                    id="nrp_date_appel_datetime"
                    type="datetime-local"
                    className="form-control"
                    value={getDateTimeLocalValue(nrpFormData.date_appel_date, nrpFormData.date_appel_time)}
                    onChange={(e) => {
                      const [date = '', time = ''] = e.target.value.split('T');
                      setNrpFormData({ ...nrpFormData, date_appel_date: date, date_appel_time: time });
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="nrp_id_sous_etat">Sous État :</label>
                  <select
                    id="nrp_id_sous_etat"
                    className="form-control"
                    value={nrpFormData.id_sous_etat}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      setNrpFormData({
                        ...nrpFormData,
                        id_sous_etat: selectedId,
                      });
                    }}
                  >
                    <option value="">Sélectionner</option>
                    {sousEtats.map(setat => (
                      <option key={setat.id} value={setat.id}>
                        {setat.titre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="nrp_conf_commentaire_produit">Commentaire :</label>
                  <textarea
                    id="nrp_conf_commentaire_produit"
                    className="form-control"
                    rows="4"
                    value={nrpFormData.conf_commentaire_produit}
                    onChange={(e) => setNrpFormData({...nrpFormData, conf_commentaire_produit: e.target.value})}
                  />
                </div>

                <div className="form-actions">
                  <button
                    className="btn-confirm"
                    onClick={handleEtatSubmit}
                    disabled={etatSubmitting || isChangementEtatBloque}
                  >
                    {etatSubmitting ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                  <button
                    className="btn-cancel"
                    onClick={() => {
                      setSelectedEtat(null);
                      setNrpFormData({
                        date_appel_date: '',
                        date_appel_time: '',
                        id_sous_etat: '',
                        conf_commentaire_produit: ''
                      });
                    }}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Formulaire ANNULER À REPROGRAMMER (état 8) - visible aussi pour commerciaux (mais seulement si pas déjà dans section compte rendu) */}
            {selectedEtat === 8 && !(user?.fonction === 5 && compteRenduOption) && (
              <div className="etat-form" style={{ marginTop: '20px' }}>
                {sousEtats.length > 0 && (
                  <div className="form-group">
                    <label htmlFor="etat_id_sous_etat_8">Sous État :</label>
                    <select
                      id="etat_id_sous_etat_8"
                      className="form-control"
                      value={etatFormData.id_sous_etat}
                      onChange={(e) => setEtatFormData({...etatFormData, id_sous_etat: e.target.value})}
                    >
                      <option value="">Sélectionner</option>
                      {sousEtats.map(setat => (
                        <option key={setat.id} value={setat.id}>
                          {setat.titre}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="etat_conf_rdv_avec_8">Appel Avec :</label>
                  <select
                    id="etat_conf_rdv_avec_8"
                    className="form-control"
                    value={etatFormData.conf_rdv_avec}
                    onChange={(e) => setEtatFormData({...etatFormData, conf_rdv_avec: e.target.value})}
                  >
                    <option value="">Sélectionner</option>
                    <option value="MR">MR</option>
                    <option value="MME">MME</option>
                    <option value="AUTRE">AUTRE</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="etat_conf_rdv_datetime_8">A Rappeler Le :</label>
                  <input
                    type="datetime-local"
                    id="etat_conf_rdv_datetime_8"
                    className="form-control"
                    value={getDateTimeLocalValue(etatFormData.conf_rdv_date, etatFormData.conf_rdv_time)}
                    onChange={(e) => {
                      const [date = '', time = ''] = e.target.value.split('T');
                      setEtatFormData({ ...etatFormData, conf_rdv_date: date, conf_rdv_time: time });
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="etat_conf_commentaire_8">Commentaire :</label>
                  <textarea
                    id="etat_conf_commentaire_8"
                    className="form-control"
                    rows="4"
                    value={etatFormData.conf_commentaire_produit}
                    onChange={(e) => setEtatFormData({...etatFormData, conf_commentaire_produit: e.target.value})}
                  />
                </div>

                <div className="form-actions">
                  <button className="btn-confirm" onClick={handleEtatSubmit} disabled={etatSubmitting || isChangementEtatBloque}>{etatSubmitting ? 'Enregistrement…' : 'Enregistrer'}</button>
                  <button className="btn-cancel" onClick={() => {
                    setSelectedEtat(null);
                    setEtatFormData({...etatFormData, conf_rdv_date: '', conf_rdv_time: '', id_sous_etat: '', conf_rdv_avec: '', conf_commentaire_produit: ''});
                  }}>Annuler</button>
                </div>
              </div>
            )}

            {/* Formulaire RAPPEL POUR BUREAU (état 19) */}
            {selectedEtat === 19 && (
              <div className="etat-form" style={{ marginTop: '20px' }}>
                <div className="form-group">
                  <label htmlFor="etat_date_rappel_datetime_19">A Rappeler Le :</label>
                  <input
                    type="datetime-local"
                    id="etat_date_rappel_datetime_19"
                    className="form-control"
                    value={getDateTimeLocalValue(etatFormData.date_rappel_date, etatFormData.date_rappel_time)}
                    onChange={(e) => {
                      const [date = '', time = ''] = e.target.value.split('T');
                      setEtatFormData({ ...etatFormData, date_rappel_date: date, date_rappel_time: time });
                    }}
                  />
                </div>

                {sousEtats.length > 0 && (
                  <div className="form-group">
                    <label htmlFor="etat_id_sous_etat_19">Sous État :</label>
                    <select
                      id="etat_id_sous_etat_19"
                      className="form-control"
                      value={etatFormData.id_sous_etat}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedSousEtat = sousEtats.find(se => String(se.id) === String(selectedId));
                        setEtatFormData({
                          ...etatFormData,
                          id_sous_etat: selectedId,
                          // Préremplit le commentaire avec le sous-état choisi (sauf AUTRE).
                          // Le confirmateur peut ensuite le modifier librement.
                          motif_qualif: getCommentPrefillFromSousEtatTitre(selectedSousEtat?.titre)
                        });
                      }}
                    >
                      <option value="">Sélectionner</option>
                      {sousEtats.map(setat => (
                        <option key={setat.id} value={setat.id}>
                          {setat.titre}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="etat_conf_commentaire_19">Commentaire :</label>
                  <textarea
                    id="etat_conf_commentaire_19"
                    className="form-control"
                    rows="4"
                    value={etatFormData.motif_qualif}
                    onChange={(e) => setEtatFormData({...etatFormData, motif_qualif: e.target.value})}
                  />
                </div>

                <div className="form-actions">
                  <button className="btn-confirm" onClick={handleEtatSubmit} disabled={etatSubmitting || isChangementEtatBloque}>{etatSubmitting ? 'Enregistrement…' : 'Enregistrer'}</button>
                  <button className="btn-cancel" onClick={() => {
                    setSelectedEtat(null);
                    setEtatFormData({...etatFormData, date_rappel_date: '', date_rappel_time: '', id_sous_etat: '', conf_commentaire_produit: '', motif_qualif: ''});
                  }}>Annuler</button>
                </div>
              </div>
            )}

            {/* Formulaire SIGNER (états 13, 44, 45) — aligné sur la session commercial */}
            {[13, 44, 45].includes(selectedEtat) && !(user?.fonction === 5 && compteRenduOption) && (
              <div className="etat-form compte-rendu-signer-form" style={{ marginTop: '20px' }}>
                <div className="form-group">
                  <label htmlFor="etat_pseudo_signer">Pseudo :</label>
                  <input
                    type="text"
                    id="etat_pseudo_signer"
                    className="form-control"
                    value={etatFormData.pseudo}
                    onChange={(e) => setEtatFormData({...etatFormData, pseudo: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="etat_produit_signer">Signature pour :</label>
                  <select
                    id="etat_produit_signer"
                    className="form-control"
                    value={etatFormData.produit}
                    onChange={(e) => setEtatFormData({...etatFormData, produit: e.target.value})}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {produits?.map(prod => (
                      <option key={prod.id} value={prod.id}>
                        {prod.nom}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sous État SIGNER : COMPLETE / IMCOMPLETE uniquement */}
                {(() => {
                  const sousEtatsSigner = (sousEtats || []).filter((se) => {
                    const titre = (se?.titre || '').toString().trim().toUpperCase();
                    return titre === 'COMPLETE' || titre === 'IMCOMPLETE' || titre === 'INCOMPLETE';
                  });
                  if (sousEtatsSigner.length === 0) return null;
                  return (
                    <div className="form-group">
                      <label htmlFor="etat_id_sous_etat_signer">Sous État :</label>
                      <select
                        id="etat_id_sous_etat_signer"
                        className="form-control"
                        value={etatFormData.id_sous_etat}
                        onChange={(e) => setEtatFormData({...etatFormData, id_sous_etat: e.target.value})}
                        required
                      >
                        <option value="">Sélectionner</option>
                        {sousEtatsSigner.map((setat) => (
                          <option key={setat.id} value={setat.id}>
                            {setat.titre}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                {([1, 2, 7].includes(user?.fonction)) && (
                  <div className="form-group">
                    <label htmlFor="etat_id_commercial_signer">Commercial :</label>
                    <select
                      id="etat_id_commercial_signer"
                      className="form-control"
                      value={etatFormData.id_commercial}
                      onChange={(e) => setEtatFormData({...etatFormData, id_commercial: e.target.value})}
                      required
                    >
                      <option value="">Sélectionner</option>
                      {commerciaux?.map(com => (
                          <option key={com.id} value={com.id}>
                            {formatCommercialDisplayName(com)}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {([1, 2, 7].includes(user?.fonction)) && (
                  <div className="form-group">
                    <label htmlFor="etat_id_commercial_2_signer">Commercial 2 (optionnel) :</label>
                    <select
                      id="etat_id_commercial_2_signer"
                      className="form-control"
                      value={etatFormData.id_commercial_2}
                      onChange={(e) => setEtatFormData({...etatFormData, id_commercial_2: e.target.value})}
                    >
                      <option value="">Aucun</option>
                      {commerciaux?.map(com => (
                          <option key={com.id} value={com.id}>
                            {formatCommercialDisplayName(com)}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {resolveSignerProduitKind(etatFormData.produit, produits) === 'pac' && (
                  <SignerPacSelect
                    idPrefix="etat"
                    value={etatFormData.ph3_pac}
                    onChange={(e) => setEtatFormData({ ...etatFormData, ph3_pac: e.target.value })}
                    required
                  />
                )}

                <div className="form-group">
                  <label htmlFor="etat_ph3_attente_signer">Financement :</label>
                  <select
                    id="etat_ph3_attente_signer"
                    className="form-control"
                    value={etatFormData.ph3_attente}
                    onChange={(e) => setEtatFormData({...etatFormData, ph3_attente: e.target.value})}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {typesFinancement.filter(t => t.etat !== 0).map(t => (
                      <option key={t.id} value={t.nom}>{t.nom}</option>
                    ))}
                  </select>
                </div>

                <SignerProduitPacPvFields
                  idPrefix="etat"
                  kind={resolveSignerProduitKind(etatFormData.produit, produits)}
                  etatFormData={etatFormData}
                  setEtatFormData={setEtatFormData}
                  required
                />

                <div className="form-group">
                  <label htmlFor="etat_ph3_prix_signer">Prix En € :</label>
                  <input
                    type="number"
                    id="etat_ph3_prix_signer"
                    className="form-control"
                    value={etatFormData.ph3_prix}
                    onChange={(e) => setEtatFormData({...etatFormData, ph3_prix: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="etat_credit_immobilier_signer">Crédit immobilier :</label>
                  <input
                    type="number"
                    id="etat_credit_immobilier_signer"
                    className="form-control"
                    value={etatFormData.credit_immobilier}
                    onChange={(e) => setEtatFormData({...etatFormData, credit_immobilier: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="etat_credit_autre_signer">Autre crédit :</label>
                  <input
                    type="number"
                    id="etat_credit_autre_signer"
                    className="form-control"
                    value={etatFormData.credit_autre}
                    onChange={(e) => setEtatFormData({...etatFormData, credit_autre: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="etat_ph3_installateur_signer">Installateur :</label>
                  <select
                    id="etat_ph3_installateur_signer"
                    className="form-control"
                    value={etatFormData.ph3_installateur}
                    onChange={(e) => setEtatFormData({...etatFormData, ph3_installateur: e.target.value})}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {installateurs?.map(inst => (
                      <option key={inst.id} value={inst.id}>
                        {inst.nom}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="etat_conf_consommations_signer">
                    Consommation annuelle
                    <br />
                    ancien système :
                  </label>
                  <input
                    type="number"
                    id="etat_conf_consommations_signer"
                    className="form-control"
                    value={etatFormData.conf_consommations}
                    onChange={(e) => setEtatFormData({...etatFormData, conf_consommations: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="etat_valeur_mensualite_signer">Partie à financer du client :</label>
                  <input
                    type="number"
                    id="etat_valeur_mensualite_signer"
                    className="form-control"
                    value={etatFormData.valeur_mensualite}
                    onChange={(e) => setEtatFormData({...etatFormData, valeur_mensualite: e.target.value})}
                    required
                  />
                </div>

                <SignerBonusAnnonceSelect
                  idPrefix="etat"
                  kind={resolveSignerProduitKind(etatFormData.produit, produits)}
                  value={etatFormData.ph3_bonus_30}
                  onChange={(e) => setEtatFormData({ ...etatFormData, ph3_bonus_30: e.target.value })}
                  required
                />

                <div className="form-group">
                  <label htmlFor="etat_ph3_mensualite_signer">Mensualité du Crédit :</label>
                  <input
                    type="number"
                    id="etat_ph3_mensualite_signer"
                    className="form-control"
                    value={etatFormData.ph3_mensualite}
                    onChange={(e) => setEtatFormData({...etatFormData, ph3_mensualite: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="etat_nbr_annee_finance_signer">Nombre de mois du crédit :</label>
                  <input
                    type="number"
                    id="etat_nbr_annee_finance_signer"
                    className="form-control"
                    value={etatFormData.nbr_annee_finance}
                    onChange={(e) => setEtatFormData({...etatFormData, nbr_annee_finance: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="etat_date_sign_datetime">Date et heure signature :</label>
                  <input
                    type="datetime-local"
                    id="etat_date_sign_datetime"
                    className="form-control"
                    value={getDateTimeLocalValue(etatFormData.date_sign_date, etatFormData.date_sign_time)}
                    onChange={(e) => {
                      const [date = '', time = ''] = e.target.value.split('T');
                      setEtatFormData({ ...etatFormData, date_sign_date: date, date_sign_time: time });
                    }}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="etat_conf_commentaire_signer">Compte rendu :</label>
                  {!hasPermission('compte_rendu_write') && (
                    <div className="alert alert-info" style={{ marginBottom: '10px', padding: '8px', fontSize: '0.77em' }}>
                      <FaInfoCircle /> Vous n'avez pas la permission de rédiger un compte rendu.
                    </div>
                  )}
                  <textarea
                    id="etat_conf_commentaire_signer"
                    className="form-control"
                    rows="4"
                    value={etatFormData.conf_commentaire_produit}
                    onChange={(e) => setEtatFormData({...etatFormData, conf_commentaire_produit: e.target.value})}
                    disabled={!hasPermission('compte_rendu_write')}
                    placeholder="Saisissez votre compte rendu..."
                    required
                  />
                </div>

                <div className="form-actions">
                  <button className="btn-confirm" onClick={handleEtatSubmit} disabled={etatSubmitting || isChangementEtatBloque}>{etatSubmitting ? 'Enregistrement…' : 'Enregistrer'}</button>
                  <button className="btn-cancel" onClick={() => {
                    setSelectedEtat(null);
                    if (user?.fonction === 5) {
                      setCompteRenduOption('');
                    }
                    setEtatFormData({
                      ...etatFormData,
                      date_sign_date: '', date_sign_time: '', produit: '', id_sous_etat: '', id_commercial: '', 
                      id_commercial_2: '', pseudo: '', ph3_pac: 'reau', ph3_rr_model: '', ph3_puissance: '', 
                      ph3_ballon: '', ph3_marque_ballon: '', ph3_alimentation: '', ph3_type: '', ph3_prix: '', 
                      ph3_installateur: '', conf_consommations: '', ph3_bonus_30: '', valeur_mensualite: '', 
                      ph3_mensualite: '', ph3_attente: '', nbr_annee_finance: '', credit_immobilier: '', 
                      credit_autre: '', conf_commentaire_produit: ''
                    });
                  }}>Annuler</button>
                </div>
              </div>
            )}

            {/* Formulaire SIGNER RETRACTER (états 16, 38) */}
            {[16, 38].includes(selectedEtat) && (
              <div className="etat-form" style={{ marginTop: '20px' }}>
                {([1, 2, 7].includes(user?.fonction)) && (
                  <div className="form-group">
                    <label htmlFor="etat_id_commercial_retracter">Commercial :</label>
                    <select
                      id="etat_id_commercial_retracter"
                      className="form-control"
                      value={etatFormData.id_commercial}
                      onChange={(e) => setEtatFormData({...etatFormData, id_commercial: e.target.value})}
                    >
                      <option value="">Sélectionner</option>
                      {commerciaux?.map(com => (
                          <option key={com.id} value={com.id}>
                            {formatCommercialDisplayName(com)}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {([1, 2, 7].includes(user?.fonction)) && (
                  <div className="form-group">
                    <label htmlFor="etat_id_commercial_2_retracter">Commercial 2 (optionnel) :</label>
                    <select
                      id="etat_id_commercial_2_retracter"
                      className="form-control"
                      value={etatFormData.id_commercial_2}
                      onChange={(e) => setEtatFormData({...etatFormData, id_commercial_2: e.target.value})}
                    >
                      <option value="">Aucun</option>
                      {commerciaux?.map(com => (
                          <option key={com.id} value={com.id}>
                            {formatCommercialDisplayName(com)}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="etat_conf_commentaire_retracter">Commentaire :</label>
                  <textarea
                    id="etat_conf_commentaire_retracter"
                    className="form-control"
                    rows="4"
                    value={etatFormData.conf_commentaire_produit}
                    onChange={(e) => setEtatFormData({...etatFormData, conf_commentaire_produit: e.target.value})}
                  />
                </div>

                <div className="form-actions">
                  <button className="btn-confirm" onClick={handleEtatSubmit} disabled={etatSubmitting || isChangementEtatBloque}>{etatSubmitting ? 'Enregistrement…' : 'Enregistrer'}</button>
                  <button className="btn-cancel" onClick={() => {
                    setSelectedEtat(null);
                    setEtatFormData({...etatFormData, id_commercial: '', id_commercial_2: '', conf_commentaire_produit: ''});
                  }}>Annuler</button>
                </div>
              </div>
            )}

            {/* Formulaire Honoré à suivre (état 9) */}
            {selectedEtat === 9 && (
              <div className="etat-form" style={{ marginTop: '20px' }}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="etat_date_rappel_9">A Rappeler le :</label>
                    <input
                      type="date"
                      id="etat_date_rappel_9"
                      className="form-control"
                      value={etatFormData.date_rappel_date}
                      onChange={(e) => setEtatFormData({...etatFormData, date_rappel_date: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="etat_date_rappel_time_9">Heure :</label>
                    <input
                      type="time"
                      id="etat_date_rappel_time_9"
                      className="form-control"
                      value={etatFormData.date_rappel_time}
                      onChange={(e) => setEtatFormData({...etatFormData, date_rappel_time: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Date d&apos;appel :</label>
                  <div className="form-control" style={{ backgroundColor: '#f8f9fa' }}>
                    {new Date().toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}
                  </div>
                  <small style={{ color: '#6c757d' }}>Remplie automatiquement à l&apos;enregistrement</small>
                </div>
                {isAdminSessionHonoreSuivre && (
                  <div className="form-group">
                    <label htmlFor="etat_commercial_9">Commercial :</label>
                    <select
                      id="etat_commercial_9"
                      className="form-control"
                      value={etatFormData.id_commercial}
                      onChange={(e) => setEtatFormData({...etatFormData, id_commercial: e.target.value})}
                    >
                      <option value="">Sélectionner</option>
                      {(commerciaux || []).filter(c => c.etat > 0 || c.etat == null).map(c => (
                        <option key={c.id} value={c.id}>{formatCommercialDisplayName(c) || c.id}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="etat_conf_commentaire_9">Commentaire :</label>
                  <textarea
                    id="etat_conf_commentaire_9"
                    className="form-control"
                    rows="4"
                    value={etatFormData.conf_commentaire_produit}
                    onChange={(e) => setEtatFormData({...etatFormData, conf_commentaire_produit: e.target.value})}
                  />
                </div>
                <div className="form-actions">
                  <button className="btn-confirm" onClick={handleEtatSubmit} disabled={etatSubmitting || isChangementEtatBloque}>{etatSubmitting ? 'Enregistrement…' : 'Enregistrer'}</button>
                  <button className="btn-cancel" onClick={() => {
                    setSelectedEtat(null);
                    setCompteRenduOption('');
                    setEtatFormData({...etatFormData, date_rappel_date: '', date_rappel_time: '', id_commercial: '', conf_commentaire_produit: ''});
                  }}>Annuler</button>
                </div>
              </div>
            )}

            {/* États avec commentaire (motif_qualif) : annuler, refus, hors cible, etc. */}
            {[5, 6, 11, 12, 22, 23, 24, 25, 26, 29, 34].includes(selectedEtat) && (
              <div className="etat-form" style={{ marginTop: '20px' }}>
                {[11, 12].includes(selectedEtat) && sousEtats.length > 0 && (
                  <div className="form-group">
                    <label htmlFor="etat_id_sous_etat_11_12">Sous-état (facultatif) :</label>
                    <select
                      id="etat_id_sous_etat_11_12"
                      className="form-control"
                      value={etatFormData.id_sous_etat || ''}
                      onChange={(e) => setEtatFormData({ ...etatFormData, id_sous_etat: e.target.value })}
                    >
                      <option value="">—</option>
                      {sousEtats.map((se) => (
                        <option key={se.id} value={String(se.id)}>{se.titre}</option>
                      ))}
                    </select>
                  </div>
                )}
                {selectedEtat === 34 && (
                  <div className="form-group">
                    <label htmlFor="etat_conf_rdv_avec_34">Appel avec qui :</label>
                    <select
                      id="etat_conf_rdv_avec_34"
                      className="form-control"
                      value={etatFormData.conf_rdv_avec || ''}
                      onChange={(e) => setEtatFormData({...etatFormData, conf_rdv_avec: e.target.value})}
                    >
                      <option value="">Sélectionner</option>
                      <option value="MR">MR</option>
                      <option value="MME">MME</option>
                      <option value="AUTRE">AUTRE</option>
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="etat_conf_commentaire_simple">
                    Commentaire * :
                  </label>
                  <textarea
                    id="etat_conf_commentaire_simple"
                    className="form-control"
                    rows="4"
                    value={etatFormData.motif_qualif}
                    onChange={(e) => setEtatFormData({...etatFormData, motif_qualif: e.target.value})}
                  />
                </div>
                <div className="form-actions">
                  <button className="btn-confirm" onClick={handleEtatSubmit} disabled={etatSubmitting || isChangementEtatBloque}>{etatSubmitting ? 'Enregistrement…' : 'Enregistrer'}</button>
                  <button className="btn-cancel" onClick={() => {
                    setSelectedEtat(null);
                    setCompteRenduOption('');
                    setEtatFormData({...etatFormData, conf_commentaire_produit: '', motif_qualif: '', conf_rdv_avec: '', id_sous_etat: ''});
                  }}>Annuler</button>
                </div>
              </div>
            )}

            {/* HC (55) : sous-état (motif) obligatoire */}
            {Number(selectedEtat) === 55 && (
              <div className="etat-form" style={{ marginTop: '20px' }}>
                <div className="form-group">
                  <label htmlFor="etat_id_sous_etat_hc">Sous-état HC *</label>
                  <select
                    id="etat_id_sous_etat_hc"
                    className="form-control"
                    value={etatFormData.id_sous_etat || ''}
                    onChange={(e) => {
                      const nextId = e.target.value;
                      const se = (sousEtats || []).find((s) => String(s.id) === String(nextId));
                      const titre = String(se?.titre || '').trim();
                      setEtatFormData({
                        ...etatFormData,
                        id_sous_etat: nextId,
                        motif_qualif:
                          titre.toUpperCase() === 'AUTRE'
                            ? ''
                            : getCommentPrefillFromSousEtatTitre(titre) || etatFormData.motif_qualif,
                      });
                    }}
                  >
                    <option value="">-- Sélectionner un sous-état --</option>
                    {sousEtats.map((se) => (
                      <option key={se.id} value={String(se.id)}>{se.titre}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="etat_commentaire_hc">
                    Commentaire
                    {String(
                      (sousEtats || []).find((se) => String(se.id) === String(etatFormData.id_sous_etat))?.titre || ''
                    )
                      .trim()
                      .toUpperCase() === 'AUTRE' ? (
                      <span> *</span>
                    ) : null}
                  </label>
                  <textarea
                    id="etat_commentaire_hc"
                    className="form-control"
                    rows="4"
                    value={etatFormData.motif_qualif}
                    onChange={(e) => setEtatFormData({ ...etatFormData, motif_qualif: e.target.value })}
                    placeholder={
                      String(
                        (sousEtats || []).find((se) => String(se.id) === String(etatFormData.id_sous_etat))?.titre || ''
                      )
                        .trim()
                        .toUpperCase() === 'AUTRE'
                        ? 'Précisez le motif…'
                        : 'Commentaire (optionnel)…'
                    }
                  />
                </div>
                <div className="form-actions">
                  <button
                    className="btn-confirm"
                    onClick={handleEtatSubmit}
                    disabled={etatSubmitting || isChangementEtatBloque || !etatFormData.id_sous_etat}
                  >
                    {etatSubmitting ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                  <button
                    className="btn-cancel"
                    onClick={() => {
                      setSelectedEtat(null);
                      setCompteRenduOption('');
                      setEtatFormData({
                        ...etatFormData,
                        conf_commentaire_produit: '',
                        motif_qualif: '',
                        id_sous_etat: '',
                      });
                    }}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Bouton Enregistrer pour les autres états sans formulaire spécifique */}
            {selectedEtat && 
             selectedEtat !== 7 && 
             selectedEtat !== 2 && 
             selectedEtat !== 8 && 
             selectedEtat !== 19 && 
             ![5, 6, 9, 11, 12, 13, 16, 22, 23, 24, 25, 26, 29, 34, 38, 44, 45, 55].includes(selectedEtat) && (
              <div className="form-actions" style={{ marginTop: '20px' }}>
                <button
                  className="btn-confirm"
                  onClick={handleEtatSubmit}
                  disabled={etatSubmitting || isChangementEtatBloque}
                >
                  {etatSubmitting ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setSelectedEtat(null);
                    setCompteRenduOption('');
                  }}
                >
                  Annuler
                </button>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Section de validation - seulement pour les fiches confirmées (état 7) et si permission accordée */}
        {false && fiche.id_etat_final === 7 && hasPermission('fiche_validate') && (
          <div className="fiche-section validation-section-bottom">
            <h2 className="section-title">Validation de la fiche</h2>
            {fiche.valider > 0 ? (
              <div className="validation-info">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                  <span className="validation-badge validated">✓ Validée</span>
                  {fiche.validateur_pseudo && (
                    <span className="validation-with">Validé par : {fiche.validateur_pseudo}</span>
                  )}
                  {fiche.conf_rdv_avec && (
                    <span className="validation-with">Avec qui : {fiche.conf_rdv_avec}</span>
                  )}
                  {fiche.conf_presence_couple && (
                    <span className="validation-with">Présence couple : {fiche.conf_presence_couple}</span>
                  )}
                </div>
                <button
                  className="btn-validate cancel"
                  onClick={() => {
                    if (window.confirm('Voulez-vous annuler la validation de cette fiche ?')) {
                      validateMutation.mutate({ type_valid: '0' });
                    }
                  }}
                  disabled={validateMutation.isLoading}
                  title="Annuler la validation"
                >
                  Annuler la validation
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label htmlFor="conf_rdv_avec_validation" style={{ fontWeight: '600', fontSize: '13px' }}>
                    Avec qui le RDV a-t-il été validé ? (optionnel)
                  </label>
                  <select
                    id="conf_rdv_avec_validation"
                    value={confRdvAvecValue || ''}
                    onChange={(e) => setConfRdvAvecValue(e.target.value)}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="MR">Mr</option>
                    <option value="MME">Mme</option>
                    <option value="MR et MME">Mr et Mme</option>
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label htmlFor="conf_presence_couple_validation" style={{ fontWeight: '600', fontSize: '13px' }}>
                    Présence du couple (optionnel)
                  </label>
                  <select
                    id="conf_presence_couple_validation"
                    value={confPresenceCoupleValue || ''}
                    onChange={(e) => setConfPresenceCoupleValue(e.target.value)}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="RAS PRESENCE CLIENT(S)">RAS PRESENCE CLIENT(S)</option>
                    <option value="MME SEULE SANS MR">MME SEULE SANS MR</option>
                    <option value="MR SEUL SANS MME">MR SEUL SANS MME</option>
                  </select>
                </div>
                <button
                  className="btn-validate"
                  onClick={() => {
                    validateMutation.mutate({ 
                      type_valid: `1${confRdvAvecValue ? '-' + confRdvAvecValue : ''}`,
                      conf_rdv_avec: confRdvAvecValue || null,
                      conf_presence_couple: confPresenceCoupleValue || null
                    });
                  }}
                  disabled={validateMutation.isLoading}
                  title="Valider la fiche confirmée"
                >
                  {validateMutation.isLoading ? 'Validation...' : 'Valider la fiche'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Formulaire de décalage de RDV — déplacé tout en bas de la page détails fiche */}
        {hasPermission('decalage_create') && 
         user.fonction !== 5 &&
         user.fonction !== 6 && 
         user.fonction !== 3 && 
         ficheData && !([13, 16, 38, 45, 44].includes(ficheData.id_etat_final)) &&
         ficheData.date_rdv_time && (
          <div className="fiche-section decalage-form" style={{ marginTop: '24px' }}>
            <h2 className="section-title" style={{ 
              background: '#9cbfc8', 
              color: '#fff', 
              padding: '10px', 
              textAlign: 'center',
              marginBottom: '0',
              fontSize: '13.6px',
              fontWeight: 'bold'
            }}>
              Demande de décalage
            </h2>
            
            {decalagesAffichables.length > 0 && (
              <div className="decalage-existing-list" style={{ 
                border: '1px solid #e0e0e0', 
                borderTop: 'none', 
                padding: '15px',
                background: '#f9f9f9',
                marginBottom: '10px'
              }}>
                <h3 style={{ marginTop: '0', marginBottom: '10px', fontSize: '11.9px', fontWeight: 'bold' }}>
                  Demande de décalage ({decalagesAffichables.length})
                </h3>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {decalagesAffichables.map((decalage, index) => (
                    <div key={decalage.id || index} className="decalage-existing-item" style={{ 
                      background: '#fff', 
                      padding: '10px', 
                      marginBottom: '8px', 
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}>
                      <div style={{ fontSize: '10.2px', color: '#666' }}>
                        <strong>Demande #{index + 1}</strong> - 
                        Créée le: {decalage.date_creation ? new Date(decalage.date_creation).toLocaleString('fr-FR') : 'N/A'}
                      </div>
                      <div style={{ fontSize: '10.2px', marginTop: '5px' }}>
                        <strong>Nouvelle date:</strong> {decalage.date_nouvelle ? formatRdvDateTime(decalage.date_nouvelle) : (decalage.date_prevu ? formatRdvDateTime(decalage.date_prevu) : 'N/A')}
                      </div>
                      {decalage.message && (
                        <div style={{ fontSize: '10.2px', marginTop: '5px', fontStyle: 'italic', color: '#555' }}>
                          "{decalage.message}"
                        </div>
                      )}
                      {decalage.etat_dec && (
                        <div style={{ fontSize: '10.2px', marginTop: '5px' }}>
                          <strong>État:</strong> {decalage.etat_dec}
                        </div>
                      )}
                      {canAnnulerDecalageEnAttente(decalage) && (
                        <div style={{ marginTop: '8px' }}>
                          <button
                            type="button"
                            className="btn-secondary decalage-annuler-demande-btn"
                            onClick={() => {
                              if (!window.confirm('Supprimer définitivement cette demande de décalage ?')) return;
                              cancelDecalageMutation.mutate(decalage.id);
                            }}
                            disabled={cancelDecalageMutation.isLoading}
                          >
                            {cancelDecalageMutation.isLoading ? 'Annulation…' : 'Annuler la demande'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="decalage-new-form" style={{ 
              border: '1px solid #e0e0e0', 
              borderTop: 'none', 
              padding: '15px',
              background: '#fff'
            }}>
              <div className="form-group">
                <label htmlFor="select_minutes_bottom">Décalage de :</label>
                <select
                  id="select_minutes_bottom"
                  className="form-control"
                  value={decalageFormData.select_minutes}
                  onChange={(e) => {
                    const minutes = parseInt(e.target.value, 10);
                    const dateRdvOriginale = ficheData?.date_rdv_time || decalageFormData.date_prevu || '';
                    const sameMode = isSameDateDecalageSelection(e.target.value);
                    if (!sameMode && minutes > 0 && dateRdvOriginale) {
                      try {
                        const formattedNewDate = addMinutesToDateTimeString(dateRdvOriginale, minutes);
                        if (!formattedNewDate) {
                          alert('Erreur : la date de rendez-vous originale est invalide.');
                          return;
                        }
                        setDecalageFormData(prev => ({
                          ...prev,
                          select_minutes: e.target.value,
                          nouvelle_date: formattedNewDate,
                          date_prevu: dateRdvOriginale
                        }));
                      } catch (error) {
                        alert('Erreur lors du calcul de la nouvelle date. Veuillez réessayer.');
                      }
                    } else {
                      setDecalageFormData(prev => ({
                        ...prev,
                        select_minutes: e.target.value,
                        nouvelle_date: '',
                        date_prevu: dateRdvOriginale
                      }));
                    }
                  }}
                >
                  <option value="0">SANS DUREE</option>
                  <option value="10">10 MIN</option>
                  <option value="15">15 MIN</option>
                  <option value="20">20 MIN</option>
                  <option value="25">25 MIN</option>
                  <option value="30">30 MIN</option>
                  <option value="35">35 MIN</option>
                  <option value="40">40 MIN</option>
                  <option value="45">45 MIN</option>
                  <option value="50">50 MIN</option>
                  <option value="55">55 MIN</option>
                  <option value="60">1 HEURE</option>
                  <option value="75">1H15</option>
                  <option value="90">1H30</option>
                  <option value="105">1H45</option>
                  <option value="120">2 HEURES</option>
                </select>
              </div>

              {!isSameDateDecalageSelection(decalageFormData.select_minutes) && decalageFormData.nouvelle_date && (
                <div className="form-group decalage-date-preview" style={{ 
                  background: '#e8f5e9', 
                  padding: '10px', 
                  borderRadius: '4px',
                  marginBottom: '15px',
                  border: '2px solid #4caf50'
                }}>
                  <strong>📅 Nouvelle date/heure :</strong> 
                  <span style={{ display: 'block', marginTop: '5px', fontSize: '13.6px', fontWeight: 'bold', color: '#2e7d32' }}>
                    {formatRdvDateTime(decalageFormData.nouvelle_date)}
                  </span>
                  {ficheData?.date_rdv_time && (
                    <div className="decalage-date-originale" style={{ marginTop: '8px', fontSize: '10.2px', color: '#666', fontStyle: 'italic' }}>
                      Date originale : {formatRdvDateTime(ficheData.date_rdv_time)}
                    </div>
                  )}
                </div>
              )}

              {user.fonction === 5 && (
                <div className="form-group">
                  <label htmlFor="id_confirmateur_dec_bottom">Confirmateur :</label>
                  {ficheData?.id_confirmateur ? (
                    <div className="decalage-readonly-value" style={{ background: '#f0f0f0', padding: '10px', borderRadius: '4px', fontWeight: 'bold' }}>
                      {confirmateurs?.find(c => c.id === ficheData.id_confirmateur)?.pseudo || `ID: ${ficheData.id_confirmateur}`}
                    </div>
                  ) : (
                    <div className="decalage-warning-text" style={{ color: 'red', fontStyle: 'italic' }}>
                      Aucun confirmateur assigné à cette fiche. Veuillez assigner un confirmateur avant de créer un décalage.
                    </div>
                  )}
                </div>
              )}

              {isAdminOrBackofficeFonction(user.fonction) && (
                <div className="form-group">
                  <label htmlFor="id_confirmateur_dec_bottom">Confirmateur :</label>
                  <select
                    id="id_confirmateur_dec_bottom"
                    className="form-control"
                    value={decalageFormData.id_confirmateur || (fiche?.id_confirmateur ? String(fiche.id_confirmateur) : '')}
                    onChange={(e) => {
                      setDecalageFormData({...decalageFormData, id_confirmateur: e.target.value});
                    }}
                    required
                  >
                    <option value="">SÉLECTIONNER UN CONFIRMATEUR</option>
                    {confirmateurs?.map(conf => (
                      <option key={conf.id} value={conf.id}>
                        {conf.pseudo}
                      </option>
                    ))}
                  </select>
                  {!decalageFormData.id_confirmateur && !fiche?.id_confirmateur && (
                    <small style={{ color: '#666', fontStyle: 'italic' }}>
                      Sélectionnez un confirmateur depuis la liste
                    </small>
                  )}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="message_dec_bottom">Message du décalage :</label>
                <textarea
                  id="message_dec_bottom"
                  className="form-control"
                  rows="4"
                  value={decalageFormData.message}
                  onChange={(e) => {
                    setDecalageFormData({...decalageFormData, message: e.target.value});
                  }}
                  placeholder="Saisissez le message expliquant le décalage..."
                />
              </div>

              <div className="form-actions" style={{ textAlign: 'center', marginTop: '15px' }}>
                <button
                  className="btn-confirm"
                  onClick={handleDecalageSubmit}
                  disabled={decalageMutation.isLoading}
                  style={{
                    display: 'table',
                    width: 'max-content',
                    margin: '0 auto',
                    borderRadius: '7px',
                    fontWeight: 'bold',
                    padding: '10px 20px'
                  }}
                >
                  {decalageMutation.isLoading ? 'Envoi...' : 'Demande de décalage'}
                </button>
              </div>
            </div>
          </div>
        )}

        <FicheCompletudeSection
          ficheHash={hash}
          enabled={showCompletudeSection}
          canCreate={isQualiteConfirmation}
          canTreat={canTreatCompletudeDetail}
        />
      </div>
        </>
      )}

      {/* Onglet Modifica */}
      {activeTab === 'modifica' && !isPartenaireModalLimitedTabs && (
        <ModificaTab ficheHash={hash} />
      )}

      {/* Onglet Affectation - Affecter la fiche à un ou deux commerciaux */}
      {activeTab === 'affectation' && showAffectationTab && !isPartenaireModalLimitedTabs && (
        <div className="fiche-section affectation-tab" style={{ padding: '20px' }}>
          <h2 className="section-title"><FaUserPlus /> Affectation commerciale</h2>
          <p style={{ color: '#333', marginBottom: '8px', fontWeight: 700 }}>
            Commercial affecte: {commercialAffecteNom || 'Aucun'}
          </p>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Affectez cette fiche à un commercial.
          </p>
          <div className="form-row" style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '400px' }}>
            <div className="form-group">
              <label htmlFor="affectation-commercial">Commercial</label>
              <select
                id="affectation-commercial"
                className="form-control"
                value={affectationCommercial}
                onChange={(e) => setAffectationCommercial(e.target.value)}
              >
                <option value="">— Aucun —</option>
                {(commerciaux || []).filter(c => c.etat > 0 || c.etat == null).map(c => (
                  <option key={c.id} value={String(c.id)}>{formatCommercialDisplayName(c) || c.id}</option>
                ))}
              </select>
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn-save"
                disabled={affectationSaving}
                onClick={saveAffectationFromTab}
              >
                {affectationSaving ? 'Enregistrement...' : 'Enregistrer l\'affectation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onglet Planning - Masqué pour qualité qualification */}
      {activeTab === 'planning' && !isQualiteQualif && (!isPartenaireModalLimitedTabs || partenaireCanFixerRdv) && (
        <PlanningTab
          ficheHash={hash}
          ficheData={ficheData}
          planningWeek={planningWeek}
          planningYear={planningYear}
          planningDep={planningDep}
          setPlanningWeek={setPlanningWeek}
          setPlanningYear={setPlanningYear}
          setPlanningDep={setPlanningDep}
          onSelectSlot={canOpenConfirmEtatFromPlanning ? handleOpenConfirmEtatFromPlanningSlot : null}
          getUserColor={getUserColor}
          getUserName={getUserName}
          getAvailabilityColor={getAvailabilityColor}
          TIME_SLOTS={TIME_SLOTS}
          user={user}
        />
      )}

      {/* Onglet SMS - Masqué pour qualité qualification */}
      {activeTab === 'sms' && !isQualiteQualif && !isPartenaireModalLimitedTabs && (
        <SMSTab
          ficheHash={hash}
          ficheData={ficheData}
        />
      )}

      {/* Onglet PDF */}
      {activeTab === 'pdf' && !isPartenaireModalLimitedTabs && (
        <div className="pdf-tab" style={{ padding: '20px' }}>
          <div style={{ 
            background: '#f5f5f5', 
            padding: '20px', 
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h2 style={{ marginTop: 0, color: '#2c3e50' }}>
              <FaFilePdf style={{ marginRight: '10px' }} />
              Génération de PDF
            </h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Cliquez sur le bouton ci-dessous pour générer et télécharger un PDF contenant toutes les informations de la fiche.
            </p>
            <button
              onClick={generatePDF}
              style={{
                background: '#e74c3c',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '5px',
                fontSize: '13.6px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <FaFilePdf /> Générer le PDF
            </button>
          </div>
          
          {fiche && (
            <div style={{ 
              background: 'white', 
              padding: '15px', 
              borderRadius: '5px',
              border: '1px solid #ddd'
            }}>
              <h3 style={{ color: '#2c3e50', marginTop: 0 }}>Aperçu des informations</h3>
              <p><strong>Client:</strong> {fiche.nom} {fiche.prenom}</p>
              <p><strong>Téléphone:</strong> {fiche.tel}</p>
              <p><strong>Produit:</strong> {fiche.produit_nom || (fiche.produit === 1 ? 'PAC' : fiche.produit === 2 ? 'PV' : '-')}</p>
              <p><strong>État:</strong> {fiche.etat_titre || '-'}</p>
            </div>
          )}
        </div>
      )}

      {/* Modal de création de RDV */}
      {showRdvModal && selectedSlot && (
        <CreateRdvModal
          ficheData={ficheData}
          user={user}
          selectedSlot={selectedSlot}
          rdvFormData={rdvFormData}
          setRdvFormData={setRdvFormData}
          confirmateurs={confirmateurs}
          commerciaux={commerciaux}
          showRdvConfirmateur2={showRdvConfirmateur2}
          setShowRdvConfirmateur2={setShowRdvConfirmateur2}
          showRdvConfirmateur3={showRdvConfirmateur3}
          setShowRdvConfirmateur3={setShowRdvConfirmateur3}
          onClose={() => {
            setShowRdvModal(false);
            setSelectedSlot(null);
          }}
          onSubmit={handleCreateRdvFromForm}
          rdvSubmitting={rdvSubmitting}
        />
      )}

      {slotCodeModal && createPortal(
        <CodeVerificationModal
          title="Créneau fermé, blindé ou en surplus"
          message={
            <>
              <strong>Attention :</strong>{' '}
              {slotCodeModal.surplus && (slotCodeModal.closed || slotCodeModal.policyClosed)
                ? 'ce créneau est fermé et déjà blindé (plein, ex. 2/2) ou en surplus.'
                : slotCodeModal.surplus
                  ? 'ce créneau est déjà blindé (plein, ex. 2/2) ou en surplus de RDV.'
                  : slotCodeModal.closed && slotCodeModal.zero
                    ? 'ce créneau est fermé et sa disponibilité est à 0.'
                    : slotCodeModal.closed || slotCodeModal.policyClosed
                      ? 'ce créneau du planning est fermé.'
                      : 'ce créneau a une disponibilité à 0 (ou non saisie).'}
              {' '}L&apos;insertion d&apos;un rendez-vous n&apos;est pas recommandée.
              Pour continuer, reproduisez le code à 4 chiffres ci-dessous.
            </>
          }
          onVerified={handleSlotCodeVerified}
          onCancel={() => {
            pendingAfterSlotCodeRef.current = null;
            slotCodeVerifiedRef.current = false;
            setSlotCodeModal(null);
          }}
        />,
        document.body
      )}

      {decalageAcceptModal && createPortal(
        <div
          className="fiche-decalage-accept-modal-overlay"
          onClick={() => {
            if (!updateDecalageStatutMutation.isLoading) setDecalageAcceptModal(null);
          }}
        >
          <form
            className="fiche-decalage-accept-modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitDecalageAcceptModal}
          >
            {decalageAcceptModal.step === 'appliquer' ? (
              <>
                <h2>Appliquer à la fiche ?</h2>
                <p>
                  Date saisie :{' '}
                  <strong>
                    {formatRdvDateTime(`${decalageAcceptDate} ${decalageAcceptTime}:00`)}
                  </strong>
                  <br />
                  Enregistrer cette date sur la fiche ?
                </p>
                <div className="fiche-decalage-accept-modal-actions">
                  <button
                    type="button"
                    className="fiche-decalage-accept-modal-cancel"
                    onClick={() =>
                      setDecalageAcceptModal((prev) => (prev ? { ...prev, step: 'saisie' } : prev))
                    }
                    disabled={updateDecalageStatutMutation.isLoading}
                  >
                    Retour
                  </button>
                  <button
                    type="button"
                    className="fiche-decalage-accept-modal-no"
                    onClick={() => confirmDecalageAcceptModal(false)}
                    disabled={updateDecalageStatutMutation.isLoading}
                  >
                    Non
                  </button>
                  <button
                    type="button"
                    className="fiche-decalage-accept-modal-confirm"
                    onClick={() => confirmDecalageAcceptModal(true)}
                    disabled={updateDecalageStatutMutation.isLoading}
                  >
                    {updateDecalageStatutMutation.isLoading ? 'Enregistrement…' : 'Oui'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2>RDV accepté par le client</h2>
                <p>
                  La demande est sans heure. Saisissez la date et l&apos;heure du RDV accepté.
                </p>
                <div className="fiche-decalage-accept-modal-fields">
                  <label>
                    Date
                    <input
                      type="date"
                      value={decalageAcceptDate}
                      min={formatLocalYmd()}
                      onChange={(e) => setDecalageAcceptDate(e.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Heure
                    <input
                      type="time"
                      value={decalageAcceptTime}
                      onChange={(e) => setDecalageAcceptTime(e.target.value)}
                      required
                    />
                  </label>
                </div>
                <div className="fiche-decalage-accept-modal-actions">
                  <button
                    type="button"
                    className="fiche-decalage-accept-modal-cancel"
                    onClick={() => setDecalageAcceptModal(null)}
                    disabled={updateDecalageStatutMutation.isLoading}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="fiche-decalage-accept-modal-confirm"
                    disabled={
                      updateDecalageStatutMutation.isLoading ||
                      !decalageAcceptDate ||
                      !decalageAcceptTime
                    }
                  >
                    Continuer
                  </button>
                </div>
              </>
            )}
          </form>
        </div>,
        document.body
      )}
    </div>
  );
};

// Libellés français pour les noms de champs techniques stockés dans modifica.type
const MODIFICA_TYPE_LABELS = {
  // Identité
  civ: 'Civilité',
  nom: 'Nom',
  prenom: 'Prénom',
  tel: 'Téléphone',
  gsm1: 'GSM 1',
  gsm2: 'GSM 2',
  email: 'Email',
  adresse: 'Adresse',
  cp: 'Code postal',
  ville: 'Ville',
  // Situation
  situation_conjugale: 'Situation conjugale',
  age_mr: 'Âge Monsieur',
  age_madame: 'Âge Madame',
  nb_enfants: "Nombre d'enfants",
  // Profession / contrat
  profession_mr: 'Profession Monsieur',
  profession_madame: 'Profession Madame',
  type_contrat_mr: 'Type contrat Monsieur',
  type_contrat_madame: 'Type contrat Madame',
  // Finance
  revenu_foyer: 'Revenu du foyer',
  credit_foyer: 'Crédit du foyer',
  conf_revenu: 'Revenu',
  conf_credit: 'Crédit',
  // Logement
  proprietaire_maison: 'Propriétaire',
  surface_habitable: 'Surface habitable',
  surface_chauffee: 'Surface chauffée',
  annee_systeme_chauffage: 'Année système chauffage',
  mode_chauffage: 'Mode de chauffage',
  complement_chauffage: 'Complément de chauffage',
  consommation_chauffage: 'Consommation chauffage',
  consommation_electricite: 'Consommation électricité',
  circuit_eau: "Circuit d'eau",
  nb_pieces: 'Nombre de pièces',
  nb_pans: 'Nombre de pans',
  orientation_toiture: 'Orientation toiture',
  site_classe: 'Site classé',
  zones_ombres: "Zones d'ombre",
  // Étude / produit
  produit: 'Produit',
  etude: 'Déjà fait une étude',
  etude_raison: "Raison de l'étude",
  // Affectations
  id_centre: 'Centre',
  id_agent: 'Agent',
  id_confirmateur: 'Confirmateur',
  id_confirmateur_2: 'Confirmateur 2',
  id_confirmateur_3: 'Confirmateur 3',
  id_commercial: 'Commercial',
  id_commercial_2: 'Commercial 2',
  // États & dates
  id_etat_final: 'État',
  id_sous_etat: 'Sous-état',
  date_rdv_time: 'Date RDV',
  date_appel_time: "Date d'appel",
  date_appel: "Date d'appel",
  date_rappel_time: 'Date rappel',
  date_sign_time: 'Date signature',
  is_urgent: 'RDV urgent',
  // Validation / qualité
  valider: 'Validation',
  ko: 'KO',
  hc: 'HC',
  archive: 'Archivée',
  cq_etat: 'Contrôle qualité — État',
  cq_dossier: 'Contrôle qualité — Dossier',
  observations_cq: 'Contrôle qualité — Observations',
  commentaire_qualite: 'Commentaire qualité',
  commentaire: 'Commentaire',
  // Confirmation
  conf_rdv_avec: 'RDV pris avec',
  conf_appel_tunisie_avec: 'Appel Tunisie avec',
  details_etude: 'Détails étude',
  conf_deja_fait_etude: 'A déjà fait une étude',
  conf_details_etude: 'Détails étude',
  conf_deja_etude: 'A déjà fait une étude',
  conf_rdv_annule_precedent: 'RDV déjà annulé précédemment',
  conf_presence_couple: 'Présence du couple',
  rdv_seul: 'Présence du couple / absence couple',
  conf_mode_chauffage: 'Mode chauffage',
  conf_complement_chauffage: 'Complément chauffage',
  conf_consommation_electricite: 'Consommation électricité',
  conf_consommation_chauffage: 'Consommation chauffage',
  conf_orientation_toiture: 'Orientation toiture',
  conf_zones_ombres: "Zones d'ombre",
  conf_site_classe: 'Site classé',
  conf_commentaire_produit: 'Commentaire confirmateur',
  conf_profession_monsieur: 'Profession Monsieur',
  conf_profession_madame: 'Profession Madame',
  conf_type_contrat_mr: 'Type contrat Monsieur',
  conf_type_contrat_madame: 'Type contrat Madame',
  // Étiquettes spéciales
  'Compte rendu': 'Compte rendu',
  SMS: 'SMS',
};

function modificaTypeLabel(type) {
  if (type == null || type === '') return '-';
  return MODIFICA_TYPE_LABELS[type] || String(type);
}

const MODIFICA_DATE_FIELDS = new Set([
  'date_rdv_time', 'date_appel_time', 'date_appel',
  'date_rappel_time', 'date_sign_time', 'date_modif_time',
  'date_insert_time', 'date_creation'
]);

const MODIFICA_BOOLEAN_FIELDS = new Set([
  'rdv_urgent', 'is_urgent', 'rdv_seul', 'archive', 'ko', 'hc'
]);

// Formate n'importe quelle valeur de date (ISO "2026-05-11 09:00:00",
// JS Date.toString() "Mon May 11 2026 09:30:00 GMT+0000 (...)", ou ISO "2026-05-11T09:00:00.000Z")
// en "JJ/MM/AAAA HH:MM" sans dépendre du fuseau horaire local.
function formatModificaDate(val) {
  if (val == null) return '-';
  const s = String(val).trim();
  if (s === '') return '-';

  // ISO "YYYY-MM-DD HH:MM[:SS]" ou "YYYY-MM-DDTHH:MM[:SS]"
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{1,2}):(\d{2})/);
  if (m) {
    const [, y, mo, d, h, mi] = m;
    return `${d}/${mo}/${y} ${h.padStart(2, '0')}:${mi}`;
  }
  // Date au format JS "Mon May 11 2026 09:30:00 GMT+0000 (...)"
  if (/^[A-Za-z]{3}\s+[A-Za-z]{3}\s+\d{1,2}\s+\d{4}/.test(s)) {
    const dt = new Date(s);
    if (!isNaN(dt.getTime())) {
      const dd = String(dt.getUTCDate()).padStart(2, '0');
      const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const yy = dt.getUTCFullYear();
      const hh = String(dt.getUTCHours()).padStart(2, '0');
      const mn = String(dt.getUTCMinutes()).padStart(2, '0');
      return `${dd}/${mm}/${yy} ${hh}:${mn}`;
    }
  }
  // Date seule "YYYY-MM-DD"
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const [, y, mo, d] = m;
    return `${d}/${mo}/${y}`;
  }
  return s;
}

function modificaValueDisplay(val, type, label) {
  if (val == null) return '-';
  const s = String(val).trim();
  if (s === '') return '-';

  // Affectations / état / produit / centre : le backend fournit un libellé
  if (label != null && String(label).trim() !== '') {
    return String(label).toUpperCase();
  }

  // Champs de date
  if (type && MODIFICA_DATE_FIELDS.has(type)) {
    return formatModificaDate(s);
  }

  // Champs booléens
  if (type && MODIFICA_BOOLEAN_FIELDS.has(type)) {
    if (s === '0') return 'Non';
    if (s === '1') return 'Oui';
  }

  // Cas particulier : champ "validation" (string "Valider"/"Non Valider")
  if (type === 'valider' || type === 'validation') {
    if (s === '0') return 'Non Valider';
    if (s === '1') return 'Valider';
  }

  // Comportement par défaut conservé
  if (s === '0') return 'Non';
  if (s === '1') return 'Oui';
  return s;
}

// Composant pour l'onglet Modifica
const ModificaTab = ({ ficheHash }) => {
  const { data: modificaData, isLoading, error } = useQuery(
    ['modifica', ficheHash],
    async () => {
      const res = await api.get(`/fiches/${ficheHash}/modifica`);
      if (res.data.success) {
        return res.data.data || [];
      } else {
        console.warn('Réponse modifica sans succès:', res.data);
        return [];
      }
    },
    { 
      enabled: !!ficheHash,
      retry: 1, // Réessayer une fois en cas d'erreur
      onError: (err) => {
        console.error('Erreur lors de la récupération des modifications:', err);
        console.error('Détails de l\'erreur:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          url: err.config?.url
        });
      }
    }
  );

  if (isLoading) return <div className="loading">Chargement de l'historique...</div>;
  
  if (error) {
    return (
      <div className="modifica-tab">
        <h2>Historique des modifications</h2>
        <div className="error">
          <p><strong>Erreur lors du chargement des modifications</strong></p>
          <p>{error.message}</p>
          {error.response && (
            <p>Status: {error.response.status} - {error.response.statusText}</p>
          )}
          {error.response?.data?.message && (
            <p>Message serveur: {error.response.data.message}</p>
          )}
          <p className="help-text">Vérifiez la console pour plus de détails.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modifica-tab">
      <h2>Historique des modifications</h2>
      {modificaData && modificaData.length > 0 ? (
        <table className="modifica-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Utilisateur</th>
              <th>Type</th>
              <th>Ancienne valeur</th>
              <th>Nouvelle valeur</th>
            </tr>
          </thead>
          <tbody>
            {modificaData.map(mod => (
              <tr key={mod.id}>
                <td>{mod.date_modif_time ? new Date(mod.date_modif_time).toLocaleString('fr-FR') : '-'}</td>
                <td>{mod.user_pseudo || '-'}</td>
                <td>{modificaTypeLabel(mod.type)}</td>
                <td className="modifica-value">{modificaValueDisplay(mod.ancien_valeur, mod.type, mod.ancien_valeur_label)}</td>
                <td className="modifica-value">{modificaValueDisplay(mod.nouvelle_valeur, mod.type, mod.nouvelle_valeur_label)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="no-modifications">
          <p>Aucune modification enregistrée.</p>
          <p className="help-text">Les modifications seront affichées ici après chaque modification de la fiche.</p>
        </div>
      )}
    </div>
  );
};

// Composant pour l'onglet Planning
const PlanningTab = ({ 
  ficheHash, 
  ficheData, 
  planningWeek, 
  planningYear, 
  planningDep, 
  setPlanningWeek, 
  setPlanningYear, 
  setPlanningDep,
  onSelectSlot,
  getUserColor,
  getUserName,
  getAvailabilityColor,
  TIME_SLOTS,
  user
}) => {
  const queryClient = useQueryClient();
  
  // Vérifier si l'utilisateur peut éditer (uniquement fonction 1)
  const canEdit = user?.fonction === 1;
  /** Confirmateur (6), RE confirmation (14), RP confirmation (13), backoffice (11) : lien dashboard par créneau */
  const sessionCanOpenSlotDashboard = [6, 14, 13, 11].includes(Number(user?.fonction));
  /** Bouton + sur le planning : uniquement confirmateur (6), RP confirmation (13), RE confirmation (14). */
  const canCreateRdvFromSlot = [6, 13, 14].includes(Number(user?.fonction));
  
  const { data: planningResponse, isLoading: isLoadingPlanning, refetch: refetchPlanning } = useQuery(
    ['planning-modal', planningWeek, planningYear, planningDep],
    async () => {
      if (!planningWeek || !planningYear || !planningDep) return null;
      const res = await api.get('/planning/week', { params: { w: planningWeek, y: planningYear, dp: planningDep } });
      return res.data;
    },
    { 
      enabled: !!planningDep && !!planningWeek && !!planningYear,
      staleTime: 60000,
      refetchInterval: 60000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true
    }
  );

  const { data: availabilityResponse, isLoading: isLoadingAvailability, refetch: refetchAvailability } = useQuery(
    ['availability-modal', planningWeek, planningYear, planningDep],
    async () => {
      if (!planningWeek || !planningYear || !planningDep) return null;
      const res = await api.get('/planning/availability', { params: { w: planningWeek, y: planningYear, dp: planningDep } });
      return res.data;
    },
    { 
      enabled: !!planningDep && !!planningWeek && !!planningYear,
      staleTime: 60000,
      refetchInterval: 60000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true
    }
  );
  const { data: planningAlertsResponse } = useQuery(
    ['planning-alerts-modal', planningDep, Number(user?.fonction), planningWeek, planningYear],
    async () => {
      if (!planningDep || !planningWeek || !planningYear) return { data: [] };
      const res = await api.get('/planning-alerts', {
        params: {
          dep: planningDep,
          viewer_fonction: Number(user?.fonction) || undefined,
          viewer_week: planningWeek,
          viewer_year: planningYear,
        },
      });
      return res.data;
    },
    {
      enabled: !!(planningDep && planningWeek && planningYear),
      staleTime: 60000,
      refetchInterval: 60000,
      refetchOnWindowFocus: false
    }
  );

  const planningData = planningResponse?.data || {};
  const availabilityData = availabilityResponse?.data || {};
  const planningAlertsData = planningAlertsResponse?.data || [];
  
  // Rafraîchir automatiquement les données à l'ouverture de l'onglet Planning
  useEffect(() => {
    if (planningDep && planningWeek && planningYear) {
      refetchPlanning();
      refetchAvailability();
    }
  }, [planningDep, planningWeek, planningYear]); // au montage / changement dep-semaine-année
  
  // Mutation pour modifier la disponibilité (définie après les queries pour accéder aux refetch)
  const updateAvailabilityMutation = useMutation(
    async ({ week, year, dep, date, hour, value, type }) => {
      const res = await api.put('/planning/availability', { week, year, dep, date, hour, value, type });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['planning-modal']);
        queryClient.invalidateQueries(['availability-modal']);
        queryClient.invalidateQueries(['planning-hebdomadaire', planningYear, planningWeek]);
        queryClient.invalidateQueries(['planning-hebdomadaire']);
        // Rafraîchir explicitement les données
        setTimeout(() => {
          refetchPlanning();
          refetchAvailability();
        }, 100);
      },
      onError: (error) => {
        console.error('Erreur modification disponibilité:', error);
        console.error('Error details:', error.response?.data);
        alert(error.response?.data?.message || 'Erreur lors de la modification');
      }
    }
  );
  
  // Handler pour mettre à jour la disponibilité
  const handleUpdateAvailability = (date, hour, value, type = 'hour') => {
    if (!planningWeek || !planningYear || !planningDep) {
      console.warn('Missing planning parameters:', { planningWeek, planningYear, planningDep });
      return;
    }
    
    // Formater l'heure correctement (HH:MM:SS)
    let hourFormatted = hour;
    if (hour && !hour.includes(':')) {
      hourFormatted = `${hour}:00:00`;
    } else if (hour && hour.split(':').length === 2) {
      hourFormatted = `${hour}:00`;
    }
    
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 0) {
      alert('Valeur invalide');
      return;
    }
    
    updateAvailabilityMutation.mutate({
      week: planningWeek,
      year: planningYear,
      dep: planningDep,
      date,
      hour: hourFormatted,
      value: numValue,
      type
    });
  };
  
  const getMondayOfWeek = (year, week) => {
    const simple = new Date(year, 0, 1 + (week - 1) * 7);
    const dow = simple.getDay();
    const ISOweekStart = simple;
    if (dow <= 4) {
      ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    } else {
      ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    }
    return ISOweekStart;
  };

  const getDaysFromPlanning = () => {
    if (!planningWeek || !planningYear) return [];
    const monday = getMondayOfWeek(planningYear, planningWeek);
    const daysFr = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
    const days = [];
    
    // Extraire les composants du lundi pour éviter les problèmes de fuseau horaire
    const mondayYear = monday.getFullYear();
    const mondayMonth = monday.getMonth();
    const mondayDay = monday.getDate();
    
    for (let i = 0; i < 5; i++) {
      // Créer la date directement avec les composants (évite les problèmes de fuseau horaire)
      const date = new Date(mondayYear, mondayMonth, mondayDay + i);
      days.push({
        date: formatDateLocal(date),
        dayName: daysFr[i]
      });
    }
    return days;
  };

  const handlePrevWeek = () => {
    if (planningWeek === 1) {
      setPlanningYear(planningYear - 1);
      setPlanningWeek(52);
    } else {
      setPlanningWeek(planningWeek - 1);
    }
  };

  const handleNextWeek = () => {
    if (planningWeek === 52) {
      setPlanningYear(planningYear + 1);
      setPlanningWeek(1);
    } else {
      setPlanningWeek(planningWeek + 1);
    }
  };

  const formatWeekRange = () => {
    const days = getDaysFromPlanning();
    if (days.length === 0) return '';
    // Parser les dates en heure locale pour éviter les problèmes de fuseau horaire
    const parseDateLocal = (dateStr) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    const start = parseDateLocal(days[0].date);
    const end = parseDateLocal(days[days.length - 1].date);
    return `${start.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} au ${end.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
  };

  const days = getDaysFromPlanning();

  return (
    <div className="planning-tab">
      <div className="planning-tab-header">
        <h2>
          Planning - Département {planningDep || 'N/A'}
          {ficheData?.cp && (
            <span className="planning-dep-info"> (Code postal: {ficheData.cp})</span>
          )}
        </h2>
        <div className="planning-week-navigation">
          <button onClick={handlePrevWeek} className="nav-btn" title="Semaine précédente">
            <FaChevronLeft />
          </button>
          <span className="week-info">
            Semaine {planningWeek} - {formatWeekRange()}
          </span>
          <button onClick={handleNextWeek} className="nav-btn" title="Semaine suivante">
            <FaChevronRight />
          </button>
        </div>
      </div>
      <div className="planning-tab-body">
        {!planningDep ? (
          <div className="error">
            Impossible de déterminer le département à partir du code postal de la fiche.
            {ficheData?.cp && <p>Code postal actuel: {ficheData.cp}</p>}
          </div>
        ) : isLoadingPlanning || isLoadingAvailability ? (
          <div className="loading">Chargement du planning pour le département {planningDep}...</div>
        ) : planningResponse && Object.keys(planningData).length > 0 ? (
          <PlanningViewForModal
            planning={planningData}
            availability={availabilityData}
            planningAlerts={planningAlertsData}
            days={days}
            timeSlots={TIME_SLOTS}
            getUserColor={getUserColor}
            getUserName={getUserName}
            getAvailabilityColor={getAvailabilityColor}
            dep={planningDep}
            week={planningWeek}
            year={planningYear}
            onSelectSlot={canCreateRdvFromSlot && onSelectSlot ? (date, hour) => onSelectSlot(date, hour, null, availabilityData) : null}
            onUpdateAvailability={handleUpdateAvailability}
            canEdit={canEdit}
            currentFicheHash={ficheHash}
            sessionCanOpenSlotDashboard={sessionCanOpenSlotDashboard}
            user={user}
          />
        ) : (
          <div className="error">Aucun planning disponible pour le département {planningDep}</div>
        )}
      </div>
    </div>
  );
};

// Composant pour l'onglet SMS
const SMSTab = ({ ficheHash, ficheData }) => {
  const { user } = useAuth();
  const [selectedTel, setSelectedTel] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('0');
  const [customMessage, setCustomMessage] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState('+33'); // France par défaut
  
  const queryClient = useQueryClient();
  
  // Liste des indicatifs téléphoniques
  const countryCodes = [
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+216', country: 'Tunisie', flag: '🇹🇳' },
    { code: '+212', country: 'Maroc', flag: '🇲🇦' },
    { code: '+213', country: 'Algérie', flag: '🇩🇿' },
    { code: '+1', country: 'États-Unis/Canada', flag: '🇺🇸' },
    { code: '+32', country: 'Belgique', flag: '🇧🇪' },
    { code: '+41', country: 'Suisse', flag: '🇨🇭' },
    { code: '+44', country: 'Royaume-Uni', flag: '🇬🇧' },
    { code: '+49', country: 'Allemagne', flag: '🇩🇪' },
    { code: '+34', country: 'Espagne', flag: '🇪🇸' },
    { code: '+39', country: 'Italie', flag: '🇮🇹' },
    { code: '+351', country: 'Portugal', flag: '🇵🇹' },
    { code: '+352', country: 'Luxembourg', flag: '🇱🇺' },
    { code: '+377', country: 'Monaco', flag: '🇲🇨' },
    { code: '+221', country: 'Sénégal', flag: '🇸🇳' },
    { code: '+225', country: 'Côte d\'Ivoire', flag: '🇨🇮' },
    { code: '+229', country: 'Bénin', flag: '🇧🇯' },
    { code: '+226', country: 'Burkina Faso', flag: '🇧🇫' },
    { code: '+227', country: 'Niger', flag: '🇳🇪' },
    { code: '+228', country: 'Togo', flag: '🇹🇬' },
    { code: '+230', country: 'Maurice', flag: '🇲🇺' },
    { code: '+262', country: 'La Réunion', flag: '🇷🇪' },
    { code: '+590', country: 'Guadeloupe', flag: '🇬🇵' },
    { code: '+594', country: 'Guyane', flag: '🇬🇫' },
    { code: '+596', country: 'Martinique', flag: '🇲🇶' },
    { code: '+687', country: 'Nouvelle-Calédonie', flag: '🇳🇨' },
    { code: '+689', country: 'Polynésie française', flag: '🇵🇫' }
  ];
  
  // Récupérer les catégories SMS depuis l'API
  const { data: smsCategories, isLoading: loadingCategories } = useQuery(
    'sms_categories',
    async () => {
      const res = await api.get('/management/sms-categories');
      return res.data.data || [];
    },
    {
      onError: (error) => {
        console.error('Erreur lors du chargement des catégories SMS:', error);
      }
    }
  );
  
  const { data: smsList, isLoading } = useQuery(
    ['sms', ficheHash],
    async () => {
      const res = await api.get(`/fiches/${ficheHash}/sms`);
      return res.data.data || [];
    },
    { enabled: !!ficheHash }
  );

  const sendSMSMutation = useMutation(
    async (data) => {
      const res = await api.post(`/fiches/${ficheHash}/sms`, data);
      return res.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['sms', ficheHash]);
        queryClient.invalidateQueries(['modifica', ficheHash]);
        const message = data?.message || data?.data?.message || 'SMS envoyé avec succès!';
        alert(message);
        setCustomMessage('');
      },
      onError: (error) => {
        console.error('Erreur:', error);
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.error || 
                            error.message || 
                            'Erreur lors de l\'envoi du SMS';
        alert('Erreur lors de l\'envoi du SMS: ' + errorMessage);
      }
    }
  );

  // Fonction pour remplacer les variables dans le message
  const replaceVariables = React.useCallback((message) => {
    if (!message || !ficheData) {
      return message;
    }
    
    let dateRdvStr = '';
    let heureRdvStr = '';
    
    if (ficheData?.date_rdv_time) {
      try {
        let dateRdvValue = ficheData.date_rdv_time;
        // Affichage indépendant du fuseau horaire : utiliser les utilitaires qui lisent la chaîne telle quelle
        if (typeof dateRdvValue === 'string') {
          dateRdvStr = formatRdvDateOnly(dateRdvValue);
          heureRdvStr = formatRdvTimeOnly(dateRdvValue);
        } else if (typeof dateRdvValue === 'number') {
          // Timestamp : on ne peut pas garantir l'indépendance TZ sans connaître le TZ de stockage
          const d = new Date(dateRdvValue * 1000);
          if (!isNaN(d.getTime())) {
            dateRdvStr = formatRdvDateOnly(d.toISOString().slice(0, 10));
            heureRdvStr = d.toISOString().slice(11, 16);
          }
        }
      } catch (error) {
        console.error('[SMS Frontend] Erreur lors du formatage de date_rdv_time:', error, ficheData.date_rdv_time);
      }
    }
    
    const processedMessage = message
      .replace(/\{\{prenom\}\}/g, ficheData.prenom?.toUpperCase() || '')
      .replace(/\{\{nom\}\}/g, ficheData.nom?.toUpperCase() || '')
      .replace(/\{\{date_rdv\}\}/g, dateRdvStr)
      .replace(/\{\{heure_rdv\}\}/g, heureRdvStr)
      .replace(/\{\{civ\}\}/g, ficheData.civ || '');
    
    return processedMessage;
  }, [ficheData]);

  // Construire les messages prédéfinis à partir des catégories
  const predefinedMessages = React.useMemo(() => {
    const messages = { '0': '' }; // Message personnalisé
    
    if (smsCategories && Array.isArray(smsCategories)) {
      smsCategories.forEach((cat) => {
        messages[String(cat.id)] = replaceVariables(cat.message);
      });
    }
    
    return messages;
  }, [smsCategories, replaceVariables]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId === '0') {
      setCustomMessage('');
      return;
    }
    // Préremplir le texte du modèle (variables déjà remplacées) — toujours modifiable ensuite
    const selectedCat = smsCategories?.find((cat) => String(cat.id) === String(categoryId));
    const fromTemplate = selectedCat
      ? replaceVariables(selectedCat.message)
      : predefinedMessages[String(categoryId)] || '';
    setCustomMessage(fromTemplate || '');
  };

  const handleSendSMS = () => {
    if (!selectedTel || selectedTel.trim() === '') {
      alert('Veuillez sélectionner un numéro de téléphone');
      return;
    }
    if (!user || !user.id) {
      alert('Utilisateur non connecté');
      return;
    }
    const message = customMessage;
    
    if (!message || message.trim() === '') {
      alert('Veuillez saisir un message');
      return;
    }
    
    // Formater le numéro avec l'indicatif
    let formattedTel = selectedTel.trim();
    // Si le numéro ne commence pas déjà par un indicatif, ajouter celui sélectionné
    if (!formattedTel.startsWith('+') && !formattedTel.startsWith('00')) {
      // Supprimer le 0 initial pour la France si présent
      if (selectedCountryCode === '+33' && formattedTel.startsWith('0')) {
        formattedTel = formattedTel.substring(1);
      }
      formattedTel = selectedCountryCode + formattedTel;
    }
    
    sendSMSMutation.mutate({
      tel: formattedTel,
      message: message.trim(),
      id_confirmateur: user.id
    });
  };

  // Récupérer les numéros disponibles
  const availableTels = React.useMemo(() => [
    { value: ficheData?.tel, label: `Téléphone: ${ficheData?.tel}` },
    { value: ficheData?.gsm1, label: `GSM1: ${ficheData?.gsm1}` },
    { value: ficheData?.gsm2, label: `GSM2: ${ficheData?.gsm2}` }
  ].filter(t => t.value), [ficheData?.tel, ficheData?.gsm1, ficheData?.gsm2]);

  useEffect(() => {
    if (availableTels.length > 0 && !selectedTel) {
      setSelectedTel(availableTels[0].value);
    }
  }, [availableTels, selectedTel]);

  if (isLoading) return <div className="loading">Chargement...</div>;

  return (
    <div className="sms-tab">
      <h2>Envoyer un SMS</h2>
      <div className="sms-form">
        <div className="form-group">
          <label>Indicatif téléphonique :</label>
          <select
            value={selectedCountryCode}
            onChange={(e) => setSelectedCountryCode(e.target.value)}
            className="form-control"
            style={{ marginBottom: '10px' }}
          >
            {countryCodes.map(country => (
              <option key={country.code} value={country.code}>
                {country.flag} {country.code} - {country.country}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Numéro de téléphone :</label>
          <select
            value={selectedTel}
            onChange={(e) => setSelectedTel(e.target.value)}
            className="form-control"
          >
            {availableTels.map(tel => (
              <option key={tel.value} value={tel.value}>{tel.label}</option>
            ))}
          </select>
          {selectedTel && (
            <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
              Numéro formaté : {(() => {
                let formatted = selectedTel.trim();
                if (!formatted.startsWith('+') && !formatted.startsWith('00')) {
                  if (selectedCountryCode === '+33' && formatted.startsWith('0')) {
                    formatted = formatted.substring(1);
                  }
                  formatted = selectedCountryCode + formatted;
                }
                return formatted;
              })()}
            </div>
          )}
        </div>
        <div className="form-group">
          <label>Catégorie de message :</label>
          <select
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="form-control"
          >
            <option value="0">Message personnalisé</option>
            {smsCategories && smsCategories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.titre}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Message :</label>
          <textarea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            className="form-control"
            rows="6"
            placeholder={
              selectedCategory === '0'
                ? 'Saisissez votre message personnalisé'
                : loadingCategories
                  ? 'Chargement des catégories...'
                  : 'Modèle chargé — vous pouvez modifier le texte avant l\'envoi'
            }
          />
          {selectedCategory !== '0' && (
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#666' }}>
              Texte du modèle modifiable avant envoi
            </div>
          )}
        </div>
        <div className="form-actions">
          <button
            className="btn-send-sms"
            onClick={handleSendSMS}
            disabled={sendSMSMutation.isLoading}
          >
            {sendSMSMutation.isLoading ? 'Envoi...' : 'Envoyer SMS'}
          </button>
        </div>
      </div>
      
      <div className="sms-history">
        <h3>Historique des SMS</h3>
        {smsList && smsList.length > 0 ? (
          <table className="sms-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Confirmateur</th>
                <th>Téléphone</th>
                <th>Message</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {smsList.map(sms => (
                <tr key={sms.id}>
                  <td>{sms.date_modif_time ? new Date(sms.date_modif_time).toLocaleString('fr-FR') : '-'}</td>
                  <td>{sms.confirmateur_pseudo || '-'}</td>
                  <td>{sms.tel || '-'}</td>
                  <td className="sms-message">{sms.message || '-'}</td>
                  <td>{sms.statut || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>Aucun SMS envoyé.</p>
        )}
      </div>
    </div>
  );
};

// Composant PlanningView pour le modal (réutilisé depuis Planning.jsx)
const PlanningViewForModal = ({ 
  planning, 
  availability, 
  planningAlerts,
  days, 
  timeSlots, 
  getUserColor, 
  getUserName, 
  getAvailabilityColor, 
  dep,
  week,
  year,
  onSelectSlot,
  onUpdateAvailability,
  canEdit = false,
  currentFicheHash, // Le hash est passé mais on ne peut plus comparer par ID car il est masqué
  sessionCanOpenSlotDashboard = false,
  user = null,
}) => {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editingDayTotal, setEditingDayTotal] = useState(null);
  const [dayTotalValue, setDayTotalValue] = useState('');
  const [pendingAvailabilityEdits, setPendingAvailabilityEdits] = useState({});
  const alertBySlotAndDay = useMemo(() => {
    const map = {};
    (planningAlerts || []).forEach((a) => {
      const slot = String(a.slot_hour || '').slice(0, 8);
      const day = normalizePlanningDayName(a.day_name);
      const key = `${day}|${slot}`;
      if (slot && day && a.message) map[key] = a.message;
    });
    return map;
  }, [planningAlerts]);

  const handleCellDoubleClick = (date, hour, e) => {
    e.stopPropagation();
    if (!canEdit) return;
    // Vérifier si le créneau est fermé
    const availData = availability?.[date]?.[hour];
    const isClosed = availData?.is_closed === 1;
    if (isClosed) return; // Ne pas permettre l'édition si le créneau est fermé
    setEditingCell(`${date}-${hour}`);
    setEditValue('');
  };
  
  const handleSave = (date, hour) => {
    if (editValue === '' || editValue === null || editValue === undefined) {
      setEditingCell(null);
      setEditValue('');
      return;
    }
    const value = parseInt(editValue);
    if (isNaN(value) || value < 0) {
      return;
    }
    if (onUpdateAvailability) {
      onUpdateAvailability(date, hour, value, 'hour');
    }
    setEditingCell(null);
    setEditValue('');
  };
  
  const handleCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Handler pour modifier la disponibilité d'un créneau via le champ texte
  const handleCellAvailabilityChange = (date, hour, value) => {
    const key = `${date}-${hour}`;
    if (value === '' || value === null || value === undefined) {
      setPendingAvailabilityEdits((prev) => ({ ...prev, [key]: '' }));
      return;
    }
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 0) {
      return;
    }
    // Édition locale uniquement : la valeur n'est sauvegardée qu'au clic sur Valider.
    setPendingAvailabilityEdits((prev) => ({ ...prev, [key]: numValue }));
  };

  const handleValidateCellAvailability = (date, hour, fallbackValue = 0) => {
    const key = `${date}-${hour}`;
    const rawValue = pendingAvailabilityEdits[key];
    const valueToSave = rawValue === '' || rawValue === null || rawValue === undefined
      ? 0
      : parseInt(rawValue, 10);
    if (isNaN(valueToSave) || valueToSave < 0) return;
    if (onUpdateAvailability) {
      onUpdateAvailability(date, hour, valueToSave, 'hour');
    }
    setPendingAvailabilityEdits((prev) => {
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleCancelCellAvailability = (date, hour) => {
    const key = `${date}-${hour}`;
    setPendingAvailabilityEdits((prev) => {
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleEditDayTotal = (date) => {
    if (!canEdit) return;
    setEditingDayTotal(date);
    setDayTotalValue('');
  };

  const handleSaveDayTotal = (date) => {
    const value = parseInt(dayTotalValue, 10);
    if (!canEdit || Number.isNaN(value) || value < 0) return;
    if (onUpdateAvailability) {
      timeSlots.forEach((slot) => {
        onUpdateAvailability(date, slot.hour, value, 'day');
      });
    }
    setEditingDayTotal(null);
    setDayTotalValue('');
  };

  return (
    <div className="planning-view">
      <div className="planning-table-container">
        <table className="planning-table">
          <thead>
            <tr>
              <th>Heure</th>
              {days.map(day => (
                <th key={day.date}>
                  <div className="day-header-planning">
                    <span>{day.dayName} {day.date.split('-')[2]}</span>
                    {canEdit && (
                      <div className="day-total-controls">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={editingDayTotal === day.date ? dayTotalValue : ''}
                          onFocus={() => handleEditDayTotal(day.date)}
                          onChange={(e) => {
                            if (editingDayTotal !== day.date) setEditingDayTotal(day.date);
                            setDayTotalValue(String(e.target.value || '').replace(/\D/g, ''));
                          }}
                          className="total-input"
                          placeholder="-"
                        />
                        <button
                          className="save-btn"
                          onClick={() => handleSaveDayTotal(day.date)}
                        >
                          <FaCheck />
                        </button>
                        <button
                          className="cancel-btn"
                          onClick={() => setEditingDayTotal(null)}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map(slot => {
              const timeKey = hourToTimeKey(slot.hour);
              const { main: slotMain, sub: slotSub } = splitSlotDisplayName(slot.name);
              return (
                <tr key={slot.hour}>
                  <td className="time-slot-header planning-time-slot-label">
                    <span className="time-slot-header-main">{slotMain}</span>
                    {slotSub ? <span className="time-slot-header-sub">{slotSub}</span> : null}
                  </td>
                  {days.map(day => {
                    // Le planning est structuré comme planning[date].time[timeKey]
                    const dayPlanning = planning?.[day.date]?.time?.[timeKey];
                    const rdvs = dayPlanning?.planning || [];
                    // Utiliser d'abord la disponibilité du planning, sinon celle de availability
                    const availabilityFromPlanning = dayPlanning?.av ?? null;
                    const availData = availability?.[day.date]?.[slot.hour];
                    const policyClosed = isPolicyClosedAvailabilityCell(availData, dep, day.date, slot.hour);
                    const isPastCalendarDay = isRdvDateBeforeToday(day.date);
                    const canCreateOnThisClosedSlot = !policyClosed || canCreateRdvOnPolicyClosedSlot(user?.fonction);
                    const availabilityCount = availabilityFromPlanning !== null ? availabilityFromPlanning : (availData?.nbr_com ?? null);
                    // availability peut être null (pas de planning créé), 0 (bloqué), ou > 0 (disponible)
                    const hasPlanning = availabilityCount !== null && availabilityCount !== undefined;
                    const isBlocked = availabilityCount === 0;
                    const dbClosed = isDbClosedAvailabilityCell(availData, policyClosed);
                    const isClosed = isSlotVisuallyClosed({
                      policyClosed,
                      dbClosed,
                      availabilityCount: hasPlanning ? availabilityCount : null,
                    });
                    // Un créneau est disponible s'il a un planning avec disponibilité > 0, OU s'il n'a pas de planning mais n'est pas bloqué
                    const isAvailable = (hasPlanning && availabilityCount > 0) || (!hasPlanning && !isBlocked);
                    // Confirmateur / RE / RP : bouton + (même sur créneau fermé / 0 dispo / surplus) → warning + code
                    const showCreateRdvBtn =
                      !!onSelectSlot &&
                      !canEdit &&
                      !isPastCalendarDay &&
                      canCreateOnThisClosedSlot &&
                      (hasPlanning || policyClosed || (isAvailable && !isBlocked && !isClosed));
                    
                    // Total RDV dans le créneau = même liste que l’API (toutes les fiches du slot, toutes heures confondues)
                    const totalRdvInSlot = rdvs.length;
                    // Fiches sans badge historique annulation pure AN / report RS (affichage secondaire / tolérance créneau)
                    const confirmedRdvs = rdvs.filter(rdv => rdv.etat_check !== 'AN' && rdv.etat_check !== 'RS');
                    const confirmedCount = confirmedRdvs.length;
                    // Compter les fiches réellement validées par le confirmateur (valider == 1, accepte nombre/chaîne/boolean)
                    const validatedCount = rdvs.filter(rdv => (Number(rdv.valider) === 1 || rdv.valider === true || rdv.valider === '1')).length;
                    
                    const hasData = hasPlanning || totalRdvInSlot > 0;
                    const displayAvailability = availabilityCount !== null ? availabilityCount : 0;
                    const hasR2Placed = rdvs.some((rdv) => ficheHasR2Placed(rdv));
                    
                    // Couleur du badge : vert si OK, orange si presque plein, rouge si plein (sur le total réel du créneau)
                    let bgColor = '#cccccc';
                    if (hasPlanning && availabilityCount > 0) {
                      bgColor = getAvailabilityColor(totalRdvInSlot, availabilityCount);
                    } else if (totalRdvInSlot > 0) {
                      bgColor = '#e74c3c'; // Rouge si des RDV mais pas de planning
                    }
                    // Fond rouge si blindé/surplus, jaune partiel, vert aucun RDV — texte toujours noir
                    const availabilityBadgeBgColor = (() => {
                      if (totalRdvInSlot <= 0) return '#4caf50';
                      if (!hasPlanning || Number(displayAvailability) <= 0) return '#f44336';
                      if (totalRdvInSlot >= Number(displayAvailability)) return '#f44336';
                      return '#ffc107';
                    })();
                    const availabilityTextColor = '#000000';
                    
                    // Note: L'ID est masqué, on ne peut plus comparer directement
                    // On marque simplement le créneau si on a des RDV
                    const currentFicheInSlot = false;
                    const isEditing = editingCell === `${day.date}-${slot.hour}`;
                    // canEditThis : éditable même si créneau fermé (is_closed=1) pour permettre la réouverture via disponibilité.
                    const canEditThis = canEdit;
                    
                    const dashboardSlotHref =
                      sessionCanOpenSlotDashboard && dep
                        ? buildDashboardUrlForPlanningSlot({ dep, date: day.date, slotHour: slot.hour })
                        : null;
                    const slotAlertKey = `${normalizePlanningDayName(day.dayName)}|${String(slot.hour || '').slice(0, 8)}`;
                    const slotAlertMessage = alertBySlotAndDay[slotAlertKey] || null;

                    return (
                      <td
                        key={`${day.date}-${slot.hour}`}
                        className={`planning-cell ${isBlocked ? 'blocked' : ''} ${isClosed ? 'closed-slot' : ''} ${hasPlanning ? 'has-planning' : ''} ${currentFicheInSlot ? 'current-fiche' : ''} ${hasData ? 'has-data' : ''} ${isAvailable && !hasData ? 'available-slot' : ''}`}
                        style={{ 
                          backgroundColor: (isBlocked || isClosed) ? '#000000' : 'transparent',
                          position: 'relative',
                          cursor: 'default',
                          border: 'none'
                        }}
                        onDoubleClick={(e) => canEditThis && handleCellDoubleClick(day.date, slot.hour, e)}
                        title={
                          (() => {
                            const validatedLabel = hasData && rdvs.length > 0
                              ? `${validatedCount} validées / ${rdvs.length} RDV au créneau${confirmedCount < rdvs.length ? ` (${rdvs.length - confirmedCount} avec historique annul./report)` : ''}`
                              : null;
                            if (isEditing) return 'Modifier la disponibilité';
                            const actionLabel = canEditThis && hasData
                              ? `Double-cliquer pour modifier la disponibilité (${day.dayName} à ${slot.name})`
                              : hasData && hasPlanning && availabilityCount > 0 && !!onSelectSlot
                              ? `Cliquer sur + pour créer un rendez-vous le ${day.dayName} à ${slot.name}`
                              : isBlocked
                              ? 'Créneau bloqué'
                              : isAvailable
                              ? `Créneau disponible (${day.dayName} à ${slot.name})`
                              : 'Créneau non disponible';
                            return validatedLabel ? `${validatedLabel} — ${actionLabel}` : actionLabel;
                          })()
                        }
                      >
                        {dashboardSlotHref && (
                          <a
                            href={dashboardSlotHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="planning-slot-dashboard-link"
                            title="Ouvrir le tableau de bord : RDV de ce créneau (filtres appliqués)"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Voir les RDV de ce créneau sur le tableau de bord"
                          >
                            <FaInfoCircle />
                          </a>
                        )}
                        {slotAlertMessage && (
                          <div
                            className="planning-slot-alert"
                            style={{
                              position: 'absolute',
                              left: '4px',
                              bottom: '4px',
                              background: '#ffeb3b',
                              color: '#333',
                              fontSize: '11px',
                              fontWeight: 700,
                              padding: '2px 6px',
                              borderRadius: '4px',
                              zIndex: 7
                            }}
                            title="Alerte planning"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {slotAlertMessage}
                          </div>
                        )}
                        {/* Badge de disponibilité avec format "X / Y" - TOUJOURS affiché si on a des données */}
                        {isEditing ? (
                          <div className="edit-controls" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={editValue}
                              onChange={(e) => setEditValue(String(e.target.value || '').replace(/\D/g, ''))}
                              className="availability-input"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSave(day.date, slot.hour);
                                } else if (e.key === 'Escape') {
                                  handleCancel();
                                }
                              }}
                              style={{
                                width: '36px',
                                minWidth: '36px',
                                maxWidth: '36px',
                                height: '22px',
                                padding: '0 3px',
                                fontSize: '11.5px',
                                lineHeight: 1,
                                border: '1px solid #ccc',
                                borderRadius: '3px'
                              }}
                            />
                            <button
                              className="save-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSave(day.date, slot.hour);
                              }}
                              style={{
                                padding: '2px 6px',
                                marginLeft: '4px',
                                fontSize: '10px',
                                background: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer'
                              }}
                            >
                              <FaCheck />
                            </button>
                            <button
                              className="cancel-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancel();
                              }}
                              style={{
                                padding: '2px 6px',
                                marginLeft: '2px',
                                fontSize: '10px',
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer'
                              }}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ) : hasData ? (
                          <>
                            <div className="availability-info">
                              <div className="availability-badge" style={{ backgroundColor: availabilityBadgeBgColor, boxShadow: 'none', padding: '2px 6px' }}>
                                <span
                                  className="availability-text-compact"
                                  style={{
                                    color: availabilityTextColor,
                                    WebkitTextFillColor: availabilityTextColor,
                                    fontWeight: 900,
                                    fontSize: '16px'
                                  }}
                                >
                                  {`${totalRdvInSlot}/${displayAvailability}`}
                                </span>
                              </div>
                              {hasR2Placed && (
                                <span
                                  className="availability-r2-star"
                                  title="R2 placé"
                                  aria-label="R2 placé"
                                >
                                  ★
                                </span>
                              )}
                            </div>
                            {showCreateRdvBtn && (
                              <button
                                type="button"
                                className="planning-create-btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onSelectSlot(day.date, slot.hour);
                                }}
                                title={
                                  policyClosed
                                    ? `Créneau fermé par obligation — créer un RDV le ${day.dayName} à ${slot.name} (code requis)`
                                    : `Créer un rendez-vous le ${day.dayName} à ${slot.name}`
                                }
                                aria-label="Créer un rendez-vous"
                                style={{
                                  position: 'absolute',
                                  right: '4px',
                                  bottom: '4px',
                                  width: '34px',
                                  height: '34px',
                                  borderRadius: '50%',
                                  border: 'none',
                                  background: '#8BC34A',
                                  color: '#000',
                                  fontSize: '24px',
                                  fontWeight: 900,
                                  lineHeight: 1,
                                  cursor: 'pointer',
                                  zIndex: 14,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)'
                                }}
                              >
                                +
                              </button>
                            )}
                            {canEditThis && (
                              <div
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.stopPropagation()}
                                style={{
                                  position: 'absolute',
                                  left: '50%',
                                  top: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  zIndex: 14,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '3px'
                                }}
                              >
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  className="availability-input"
                                  value={pendingAvailabilityEdits[`${day.date}-${slot.hour}`] !== undefined
                                    ? pendingAvailabilityEdits[`${day.date}-${slot.hour}`]
                                    : ''}
                                  onChange={(e) => handleCellAvailabilityChange(day.date, slot.hour, String(e.target.value || '').replace(/\D/g, ''))}
                                  placeholder=""
                                  title={
                                    policyClosed
                                      ? 'Créneau fermé par obligation — admin : saisir une disponibilité pour ouvrir'
                                      : 'Saisir la valeur puis cliquer sur Valider'
                                  }
                                  style={{ width: '36px', minWidth: '36px', maxWidth: '36px', height: '22px', padding: '0 3px', fontSize: '11.5px', lineHeight: 1 }}
                                />
                                {pendingAvailabilityEdits[`${day.date}-${slot.hour}`] !== undefined && (
                                  <>
                                    <button
                                      className="save-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleValidateCellAvailability(day.date, slot.hour, availabilityCount ?? 0);
                                      }}
                                      title="Valider"
                                      style={{ padding: '1px 4px', fontSize: '8px', lineHeight: 1 }}
                                    >
                                      <FaCheck />
                                    </button>
                                    <button
                                      className="cancel-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelCellAvailability(day.date, slot.hour);
                                      }}
                                      title="Annuler"
                                      style={{ padding: '1px 4px', fontSize: '8px', lineHeight: 1 }}
                                    >
                                      <FaTimes />
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </>
                        ) : (isAvailable && !isBlocked && !isClosed) || (canEditThis && (policyClosed || isClosed)) || showCreateRdvBtn ? (
                          <>
                            {showCreateRdvBtn && (
                              <button
                                type="button"
                                className="planning-create-btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onSelectSlot(day.date, slot.hour);
                                }}
                                title={
                                  policyClosed
                                    ? `Créneau fermé par obligation — créer un RDV le ${day.dayName} à ${slot.name} (code requis)`
                                    : `Créer un rendez-vous le ${day.dayName} à ${slot.name}`
                                }
                                aria-label="Créer un rendez-vous"
                                style={{
                                  position: 'absolute',
                                  right: '4px',
                                  bottom: '4px',
                                  width: '34px',
                                  height: '34px',
                                  borderRadius: '50%',
                                  border: 'none',
                                  background: '#8BC34A',
                                  color: '#000',
                                  fontSize: '24px',
                                  fontWeight: 900,
                                  lineHeight: 1,
                                  cursor: 'pointer',
                                  zIndex: 14,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)'
                                }}
                              >
                                +
                              </button>
                            )}
                            {canEditThis && (
                              <div
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.stopPropagation()}
                                style={{
                                  position: 'absolute',
                                  left: '50%',
                                  top: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  zIndex: 14,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '3px'
                                }}
                              >
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  className="availability-input"
                                  value={pendingAvailabilityEdits[`${day.date}-${slot.hour}`] !== undefined
                                    ? pendingAvailabilityEdits[`${day.date}-${slot.hour}`]
                                    : ''}
                                  onChange={(e) => handleCellAvailabilityChange(day.date, slot.hour, String(e.target.value || '').replace(/\D/g, ''))}
                                  placeholder=""
                                  title={
                                    policyClosed
                                      ? 'Créneau fermé par obligation — admin : saisir une disponibilité pour ouvrir'
                                      : 'Saisir la valeur puis cliquer sur Valider'
                                  }
                                  style={{ width: '36px', minWidth: '36px', maxWidth: '36px', height: '22px', padding: '0 3px', fontSize: '11.5px', lineHeight: 1 }}
                                />
                                {pendingAvailabilityEdits[`${day.date}-${slot.hour}`] !== undefined && (
                                  <>
                                    <button
                                      className="save-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleValidateCellAvailability(day.date, slot.hour, 0);
                                      }}
                                      title="Valider"
                                      style={{ padding: '1px 4px', fontSize: '8px', lineHeight: 1 }}
                                    >
                                      <FaCheck />
                                    </button>
                                    <button
                                      className="cancel-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelCellAvailability(day.date, slot.hour);
                                      }}
                                      title="Annuler"
                                      style={{ padding: '1px 4px', fontSize: '8px', lineHeight: 1 }}
                                    >
                                      <FaTimes />
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Composant Modal pour créer un RDV
const CreateRdvModal = ({ 
  ficheData, 
  user,
  selectedSlot, 
  rdvFormData, 
  setRdvFormData, 
  confirmateurs,
  commerciaux,
  showRdvConfirmateur2 = false,
  setShowRdvConfirmateur2 = () => {},
  showRdvConfirmateur3 = false,
  setShowRdvConfirmateur3 = () => {},
  onClose, 
  onSubmit,
  rdvSubmitting = false
}) => {
  const isConfirmateurSession = Number(user?.fonction) === 6;
  const nouvelleConfConf1Seul = isNouvelleConfirmationConf1Seul(ficheData);
  const [showRdvConfFields, setShowRdvConfFields] = useState(true);

  const confirmateur1Options = (() => {
    if (isConfirmateurSession && user?.id) {
      const self = (confirmateurs || []).find((c) => Number(c.id) === Number(user.id));
      return self
        ? [self]
        : [{ id: user.id, pseudo: user.pseudo || `ID: ${user.id}` }];
    }
    return confirmateurs || [];
  })();

  // Préremplir tous les champs confirmation vides depuis la fiche (déjà confirmée auparavant)
  useEffect(() => {
    if (!ficheData) return;

    const fromFiche = buildConfFormStateFromFiche(ficheData, user);

    setRdvFormData((prev) => {
      const updates = {};
      let hasChanges = false;

      Object.keys(fromFiche).forEach((key) => {
        if (key === 'conf_rdv_date' || key === 'conf_rdv_time') return;
        const prevVal = prev[key];
        const nextVal = fromFiche[key];
        const prevEmpty = prevVal == null || String(prevVal).trim() === '';
        const nextFilled = nextVal != null && String(nextVal).trim() !== '';
        if (prevEmpty && nextFilled) {
          updates[key] = nextVal;
          hasChanges = true;
        }
      });

      return hasChanges ? { ...prev, ...updates } : prev;
    });

    if (fromFiche.id_confirmateur_2) setShowRdvConfirmateur2(true);
    if (fromFiche.id_confirmateur_3) setShowRdvConfirmateur3(true);
  }, [ficheData, user, setRdvFormData, setShowRdvConfirmateur2, setShowRdvConfirmateur3]);

  const { data: typeContratRdv } = useQuery('type-contrat', async () => {
    const res = await api.get('/management/type-contrat');
    return res.data?.data || res.data || [];
  });

  const applyRdvTypeContratChange = (field, value) => {
    setRdvFormData((prev) => {
      const next = { ...prev, [field]: value };
      next.conf_revenu = resolveConfRevenuAfterTypeContratChange(
        prev.conf_revenu,
        next.conf_type_contrat_mr,
        next.conf_type_contrat_madame,
        typeContratRdv
      );
      return next;
    });
  };

  const { data: produits, isLoading: isLoadingProduits, error: produitsError } = useQuery(
    'produits-modal', 
    async () => {
      try {
        const res = await api.get('/management/produits');
        const produitsData = res.data?.data || res.data || [];
        return produitsData;
      } catch (error) {
        console.error('Erreur lors de la récupération des produits:', error);
        return [];
      }
    },
    {
      enabled: true, // Toujours activer la requête
      staleTime: 5 * 60 * 1000, // Cache pendant 5 minutes
      retry: 2,
    }
  );

  useEffect(() => {
    setShowRdvConfFields(true);
  }, [selectedSlot?.date, selectedSlot?.hour]);

  const dateFormatted = selectedSlot 
    ? formatRdvDateTime(`${selectedSlot.date} ${String(selectedSlot.hour).substring(0, 5)}`)
    : '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content rdv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ color: '#000' }}>Créer un rendez-vous</h2>
          <button className="modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        <div className="modal-body">
          <div className="rdv-form-info">
            <p><strong>Date et heure :</strong> {dateFormatted}</p>
            <p><strong>Fiche :</strong> {ficheData?.nom || ''} {ficheData?.prenom || ''} ({ficheData?.tel || ''})</p>
          </div>

          <form className="rdv-form" onSubmit={async (e) => {
            e.preventDefault();
            if (rdvSubmitting) return;
            const validation = validateCreateRdvForm({
              formData: {
                ...rdvFormData,
                id_confirmateur:
                  isConfirmateurSession && user?.id
                    ? String(user.id)
                    : rdvFormData.id_confirmateur,
              },
              produits,
              requireConfirmateur: true,
              mode: 'create',
            });
            if (!validation.valid) {
              setShowRdvConfFields(true);
              alertCreateRdvValidation(validation.missing);
              return;
            }
            onSubmit(rdvFormData);
          }}>
            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'flex-end' }}>
              {!showRdvConfFields ? (
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowRdvConfFields(true)}
                  title="Afficher les champs conf_"
                >
                  <FaReplyAll size={20} />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowRdvConfFields(false)}
                  title="Réduire les champs conf_"
                >
                  <FaChevronUp size={20} />
                </button>
              )}
            </div>
            <table className="rdv-form-table">
              <tbody>
                {/* RDV urgent */}
                <tr>
                  <td>
                    {(() => {
                      const rdvDateStr = rdvFormData.date_rdv_time ? rdvFormData.date_rdv_time.split(' ')[0] : '';
                      let isAutoUrgent = false;
                      if (rdvDateStr) {
                        const rdvDate = new Date(rdvDateStr);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const tomorrow = new Date(today);
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        const rdvDateOnly = new Date(rdvDate);
                        rdvDateOnly.setHours(0, 0, 0, 0);
                        isAutoUrgent = rdvDateOnly.getTime() === today.getTime() || rdvDateOnly.getTime() === tomorrow.getTime();
                      }
                      const isUrgent = rdvFormData.is_urgent || isAutoUrgent;
                      return <strong>RDV URGENT</strong>;
                    })()}
                  </td>
                  <td>
                    {(() => {
                      const rdvDateStr = rdvFormData.date_rdv_time ? rdvFormData.date_rdv_time.split(' ')[0] : '';
                      let isAutoUrgent = false;
                      if (rdvDateStr) {
                        const rdvDate = new Date(rdvDateStr);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const tomorrow = new Date(today);
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        const rdvDateOnly = new Date(rdvDate);
                        rdvDateOnly.setHours(0, 0, 0, 0);
                        isAutoUrgent = rdvDateOnly.getTime() === today.getTime() || rdvDateOnly.getTime() === tomorrow.getTime();
                      }
                      const isUrgent = rdvFormData.is_urgent || isAutoUrgent;
                      return (
                        <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={isUrgent}
                            onChange={(e) => setRdvFormData({...rdvFormData, is_urgent: e.target.checked, id_etat_final: 7})}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span style={{ fontWeight: 'bold' }}>RDV URGENT</span>
                          {isAutoUrgent && (
                            <span style={{ fontSize: '0.72em', color: '#f44336', fontStyle: 'italic', marginLeft: '8px' }}>
                              (Automatique : RDV aujourd'hui ou demain)
                            </span>
                          )}
                        </label>
                      );
                    })()}
                  </td>
                </tr>
                <tr>
                  <td><label htmlFor="rdv_date">Date RDV *</label></td>
                  <td>
                    <input
                      type="date"
                      id="rdv_date"
                      className="form-control"
                      min={formatLocalYmd()}
                      value={rdvFormData.date_rdv_time ? rdvFormData.date_rdv_time.split(' ')[0] : ''}
                      onChange={(e) => {
                        const time = rdvFormData.date_rdv_time ? rdvFormData.date_rdv_time.split(' ')[1] : '00:00';
                        setRdvFormData({...rdvFormData, date_rdv_time: `${e.target.value} ${time}`});
                      }}
                      required
                    />
                  </td>
                </tr>
                <tr>
                  <td><label htmlFor="rdv_time">Heure RDV *</label></td>
                  <td>
                    <input
                      type="time"
                      id="rdv_time"
                      className="form-control"
                      value={rdvFormData.date_rdv_time ? rdvFormData.date_rdv_time.split(' ')[1]?.substring(0, 5) : ''}
                      onChange={(e) => {
                        const date = rdvFormData.date_rdv_time ? rdvFormData.date_rdv_time.split(' ')[0] : selectedSlot?.date || '';
                        setRdvFormData({...rdvFormData, date_rdv_time: `${date} ${e.target.value}`});
                      }}
                      required
                    />
                  </td>
                </tr>
                <tr>
                  <td><label htmlFor="rdv_is_r2">R2 *</label></td>
                  <td>
                    <select
                      id="rdv_is_r2"
                      className="form-control"
                      value={rdvFormData.is_r2 || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRdvFormData({
                          ...rdvFormData,
                          is_r2: v,
                          ...(v !== 'OUI'
                            ? { date_r1: '', id_commercial_r1: '', commentaire_r1: '' }
                            : {}),
                        });
                      }}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="OUI">OUI</option>
                      <option value="NON">NON</option>
                    </select>
                  </td>
                </tr>
                {rdvFormData.is_r2 === 'OUI' && (
                  <>
                    <tr>
                      <td><label htmlFor="rdv_date_r1">Date R1 *</label></td>
                      <td>
                        <input
                          type="datetime-local"
                          id="rdv_date_r1"
                          className="form-control"
                          value={rdvFormData.date_r1 || ''}
                          onChange={(e) => setRdvFormData({ ...rdvFormData, date_r1: e.target.value })}
                          required
                        />
                      </td>
                    </tr>
                    <tr>
                      <td><label htmlFor="rdv_id_commercial_r1">Commercial R1 *</label></td>
                      <td>
                        <select
                          id="rdv_id_commercial_r1"
                          className="form-control"
                          value={rdvFormData.id_commercial_r1 || ''}
                          onChange={(e) =>
                            setRdvFormData({ ...rdvFormData, id_commercial_r1: e.target.value })
                          }
                          required
                        >
                          <option value="">Sélectionner</option>
                          {(commerciaux || [])
                            .filter((u) => u.etat > 0 || u.etat == null)
                            .map((com) => (
                              <option key={com.id} value={com.id}>
                                {formatCommercialDisplayName(com)}
                              </option>
                            ))}
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td><label htmlFor="rdv_commentaire_r1">Commentaire R1 *</label></td>
                      <td>
                        <textarea
                          id="rdv_commentaire_r1"
                          className="form-control"
                          rows="3"
                          value={rdvFormData.commentaire_r1 || ''}
                          onChange={(e) =>
                            setRdvFormData({ ...rdvFormData, commentaire_r1: e.target.value })
                          }
                          required
                        />
                      </td>
                    </tr>
                  </>
                )}
                <tr>
                  <td><label htmlFor="rdv_produit">Produit *</label></td>
                  <td>
                    {isLoadingProduits ? (
                      <div>Chargement des produits...</div>
                    ) : produitsError ? (
                      <div style={{ color: 'red' }}>Erreur lors du chargement des produits</div>
                    ) : (
                      <select
                        id="rdv_produit"
                        className="form-control"
                        value={rdvFormData.produit}
                        onChange={(e) => setRdvFormData({...rdvFormData, produit: e.target.value})}
                        required
                      >
                        <option value="">Sélectionner un produit</option>
                        {produits && Array.isArray(produits) && produits.length > 0 ? (
                          produits
                            .filter(p => p && (p.etat > 0 || p.etat === undefined))
                            .map(prod => (
                              <option key={prod.id} value={prod.id}>
                                {prod.nom || `Produit ${prod.id}`}
                              </option>
                            ))
                        ) : (
                          <option value="" disabled>Aucun produit disponible</option>
                        )}
                      </select>
                    )}
                    {produits && Array.isArray(produits) && (
                      <small style={{ color: '#666', fontSize: '10.2px' }}>
                        {produits.length} produit(s) disponible(s)
                      </small>
                    )}
                  </td>
                </tr>
                <tr>
                  <td><label htmlFor="rdv_confirmateur">Confirmateur *</label></td>
                  <td>
                    <select
                      id="rdv_confirmateur"
                      className="form-control"
                      value={rdvFormData.id_confirmateur}
                      onChange={(e) => setRdvFormData({...rdvFormData, id_confirmateur: e.target.value})}
                      required
                    >
                      {!isConfirmateurSession && <option value="">Sélectionner</option>}
                      {confirmateur1Options.map(conf => (
                        <option key={conf.id} value={conf.id}>
                          {conf.pseudo}
                        </option>
                      ))}
                    </select>
                    {nouvelleConfConf1Seul && (
                      <small style={{ display: 'block', marginTop: 4, color: '#666', fontSize: '11px' }}>
                        Après refus / rétractation : seule la confirmation du créateur du RDV est enregistrée (pas de 2ᵉ confirmateur).
                      </small>
                    )}
                  </td>
                </tr>
                {!nouvelleConfConf1Seul && (showRdvConfirmateur2 || rdvFormData.id_confirmateur_2) && (
                <tr>
                  <td><label htmlFor="rdv_confirmateur_2">Confirmateur 2 (optionnel)</label></td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        id="rdv_confirmateur_2"
                        className="form-control"
                        value={rdvFormData.id_confirmateur_2}
                        onChange={(e) => setRdvFormData({...rdvFormData, id_confirmateur_2: e.target.value})}
                      >
                        <option value="">Aucun</option>
                        {confirmateurs?.map(conf => (
                          <option key={conf.id} value={conf.id}>
                            {conf.pseudo}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn-cancel"
                        title="Retirer le confirmateur 2"
                        onClick={() => {
                          setShowRdvConfirmateur2(false);
                          setShowRdvConfirmateur3(false);
                          setRdvFormData((prev) => ({ ...prev, id_confirmateur_2: '', id_confirmateur_3: '' }));
                        }}
                        style={{ padding: '6px 10px', flexShrink: 0 }}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </td>
                </tr>
                )}
                {!nouvelleConfConf1Seul && (showRdvConfirmateur3 || rdvFormData.id_confirmateur_3) && (
                <tr>
                  <td><label htmlFor="rdv_confirmateur_3">Confirmateur 3 (optionnel)</label></td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        id="rdv_confirmateur_3"
                        className="form-control"
                        value={rdvFormData.id_confirmateur_3}
                        onChange={(e) => setRdvFormData({...rdvFormData, id_confirmateur_3: e.target.value})}
                      >
                        <option value="">Aucun</option>
                        {confirmateurs?.map(conf => (
                          <option key={conf.id} value={conf.id}>
                            {conf.pseudo}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn-cancel"
                        title="Retirer le confirmateur 3"
                        onClick={() => {
                          setShowRdvConfirmateur3(false);
                          setRdvFormData((prev) => ({ ...prev, id_confirmateur_3: '' }));
                        }}
                        style={{ padding: '6px 10px', flexShrink: 0 }}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </td>
                </tr>
                )}
                {!nouvelleConfConf1Seul && !showRdvConfirmateur3 && !rdvFormData.id_confirmateur_3 && (
                <tr>
                  <td colSpan={2}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!showRdvConfirmateur2 && !rdvFormData.id_confirmateur_2) {
                          setShowRdvConfirmateur2(true);
                        } else {
                          setShowRdvConfirmateur3(true);
                        }
                      }}
                      title={
                        showRdvConfirmateur2 || rdvFormData.id_confirmateur_2
                          ? 'Ajouter un 3ᵉ confirmateur'
                          : 'Ajouter un 2ᵉ confirmateur'
                      }
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'transparent',
                        border: 'none',
                        color: '#0b6e99',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      <FaPlus />
                      {showRdvConfirmateur2 || rdvFormData.id_confirmateur_2
                        ? 'Ajouter un 3ᵉ confirmateur'
                        : 'Ajouter un 2ᵉ confirmateur'}
                    </button>
                  </td>
                </tr>
                )}
                {showRdvConfFields && (
                  <>
                <tr>
                  <td><label htmlFor="rdv_avec">RDV pris avec *</label></td>
                  <td>
                    <select
                      id="rdv_avec"
                      className="form-control"
                      value={rdvFormData.conf_rdv_avec}
                      onChange={(e) => setRdvFormData({...rdvFormData, conf_rdv_avec: e.target.value})}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="MR">MR</option>
                      <option value="MME">MME</option>
                      <option value="AUTRE">AUTRE</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td><label>Appel en Tunisie avec *</label></td>
                  <td>
                    <select
                      className="form-control"
                      value={rdvFormData.conf_appel_tunisie_avec || ''}
                      onChange={(e) => setRdvFormData({...rdvFormData, conf_appel_tunisie_avec: e.target.value})}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="MR">Mr</option>
                      <option value="MME">Mme</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td><label>A déjà fait une étude *</label></td>
                  <td>
                    <select
                      className="form-control"
                      value={rdvFormData.conf_deja_etude || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setRdvFormData({
                          ...rdvFormData,
                          conf_deja_etude: value,
                          conf_deja_fait_etude: value,
                          conf_details_etude: value === 'OUI' ? rdvFormData.conf_details_etude : ''
                        });
                      }}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="OUI">OUI</option>
                      <option value="NON">NON</option>
                    </select>
                  </td>
                </tr>
                {(rdvFormData.conf_deja_etude === 'OUI' || rdvFormData.conf_deja_fait_etude === 'OUI') && (
                <tr>
                  <td><label>Détails étude *</label></td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={rdvFormData.conf_details_etude || ''}
                      onChange={(e) => setRdvFormData({ ...rdvFormData, conf_details_etude: e.target.value })}
                      required
                    />
                  </td>
                </tr>
                )}
                <tr>
                  <td><label>Présence du couple ou célibataire *</label></td>
                  <td>
                    <select
                      className="form-control"
                      value={rdvFormData.conf_presence_couple || ''}
                      onChange={(e) => setRdvFormData({...rdvFormData, conf_presence_couple: e.target.value})}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="RAS PRESENCE CLIENT(S)">RAS PRESENCE CLIENT(S)</option>
                      <option value="MME SEULE SANS MR">MME SEULE SANS MR</option>
                      <option value="MR SEUL SANS MME">MR SEUL SANS MME</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td><label>RDV déjà annulé précédemment *</label></td>
                  <td>
                    <select
                      className="form-control"
                      value={rdvFormData.conf_rdv_annule_precedent || ''}
                      onChange={(e) => setRdvFormData({...rdvFormData, conf_rdv_annule_precedent: e.target.value})}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="OUI">OUI</option>
                      <option value="NON">NON</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td><label>Profession MR</label></td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      autoComplete="off"
                      value={rdvFormData.conf_profession_monsieur || ''}
                      onChange={(e) => setRdvFormData({ ...rdvFormData, conf_profession_monsieur: e.target.value })}
                    />
                  </td>
                </tr>
                <tr>
                  <td><label>Type de Contrat MR</label></td>
                  <td>
                    <select
                      className="form-control"
                      value={rdvFormData.conf_type_contrat_mr || ''}
                      onChange={(e) => applyRdvTypeContratChange('conf_type_contrat_mr', e.target.value)}
                    >
                      <option value="">Sélectionner</option>
                      {(typeContratRdv || []).map(t => (
                        <option key={t.id} value={t.id}>{t.nom}</option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <td><label>Profession MME</label></td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      autoComplete="off"
                      value={rdvFormData.conf_profession_madame || ''}
                      onChange={(e) => setRdvFormData({ ...rdvFormData, conf_profession_madame: e.target.value })}
                    />
                  </td>
                </tr>
                <tr>
                  <td><label>Type de Contrat MME</label></td>
                  <td>
                    <select
                      className="form-control"
                      value={rdvFormData.conf_type_contrat_madame || ''}
                      onChange={(e) => applyRdvTypeContratChange('conf_type_contrat_madame', e.target.value)}
                    >
                      <option value="">Sélectionner</option>
                      {(typeContratRdv || []).map(t => (
                        <option key={t.id} value={t.id}>{t.nom}</option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <td><label>Revenu *</label></td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={rdvFormData.conf_revenu || ''}
                      onChange={(e) => setRdvFormData({...rdvFormData, conf_revenu: e.target.value})}
                      required
                    />
                  </td>
                </tr>
                <tr>
                  <td><label>Crédit *</label></td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={rdvFormData.conf_credit || ''}
                      onChange={(e) => setRdvFormData({...rdvFormData, conf_credit: e.target.value})}
                      required
                    />
                  </td>
                </tr>
                <tr>
                  <td><label>Mode de chauffage *</label></td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={rdvFormData.conf_mode_chauffage || ''}
                      onChange={(e) => setRdvFormData({ ...rdvFormData, conf_mode_chauffage: e.target.value })}
                      autoComplete="off"
                      required
                    />
                  </td>
                    </tr>
                <tr>
                  <td>
                    <label>
                      Consommations électrique
                      {!(
                        Number(rdvFormData.produit) === 1 ||
                        String(produits?.find((p) => String(p.id) === String(rdvFormData.produit))?.nom || '')
                          .toUpperCase()
                          .includes('PAC')
                      ) && ' *'}
                    </label>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={rdvFormData.conf_consommation_electricite || ''}
                      onChange={(e) => setRdvFormData({...rdvFormData, conf_consommation_electricite: e.target.value})}
                      required={
                        !(
                          Number(rdvFormData.produit) === 1 ||
                          String(produits?.find((p) => String(p.id) === String(rdvFormData.produit))?.nom || '')
                            .toUpperCase()
                            .includes('PAC')
                        )
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <label>
                      Consommations chauffage
                      {!(
                        Number(rdvFormData.produit) === 2 ||
                        String(produits?.find((p) => String(p.id) === String(rdvFormData.produit))?.nom || '')
                          .toUpperCase()
                          .includes('PV')
                      ) && ' *'}
                    </label>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={rdvFormData.conf_consommation_chauffage || ''}
                      onChange={(e) => setRdvFormData({...rdvFormData, conf_consommation_chauffage: e.target.value})}
                      required={
                        !(
                          Number(rdvFormData.produit) === 2 ||
                          String(produits?.find((p) => String(p.id) === String(rdvFormData.produit))?.nom || '')
                            .toUpperCase()
                            .includes('PV')
                        )
                      }
                    />
                  </td>
                </tr>

                {/* Champs spécifiques PAC */}
                {(() => {
                  const selectedProduit = produits?.find(p => String(p.id) === String(rdvFormData.produit));
                  const isPAC = selectedProduit?.nom?.toUpperCase().includes('PAC') || rdvFormData.produit === '1';
                  if (!isPAC) return null;
                  return (
                    <>
                      <tr>
                        <td><label htmlFor="rdv_surface_chauffee">Surface chauffée (m²) *</label></td>
                        <td>
                          <input
                            type="number"
                            id="rdv_surface_chauffee"
                            className="form-control"
                            value={rdvFormData.surface_chauffee || ''}
                            onChange={(e) => setRdvFormData({...rdvFormData, surface_chauffee: e.target.value})}
                            required
                          />
                        </td>
                      </tr>
                      <tr>
                        <td><label htmlFor="rdv_consommation_chauffage">Consommation chauffage (€) *</label></td>
                        <td>
                          <input
                            type="text"
                            id="rdv_consommation_chauffage"
                            className="form-control"
                            value={rdvFormData.consommation_chauffage || ''}
                            onChange={(e) => setRdvFormData({...rdvFormData, consommation_chauffage: e.target.value})}
                            required
                          />
                        </td>
                      </tr>
                      <tr>
                        <td><label htmlFor="rdv_annee_systeme">Année système chauffage *</label></td>
                        <td>
                          <input
                            type="number"
                            id="rdv_annee_systeme"
                            className="form-control"
                            value={rdvFormData.annee_systeme_chauffage || ''}
                            onChange={(e) => setRdvFormData({...rdvFormData, annee_systeme_chauffage: e.target.value})}
                            required
                          />
                        </td>
                      </tr>
                    </>
                  );
                })()}
                {/* Champs spécifiques PV */}
                {(() => {
                  const selectedProduit = produits?.find(p => String(p.id) === String(rdvFormData.produit));
                  const isPV = selectedProduit?.nom?.toUpperCase().includes('PV') || rdvFormData.produit === '2';
                  if (!isPV) return null;
                  return (
                    <>
                      <tr>
                        <td><label htmlFor="rdv_orientation">Orientation toiture *</label></td>
                        <td>
                          <input
                            type="text"
                            id="rdv_orientation"
                            className="form-control"
                            value={rdvFormData.conf_orientation_toiture || ''}
                            onChange={(e) => setRdvFormData({...rdvFormData, conf_orientation_toiture: e.target.value})}
                            required
                          />
                        </td>
                      </tr>
                      <tr>
                        <td><label htmlFor="rdv_zones_ombres">Zones ombres *</label></td>
                        <td>
                          <input
                            type="text"
                            id="rdv_zones_ombres"
                            className="form-control"
                            value={rdvFormData.conf_zones_ombres}
                            onChange={(e) => setRdvFormData({...rdvFormData, conf_zones_ombres: e.target.value})}
                            required
                          />
                        </td>
                      </tr>
                      <tr>
                        <td><label htmlFor="rdv_site_classe">Proche d&apos;un site classé *</label></td>
                        <td>
                          <input
                            type="text"
                            id="rdv_site_classe"
                            className="form-control"
                            value={rdvFormData.conf_site_classe || ''}
                            onChange={(e) => setRdvFormData({...rdvFormData, conf_site_classe: e.target.value})}
                            required
                          />
                        </td>
                      </tr>
                    </>
                  );
                })()}
                  </>
                )}
                <tr>
                  <td><label htmlFor="rdv_commentaire">Commentaire Confirmation *</label></td>
                  <td>
                    <textarea
                      id="rdv_commentaire"
                      className="form-control"
                      rows="4"
                      value={rdvFormData.conf_commentaire_produit}
                      onChange={(e) => setRdvFormData({...rdvFormData, conf_commentaire_produit: e.target.value})}
                      required
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Annuler
              </button>
              <button type="submit" className="btn-save" disabled={rdvSubmitting}>
                {rdvSubmitting ? 'Création…' : 'Créer le RDV'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FicheDetail;
